import maplibregl, {
  LayerSpecification,
  Map as MlMap,
  MapLayerMouseEvent,
  MapMouseEvent,
  Point,
} from "maplibre-gl";
import type {
  FramingCamera,
  FramingRect,
  MapFraming,
  MercatorPoint,
} from "../../core/framing";
import {
  FRAMING_MAX_PITCH_DEG,
  FRAMING_MAX_ZOOM,
  constrainViewToLimit,
  coverZoom,
  limitZoomFloor,
  fitZoom,
  metersPerPixelAtZoom as mercatorMetersPerPixel,
  rectCorners,
  resolveFramingGeometry,
  resolveLimitRect,
  resolveMaxZoom,
  resolveStartCamera,
  resolveStartRect,
  shapesExtentPoints,
} from "../../core/framing";
import { generateId } from "../../core/generateId";
import { translateOnGround } from "../../core/groundTranslation";
import { lngLatToMercator, mercatorToLngLat } from "../../loaders/formats/shared";
import { createIcon } from "../../ui/icons";
import { BackgroundMap } from "./BackgroundMap";
import {
  addBackgroundTileLayer,
  applyOsmOptions,
  captureAndStripStyleLayers,
  DEFAULT_STYLE,
  fetchOsmStyleLayers,
  OSM_BUILDING_3D_LAYER_ID,
  removeBackgroundTileLayer,
  resolveOsmOptions,
} from "./backgroundTileLayer";
import { isSvgShapeImage } from "./imageFormat";
import {
  canvasToBlob,
  drawMapCanvasInto,
  drawOverlayImagesInto,
  fitWithin,
  waitForMapIdle,
} from "./mapSnapshot";
import {
  boundsOf,
  circleRingLngLat,
  containQuad,
  containReferenceQuadPx,
  effectiveImageRotationDeg,
  defaultImageRotationDeg,
  outwardExpandedRingPx,
  quadMatrix2d,
  quadMatrix3d,
  rotatePointPx,
  scaleAndRotateQuadPx,
  shapeImageQuad,
  shapeOwnImageRotationDeg,
  shrinkQuadToFitPolygonPx,
  trustedImageDimensions,
  type ImageQuad,
} from "./imageQuad";
import { circleMeasureText, draftMeasureText, rectangleMeasureText } from "../../core/measure";
import { constrainAnglePx, constrainSquarePx } from "./drawingConstraint";
import { extrusionProps, type ExtrusionProps } from "./extrusion";
import { lineWallRing, lineWallThicknessMeters } from "./lineWall";
import { shapeTextRotationDeg } from "./textRotation";
import { startViewBearingDeg } from "./textRotation";
import { hoverPreviewVisualProps } from "./hoverPreview";
import { BORDER_WIDTH_MAX_ZOOM, borderWidthStopsPx, shapeReferenceLatitude } from "./borderWidth";
import type { BorderWidthUnit } from "./borderWidth";
import type {
  DrawAdapter,
  DrawTriggerOptions,
  EdgeInsets,
  FramingGeometry,
  HighlightShapesOptions,
  LngLat,
  MapAdapter,
  MapAdapterEvent,
  MapAdapterEventListener,
  MapViewState,
  OsmBackgroundOptions,
  OsmStyleName,
  RemoteCursor,
  Shape,
  ShapeImage,
  ShapeStyle,
  ShapeText,
  ShapeTextStyle,
  ShapeThemes,
  StartDrawingImageOptions,
  StartDrawingOptions,
  StartEditingMapViewOptions,
  TileLayerName,
  TokaMapColors,
} from "../types";
import {
  buildSnapCandidates,
  DEFAULT_SNAP_THRESHOLDS,
  draftSnapCandidates,
  mergeSnapCandidates,
  nearestPointOnSegment,
  resolveSnap,
  type PixelPoint,
  type SnapCandidates,
  type SnapResult,
  type SnapThresholds,
} from "./snapping";

/** Taille cible (pixels device) d'une image de forme, pour aller en demander une variante adaptée au CDN. */
export interface ImageTargetSize {
  width: number;
  height: number;
}

/**
 * Transforme l'URL source d'une image de forme (`Shape.image.src`) pour demander au CDN une
 * variante adaptée à `target` (typiquement en ajoutant des paramètres de redimensionnement).
 * Optionnel : sans ce hook, l'image est chargée telle quelle (résolution native, potentiellement
 * bien plus grande que nécessaire — voir le commentaire sur `computeImageTargetSize`).
 */
export type ImageUrlResolver = (src: string, target: ImageTargetSize) => string;

export interface MapLibreAdapterOptions {
  resolveImageUrl?: ImageUrlResolver;
  /** Voir `MIN_IMAGE_OVERLAY_SCREEN_PX`. Par défaut : 40. */
  minImageOverlayScreenPx?: number;
  /**
   * Affiche (ou non) les images plaquées sur les formes (`Shape.image`), quelle que soit leur
   * origine — voir `TokaMapConfig.includeImages`. Par défaut : `true`.
   */
  includeImages?: boolean;
  /**
   * Parmi les images affichées (voir `includeImages` ci-dessus, qui reste prioritaire — désactivée,
   * celle-ci n'a plus d'effet), affiche (ou non) celles portées par une forme dont `shapeKind` N'EST
   * PAS `"image"` (issues d'un `icon`/d'une `entity` v1, jamais explicitement configurées comme
   * "l'image de la forme") — voir `TokaMapConfig.includeIndirectImages`. Une forme-image reste
   * toujours affichée quelle que soit cette option. Par défaut : `true`.
   */
  includeIndirectImages?: boolean;
  /**
   * Affiche, pendant le tracé, l'étiquette de mesure qui suit le curseur (longueurs, périmètre,
   * surface, rayon — voir `core/measure`). Par défaut : false.
   */
  showDrawingMeasures?: boolean;
  /**
   * "Puissance de l'aimant" du magnétisme au dessin/à l'édition (voir `src/adapters/maplibre/snapping.ts`) —
   * rayons d'accroche sommet/arête et tolérance des guides d'alignement, en pixels écran. Chaque
   * champ omis garde sa valeur par défaut (`DEFAULT_SNAP_THRESHOLDS`) ; passer des rayons à 0
   * désactive le palier correspondant.
   */
  snapThresholds?: Partial<SnapThresholds>;
  /**
   * Mode édition (voir `TokaMapConfig.editMode`) : active le survol des formes (voir
   * `Shape.hoverStyle`, purement visuel, sans rien changer à `Shape.style`) — sauf sur une
   * forme-image (`shapeKind: "image"`), qui porte déjà sa propre représentation
   * visuelle. Par défaut : false — les formes gardent alors leur seule couleur explicite
   * (`Shape.style`), sans survol. Tout champ absent de `Shape.hoverStyle` (objet absent, partiel, ou
   * même vide) retombe, champ par champ, sur un survol FIXE non configurable (petit fond bleu +
   * bordure `colors.accent`, voir `SHAPE_HOVER_FILL_COLOR` dans `shapesToGeoJSON`/
   * `resolveShapeVisualProps`) : pas de notion de "thème de survol par défaut" configurable, et
   * jamais accidentellement invisible.
   */
  editMode?: boolean;
  /**
   * Autorise l'inclinaison 3D de la caméra (`pitch`) à l'initialisation — voir
   * `TokaMapConfig.defaultAllowPitch`, et `MapAdapter.getAllowPitch`/`setAllowPitch` pour la version
   * dynamique/persistée (même mécanisme que `defaultTileLayerName`/`tileLayerName` : cette option ne
   * sert que de secours, écrasée par `configurations.allowPitch` une fois les données initiales
   * chargées). Par défaut : `false`, la carte reste bloquée à la verticale (`maxPitch: 0`, voir
   * `init()`). À `true` : le clic-droit-glisser (et, hors `editMode`, Ctrl+clic-gauche-glisser)
   * repasse au geste NATIF `dragRotate` de MapLibre (bearing + pitch), au lieu du handler maison
   * `handleMapRotateDragStart` (bearing seul) — voir son commentaire et celui de
   * `isMapRotateTrigger`. En `editMode`, Ctrl+clic-gauche-glisser reste réservé au rectangle de
   * sélection multiple : `dragRotate` natif est désactivé/réactivé ponctuellement autour de ce geste
   * précis (voir le `mousedown` du rectangle de sélection et `handleSelectionDrawEnd`), MapLibre
   * n'ayant pas de moyen de lier `dragRotate` au seul bouton droit.
   */
  defaultAllowPitch?: boolean;
  /**
   * Style appliqué par défaut à `Shape.style` des formes créées par dessin (polygone/rectangle) —
   * voir `TokaMapConfig.defaultShapeTheme`. Sans effet sur les formes chargées depuis les données
   * initiales (`jsonLoader`), qui portent déjà leur propre style le cas échéant.
   */
  defaultShapeTheme?: ShapeStyle;
  /**
   * Table initiale des thèmes référençables par `Shape.themeId` (voir `ShapeTheme`/
   * `MapAdapter.setThemes`). Sert de valeur de départ seulement : `setThemes` la remplace après
   * coup, et `configurations.themes` des données chargées prend le dessus une fois le chargement
   * terminé (voir `createTokaMap`), même mécanisme que `defaultShapeTheme`.
   */
  themes?: ShapeThemes;
  /**
   * Thème posé d'office sur toute forme nouvellement dessinée (voir `MapAdapter.setDefaultThemeId`).
   * Valeur de départ seulement : `setDefaultThemeId` la remplace après coup.
   */
  defaultThemeId?: string;
  /**
   * Fond de carte affiché à l'initialisation (voir `TileLayerName`/`TokaMapConfig.defaultTileLayerName`).
   * Par défaut : `"unset"` (comportement historique, aucun fond). Sans effet sur les données initiales
   * chargées (`jsonLoader`) : `configurations.tileLayerName`, quand présent, prend le dessus une fois le
   * chargement terminé (voir `createTokaMap`), même mécanisme que `defaultShapeTheme`.
   */
  defaultTileLayerName?: TileLayerName;
  /** Voir `OsmBackgroundOptions`. Valeur de secours seulement : `configurations.osmOptions`, quand
   * présent, prend le dessus au chargement — même mécanisme que `defaultTileLayerName`. */
  defaultOsmOptions?: OsmBackgroundOptions;
  /**
   * Couleur de fond posée sur le conteneur DOM à l'initialisation (voir
   * `TokaMapConfig.defaultBackgroundColor`) — `style.backgroundColor` du conteneur passé à `init()`,
   * PAS un fond peint dans le canvas comme `defaultTileLayerName`. Par défaut : `undefined`
   * (comportement historique, laisse le CSS du consommateur décider). Sans effet sur les données
   * initiales chargées (`jsonLoader`) : `configurations.backgroundColor`, quand présent, prend le
   * dessus une fois le chargement terminé (voir `createTokaMap`), même mécanisme que
   * `defaultTileLayerName`.
   */
  defaultBackgroundColor?: string;
  /**
   * Voir `TokaMapColors`/`TokaMapConfig.colors` — remplace, champ par champ, les couleurs jusqu'ici
   * en dur du rendu MapLibre (`DEFAULT_COLORS` ci-dessous). Sans effet sur le CSS des widgets `ui/*`
   * (`style.css`) — c'est `createTokaMap` qui pose séparément les variables `--toka-*`
   * correspondantes sur le conteneur.
   */
  colors?: Partial<TokaMapColors>;
}

/**
 * Valeurs par défaut de `MapLibreAdapterOptions.colors` — voir `TokaMapColors` pour le détail de
 * chaque usage. `draft` (`"#0d9488"`, un teal) remplace l'orange (`"#ff9800"`) utilisé jusqu'ici
 * pour le tracé en cours de dessin — une teinte franchement différente, pas juste un orange plus
 * sourd (un ambre reste perçu comme "orange"), et absente du reste de la palette ci-dessous ; les
 * autres reprennent tels quels les littéraux historiques (`accent` unifie 3 bleus jusqu'ici
 * légèrement distincts — survol, sélection/halo, géolocalisation — qui n'avaient jamais eu vocation
 * à diverger).
 */
const DEFAULT_COLORS: Required<TokaMapColors> = {
  accent: "#2979ff",
  accentSecondary: "#e91e63",
  draft: "#0d9488",
  rotate: "#673ab7",
  highlight: "#2ecc71",
  danger: "#c62828",
  success: "#2e7d32",
};

const SHAPES_SOURCE_ID = "shapes";
/**
 * Préfixes (pas des ids de layer directement) : les formes sont désormais réparties en "bandes"
 * MapLibre, une par valeur distincte de `Shape.layer` actuellement en jeu — voir `ShapeLayerBand`/
 * `ensureLayerBand` — pour obtenir un empilement unifié avec les images de forme raster (BESOINS.md
 * §4/§8), au lieu d'un seul layer fixe couvrant toutes les formes. Le suffixe de bande (`bandSuffix`)
 * complète chaque préfixe pour former l'id réel du layer.
 */
const SHAPES_FILL_LAYER_PREFIX = "shapes-fill";
const SHAPES_OUTLINE_LAYER_PREFIX = "shapes-outline";
/** Variante pointillée de `SHAPES_OUTLINE_LAYER_PREFIX` (voir `ShapeStyle.borderDashed`/
 * `ShapeLayerBand.outlineDashedLayerId`) — même raison de layer séparé que
 * `SHAPES_OUTLINE_DEFAULT_LAYER_PREFIX` ci-dessous : `line-dasharray` n'est pas data-driven. */
const SHAPES_OUTLINE_DASHED_LAYER_PREFIX = "shapes-outline-dashed";
/** Bordure de secours (voir `borderIsDefault` / `EDIT_MODE_DEFAULT_BORDER_COLOR`) — layer séparé
 * car `line-dasharray` n'est pas data-driven dans le style spec MapLibre (zoom uniquement). */
const SHAPES_OUTLINE_DEFAULT_LAYER_PREFIX = "shapes-outline-default";
/** Contour de survol (voir `SHAPE_HOVER_OUTLINE_COLOR`) — layer séparé pour la même raison que
 * `SHAPES_OUTLINE_DEFAULT_LAYER_PREFIX` : `line-dasharray` n'est pas data-driven. Toujours présent
 * dans la source, seule `line-opacity` (elle, data-driven) bascule sur `feature-state "hovered"`. */
const SHAPES_HOVER_OUTLINE_LAYER_PREFIX = "shapes-hover-outline";
/**
 * Source dédiée aux formes cercle (`shapeKind: "circle"`), alimentée par `circleShapesToGeoJSON` —
 * features `Point`, une par cercle, consommées par `ShapeLayerBand.circleLayerId` (layer `circle`
 * natif MapLibre, GPU, parfaitement rond à tout zoom — voir son commentaire). Le polygone
 * approximant le cercle reste PAR AILLEURS présent, invisible (`fill-opacity`/`line-opacity` à 0
 * pour `shapeKind: "circle"`, voir `ensureLayerBand`), dans `SHAPES_SOURCE_ID` — c'est lui qui reste
 * interrogé par `queryRenderedFeatures` pour le picking/survol/sélection, inchangé par cet ajout.
 * Feature-state (survol/sélection) répliqué sur les DEUX sources par `setShapeFeatureState` — un
 * `setFeatureState` sur un id absent d'une source est un no-op silencieux, donc appelé
 * inconditionnellement plutôt que de vérifier `shapeKind` à chaque site d'appel.
 */
const CIRCLE_SOURCE_ID = "shapes-circle";
const SHAPES_CIRCLE_LAYER_PREFIX = "shapes-circle";
/** Layer de picking dédié à `shapeKind: "line"` (voir `ShapeLayerBand.lineHitLayerId`). */
const SHAPES_LINE_HIT_LAYER_PREFIX = "shapes-line-hit";
/** Halo additif (polygone/ligne) d'une forme "selected" (sélection multiple, BESOINS.md §13) OU
 * "focused" (sélectionnée au clic simple, voir `setFocusedShape`) — voir
 * `SELECTED_SHAPE_HALO_MARGIN_PX`/`ShapeLayerBand.haloLayerId`. */
const SHAPES_HALO_LAYER_PREFIX = "shapes-selected-halo";
/** Halo additif d'un cercle "selected" OU "focused" — voir
 * `SELECTED_SHAPE_HALO_MARGIN_PX`/`ShapeLayerBand.circleHaloLayerId`. */
const SHAPES_CIRCLE_HALO_LAYER_PREFIX = "shapes-circle-selected-halo";
/** Typé `ExpressionSpecification` (pas seulement `FilterSpecification`) : utilisé à la fois comme
 * `filter` de layer et comme condition à l'intérieur d'un `["case", ...]` de paint (`fill-opacity`/
 * `line-opacity`, voir `ensureLayerBand`) — seule une expression, pas la syntaxe de filtre "legacy"
 * (`FilterSpecification` au sens large), est acceptée dans ce second contexte. */
const IS_CIRCLE_FILTER: maplibregl.ExpressionSpecification = ["==", ["get", "shapeKind"], "circle"];
/** Voir `ShapeLayerBand.lineHitLayerId` — isole les formes `shapeKind: "line"` (rendues en
 * `LineString`, voir `shapesToGeoJSON`) pour leur layer de picking dédié. */
const IS_LINE_FILTER: maplibregl.ExpressionSpecification = ["==", ["get", "shapeKind"], "line"];
/** Exclut les lignes des layers `fill` : MapLibre y triangule aussi une ligne ouverte, qui serait alors
 * survolable et cliquable dans l'aire qu'elle délimite. */
const NOT_LINE_FILTER: maplibregl.ExpressionSpecification = ["!", IS_LINE_FILTER];

/**
 * Survol d'une forme en mode édition (voir `MapLibreAdapterOptions.editMode`) — FIXE, non
 * configurable (pas de notion de "thème de survol par défaut" réglable, voir BESOINS.md §3). Repli
 * PAR CHAMP (voir `shapesToGeoJSON`) pour tout champ absent de `Shape.hoverStyle` — y compris quand
 * `Shape.hoverStyle` est un objet PARTIEL ou même vide (`{}`, ex. via `setShapeHoverStyle(id, {})`) :
 * le survol doit toujours rester visible en mode édition, jamais accidentellement transparent.
 *
 * `SHAPE_HOVER_FILL_COLOR` ne s'applique que si la forme n'a NI `bgColor` NI `hoverStyle.bgColor`
 * (sinon son fond, normal ou de survol personnalisé, reste inchangé) — voir `shapesToGeoJSON`.
 * `colors.accent`/`DEFAULT_BORDER_WIDTH` ne servent eux que de repli pour `hoverStyle.borderColor`/
 * `borderWidth` explicite : sans personnalisation, la bordure de la forme ne change plus du tout au
 * survol — l'indication visuelle générique est portée par le contour pointillé dédié
 * (`SHAPES_HOVER_OUTLINE_LAYER_PREFIX`), pas par la bordure elle-même.
 */
const SHAPE_HOVER_FILL_COLOR = "#3388ff40";
/** Épaisseur fixe du contour de survol dédié (voir `SHAPES_HOVER_OUTLINE_LAYER_PREFIX`) — non
 * personnalisable, contrairement à `hoverStyle.borderWidth` qui régit la bordure propre de la forme. */
const SHAPE_HOVER_OUTLINE_WIDTH = 2;

/** Surbrillance des formes actuellement dans une sélection multiple (BESOINS.md §13), ou de la forme
 * actuellement sélectionnée au clic simple (voir `setFocusedShape`, feature-state "focused" — même
 * couleur, pour rester un seul langage visuel de "sélection" plutôt que deux) : un halo `colors.accent`
 * dessiné EN PLUS du contour propre de la forme (jamais à la place, voir `SHAPES_HALO_LAYER_PREFIX`/
 * `SHAPES_CIRCLE_HALO_LAYER_PREFIX`) — la forme garde SA couleur/largeur de bordure habituelle, aussi
 * bien "focused" que "selected", plusieurs formes sélectionnées ensemble restant ainsi distinguables
 * les unes des autres. Le fond, lui, ne change jamais non plus (reste toujours `bgColor`) : un fond
 * teinté aurait le même rendu qu'un rectangle "classique" (comme le rectangle de sélection
 * lui-même), rendant les formes individuelles indiscernables les unes des autres à l'intérieur du
 * rectangle. Même couleur que le rectangle de sélection multiple lui-même (voir plus bas) et le cône
 * de géolocalisation — un seul `colors.accent`, langage visuel commun (voir `DEFAULT_COLORS`).
 */
/** Épaisseur de bordure affichée (celle du survol si la forme est survolée) à un zoom de référence
 * (voir `borderWidthStopsPx`) — sert aussi de base au halo additif (`SELECTED_SHAPE_HALO_MARGIN_PX`). */
function actualBorderWidthAt(stop: "Z0" | "ZMax"): maplibregl.ExpressionSpecification {
  return [
    "case",
    ["boolean", ["feature-state", "hovered"], false],
    ["get", `hoverBorderWidth${stop}`],
    ["get", `borderWidth${stop}`],
  ];
}

/** Valeur interpolée sur le zoom entre ses valeurs aux deux zooms de référence des épaisseurs. */
function overBorderZoom(
  atZ0: maplibregl.ExpressionSpecification | number,
  atZMax: maplibregl.ExpressionSpecification | number,
): maplibregl.ExpressionSpecification {
  return ["interpolate", ["exponential", 2], ["zoom"], 0, atZ0, BORDER_WIDTH_MAX_ZOOM, atZMax];
}

/** `line-width`/`circle-stroke-width` d'une bordure, en pixels ou en mètres (`ShapeStyle.borderWidthUnit`). */
const BORDER_WIDTH_EXPRESSION = overBorderZoom(actualBorderWidthAt("Z0"), actualBorderWidthAt("ZMax"));
/** Même chose pour la source d'édition, qui n'a pas d'état de survol. */
const EDITING_BORDER_WIDTH_EXPRESSION = overBorderZoom(["get", "borderWidthZ0"], ["get", "borderWidthZMax"]);
/**
 * Marge (px) toujours visible au-delà du bord extérieur du contour propre d'une forme, occupée par
 * son halo additif "selected"/"focused" — remplace une ancienne largeur de halo FIXE
 * (`SELECTED_SHAPE_HALO_WIDTH`, 4px), qui se faisait soit entièrement recouvrir par une bordure
 * configurée plus épaisse qu'elle (aucun bleu visible, la bordure semblait "gagner"), soit recouvrait
 * entièrement une bordure plus fine qu'elle (bordure invisible sous le bleu, semblant "remplacée") —
 * constaté en testant avec des `Shape.style.borderWidth` variés. Pour un cercle
 * (`ShapeLayerBand.circleHaloLayerId`), `circle-stroke-width` de MapLibre s'ajoute en dehors de
 * `circle-radius` (jamais centrée dessus), donc `circle-radius` du halo = rayon réel +
 * `actualBorderWidthAt` + cette marge, un anneau déjà purement extérieur par construction.
 * Pour un polygone/rectangle (`HALO_LINE_WIDTH_EXPRESSION`/`HALO_LINE_OFFSET_EXPRESSION` ci-dessous),
 * le halo est un simple `line-width` égal à cette marge, décalé via `line-offset` pour occuper
 * exactement la bande entre le bord extérieur du contour et cette marge au-delà — PAS centré sur le
 * tracé de la forme comme avant : un halo centré, deux fois plus large que la bordure, laissait
 * dépasser une bande de cette marge aussi bien à l'INTÉRIEUR qu'à l'EXTÉRIEUR de la forme, visible dès
 * que son fond n'est pas parfaitement opaque (`Shape.style.bgColor` avec un canal alpha, ex.
 * `#FFFFFF40`) — lisible comme une "double bordure" (une de chaque côté) plutôt qu'un simple liseré
 * extérieur. Une ligne (`shapeKind: "line"`) reste seule exception, toujours centrée/symétrique (voir
 * ces 2 expressions) : sans intérieur à distinguer d'un extérieur, ses 2 faces doivent rester
 * également mises en évidence.
 */
const SELECTED_SHAPE_HALO_MARGIN_PX = 3;
/**
 * `line-width` du halo additif d'une bande polygone/ligne (`ShapeLayerBand.haloLayerId`/
 * `EDITING_SHAPE_SELECTED_HALO_LAYER_ID`) — une ligne garde l'ancienne largeur symétrique (bordure +
 * marge de chaque côté, voir `SELECTED_SHAPE_HALO_MARGIN_PX`) ; un polygone/rectangle n'occupe plus
 * que la marge elle-même, positionnée entièrement à l'extérieur via `HALO_LINE_OFFSET_EXPRESSION`.
 */
const haloLineWidthAt = (stop: "Z0" | "ZMax"): maplibregl.ExpressionSpecification => [
  "case",
  IS_LINE_FILTER,
  ["+", actualBorderWidthAt(stop), 2 * SELECTED_SHAPE_HALO_MARGIN_PX],
  SELECTED_SHAPE_HALO_MARGIN_PX,
];
const HALO_LINE_WIDTH_EXPRESSION = overBorderZoom(haloLineWidthAt("Z0"), haloLineWidthAt("ZMax"));
/**
 * `line-offset` du halo additif d'une bande polygone/ligne — `0` (centré, inchangé) pour une ligne ;
 * pour un polygone/rectangle, pousse la bande entièrement au-delà du bord extérieur du contour
 * (`actualBorderWidthAt / 2` depuis le tracé) de `SELECTED_SHAPE_HALO_MARGIN_PX / 2`
 * supplémentaires (la moitié de la largeur du halo lui-même, pour centrer CETTE bande exactement sur
 * la marge visée). Le signe est une constante FIXE (négative), pas dérivée du sens de parcours des
 * sommets de chaque forme (`Shape.coordinates`, jamais garanti dans un sens constant — dessin à la
 * main, formes chargées) : vérifié empiriquement (rectangles dessinés dans les 2 sens, pentagone
 * irrégulier, carte tournée) que MapLibre normalise apparemment l'orientation de l'anneau d'un
 * polygone en interne avant d'en dériver le `line-offset` — un signe par forme calculé depuis
 * `Shape.coordinates` brut (`ringWindingSign`, tenté puis retiré) donnait un anneau tantôt à
 * l'intérieur, tantôt à l'extérieur selon le sens de dessin, alors qu'un signe fixe reste correct
 * dans tous les cas testés.
 */
const haloLineOffsetAt = (stop: "Z0" | "ZMax"): maplibregl.ExpressionSpecification => [
  "case",
  IS_LINE_FILTER,
  0,
  ["*", -1, ["+", ["/", actualBorderWidthAt(stop), 2], SELECTED_SHAPE_HALO_MARGIN_PX / 2]],
];
const HALO_LINE_OFFSET_EXPRESSION = overBorderZoom(haloLineOffsetAt("Z0"), haloLineOffsetAt("ZMax"));

/**
 * Bordure de secours, en mode édition uniquement (`MapLibreAdapterOptions.editMode`), pour une
 * forme sans `borderColor` explicite (ni thème par défaut, ni donnée `exemple.json`) : sans cela
 * elle serait invisible (bordure transparente). Jamais appliquée hors mode édition — voir
 * `shapesToGeoJSON`.
 */
const EDIT_MODE_DEFAULT_BORDER_COLOR = "#9e9e9e";
const EDIT_MODE_DEFAULT_BORDER_DASHARRAY: [number, number] = [2, 2];

/**
 * Couleur de trait par défaut d'une forme `shapeKind: "line"` sans `style.borderColor` — contrairement
 * au reste (`EDIT_MODE_DEFAULT_BORDER_COLOR`, repli UNIQUEMENT en `editMode`), s'applique TOUJOURS :
 * une ligne n'a pas de fond (`bgColor`) pour rester visible par défaut, sa bordure/son trait EST son
 * unique représentation visuelle — sans repli permanent elle serait invisible hors `editMode` dès
 * qu'aucun style n'a été configuré, contrairement à un polygone (toujours visible via son fond).
 */
const LINE_DEFAULT_BORDER_COLOR = "#3c3c3c";

/** Épaisseur de bordure par défaut (voir `ShapeStyle.borderWidth`/`Shape.hoverStyle`) — utilisée
 * quand ni la forme ni son thème de survol ne précisent `borderWidth`. */
const DEFAULT_BORDER_WIDTH = 1;

const NOT_DEFAULT_BORDER_FILTER: maplibregl.FilterSpecification = ["!=", ["get", "borderIsDefault"], true];
const DEFAULT_BORDER_FILTER: maplibregl.FilterSpecification = ["==", ["get", "borderIsDefault"], true];
/** Voir `ShapeStyle.borderDashed`/`ShapeLayerBand.outlineDashedLayerId` — ne s'applique qu'à la
 * bordure "réelle" (`NOT_DEFAULT_BORDER_FILTER`), la bordure de secours étant déjà toujours
 * pointillée (`outlineDefaultLayerId`) indépendamment de ce champ. */
const BORDER_DASHED_FILTER: maplibregl.FilterSpecification = ["==", ["get", "borderDashed"], true];
const NOT_BORDER_DASHED_FILTER: maplibregl.FilterSpecification = ["!=", ["get", "borderDashed"], true];

/** Un quintette de layers MapLibre (fill + 2 variantes d'outline + contour de survol dédié + cercle
 * natif) portant les formes d'une seule valeur de `Shape.layer` — voir `ensureLayerBand`.
 * `layerValue: null` = formes sans `layer` (toujours la bande la plus haute, même sémantique que
 * `ShapeStore.getAll()`). `circleLayerId` (voir `CIRCLE_SOURCE_ID`) rend les formes cercle de cette
 * bande à la même hauteur d'empilement que les 4 autres layers — un cercle participe ainsi
 * normalement à l'ordre `Shape.layer`, comme n'importe quelle autre forme. */
interface ShapeLayerBand {
  layerValue: number | null;
  fillLayerId: string;
  outlineLayerId: string;
  /** Variante pointillée de `outlineLayerId` (voir `ShapeStyle.borderDashed`) — layer séparé pour
   * la même raison que `outlineDefaultLayerId` : `line-dasharray` n'est pas data-driven dans le
   * style spec MapLibre. */
  outlineDashedLayerId: string;
  outlineDefaultLayerId: string;
  hoverOutlineLayerId: string;
  circleLayerId: string;
  /**
   * Layer `line` invisible dédié au picking d'une forme `shapeKind: "line"` (voir
   * `LINE_HIT_WIDTH_PX`) — son rendu VISIBLE passe déjà par `outlineLayerId`/`outlineDefaultLayerId`
   * (une `LineString`, contrairement à un `Polygon`, s'y dessine directement, voir
   * `shapesToGeoJSON`) ; ce layer-ci n'existe que pour élargir sa zone cliquable au-delà de son
   * `line-width` réel, au même titre que `fillLayerId` pour les autres `shapeKind` (voir
   * `currentFillLayerIds`/`queryShapeIdAtPoint`).
   */
  lineHitLayerId: string;
  /** Halo additif (polygone/ligne) d'une forme "selected" (sélection multiple) OU "focused" (clic
   * simple) de cette bande — voir `SELECTED_SHAPE_HALO_MARGIN_PX`. */
  haloLayerId: string;
  /** Halo additif d'un cercle "selected" OU "focused" de cette bande — voir
   * `SELECTED_SHAPE_HALO_MARGIN_PX`. */
  circleHaloLayerId: string;
}

/** Filtre MapLibre isolant les formes d'une seule bande — `["has", "layer"]` plutôt qu'une
 * comparaison à `null` pour la bande "sans calque" (voir `shapesToGeoJSON`, qui omet la propriété
 * plutôt que de la mettre à `null`). */
function bandLayerFilter(layerValue: number | null): maplibregl.FilterSpecification {
  return layerValue == null ? ["!", ["has", "layer"]] : ["==", ["get", "layer"], layerValue];
}

const SHAPES_EXTRUSION_LAYER_ID = "shapes-extrusion";
const SHAPES_EXTRUSION_FILTER: maplibregl.FilterSpecification = ["has", "height"];
/** Murs des lignes qui ont une hauteur : une bande autour du trait, montée en volume (voir
 * `lineWallsToGeoJSON`) — MapLibre n'extrude que des polygones. */
const LINE_WALL_SOURCE_ID = "line-walls";
const LINE_WALL_LAYER_ID = "line-walls-extrusion";
const SHAPE_EXTRUSION_OPACITY = 0.9;

const EDITING_SHAPE_SOURCE_ID = "editing-shape";
const EDITING_SHAPE_FILL_LAYER_ID = "editing-shape-fill";
const EDITING_SHAPE_OUTLINE_LAYER_ID = "editing-shape-outline";
/** Variante pointillée de `EDITING_SHAPE_OUTLINE_LAYER_ID` (voir `ShapeStyle.borderDashed`) — même
 * raison de layer séparé que `EDITING_SHAPE_OUTLINE_DEFAULT_LAYER_ID` : `line-dasharray` non
 * data-driven. */
const EDITING_SHAPE_OUTLINE_DASHED_LAYER_ID = "editing-shape-outline-dashed";
const EDITING_SHAPE_OUTLINE_DEFAULT_LAYER_ID = "editing-shape-outline-default";
/** Contour pointillé bleu affiché pour TOUTE forme présente dans `EDITING_SHAPE_SOURCE_ID` (édition
 * simple ou groupe en sélection multiple, BESOINS.md §13) — pas de feature-state ici, contrairement
 * à `SHAPES_HOVER_OUTLINE_LAYER_PREFIX` : être dans cette source EST la condition, pas besoin d'un
 * état supplémentaire. Même style fixe que le contour de survol (`colors.accent`/
 * `SHAPE_HOVER_OUTLINE_WIDTH`). */
const EDITING_SHAPE_MODIFY_OUTLINE_LAYER_ID = "editing-shape-modify-outline";
/** Halo additif (voir `SELECTED_SHAPE_HALO_MARGIN_PX`) pour une forme présente dans
 * `EDITING_SHAPE_SOURCE_ID` avec `selected: true` (déplacement/redimensionnement/rotation de
 * sélection multiple en cours, voir `updateEditingShapeSource`) — même principe que
 * `ShapeLayerBand.haloLayerId`, mais pour l'aperçu live plutôt que l'état au repos. */
const EDITING_SHAPE_SELECTED_HALO_LAYER_ID = "editing-shape-selected-halo";

/** Label (`Shape.text`) affiché au centroïde de chaque forme — voir `shapesTextToGeoJSON`. Source/
 * layer séparés de `SHAPES_SOURCE_ID`/bandes (pas de notion de `Shape.layer` pour les labels : ils
 * s'affichent tous au-dessus de toutes les formes/images, voir leur position dans `init()`). Une
 * paire équivalente (`EDITING_SHAPE_TEXT_*`) suit la même logique que `EDITING_SHAPE_SOURCE_ID` :
 * la forme en cours d'édition (BESOINS.md §5/§13) est exclue de `SHAPES_TEXT_LAYER_ID` (voir
 * `setEditingShapeFilter`) et son label vit dans cette source légère à la place, mis à jour à
 * chaque mousemove en même temps que le reste de son aperçu (voir `updateEditingShapeSource`). */
const SHAPES_TEXT_SOURCE_ID = "shapes-text";
const SHAPES_TEXT_LAYER_ID = "shapes-text";
/** Textes restés à l'endroit à l'écran (`ShapeText.rotateWithMap`), même source que `SHAPES_TEXT_LAYER_ID`. */
const SHAPES_TEXT_UPRIGHT_LAYER_ID = "shapes-text-upright";
const UPRIGHT_TEXT_FILTER: maplibregl.ExpressionSpecification = ["==", ["get", "upright"], true];
const GROUND_TEXT_FILTER: maplibregl.ExpressionSpecification = ["!=", ["get", "upright"], true];
const EDITING_SHAPE_TEXT_SOURCE_ID = "editing-shape-text";
const EDITING_SHAPE_TEXT_LAYER_ID = "editing-shape-text";
const EDITING_SHAPE_TEXT_UPRIGHT_LAYER_ID = "editing-shape-text-upright";
/** Valeurs par défaut quand `ShapeText.size`/`.color` sont omis. */
const DEFAULT_SHAPE_TEXT_SIZE_RATIO = 1;
const DEFAULT_SHAPE_TEXT_COLOR = "#000000";
/**
 * MapLibre GL refuse (silencieusement à l'écran, avec juste un `console.warn`) tout `text-size`
 * ÉVALUÉ à ≥ 255px — limite dure du moteur (précision de packing interne des tailles, voir
 * `maplibre-gl.js` : `const rc=255,nc=128,ic=rc*nc; ... x[0]>ic && warn('Value for "text-size" is >=
 * 255. Reduce your "text-size".')`), pas une limite qu'on impose nous-mêmes. `MAX_SHAPE_TEXT_SIZE_PX`
 * reste volontairement en dessous (marge de sécurité) — utilisé comme garde-fou dans
 * `shapesTextToGeoJSON` (voir son commentaire : la taille `fitTextSizePx` calculée AU ZOOM COURANT
 * dépasse rarement ce seuil en pratique, sauf forme couvrant la quasi-totalité de l'écran avec un
 * texte très court).
 */
const MAX_SHAPE_TEXT_SIZE_PX = 200;
/**
 * `text-font` explicite : la valeur par défaut du spec MapLibre (`["Open Sans Regular", "Arial
 * Unicode MS Regular"]`, utilisée si `text-font` est omis) n'existe pas sur le endpoint `glyphs` de
 * `DEFAULT_STYLE` (openfreemap/positron, qui ne sert que des polices "Noto Sans …") — sans ce champ,
 * MapLibre échoue silencieusement à charger les glyphes et n'affiche aucun texte, sans erreur.
 */
const SHAPE_TEXT_FONT = ["Noto Sans Regular"];
/** Variantes de `SHAPE_TEXT_FONT` selon `ShapeText.fontStyle` — les seules que sert le même endpoint `glyphs`. */
const SHAPE_TEXT_FONT_EXPRESSION: maplibregl.ExpressionSpecification = [
  "match",
  ["get", "fontStyle"],
  "bold",
  ["literal", ["Noto Sans Bold"]],
  "italic",
  ["literal", ["Noto Sans Italic"]],
  ["literal", SHAPE_TEXT_FONT],
];

/**
 * `text-max-width` "assez grand pour ne jamais déclencher de retour à la ligne automatique" —
 * PAS `0` (essayé dans une première version, voir git blame) : le spec MapLibre décrit cette
 * propriété comme "la largeur de ligne MAXIMALE avant retour à la ligne", donc `0` force au
 * contraire un retour après CHAQUE mot (largeur maximale nulle) plutôt que de désactiver le
 * comportement — révélé visuellement ("Salle A" affiché sur deux lignes). `fitTextSizePx` ne mesure
 * que les retours à la ligne EXPLICITES (`\n`) du texte : un retour automatique en plus romprait la
 * cohérence entre la taille calculée (une ligne) et le texte réellement rendu (plusieurs lignes, donc
 * plus haut que prévu — débordement vertical). 1000 ems est très largement au-dessus de tout texte
 * réaliste sur une forme.
 */
const NO_TEXT_WRAP_MAX_WIDTH_EMS = 1000;

/**
 * `text-size` des deux layers de labels — un simple passthrough de la propriété `size` calculée par
 * `shapesTextToGeoJSON` (voir son commentaire) : PAS d'expression de zoom ici (contrairement à une
 * première version, voir git blame). `["zoom"]` n'est autorisé par MapLibre que comme entrée directe
 * d'un `interpolate`/`step` de PREMIER NIVEAU (erreur de validation silencieuse sinon — le layer
 * entier n'est alors jamais créé, aucune exception JS ne remonte — révélé en développant une variante
 * qui enveloppait un tel `interpolate` dans un `["min", ...]`), donc impossible de plafonner
 * proprement une taille dépendante du zoom SANS recalculer nous-mêmes à chaque zoom — ce que fait
 * `refreshShapesTextSize`, appelé sur `"moveend"` (comme `updateImageOverlayMounts`, voir `init()`),
 * en plus de `renderShapes`/`updateEditingShapeSource`.
 */
const SHAPE_TEXT_SIZE_EXPRESSION: maplibregl.ExpressionSpecification = ["get", "size"];

const DRAFT_SOURCE_ID = "draft-shape";
const DRAFT_LINE_LAYER_ID = "draft-shape-line";
const DRAFT_VERTICES_LAYER_ID = "draft-shape-vertices";

/** Id (jamais celui d'une vraie forme, voir `generateId`) de l'aperçu live de l'image en cours de
 * dessin (voir `handleImageDrawingMove`) — un `<img>`/layer raster "upserté" par
 * `repositionShapeImageDuringEdit` comme pour n'importe quelle forme-image réelle, démonté par
 * `removeImageDrawingDraftPreview` à la fin de la session (dessin terminé ou annulé). */
const IMAGE_DRAWING_DRAFT_ID = "__image-drawing-draft__";

const EDIT_SOURCE_ID = "edit-vertices";
const EDIT_VERTICES_LAYER_ID = "edit-vertices-layer";

/** Poignées de redimensionnement par côté d'un rectangle en édition (voir `EdgeResizingState`) —
 * une petite barre au milieu de chaque arête, affichée en permanence (comme
 * `EDIT_VERTICES_LAYER_ID`) tant que la forme éditée est un rectangle à 4 sommets propres, jamais
 * au survol comme `ADD_VERTEX_HOVER_*` : sa position est fixe (le milieu de l'arête), pas un point
 * arbitraire le long de celle-ci. */
const EDGE_RESIZE_SOURCE_ID = "edge-resize-handles";
const EDGE_RESIZE_LAYER_ID = "edge-resize-handles-layer";
/** Longueur (px écran) de chaque barre, centrée sur le milieu de l'arête qu'elle représente. */
const EDGE_RESIZE_HANDLE_LENGTH_PX = 20;
/** Layer invisible, même source que `EDGE_RESIZE_LAYER_ID`, qui porte TOUTE l'interaction
 * (mousedown/mouseenter/mouseleave) — même principe que `ShapeLayerBand.lineHitLayerId`/
 * `LINE_HIT_WIDTH_PX` : la barre visible (`line-width: 6`) est trop fine pour rester cliquable/
 * survolable de façon fiable, ce layer élargit sa zone sans l'épaissir visuellement. */
const EDGE_RESIZE_HIT_LAYER_ID = "edge-resize-handles-hit-layer";
const EDGE_RESIZE_HIT_WIDTH_PX = 22;

/** Voir `handleAddVertexHoverMove`/`updateAddVertexHoverSource` : sommet fantôme affiché au survol
 * d'une arête de polygone/ligne en édition, cliquable pour y insérer un vrai sommet. */
const ADD_VERTEX_HOVER_SOURCE_ID = "add-vertex-hover";
const ADD_VERTEX_HOVER_LAYER_ID = "add-vertex-hover-layer";
/** Rayon (px) en-deçà duquel le curseur est considéré "sur une arête" pour afficher le sommet
 * fantôme — même ordre de grandeur que `DEFAULT_SNAP_THRESHOLDS.edgeRadiusPx`, mais un mécanisme
 * indépendant (celui-ci ne fait que décider l'affichage, pas de magnétisme). */
const ADD_VERTEX_HOVER_RADIUS_PX = 10;
/** En-deçà de ce rayon (px) d'une des 2 extrémités de l'arête survolée, le sommet fantôme ne
 * s'affiche pas : le curseur vise alors le sommet RÉEL déjà là (EDIT_VERTICES_LAYER_ID), pas un
 * nouveau à insérer juste à côté. */
const ADD_VERTEX_NEAR_VERTEX_RADIUS_PX = 10;
/** En-deçà de ce rayon (px) de la poignée de déplacement (MOVE_SOURCE_ID, voir
 * `computeMoveHandleCenter`), le sommet fantôme ne s'affiche pas non plus — même raison que
 * `ADD_VERTEX_NEAR_VERTEX_RADIUS_PX` juste au-dessus, mais pour un point qui n'est pas un vrai
 * sommet : le centre d'une ligne à 2 sommets tombe exactement sur son unique arête, donc pile sous
 * la poignée ; sans cette exclusion, cliquer la poignée y insérait un sommet à la place de saisir la
 * forme entière. Légèrement plus généreux que le rayon de la poignée elle-même (`circle-radius: 9`
 * sur MOVE_HANDLE_LAYER_ID) pour rester fiable même à la limite de son cercle cliquable. */
const MOVE_HANDLE_HOVER_EXCLUSION_RADIUS_PX = 12;

const ROTATE_SOURCE_ID = "rotate-handle";
const ROTATE_LINE_LAYER_ID = "rotate-handle-line";
const ROTATE_HANDLE_LAYER_ID = "rotate-handle-point";
const ROTATE_HANDLE_ICON_LAYER_ID = "rotate-handle-icon";

/** Icône de flèche de rotation (voir `createRotateArrowIconImageData`) affichée par-dessus les
 * points de rotation, forme seule (`ROTATE_HANDLE_ICON_LAYER_ID`) comme sélection multiple
 * (`SELECTION_ROTATE_HANDLE_ICON_LAYER_ID`) — une seule image partagée, dessinée directement en
 * blanc (pas en SDF : la couleur est fixe, pas data-driven, et le rendu SDF appliqué à un tracé fin
 * comme celui-ci le rendait flou/crénelé) pour rester lisible aussi bien sur `colors.rotate` que sur
 * `colors.accent`. */
const ROTATE_HANDLE_ICON_ID = "rotate-handle-arrow-icon";
/** Taille (px) du canvas source de l'icône. L'atlas d'icônes de MapLibre n'a pas de mipmaps : un
 * canvas bien plus grand que la taille d'affichage finale (l'approche précédente, 96px affiché à
 * `icon-size: 0.175` soit ~17px) se retrouve réduit de ~5,6x par un simple filtrage bilinéaire —
 * trop peu d'échantillons pour un tracé fin, d'où le rendu flou/"baveux". `ROTATE_HANDLE_ICON_PIXEL_RATIO`
 * ci-dessous fait qu'un `icon-size: 1` affiche ce canvas à `size / pixelRatio` px CSS — proche de sa
 * taille physique réelle sur un écran Retina (pixelRatio 2), donc quasi aucune réduction à faire. */
const ROTATE_HANDLE_ICON_CANVAS_PX = 28;
/** Voir `ROTATE_HANDLE_ICON_CANVAS_PX` — déclare à MapLibre que ce canvas est fourni en résolution
 * "2x" (comme un asset `@2x`), pour qu'un `icon-size: 1` l'affiche à sa taille voulue plutôt qu'à sa
 * pleine taille de canvas. */
const ROTATE_HANDLE_ICON_PIXEL_RATIO = 2;
/** Taille d'affichage de la flèche (`icon-size`) — 1 = taille "native" du canvas source compte tenu
 * de `ROTATE_HANDLE_ICON_PIXEL_RATIO` (ici 14px), pour rester contenue dans le point qui la porte
 * (`circle-radius: 9`). */
const ROTATE_HANDLE_ICON_SIZE = 1;

/**
 * Poignée de déplacement (translation) — même couleur (`colors.rotate`) et même gabarit visuel
 * (cercle plein + icône blanche par-dessus) que la poignée de rotation, mais posée au CENTRE de la
 * forme plutôt qu'à côté d'un sommet. Additive : le déplacement en glissant le corps/contour de la
 * forme (`handleMoveShapeDragStart` sur `EDITING_SHAPE_FILL_LAYER_ID`/`EDITING_SHAPE_OUTLINE_*`)
 * reste inchangé, cette poignée appelle le même handler — elle sert surtout de cible fiable pour une
 * ligne, dont le tracé réel (1-2px) est difficile à saisir précisément.
 */
const MOVE_SOURCE_ID = "move-handle";
const MOVE_HANDLE_LAYER_ID = "move-handle-point";
const MOVE_HANDLE_ICON_LAYER_ID = "move-handle-icon";
/** Voir `ROTATE_HANDLE_ICON_ID` — même raisonnement (couleur fixe, pas de recoloration data-driven). */
const MOVE_HANDLE_ICON_ID = "move-handle-arrow-icon";
/** Voir `ROTATE_HANDLE_ICON_CANVAS_PX`. */
const MOVE_HANDLE_ICON_CANVAS_PX = 28;
/** Voir `ROTATE_HANDLE_ICON_PIXEL_RATIO`. */
const MOVE_HANDLE_ICON_PIXEL_RATIO = 2;
/** Voir `ROTATE_HANDLE_ICON_SIZE`. */
const MOVE_HANDLE_ICON_SIZE = 1;

const SNAP_SOURCE_ID = "snap-guides";
const SNAP_GUIDE_LINE_LAYER_ID = "snap-guide-line";
const SNAP_INDICATOR_LAYER_ID = "snap-indicator-point";

// Géolocalisation (BESOINS.md §15) : point de position + flèche de cap (voir `geolocationToGeoJSON`).
const GEOLOCATION_SOURCE_ID = "geolocation";
const GEOLOCATION_HEADING_ICON_ID = "geolocation-heading-icon";
const GEOLOCATION_HEADING_LAYER_ID = "geolocation-heading-arrow";
const GEOLOCATION_DOT_LAYER_ID = "geolocation-dot";
/** Taille (px) du canvas source de la flèche — voir `createGeolocationHeadingIconImageData`. Très
 * suréchantillonné par rapport à la taille d'affichage réelle (voir `GEOLOCATION_HEADING_ICON_PIXEL_RATIO`
 * ci-dessous) : l'atlas d'icônes de MapLibre n'a pas de mipmaps, un canvas proche de sa taille
 * affichée se retrouve donc jamais réduit et garde ses arêtes crénelées — un fort suréchantillonnage
 * (~4x) force un filtrage bilinéaire sur suffisamment d'échantillons pour lisser la pointe fine et
 * l'encoche concave du glyphe. */
const GEOLOCATION_HEADING_ICON_CANVAS_PX = 120;
/** Voir `GEOLOCATION_HEADING_ICON_CANVAS_PX` — déclare à MapLibre que ce canvas est fourni en
 * résolution "4x", pour qu'un `icon-size: 1` l'affiche à `size / pixelRatio` (30px) plutôt qu'à sa
 * pleine taille de canvas. */
const GEOLOCATION_HEADING_ICON_PIXEL_RATIO = 4;
/** Taille d'affichage de la flèche (`icon-size`, fraction de la taille "logique" ci-dessus, voir
 * `GEOLOCATION_HEADING_ICON_PIXEL_RATIO`) — une taille fixe en pixels écran, contrairement à l'ancien
 * indicateur en ligne dont la longueur était fixée en mètres réels (`offsetLngLat`) et devenait donc
 * imperceptible en dézoomant. */
const GEOLOCATION_HEADING_ICON_SIZE = 1;

// Sélection multiple (BESOINS.md §13) : rectangle de sélection persistant, avec ses propres
// poignées de redimensionnement (coins) et de rotation — même vocabulaire visuel que l'édition
// d'une forme seule (EDIT_*/ROTATE_*), `colors.accent` (même couleur que le halo des formes
// sélectionnées, voir plus haut) pour rester identifiable.
// Le corps du rectangle est délibérément blanc (pas `colors.accent`) : les formes sélectionnées
// prennent déjà cette couleur (halo additif, voir plus haut), un rectangle de la même teinte
// par-dessus rendait leur contour illisible (couleur sur couleur identique). Le remplissage n'est visible QUE
// pendant un glissé actif (tracé/redimensionnement/rotation/déplacement, voir
// `setSelectionInteracting`) — au repos (rectangle persistant entre deux glissés), il redevient
// transparent. La bordure, elle, reste TOUJOURS visible (`SELECTION_OUTLINE_LAYER_ID`), mais change
// de couleur selon l'état : blanche pendant le glissé (assortie au remplissage, bien visible sur un
// fond de plan déjà clair), bleue au repos (même langage visuel que les poignées/la ligne de
// rotation, qui restent bleues en permanence).
const SELECTION_RECT_FILL_COLOR = "#ffffff";
/** `fill-opacity` du remplissage pendant un glissé actif (voir `setSelectionInteracting`) — 0 au
 * repos. Volontairement soutenu (plus qu'un simple survol) pour rester bien visible même sur un
 * fond de plan déjà clair. */
const SELECTION_FILL_OPACITY_DRAGGING = 0.55;
const SELECTION_SOURCE_ID = "selection-rect";
const SELECTION_FILL_LAYER_ID = "selection-rect-fill";
/** Bordure TOUJOURS visible (contrairement au remplissage) — seule sa couleur change selon l'état,
 * voir `setSelectionInteracting`. */
const SELECTION_OUTLINE_LAYER_ID = "selection-rect-outline";
const SELECTION_HANDLES_SOURCE_ID = "selection-handles";
const SELECTION_HANDLES_LAYER_ID = "selection-handles-layer";
const SELECTION_ROTATE_SOURCE_ID = "selection-rotate-handle";
const SELECTION_ROTATE_LINE_LAYER_ID = "selection-rotate-handle-line";
const SELECTION_ROTATE_HANDLE_LAYER_ID = "selection-rotate-handle-point";
const SELECTION_ROTATE_HANDLE_ICON_LAYER_ID = "selection-rotate-handle-icon";

/**
 * Surbrillance ponctuelle d'un ensemble de formes (ex. les repérer depuis un résultat de recherche
 * côté consommateur) — voir `MapLibreAdapter.highlightShapes`. `colors.highlight` volontairement
 * distincte de `colors.accent` (sélection multiple) pour ne pas laisser croire à une sélection
 * active. Ajoutée en dernier dans `init()`, donc au-dessus de tout le reste (y compris la sélection
 * multiple) : une forme surlignée doit rester visible quel que soit ce qui se passe par ailleurs sur
 * la carte.
 */
const HIGHLIGHT_SOURCE_ID = "highlight-shapes";
const HIGHLIGHT_FILL_LAYER_ID = "highlight-shapes-fill";
/** Source dédiée du CONTOUR de surbrillance — géométrie distincte de `HIGHLIGHT_SOURCE_ID` (celle du
 * fond), voir `MapLibreAdapter.updateHighlightOutlineSource`/`outwardExpandedRingPx` : le contour est
 * un anneau agrandi vers l'extérieur, pas la silhouette exacte de la forme. */
const HIGHLIGHT_OUTLINE_SOURCE_ID = "highlight-shapes-outline-src";
const HIGHLIGHT_OUTLINE_LAYER_ID = "highlight-shapes-outline";
/** Durée d'un cycle complet de pulsation (ms) — voir `startHighlightAnimation`. */
const HIGHLIGHT_PULSE_PERIOD_MS = 900;
const HIGHLIGHT_OUTLINE_WIDTH_MIN = 3;
const HIGHLIGHT_OUTLINE_WIDTH_MAX = 11;
const HIGHLIGHT_FILL_OPACITY_MIN = 0.1;
const HIGHLIGHT_FILL_OPACITY_MAX = 0.5;
/** Exposant appliqué à la sinusoïde brute du pulse (voir `startHighlightAnimation`) — < 1 pour un
 * effet "rebond" (montée/descente franches, léger palier au pic) plutôt qu'une ondulation lisse. */
const HIGHLIGHT_PULSE_SHARPNESS = 0.45;
/** Marge (px écran) laissée par `HighlightShapesOptions.zoomToFit` autour des formes surlignées —
 * évite qu'une forme se retrouve collée au bord de l'écran une fois le zoom maximal atteint. */
const HIGHLIGHT_FIT_PADDING_PX = 80;
/** Durée (ms) de la transition caméra de `HighlightShapesOptions.zoomToFit` — même ordre de
 * grandeur que les autres transitions animées de l'adapter (voir `panByScreenPixels`). */
/** Durée du vol de caméra d'`animateToView` — assez long pour qu'on suive le déplacement des yeux,
 * assez court pour ne pas faire attendre entre deux cartes. */
/** Âge maximal d'un relevé déjà en cache que `waitForUserLocation` accepte plutôt que d'en attendre
 * un neuf. Une minute : à pied on n'a pas pu aller bien loin, et le suivi continu corrigera de
 * toute façon dès qu'il livrera sa propre position — alors qu'exiger un relevé frais, c'est
 * précisément l'attente qu'on cherche à éviter. */
const USER_LOCATION_MAX_AGE_MS = 60_000;

/** Délai au-delà duquel `waitForUserLocation` renonce — large, parce qu'un premier relevé par le
 * réseau peut dépasser la dizaine de secondes, mais borné : au-delà, mieux vaut le dire que laisser
 * l'utilisateur devant un écran qui ne répond pas. */
const USER_LOCATION_WAIT_MS = 20_000;

const VIEW_ANIMATION_DURATION_MS = 900;

const HIGHLIGHT_FIT_DURATION_MS = 250;

/**
 * Tracé de l'itinéraire calculé par le mode "itinéraire" (voir `MapAdapter.showRoute`/`clearRoute`,
 * `createTokaMap.ts`, `core/pathfinding.ts`) — une simple ligne, `colors.accentSecondary` (même
 * couleur que le bouton "🧭 Y aller" de la fiche entité, voir `style.css`), distincte de
 * `colors.highlight`/`colors.accent` pour ne pas se confondre avec un surlignage de recherche ou
 * une sélection multiple. Ajoutée juste après la surbrillance (donc au-dessus des formes/de la
 * sélection, mais sous les curseurs distants).
 */
const ROUTE_SOURCE_ID = "route-line";
const ROUTE_LINE_LAYER_ID = "route-line-layer";
const ROUTE_LINE_WIDTH = 4;
/**
 * Animation du tracé par pointillés qui défilent ("marching ants", voir `startRouteAnimation`/
 * `buildRouteDasharray`) plutôt qu'un `line-gradient` (essayé d'abord — un dégradé de couleur qui
 * glisse est un effet plus voyant, difficile à alléger sans le rendre invisible, voir l'historique
 * de conception dans CLAUDE.md). Un simple `line-dasharray` recalculé en boucle, sans besoin de
 * `lineMetrics`/`["line-progress"]` : `ROUTE_DASH_LENGTH`/`ROUTE_DASH_GAP` sont en unités de largeur
 * de trait (convention MapLibre pour `line-dasharray`), pas en fraction du tracé.
 */
const ROUTE_DASH_LENGTH = 1.2;
const ROUTE_DASH_GAP = 0.55;
/** Temps (ms) pour qu'un pointillé parcoure un cycle complet du motif (`ROUTE_DASH_LENGTH +
 * ROUTE_DASH_GAP`) — indépendant de la longueur du tracé (le motif se répète localement, pas une
 * seule fois sur toute sa longueur comme l'aurait fait un halo `line-gradient`). */
const ROUTE_DASH_CYCLE_MS = 950;
/** Nombre d'étapes discrètes d'un cycle complet du motif (voir `ROUTE_DASH_PATTERNS`) — PAS un
 * simple pas d'échantillonnage temporel : MapLibre met en cache chaque valeur DISTINCTE de
 * `line-dasharray` qu'on lui passe dans un atlas de taille fixe (une texture rastérisant le motif) ;
 * un tableau de floats légèrement différent à chaque frame (essayé d'abord, calculé en continu à
 * partir du temps écoulé) crée une nouvelle entrée à chaque appel, qui finit par saturer cet atlas —
 * constaté en testant : le tracé se met à clignoter/disparaître après quelques secondes d'animation.
 * Boucler sur un nombre BORNÉ de motifs réutilisés (même technique que l'exemple officiel
 * MapLibre/Mapbox "animate a line") garde l'atlas stable indéfiniment. */
const ROUTE_DASH_STEPS = 24;

/**
 * Aperçu de debug de la grille de `core/pathfinding.ts`'s `gridRoute` (voir
 * `MapAdapter.showPathfindingDebugGrid`/`findRouteDebugGrid`) — 2 sources/layers `fill` distincts
 * (bloquées en rouge, à prix réduit en vert) plutôt qu'un seul avec une propriété "type de cellule" :
 * seules les cellules à prix réduit ont besoin d'une opacité VARIABLE selon leur poids (voir
 * `PATHFINDING_DEBUG_CHEAP_LAYER_ID`), les bloquées restent à une opacité fixe — 2 `paint` simples
 * plutôt qu'une expression conditionnelle unique sur les deux propriétés à la fois.
 */
const PATHFINDING_DEBUG_BLOCKED_SOURCE_ID = "pathfinding-debug-blocked";
const PATHFINDING_DEBUG_BLOCKED_LAYER_ID = "pathfinding-debug-blocked-layer";
const PATHFINDING_DEBUG_CHEAP_SOURCE_ID = "pathfinding-debug-cheap";
const PATHFINDING_DEBUG_CHEAP_LAYER_ID = "pathfinding-debug-cheap-layer";
const PATHFINDING_DEBUG_CHEAP_OUTLINE_LAYER_ID = "pathfinding-debug-cheap-outline-layer";

/**
 * Curseurs distants (collaboration multi-utilisateur temps réel, voir `MapAdapter.setRemoteCursor`/
 * `RemoteCursor`) : un `maplibregl.Marker` par curseur affiché, plutôt qu'une source/layer GL comme
 * le reste de l'adapter — voir `createRemoteCursorElement`/`updateRemoteCursorElement`. Un rectangle
 * plein coloré (fond = `RemoteCursor.color`, texte blanc) doit épouser exactement la largeur d'un nom
 * arbitraire ; ni un layer `symbol` classique (`text-halo` ne peut dessiner qu'un contour de texte,
 * pas un fond plein) ni une icône SDF étirée via `icon-text-fit` (le champ de distance signée d'un
 * coin arrondi se déforme en pointe dès que l'étirement horizontal/vertical diverge trop, et l'option
 * d'image étirable "9-patch" — `stretchX`/`stretchY`/`content` — produit un rendu totalement hors
 * d'échelle une fois combinée à `sdf: true` sur cette version de maplibre-gl) ne pouvaient donc
 * convenir : seul du DOM/CSS donne un contrôle exact de la forme. Un `<div>` HTML classique (flèche
 * en SVG + étiquette en `<div>` avec `border-radius`/`background`) résout ça sans aucun de ces
 * artefacts. Le marker est ajouté directement à `this.map`, donc déjà au-dessus du canvas (et de
 * `imageOverlayContainer`, lui-même derrière le canvas) : le curseur d'un autre utilisateur reste
 * visible quoi qu'il se passe par ailleurs sur la carte, sans layer dédié à ordonner.
 */
/** Ajouté après le nom d'une étiquette de sélection distante (voir `buildRemoteSelectionGeoJSON`) —
 * signale visuellement que cette étiquette est ancrée sur une sélection, pas sur une position de
 * souris réelle. */
const REMOTE_CURSOR_EDITING_SUFFIX = " ✏️";

/**
 * Indicateur affiché sur la forme sélectionnée par un curseur distant (voir
 * `RemoteCursor.selectedShapeId`) — un contour (`REMOTE_SELECTION_OUTLINE_*`, même construction que
 * `HIGHLIGHT_OUTLINE_SOURCE_ID` : anneau agrandi vers l'extérieur, recalculé en continu en espace
 * écran, voir `updateRemoteSelectionSources`/`startRemoteSelectionAnimation`) ET une étiquette
 * (`REMOTE_SELECTION_LABEL_*`) ancrée juste AU-DESSUS de ce contour (jamais au centre de la forme :
 * ancrer au centre donnerait l'impression trompeuse d'un clic réel à cet endroit). Les deux sont
 * teintés par curseur (`*-color: ["get", "color"]`) plutôt qu'une couleur fixe, et sans pulsation contrairement à
 * `HIGHLIGHT_*` : ce n'est pas une surbrillance ponctuelle destinée à attirer l'œil, mais un
 * indicateur permanent de "qui édite quoi", pensé pour rester discret.
 */
const REMOTE_SELECTION_OUTLINE_SOURCE_ID = "remote-selection-outline";
const REMOTE_SELECTION_OUTLINE_LAYER_ID = "remote-selection-outline-layer";
const REMOTE_SELECTION_OUTLINE_WIDTH_PX = 3;
const REMOTE_SELECTION_OUTLINE_MARGIN_PX = REMOTE_SELECTION_OUTLINE_WIDTH_PX / 2;
/** Pastille de fond de l'étiquette (voir `roundedRectRingPx`) — un simple polygone `fill` construit
 * en espace écran, PAS une icône SDF `icon-text-fit`/9-slice : reste dans le même paradigme "polygone
 * calculé en pixels puis unprojeté" que `REMOTE_SELECTION_OUTLINE_*`/`HIGHLIGHT_OUTLINE_*` ci-dessus,
 * sans dépendre d'un mécanisme MapLibre distinct pour un simple rectangle à coins arrondis. */
const REMOTE_SELECTION_LABEL_BG_SOURCE_ID = "remote-selection-label-bg";
const REMOTE_SELECTION_LABEL_BG_LAYER_ID = "remote-selection-label-bg-layer";
const REMOTE_SELECTION_LABEL_SOURCE_ID = "remote-selection-label";
const REMOTE_SELECTION_LABEL_LAYER_ID = "remote-selection-label-layer";
/** Taille de police (px, voir `text-size` dans `init()`) de l'étiquette — dupliquée ici pour que
 * `buildRemoteSelectionGeoJSON` puisse dimensionner la pastille de fond à la même taille (voir
 * `measureTextLinesPx`, mesuré à `FIT_MEASURE_FONT_SIZE_PX` puis mis à l'échelle). */
const REMOTE_SELECTION_LABEL_TEXT_SIZE_PX = 13;
/** Marge (px écran) entre le texte et le bord de sa pastille de fond, horizontale/verticale. */
const REMOTE_SELECTION_LABEL_PADDING_X_PX = 8;
const REMOTE_SELECTION_LABEL_PADDING_Y_PX = 5;
/** Rayon (px écran) des coins arrondis de la pastille — voir `roundedRectRingPx`. */
const REMOTE_SELECTION_LABEL_CORNER_RADIUS_PX = 6;
/** Marge (px écran) entre le haut du contour agrandi et le bas de la pastille — plus resserrée que
 * `HOVER_TOOLBAR_GAP_PX`/`ROTATE_HANDLE_OFFSET_PX` (voir plus bas) : la pastille pleine se distingue
 * déjà bien de la forme, pas besoin d'un grand espace pour éviter la confusion. */
const REMOTE_SELECTION_LABEL_GAP_PX = 6;

/** Plafond du ratio de résolution du rendu (voir `init()`) — au-delà, le gain de netteté ne vaut plus le coût GPU. */
const MAX_PIXEL_RATIO = 1.5;

const ZOOM_STEP_DURATION_MS = 300;

/** Plafond d'inclinaison (`pitch`, en degrés) appliqué en mode 3D (voir `init()`, `setAllowPitch`,
 * `MapLibreAdapterOptions.defaultAllowPitch`) — le plafond par défaut de MapLibre lui-même. */
const MAX_PITCH_DEG = 60;

/** Nombre minimum de sommets avant de pouvoir clôturer le polygone. */
const MIN_POINTS_TO_CLOSE = 3;
/**
 * Distance en pixels (voir `isNearClosingPoint`) sous laquelle le curseur "s'aimante" sur le 1er
 * sommet (survol ET clic) pour clôturer le polygone — volontairement plus généreuse que le rayon
 * d'accroche des sommets en édition normale (`DEFAULT_SNAP_THRESHOLDS.vertexRadiusPx`, 10px, voir
 * `snapping.ts`) : c'est le seul moyen de terminer un dessin de polygone (BESOINS.md §5), il doit
 * rester facile à viser même après plusieurs sommets, quand le 1er est déjà loin du curseur. Le
 * même rayon pilote à la fois le survol (`handlePolygonDrawingMove` → `closeHoverActive`, qui
 * grossit visuellement ce sommet et aimante la ligne élastique dessus, voir `draftToGeoJSON`) et
 * le clic qui finalise (`handleDrawingClick`) — ce que le survol montre est exactement ce que le
 * clic validera.
 */
const CLOSE_CLICK_RADIUS_PX = 18;
/** Nombre minimum de sommets avant de pouvoir terminer une ligne (`shapeKind: "line"`) en cliquant
 * près du DERNIER sommet posé — voir `isNearLastPoint`. Une ligne a besoin d'au moins 2 points pour
 * exister (contrairement à `MIN_POINTS_TO_CLOSE`, qui vaut 3 pour un polygone). */
const MIN_POINTS_TO_CLOSE_LINE = 2;
/** Largeur (px, invisible — `line-opacity: 0`) du layer de picking dédié à une ligne
 * (`ShapeLayerBand.lineHitLayerId`) — une `LineString` réellement dessinée avec `borderWidth` (1-2px
 * typiquement) serait très difficile à cliquer précisément ; ce layer élargit sa zone cliquable sans
 * changer son rendu visuel. */
const LINE_HIT_WIDTH_PX = 16;
/** Distance en pixels entre la forme et sa poignée de rotation. */
const ROTATE_HANDLE_OFFSET_PX = 30;
/** Distance en pixels entre le haut d'une forme et sa barre d'outils flottante au survol (voir
 * `showHoverToolbar`). */
const HOVER_TOOLBAR_GAP_PX = 10;
/**
 * Délai (ms) avant de cacher la barre d'outils flottante une fois le curseur sorti de la forme —
 * voir `scheduleHoverToolbarHide`. Nécessaire car `HOVER_TOOLBAR_GAP_PX` sépare la forme de sa barre
 * par une zone de carte "vide" : sans ce délai, traverser cette zone en chemin vers la barre fait
 * disparaître celle-ci avant que le curseur ne l'atteigne (le `mouseenter` de la barre annule ce
 * minuteur avant qu'il n'expire, voir `createHoverToolbarElement`).
 */
const HOVER_TOOLBAR_HIDE_DELAY_MS = 250;
/** Marge (px) ajoutée sous la bbox de la forme dans `hoverToolbarCorridor` — la bbox brute (forme ∪
 * barre) colle exactement aux bords réels, mais le curseur peut légèrement déborder de quelques
 * pixels (précision du pointeur, arrondis d'écran, comportement observé à certains niveaux de zoom)
 * sans pour autant avoir "quitté" cette zone du point de vue de l'utilisateur. */
const HOVER_TOOLBAR_CORRIDOR_BOTTOM_MARGIN_PX = 6;

/**
 * Calque (`Shape.layer`, voir BESOINS.md §4/§8) assigné par défaut à toute forme nouvellement
 * dessinée (polygone ou rectangle) — au-dessus des calques 10/20/30 d'`exemple.json`, mais toujours
 * un nombre concret (pas `undefined`) : la forme reste ainsi descendable via "Descendre" (menu
 * contextuel), contrairement à l'ancien comportement où une forme sans `layer` s'affichait certes
 * déjà au-dessus de tout (voir `ShapeStore.getAll`), mais ne pouvait jamais être renvoyée sous une
 * forme calquée existante.
 */
export const DEFAULT_NEW_SHAPE_LAYER = 50;

/** En dessous de cet angle (degrés), la carte est considérée à plat : pas de vraie perspective à gérer. */
const FLAT_PITCH_THRESHOLD_DEG = 0.5;

/** Marge (fraction de la taille de la vue) au-delà de laquelle une image hors-écran est démontée. */
const IMAGE_OVERLAY_VIEWPORT_MARGIN = 0.15;

/** Tolérance sur le seuil de `Shape.minZoomOffset` : un zoom posé exactement au seuil l'atteint. */
const MIN_ZOOM_VISIBILITY_EPSILON = 1e-6;

/** Opacité, en mode édition, d'une forme que `Shape.minZoomOffset` masquerait au zoom courant. */
const UNDER_ZOOM_OPACITY = 0.25;

const UNDER_ZOOM_OPACITY_EXPRESSION: maplibregl.ExpressionSpecification = [
  "case",
  ["boolean", ["get", "underZoom"], false],
  UNDER_ZOOM_OPACITY,
  1,
];

const SHAPE_OPACITY_EXPRESSION: maplibregl.ExpressionSpecification = [
  "*",
  UNDER_ZOOM_OPACITY_EXPRESSION,
  ["number", ["feature-state", "fadeIn"], 1],
];

/**
 * Valeur par défaut de `MapLibreAdapterOptions.minImageOverlayScreenPx` : en dessous de cette
 * taille à l'écran (CSS px, sur le plus grand côté), une image de forme n'est pas montée — trop
 * petite pour être perceptible, ne vaut pas son coût réseau/décodage — pertinent surtout en vue
 * d'ensemble (zoom large), où beaucoup de formes-image peuvent sinon être visibles (et donc
 * montées) simultanément.
 */
const DEFAULT_MIN_IMAGE_OVERLAY_SCREEN_PX = 40;

/**
 * Durée (ms) du fondu d'apparition d'une forme passant son seuil de zoom (voir
 * `startShapesFadeIn`), et de celui d'une image de forme nouvellement montée, raster ou SVG — pour
 * adoucir l'apparition autrement brutale d'une image franchissant `DEFAULT_MIN_IMAGE_OVERLAY_SCREEN_PX`
 * en zoomant, ou simplement chargée pour la première fois. Appliquée via `raster-opacity-transition`
 * côté raster (voir `upsertRasterShapeImage`), et via une transition CSS `opacity` sur l'`<img>` côté
 * SVG (voir `upsertShapeImageOverlay`).
 */
const SHAPE_FADE_IN_DURATION_MS = 200;

/** Préfixes d'id pour la source/le layer MapLibre d'une image de forme raster — un couple par forme
 * montée, voir `RasterShapeImageLayer`/`upsertRasterShapeImage`. */
const RASTER_IMAGE_SOURCE_ID_PREFIX = "shape-image-source";
const RASTER_IMAGE_LAYER_ID_PREFIX = "shape-image-layer";
/** Plus grand côté du canevas d'une image arrondie (voir `roundedRasterSrc`) : borne sa mémoire. */
const ROUNDED_IMAGE_MAX_SIDE_PX = 512;
/** Pause attendue avant de redessiner un arrondi (voir `roundedRasterSrc`). */
const ROUNDED_IMAGE_DRAW_DELAY_MS = 80;
/** Arrondis gardés en cache par image (voir `drawRoundedImage`). */
const ROUNDED_IMAGE_CACHE_PER_SRC = 6;

/** Source/layer MapLibre d'une image de forme raster actuellement montée — voir
 * `upsertRasterShapeImage`. `layerValue`/`resolvedSrc` mémorisés pour détecter un changement de
 * calque (Monter/Descendre) ou d'URL cible (zoom) depuis le dernier montage, seul cas où on
 * supprime/recrée plutôt que de simplement repositionner. `opacity` (`Shape.image.opacity`, défaut
 * 1) mémorisée pour ne réappliquer `raster-opacity` que si elle a changé — sinon écraserait, à
 * chaque repositionnement, le fondu d'apparition encore en cours (`RASTER_IMAGE_FADE_IN_DURATION_MS`). */
interface RasterShapeImageLayer {
  sourceId: string;
  layerId: string;
  layerValue: number | null;
  resolvedSrc: string;
  opacity: number;
}

/**
 * Marge (fraction de la taille de la vue) utilisée comme pré-filtre bon marché avant de calculer
 * les candidats au snapping — sur des coordonnées lng/lat, donc *sans* tenir compte d'une éventuelle
 * rotation de la carte. Un filtre pixel précis (voir `snapCandidatesPx`) élimine ensuite les faux
 * positifs que cette marge laisse passer ; elle doit juste être assez généreuse pour ne jamais
 * exclure à tort une forme réellement à l'écran (la boîte englobante lng/lat d'une vue pivotée est
 * plus grande que la vue elle-même — jusqu'à ~1.4x à 45°).
 */
const SNAP_CANDIDATE_PREFILTER_MARGIN = 0.5;

function angleDeg(center: PixelPoint, point: PixelPoint): number {
  return (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI;
}

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function centroid(points: LngLat[]): LngLat {
  const sum = points.reduce<[number, number]>(
    (acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat],
    [0, 0],
  );
  return [sum[0] / points.length, sum[1] / points.length];
}

/** Un simple clic (mousedown+mouseup sans mousemove entre les deux) laisse les points strictement
 * inchangés — pas d'imprécision flottante à tolérer ici, contrairement à un vrai drag. Utilisé pour
 * ne pas émettre de `MapAdapterEvent` de déplacement quand rien n'a réellement bougé (voir
 * `handleVertexDragEnd`/`handleMoveShapeDragEnd`). */
function arePointsEqual(a: LngLat[], b: LngLat[]): boolean {
  return a.length === b.length && a.every(([lng, lat], i) => lng === b[i]![0] && lat === b[i]![1]);
}

/**
 * Centre d'une forme — `coordinates[0]` pour un cercle (voir `Shape.coordinates` : PAS un anneau
 * fermé pour `shapeKind: "circle"`, juste `[centre, point de bord]` ; le centroïde générique des 2
 * points donnerait leur MILIEU, pas le vrai centre), le centroïde de tous les points pour une ligne
 * (`coordinates` déjà OUVERTE, pas de point de clôture à retirer — le retirer amputerait un vrai
 * sommet), le centroïde de l'anneau (sans le point de clôture dupliqué) sinon.
 */
function shapeCenter(shape: Shape): LngLat {
  if (shape.shapeKind === "circle") return shape.coordinates[0]!;
  if (shape.shapeKind === "line") return centroid(shape.coordinates);
  return centroid(shape.coordinates.slice(0, -1));
}

/**
 * Sommets uniques d'une forme (sans le point de clôture dupliqué) — pour un cercle ni pour une
 * ligne, `coordinates` n'est PAS un anneau fermé (voir leurs commentaires dans `types.ts`) : leurs
 * points sont déjà uniques, rien à retirer.
 */
function shapeVertices(shape: Shape): LngLat[] {
  return shape.shapeKind === "circle" || shape.shapeKind === "line" ? shape.coordinates : shape.coordinates.slice(0, -1);
}

/**
 * `editMode` : en dehors du mode édition, une forme sans `borderColor` reste transparente/invisible
 * (comportement historique, voir BESOINS.md §4). En mode édition, une bordure de secours pointillée
 * (`EDIT_MODE_DEFAULT_BORDER_COLOR`) est appliquée à la place, pour qu'aucune forme ne disparaisse.
 *
 * Une forme-image (`shapeKind: "image"`) n'a jamais ni fond ni bordure NORMAUX
 * — pas même la bordure de secours en mode édition, ni un `style.bgColor`/`borderColor` éventuellement
 * fourni par les données — puisqu'elle porte déjà sa propre représentation visuelle et qu'un
 * fond/une bordure la recouvrirait visuellement au repos. Son SURVOL n'est en revanche PAS exclu
 * (demande explicite, voir `resolveShapeVisualProps`/`handleShapeMouseMove`) : elle reçoit le même
 * fond/contour bleu par défaut que toute autre forme au survol (sauf verrouillée, `Shape.locked`),
 * malgré son absence de fond/bordure normaux.
 *
 * Une forme dont le `shapeKind` N'EST PAS `"image"` (icône ou image déduite d'`entity`, portées par
 * `shapeKind: "rectangle"`/`"polygon"` normal) n'est PAS concernée par cette exclusion,
 * volontairement : son fond/sa bordure NORMAUX restent visibles dans
 * les marges laissées par le mode "contain" de l'image — contrairement à une forme-image,
 * historiquement étirée sur 100% de la forme, où fond/bordure normaux ne se seraient de toute façon
 * jamais vus (son survol bleu, lui, reste visible pour les deux : contour en ligne, il se dessine
 * précisément sur le tracé de la forme, indépendamment de ce que l'image recouvre).
 *
 * `id` (au niveau Feature, pas `properties.id`) doit être un **nombre** : MapLibre GL (testé en
 * 4.7.1) stocke/retourne correctement un feature-state indexé par un id `string` via
 * `setFeatureState`/`getFeatureState`, mais n'applique PAS ce state aux expressions `["feature-
 * state", ...]` évaluées côté rendu (`fill-color`/`line-color`) quand l'id est une chaîne — le
 * survol/la sélection restent alors silencieusement sans effet visuel, sans aucune erreur. D'où
 * `getFeatureId`, qui fournit un id numérique stable par forme (voir `numericFeatureId`).
 * `properties.id` garde `shape.id` (string) pour tous les autres usages (recherche de forme,
 * `queryShapeIdAtPoint`, etc.).
 */
interface ShapeVisualProps {
  bgColor: string;
  borderColor: string;
  borderWidth: number;
  borderWidthUnit: BorderWidthUnit;
  hoverBgColor: string;
  hoverBorderColor: string;
  hoverBorderWidth: number;
  hoverBorderWidthUnit: BorderWidthUnit;
  borderIsDefault: boolean;
  borderDashed: boolean;
}

/**
 * Une ligne n'a pas d'aire (voir `Shape.coordinates`, resté une polyligne ouverte) : `bgColor`
 * n'a donc aucun sens pour elle, même s'il ne s'affiche déjà pas (le layer `fill` ignore une
 * géométrie `LineString`, voir CLAUDE.md) — retiré de son style dès la création (dessin d'une
 * nouvelle ligne, ou conversion d'un polygone en ligne via "Diviser ici", voir `cutShapeAtVertex`) pour
 * qu'un consommateur affichant `Shape.style.bgColor` (ex. `Sidebar.tsx`) n'expose jamais un champ
 * "couleur de fond" sans rapport pour elle.
 */
function lineShapeStyle(style: ShapeStyle | undefined): ShapeStyle | undefined {
  if (!style || style.bgColor == null) return style;
  const { bgColor: _bgColor, ...rest } = style;
  return rest;
}

/**
 * Résout le fond/la bordure (normaux ET au survol) d'une forme — extrait de `shapesToGeoJSON` pour
 * être partagé avec `circleShapesToGeoJSON` (voir `CIRCLE_SOURCE_ID`), qui a besoin des mêmes
 * champs pour son propre layer `circle` natif MapLibre. `forCircleLayer` (`true` seulement pour ce
 * second appelant) : le survol d'un cercle ne peut pas s'appuyer sur le contour pointillé dédié
 * (`SHAPES_HOVER_OUTLINE_LAYER_PREFIX`, `line-dasharray` non supporté par un layer `circle`) —
 * `hoverBorderColor`/`hoverBorderWidth` retombent donc directement sur les mêmes `colors.accent`/
 * `SHAPE_HOVER_OUTLINE_WIDTH` fixes que ce contour utilise pour les autres formes, plutôt que de
 * rester inchangés par défaut.
 */
/**
 * Résout `Shape.themeId` (voir `ShapeTheme`) en une forme dont `style`/`hoverStyle` sont ceux du
 * thème, pour que tout le reste de la résolution visuelle continue de ne lire que
 * `shape.style`/`shape.hoverStyle` sans rien savoir des thèmes.
 *
 * REMPLACEMENT, pas fusion : une forme liée n'utilise plus RIEN de son style propre, y compris pour
 * les champs que le thème laisse vides — ceux-là retombent sur les défauts de l'adapter (bordure
 * transparente en visualisation, bordure de secours pointillée en `editMode`, `DEFAULT_BORDER_WIDTH`),
 * exactement comme une forme qui n'aurait jamais eu de style. Le style propre de la forme n'est pas
 * effacé pour autant : il redevient effectif dès qu'on la délie.
 *
 * Forme non liée, ou liée à un thème absent de la table (supprimé, table pas encore chargée) :
 * renvoyée telle quelle par identité — aucune copie, et jamais une forme invisible.
 */
function applyShapeTheme(shape: Shape, themes: ShapeThemes): Shape {
  const theme = shape.themeId ? themes[shape.themeId] : undefined;
  if (!theme) return shape;

  return {
    ...shape,
    style: theme.style,
    hoverStyle: theme.hoverStyle,
    text: applyThemeTextStyle(shape.text, theme.text, theme.text?.fixedSizePx),
    // Survol : le CONTENU vient de la forme (son `hoverText` si elle en a un, sinon son `text` —
    // `shapesTextToGeoJSON` bascule sur `hoverText` dès qu'il a du contenu), les couleurs du thème.
    // Sans couleurs de survol dans le thème, on garde celles de son état normal : sinon survoler une
    // forme thémée ferait retomber son texte sur les couleurs par défaut de l'adapter.
    hoverText: applyThemeTextStyle(
      shape.hoverText ?? shape.text,
      theme.hoverText ?? theme.text,
      theme.hoverText?.fixedSizePx ?? theme.text?.fixedSizePx,
    ),
    height: theme.height ?? shape.height,
    elevation: theme.elevation ?? shape.elevation,
    hoverHeight: theme.hoverHeight ?? shape.hoverHeight,
    hoverElevation: theme.hoverElevation ?? shape.hoverElevation,
    minZoomOffset: theme.minZoomOffset ?? shape.minZoomOffset,
  };
}

/**
 * Applique le style de texte d'un thème sans toucher au contenu ni aux alignements, qui restent ceux
 * de la forme — un thème ne porte pas de contenu (voir `ShapeTheme.text`).
 *
 * Les deux couleurs sont posées même quand le thème les laisse vides, comme pour `style` : le texte
 * retombe alors sur les défauts de l'adapter, pas sur les couleurs propres de la forme. La taille
 * fixe, elle, n'est imposée que si le thème en porte une.
 */
function applyThemeTextStyle(
  own: ShapeText | undefined,
  themeText: ShapeTextStyle | undefined,
  themeFixedSizePx: number | undefined,
): ShapeText | undefined {
  if (!own?.text) return own;

  return {
    ...own,
    color: themeText?.color,
    bgColor: themeText?.bgColor,
    fixedSizePx: themeFixedSizePx ?? own.fixedSizePx,
  };
}

function resolveShapeVisualProps(
  rawShape: Shape,
  editMode: boolean,
  colors: Required<TokaMapColors>,
  forCircleLayer = false,
  themes: ShapeThemes = {},
): ShapeVisualProps {
  // Le thème lié remplace entièrement le style propre de la forme : résolu une seule fois ici, le
  // reste de la fonction est inchangé et ignore la notion de thème.
  const shape = applyShapeTheme(rawShape, themes);
  const bgColor = shape.shapeKind === "image" ? "transparent" : (shape.style?.bgColor ?? "transparent");
  // Une ligne (`shapeKind: "line"`) n'a pas de fond (`bgColor`) pour rester visible au repos —
  // en `editMode`, son repli `LINE_DEFAULT_BORDER_COLOR` remplace `EDIT_MODE_DEFAULT_BORDER_COLOR`
  // et n'est jamais le "secours pointillé" (`borderIsDefault`) : une ligne sans style explicite
  // reste un trait plein pendant l'édition. Hors `editMode`, elle retombe sur `"transparent"`
  // comme toute autre forme sans bordure explicite — une ligne sans couleur définie ne doit pas
  // apparaître pour un visiteur en mode consultation.
  const borderIsDefault = shape.shapeKind !== "image" && shape.shapeKind !== "line" && editMode && shape.style?.borderColor == null;
  const borderColor = shape.shapeKind === "image"
    ? "transparent"
    : (shape.style?.borderColor ??
        (editMode ? (shape.shapeKind === "line" ? LINE_DEFAULT_BORDER_COLOR : EDIT_MODE_DEFAULT_BORDER_COLOR) : "transparent"));
  const borderWidth = shape.style?.borderWidth ?? DEFAULT_BORDER_WIDTH;
  // Sans effet pour un cercle (pas de `circle-stroke-dasharray` dans le spec MapLibre, voir
  // `ShapeStyle.borderDashed`) — porté quand même par `ShapeVisualProps` pour rester uniforme avec
  // le reste de cette fonction, `circleShapesToGeoJSON` l'ignore simplement à l'usage.
  const borderDashed = shape.style?.borderDashed ?? false;
  // Survol : `Shape.hoverStyle` (`extra.styleHover`) prime champ par champ. Le fond bleu clair
  // fixe (SHAPE_HOVER_FILL_COLOR) ne sert de repli que si la forme n'a NI `bgColor` NI
  // `hoverStyle.bgColor` — sinon son fond (normal ou de survol personnalisé) reste inchangé.
  // La bordure, elle, ne change plus du tout au survol par défaut (repli sur `borderColor`/
  // `borderWidth`, pas sur un bleu fixe) : l'indication visuelle générique de survol est
  // désormais portée par le contour pointillé dédié (voir SHAPES_HOVER_OUTLINE_LAYER_PREFIX),
  // pas par la bordure elle-même — seul un `hoverStyle.borderColor`/`borderWidth` explicite
  // change encore la bordure propre de la forme.
  //
  // Une forme verrouillée (`Shape.locked`) garde un éventuel `hoverStyle` personnalisé (prime
  // toujours en premier ci-dessous), mais ne reçoit plus NOTRE indication visuelle générique par
  // défaut : ces replis par défaut redeviennent alors ses couleurs NORMALES (aucun changement au
  // survol), pas les couleurs de survol fixes — `forCircleLayer` mis à part (voir son commentaire :
  // pour un cercle, l'indication générique de survol N'EST PAS le contour pointillé dédié mais
  // directement `hoverBorderColor`/`hoverBorderWidth`, donc concernée par ce même repli).
  //
  // Contrairement à `bgColor`/`borderColor` ci-dessus (jamais de fond/bordure normaux pour une
  // forme-image, `shapeKind: "image"` — inchangé), le SURVOL d'une telle forme
  // N'EST PLUS exclu : elle reçoit le même fond/contour bleu par défaut que toute autre forme
  // (demande explicite) — `bgColor`/`borderColor` valant "transparent" pour elle, son repli "reste
  // inchangée si verrouillée" (`defaultHoverBgColor`/`defaultHoverBorderColor` ci-dessous) retombe
  // naturellement sur "transparent" dans ce cas, sans traitement spécial supplémentaire.
  // Une ligne n'a pas d'aire (voir `bgColor` plus haut) : pas de fond bleu de survol non plus, même
  // raison — sans ce carve-out (le même que `bgColor`/`borderColor` ont déjà), une ligne survolée en
  // `editMode` recevrait `SHAPE_HOVER_FILL_COLOR` comme si elle avait un intérieur à teinter.
  // `!editMode` : ce repli bleu est une aide à l'ÉDITION (signaler une zone cliquable/éditable sans
  // fond propre) — sans objet en visualisation seule, où `handleShapeMouseMove` est pourtant
  // désormais enregistré inconditionnellement (voir `init()`) pour que `hoverStyle`/`hoverText`
  // explicites (juste au-dessus/en dessous), eux, restent actifs dans les deux modes.
  const defaultHoverBgColor =
    !editMode || shape.locked || shape.shapeKind === "line" ? bgColor : SHAPE_HOVER_FILL_COLOR;
  const defaultHoverBorderColor = forCircleLayer ? (!editMode || shape.locked ? borderColor : colors.accent) : borderColor;
  const defaultHoverBorderWidth = forCircleLayer
    ? !editMode || shape.locked
      ? borderWidth
      : SHAPE_HOVER_OUTLINE_WIDTH
    : borderWidth;
  const hoverBgColor = shape.hoverStyle?.bgColor ?? shape.style?.bgColor ?? defaultHoverBgColor;
  const hoverBorderColor = shape.hoverStyle?.borderColor ?? defaultHoverBorderColor;
  const hoverBorderWidth = shape.hoverStyle?.borderWidth ?? defaultHoverBorderWidth;
  const borderWidthUnit: BorderWidthUnit = shape.style?.borderWidthUnit === "m" ? "m" : "px";
  // Une épaisseur de survol choisie a sa propre unité ; sinon, celle de l'épaisseur reprise.
  const hoverBorderWidthUnit: BorderWidthUnit = shape.hoverStyle?.borderWidth !== undefined
    ? (shape.hoverStyle.borderWidthUnit === "m" ? "m" : "px")
    : (hoverBorderWidth === borderWidth ? borderWidthUnit : "px");
  return {
    bgColor,
    borderColor,
    borderWidth,
    borderWidthUnit,
    hoverBgColor,
    hoverBorderColor,
    hoverBorderWidth,
    hoverBorderWidthUnit,
    borderIsDefault,
    borderDashed,
  };
}

/** Épaisseurs de bordure (normale et de survol) aux zooms de référence, lues par `BORDER_WIDTH_EXPRESSION`. */
function borderWidthStopProps(visual: ShapeVisualProps, shape: Shape) {
  const lat = shapeReferenceLatitude(shape);
  const border = borderWidthStopsPx(visual.borderWidth, visual.borderWidthUnit, lat);
  const hover = borderWidthStopsPx(visual.hoverBorderWidth, visual.hoverBorderWidthUnit, lat);

  return {
    borderWidthZ0: border.atZ0,
    borderWidthZMax: border.atZMax,
    hoverBorderWidthZ0: hover.atZ0,
    hoverBorderWidthZMax: hover.atZMax,
  };
}

/** Aucune forme en aperçu de survol (voir `MapLibreAdapter.setHoverPreviewShapes`). */
const NO_HOVER_PREVIEW: ReadonlySet<string> = new Set();

function shapesToGeoJSON(
  shapes: Shape[],
  editMode: boolean,
  colors: Required<TokaMapColors>,
  selected = false,
  getFeatureId: (shapeId: string | number) => number = (id) => Number(id),
  themes: ShapeThemes = {},
  hoverPreviewIds: ReadonlySet<string> = NO_HOVER_PREVIEW,
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: shapes.map((shape) => {
      const visual = hoverPreviewVisualProps(resolveShapeVisualProps(shape, editMode, colors, false, themes), shape, hoverPreviewIds);
      const { bgColor, borderColor, borderWidth, hoverBgColor, hoverBorderColor, hoverBorderWidth, borderIsDefault, borderDashed } = visual;
      return {
        type: "Feature",
        id: getFeatureId(shape.id),
        properties: {
          id: shape.id,
          // Lu par `IS_CIRCLE_FILTER`/`IS_NOT_CIRCLE_FILTER` (voir `ensureLayerBand`) pour rendre
          // ce polygone invisible (fill/line-opacity 0) là où un cercle a désormais son propre
          // layer `circle` natif (`CIRCLE_SOURCE_ID`) — ce polygone reste néanmoins présent et
          // interrogeable via `queryRenderedFeatures` (picking/survol/sélection inchangés).
          shapeKind: shape.shapeKind ?? "polygon",
          bgColor,
          borderColor,
          borderWidth,
          hoverBgColor,
          hoverBorderColor,
          hoverBorderWidth,
          ...borderWidthStopProps(visual, shape),
          borderIsDefault,
          borderDashed,
          // Lu par `hoverOutlineLayerId` (voir son commentaire) pour désactiver le contour
          // pointillé dédié sur une forme verrouillée — cette valeur ne change jamais dynamiquement
          // comme `hovered`/`selected` (pas de feature-state ici), une simple propriété suffit.
          locked: !!shape.locked,
          // Utilisé par `EDITING_SHAPE_OUTLINE_LAYER_ID`/`..._DEFAULT_LAYER_ID` (voir
          // `updateEditingShapeSource`) : `true` pendant un déplacement/redimensionnement/rotation
          // de sélection multiple (BESOINS.md §13, ces formes y sont TOUTES sélectionnées par
          // construction), `false` pour une édition à la forme seule ("Modifier"). Remplace le
          // survol en SVG de la bordure de sélection (`renderInteractiveOverlay`, désormais retiré)
          // qui compensait l'absence de feature-state "selected" sur cette source dédiée.
          selected,
          // Absente (plutôt que `null`/`undefined`) quand la forme n'a pas de calque — voir
          // `bandLayerFilter`, qui distingue "aucun calque" via `["has", "layer"]` plutôt qu'une
          // comparaison de valeur, pour ne dépendre d'aucune convention de sérialisation de `null`.
          ...(shape.layer != null ? { layer: shape.layer } : {}),
          ...extrusionHeightProperty(shape, themes, hoverPreviewIds),
        },
        geometry:
          shape.shapeKind === "line"
            ? { type: "LineString", coordinates: shape.coordinates }
            : {
                type: "Polygon",
                coordinates: [
                  shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates,
                ],
              },
      };
    }),
  };
}

function extrusionHeightProperty(shape: Shape, themes: ShapeThemes, hoverPreviewIds: ReadonlySet<string> = NO_HOVER_PREVIEW): Partial<ExtrusionProps> {
  if (shape.shapeKind === "line") return {};
  return extrusionProps(applyShapeTheme(shape, themes), hoverPreviewIds.has(String(shape.id))) ?? {};
}

/** Sommet et base d'un volume, selon que la forme est survolée — voir `extrusionProps`. */
const EXTRUSION_HEIGHT_EXPRESSION: maplibregl.ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "hovered"], false],
  ["get", "hoverHeight"],
  ["get", "height"],
];
const EXTRUSION_BASE_EXPRESSION: maplibregl.ExpressionSpecification = [
  "case",
  ["boolean", ["feature-state", "hovered"], false],
  ["get", "hoverBase"],
  ["get", "base"],
];

/** Circonférence terrestre (mètres, sphère IUGG utilisée par Web Mercator) — voir
 * `metersPerPixelAtZoom`. */
const EARTH_CIRCUMFERENCE_METERS = 40_075_016.686;

/** Résolution au sol (mètres par pixel écran) d'une tuile Web Mercator à un zoom et une latitude
 * donnés. `512` (pas `256`, la convention "tuile raster" historique de Leaflet/Google Maps) : MapLibre
 * GL JS (comme Mapbox GL JS) utilise en interne des tuiles vectorielles de 512px — vérifié
 * empiriquement (`map.project()` sur deux points connus, comparé aux deux constantes, à plusieurs
 * zooms) ; utiliser `256` ici sous-évaluait de moitié la taille RÉELLE à l'écran de toute forme,
 * donc `fitTextSizePx` calculait une police deux fois trop petite pour vraiment remplir sa forme.
 * Sert uniquement à `shapeBoundingBoxPx` ci-dessous, en JS (pas dans une expression MapLibre), donc
 * n'a pas besoin d'être exprimée en fonctions trigonométriques du style spec. */
function metersPerPixelAtZoom(zoom: number, latDeg: number): number {
  return (EARTH_CIRCUMFERENCE_METERS * Math.cos((latDeg * Math.PI) / 180)) / (512 * 2 ** zoom);
}

/**
 * Zoom de référence "haut" utilisé pour ancrer l'expression `circle-radius` (voir
 * `circleShapesToGeoJSON`/`ensureLayerBand`) — la relation pixels-par-mètre suit exactement une loi
 * en 2^zoom (propriété de Web Mercator), donc n'importe quelle paire de zooms de référence
 * reproduit la courbe exacte à TOUT zoom intermédiaire (l'interpolation exponentielle de base 2
 * n'est pas une approximation entre les 2 points, c'est la formule elle-même) — celui-ci doit juste
 * rester au-dessus de tout zoom réellement atteignable (au-delà, MapLibre "clampe" au lieu
 * d'extrapoler, voir le spec `interpolate`), largement au-delà du zoom max par défaut de MapLibre
 * GL (22).
 */
const CIRCLE_RADIUS_MAX_ZOOM = 24;

/**
 * Une feature Point par forme cercle (`shapeKind: "circle"`) — alimente le layer `circle` natif de
 * MapLibre (voir `ensureLayerBand`), qui rend un vrai cercle GPU (parfaitement rond à tout zoom,
 * contrairement au polygone régulier utilisé pour le picking/l'édition/le survolignage — voir
 * `circleRingLngLat`, toujours utilisé par `shapesToGeoJSON`/`highlightShapesToGeoJSON` pour ces
 * chemins-là, restés polygonaux). `circle-radius` de MapLibre s'exprime en pixels écran, pas en
 * mètres : pour qu'un cercle garde une taille GÉOGRAPHIQUE constante (comme toute autre forme) en
 * zoomant, on précalcule ici le rayon en pixels à 2 zooms de référence (0 et
 * `CIRCLE_RADIUS_MAX_ZOOM`) — voir son commentaire pour pourquoi 2 points suffisent à reproduire la
 * courbe exacte, pas juste ces 2 zooms précis.
 */
/** Murs des lignes qui ont une hauteur, de l'épaisseur de leur trait (voir `lineWallThicknessMeters`). */
function lineWallsToGeoJSON(
  shapes: Shape[],
  editMode: boolean,
  colors: Required<TokaMapColors>,
  getFeatureId: (shapeId: string | number) => number = (id) => Number(id),
  themes: ShapeThemes = {},
  hoverPreviewIds: ReadonlySet<string> = NO_HOVER_PREVIEW,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const shape of shapes) {
    if (shape.shapeKind !== "line") continue;
    const themed = applyShapeTheme(shape, themes);
    const extrusion = extrusionProps(themed, hoverPreviewIds.has(String(shape.id)));
    if (!extrusion) continue;
    const ring = lineWallRing(shape.coordinates, lineWallThicknessMeters(themed.style));
    if (!ring) continue;
    const visual = hoverPreviewVisualProps(resolveShapeVisualProps(shape, editMode, colors, false, themes), shape, hoverPreviewIds);
    features.push({
      type: "Feature",
      id: getFeatureId(shape.id),
      properties: { id: shape.id, color: visual.borderColor, hoverColor: visual.hoverBorderColor, ...extrusion },
      geometry: { type: "Polygon", coordinates: [ring] },
    });
  }
  return { type: "FeatureCollection", features };
}

function circleShapesToGeoJSON(
  shapes: Shape[],
  editMode: boolean,
  colors: Required<TokaMapColors>,
  getFeatureId: (shapeId: string | number) => number = (id) => Number(id),
  themes: ShapeThemes = {},
  hoverPreviewIds: ReadonlySet<string> = NO_HOVER_PREVIEW,
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: shapes
      .filter((shape) => shape.shapeKind === "circle")
      .map((shape) => {
        const visual = hoverPreviewVisualProps(resolveShapeVisualProps(shape, editMode, colors, true, themes), shape, hoverPreviewIds);
        const { bgColor, borderColor, borderWidth, hoverBgColor, hoverBorderColor, hoverBorderWidth } = visual;
        const [centerLng, centerLat] = shape.coordinates[0]!;
        const [edgeLng, edgeLat] = shape.coordinates[1]!;
        const metersPerDegLat = 111_320;
        const metersPerDegLng = 111_320 * Math.cos((centerLat * Math.PI) / 180);
        const radiusMeters = Math.hypot(
          (edgeLng - centerLng) * metersPerDegLng,
          (edgeLat - centerLat) * metersPerDegLat,
        );
        return {
          type: "Feature" as const,
          id: getFeatureId(shape.id),
          properties: {
            id: shape.id,
            bgColor,
            borderColor,
            borderWidth,
            hoverBgColor,
            hoverBorderColor,
            hoverBorderWidth,
            ...borderWidthStopProps(visual, shape),
            radiusPxAtZ0: radiusMeters / metersPerPixelAtZoom(0, centerLat),
            radiusPxAtZMax: radiusMeters / metersPerPixelAtZoom(CIRCLE_RADIUS_MAX_ZOOM, centerLat),
            // Même convention que `shapesToGeoJSON` (voir son commentaire `bandLayerFilter`).
            ...(shape.layer != null ? { layer: shape.layer } : {}),
          },
          geometry: { type: "Point" as const, coordinates: shape.coordinates[0]! },
        };
      }),
  };
}

/**
 * Largeur/hauteur d'une forme, en pixels écran AU ZOOM DONNÉ (le zoom courant de la carte — voir
 * `shapesTextToGeoJSON`, PAS un zoom de référence fixe, contrairement à une première version, voir
 * git blame) — sert de boîte disponible à `fitTextSizePx`.
 *
 * `rotationDeg` (`Shape.rotation`) est ANNULÉE avant de mesurer : `shape.coordinates` reflète déjà
 * la forme dans son orientation ACTUELLE (pivotée), donc sa bounding box axis-aligned brute est plus
 * grande que la forme elle-même dès que `rotation !== 0` (jusqu'à √2× à 45°) — le texte, qui reste
 * lui-même axis-aligned à l'écran quelle que soit `rotation` (pas de `text-rotate` lié à la forme,
 * voir les layers), débordait donc des côtés RÉELS (pivotés) de la forme tout en restant dans cette
 * bounding box trop généreuse. On calcule ici la boîte dans le repère LOCAL de la forme (après avoir
 * annulé sa rotation autour de son centroïde, même principe que `computeSelectionResizeScale` en
 * édition, voir BESOINS.md §13) : ça donne la vraie largeur/hauteur du contour, quelle que soit son
 * orientation à l'écran.
 *
 * `shapeKind === "rectangle"` (ou `"image"`, dessinée avec le même geste 2-clics coin à coin)
 * n'utilise PAS cette annulation par `rotationDeg` (voir plus bas) :
 * `rotationDeg` ne reflète pas forcément l'inclinaison RÉELLE de `coordinates` — un rectangle dessiné
 * à l'écran (2 clics en coin opposé) pendant que la carte a elle-même un bearing non nul se retrouve
 * avec des coordonnées inclinées par rapport au nord géographique tout en gardant `rotation: 0` (rien
 * dans ce geste de dessin ne renseigne `rotation`, contrairement à un geste de rotation explicite via
 * la poignée dédiée). Annuler `rotationDeg` dans ce cas ne fait alors rien (angle nul), et la fonction
 * retombe dans le bug qu'elle est censée éviter : bounding box axis-aligned brute d'un rectangle en
 * réalité incliné, jusqu'à ~2× trop grande sur un axe — largement suffisant pour faire déborder le
 * texte hors de la forme dès qu'un `verticalAlign`/`horizontalAlign` proche de 0 ou 1 consomme cette
 * marge fictive. Voir git blame pour un cas réel observé (rectangle à bearing 115°, hauteur mesurée
 * 238px au lieu de ~118px réels).
 *
 * Approximation plate (degrés → mètres via un facteur `cos(latitude)`, pas de grand cercle, rotation
 * appliquée directement sur ces mètres locaux) : suffisante à l'échelle d'une forme individuelle,
 * pas d'une distance planétaire.
 */
function shapeBoundingBoxPx(
  coordinates: LngLat[],
  rotationDeg: number,
  zoom: number,
  shapeKind?: Shape["shapeKind"],
): { widthPx: number; heightPx: number } {
  const [centerLng, centerLat] =
    shapeKind === "circle"
      ? coordinates[0]!
      : shapeKind === "line"
        ? centroid(coordinates)
        : centroid(coordinates.slice(0, -1));
  const metersPerDegLat = 111_320;
  const metersPerDegLng = 111_320 * Math.cos((centerLat * Math.PI) / 180);
  const metersPerPx = metersPerPixelAtZoom(zoom, centerLat);

  // Un cercle est invariant par rotation (sa bbox est toujours le carré 2×rayon centré dessus) —
  // pas besoin d'annuler `rotationDeg`/de parcourir des sommets, contrairement au cas général.
  if (shapeKind === "circle") {
    const [edgeLng, edgeLat] = coordinates[1]!;
    const radiusMeters = Math.hypot((edgeLng - centerLng) * metersPerDegLng, (edgeLat - centerLat) * metersPerDegLat);
    const sizePx = (2 * radiusMeters) / metersPerPx;
    return { widthPx: sizePx, heightPx: sizePx };
  }

  // Un rectangle (ou une image, dessinée avec le même geste 2-clics coin à coin) a 4 sommets
  // perpendiculaires par construction (voir `VertexResizingState`) : la
  // longueur de ses arêtes donne directement ses dimensions RÉELLES, sans passer par `rotationDeg`
  // (voir le commentaire au-dessus de cette fonction — potentiellement non fiable pour ce shapeKind).
  // Une longueur d'arête est invariante par rotation (la sienne ou celle de la carte), donc toujours
  // correcte ici, contrairement à la bounding box axis-aligned brute des sommets. `ring[0]->ring[1]`
  // et `ring[1]->ring[2]` sont les 2 arêtes adjacentes (perpendiculaires) du rectangle ; leur ordre
  // (largeur puis hauteur) suit celui posé au moment du dessin (`startDrawingRectangle`), inchangé par
  // un `rotation` appliqué ensuite via la poignée dédiée (elle pivote les sommets sans réordonner le
  // tableau).
  if (shapeKind === "rectangle" || shapeKind === "image") {
    const ring = coordinates.slice(0, -1);
    const [p0Lng, p0Lat] = ring[0]!;
    const [p1Lng, p1Lat] = ring[1]!;
    const [p2Lng, p2Lat] = ring[2]!;
    const widthMeters = Math.hypot((p1Lng - p0Lng) * metersPerDegLng, (p1Lat - p0Lat) * metersPerDegLat);
    const heightMeters = Math.hypot((p2Lng - p1Lng) * metersPerDegLng, (p2Lat - p1Lat) * metersPerDegLat);
    return { widthPx: widthMeters / metersPerPx, heightPx: heightMeters / metersPerPx };
  }

  // Même formule/convention que `rotatePointPx` (voir son commentaire dans imageQuad.ts) — annuler
  // la rotation, donc l'angle opposé.
  const rad = (-rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [lng, lat] of coordinates) {
    const dx = (lng - centerLng) * metersPerDegLng;
    const dy = (lat - centerLat) * metersPerDegLat;
    const x = dx * cos - dy * sin;
    const y = dx * sin + dy * cos;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }
  return { widthPx: (maxX - minX) / metersPerPx, heightPx: (maxY - minY) / metersPerPx };
}

/** Taille de police (px) de référence utilisée pour mesurer `text` via `<canvas>` (voir
 * `fitTextSizePx`) — arbitraire, sert uniquement de point de départ pour une mise à l'échelle
 * proportionnelle, sa valeur exacte n'a aucun effet sur le résultat. */
const FIT_MEASURE_FONT_SIZE_PX = 100;

/** Épaisseur du halo (px, voir `text-halo-width`) quand `ShapeText.bgColor` est défini — approxime
 * `ShapeText.bgColor` (voir `shapesTextToGeoJSON`). Doit être soustraite de la boîte disponible
 * AVANT `fitTextSizePx` (des deux côtés de chaque axe) : sinon le halo, qui s'ajoute autour du
 * texte une fois celui-ci dimensionné pour remplir toute la boîte, déborde de la forme. */
const TEXT_HALO_WIDTH_PX = 2;

/**
 * Marge de sécurité appliquée à `fitTextSizePx` (voir son appel dans `shapesTextToGeoJSON`) — le
 * texte vise 80% de la boîte disponible, pas 100%. `fitTextSizePx` mesure via `canvas.measureText`
 * avec la police du NAVIGATEUR nommée "Noto Sans Regular" (voir `SHAPE_TEXT_FONT`), qui peut différer
 * de façon variable (selon la machine/OS/police système substituée) des glyphes RÉELLEMENT dessinés
 * par MapLibre (chargés depuis le style, indépendamment des polices installées localement) — un
 * texte visant pile 100% (ou même 95%, une première valeur insuffisante en pratique, voir git blame)
 * peut déborder de plusieurs pixels selon la substitution de police locale. Cette marge absorbe cet
 * écart résiduel (pas une garantie absolue, un compromis pragmatique — la contrepartie est qu'à
 * `ShapeText.size: 1`, le texte remplit ~80% de la forme plutôt que "tout juste sans déborder").
 */
const FIT_SAFETY_MARGIN = 0.8;

/** Contexte 2D hors-écran réutilisé par `fitTextSizePx` — un seul `<canvas>` pour tout le processus,
 * créé au premier appel (pas au chargement du module, pour rester inoffensif si ce fichier était un
 * jour importé hors d'un navigateur). */
let fitMeasureContext: CanvasRenderingContext2D | null = null;
function getFitMeasureContext(): CanvasRenderingContext2D {
  if (!fitMeasureContext) {
    fitMeasureContext = document.createElement("canvas").getContext("2d")!;
  }
  return fitMeasureContext;
}

/**
 * Taille de police (px) à laquelle `text`, rendu avec `SHAPE_TEXT_FONT`, remplit tout juste une
 * boîte de `availableWidthPx` × `availableHeightPx` sans en dépasser (ni en largeur, ni en
 * hauteur) — c'est la valeur "1" de `ShapeText.size` (voir son commentaire dans `types.ts`) ;
 * `shapesTextToGeoJSON` la multiplie ensuite par `ShapeText.size ?? 1`.
 *
 * Mesuré via un `<canvas>` 2D hors-écran (`measureText`), pas via le vrai rendu MapLibre (glyphes
 * PBF du style, potentiellement légèrement différents de la police du navigateur nommée pareil) —
 * approximation suffisante pour dimensionner, pas une garantie pixel-perfect (même compromis que
 * `ShapeImage.width`/`.height`, voir leur commentaire).
 *
 * Un seul `measureText` par ligne, PAS de recherche itérative de taille : la largeur/hauteur d'un
 * texte étant proportionnelle à sa taille de police, on mesure une fois à `FIT_MEASURE_FONT_SIZE_PX`
 * puis on déduit directement le facteur d'échelle qui sature la première des deux contraintes
 * (largeur ou hauteur) à atteindre sa limite — `Math.min` des deux facteurs, jamais une moyenne, pour
 * ne jamais déborder sur l'axe le plus contraignant.
 *
 * `text` peut contenir des retours à la ligne explicites (`\n`, comme les interprète `text-field`) :
 * chaque ligne est mesurée séparément (largeur retenue = la plus large de toutes), la hauteur totale
 * est la somme des hauteurs de ligne — pas de retour à la ligne AUTOMATIQUE pris en compte ici (voir
 * `NO_TEXT_WRAP_MAX_WIDTH_EMS` sur les layers, qui désactive ce comportement pour rester cohérent
 * avec ce calcul).
 */
/**
 * Largeur (ligne la plus large) et hauteur totale de `text` mesurées à `FIT_MEASURE_FONT_SIZE_PX` —
 * factorisé hors de `fitTextSizePx` pour être réutilisé par `shapesTextToGeoJSON`, qui a aussi besoin
 * de la largeur mesurée pour placer le texte horizontalement (`ShapeText.horizontalAlign`), pas
 * seulement de la taille de police qui en dérive.
 */
type TextMeasureStyle = Pick<ShapeText, "fontStyle" | "uppercase">;

function measureTextLinesPx(
  text: string,
  style: TextMeasureStyle = {},
): { maxLineWidthPx: number; totalHeightPx: number } {
  const ctx = getFitMeasureContext();
  const variant = style.fontStyle === "bold" ? "bold " : style.fontStyle === "italic" ? "italic " : "";
  ctx.font = `${variant}${FIT_MEASURE_FONT_SIZE_PX}px ${SHAPE_TEXT_FONT[0]}`;
  let maxLineWidthPx = 0;
  let totalHeightPx = 0;
  for (const rawLine of text.split("\n")) {
    const line = style.uppercase ? rawLine.toUpperCase() : rawLine;
    const metrics = ctx.measureText(line.length > 0 ? line : " ");
    maxLineWidthPx = Math.max(maxLineWidthPx, metrics.width);
    // `fontBoundingBox*` (métriques globales de la police), PAS `actualBoundingBox*` (bornes réelles
    // des pixels encrés de CE texte précis) : un texte sans hampe/jambage ("Salle A", "coucou" — pas
    // de b/d/f/h/k/l ni de g/j/p/q/y) mesure un `actualBoundingBoxDescent` proche de 0, très inférieur
    // à l'espace vertical que MapLibre réserve réellement pour la ligne (basé sur la police entière,
    // pas sur les glyphes présents) — mesuré ~56% d'écart sur "Salle A" (71px vs 111px à 100px de
    // police). Sous-estimer la hauteur ici fait déborder le texte réel une fois rendu.
    totalHeightPx +=
      (metrics.fontBoundingBoxAscent || metrics.actualBoundingBoxAscent || FIT_MEASURE_FONT_SIZE_PX * 0.8) +
      (metrics.fontBoundingBoxDescent || metrics.actualBoundingBoxDescent || FIT_MEASURE_FONT_SIZE_PX * 0.2);
  }
  return { maxLineWidthPx, totalHeightPx };
}

function fitTextSizePx(
  text: string,
  availableWidthPx: number,
  availableHeightPx: number,
  style?: TextMeasureStyle,
): number {
  const { maxLineWidthPx, totalHeightPx } = measureTextLinesPx(text, style);
  if (maxLineWidthPx <= 0 || totalHeightPx <= 0) return 0;
  const widthScale = availableWidthPx / maxLineWidthPx;
  const heightScale = availableHeightPx / totalHeightPx;
  return FIT_MEASURE_FONT_SIZE_PX * Math.min(widthScale, heightScale);
}

/**
 * Point d'ancrage du label d'une forme : son centre, décalé de (`offsetXPx`, `offsetYPx`) — voir
 * `ShapeText.horizontalAlign`/`.verticalAlign`. Le décalage est converti en mètres le long des axes
 * est/sud : le label reste à la même place sur le plan quand la carte pivote, qu'il soit posé au sol
 * ou gardé à l'endroit (`ShapeText.rotateWithMap`, qui ne fait alors que le tourner sur lui-même).
 */
function groundTextAnchorPosition(shape: Shape, offsetXPx: number, offsetYPx: number, zoom: number): LngLat {
  const center = shapeCenter(shape);
  if (!offsetXPx && !offsetYPx) return center;
  const [lng, lat] = center;
  const metersPerPx = metersPerPixelAtZoom(zoom, lat);
  const metersPerDegLat = 111_320;
  const metersPerDegLng = 111_320 * Math.cos((lat * Math.PI) / 180);
  return [lng + (offsetXPx * metersPerPx) / metersPerDegLng, lat - (offsetYPx * metersPerPx) / metersPerDegLat];
}

/**
 * Un point (centroïde de l'anneau, voir `centroid`) par forme portant `Shape.text` — source des
 * layers de labels (`SHAPES_TEXT_LAYER_ID`/`EDITING_SHAPE_TEXT_LAYER_ID`). `properties.id` (même
 * convention que `shapesToGeoJSON`) sert au filtre d'exclusion pendant l'édition, voir
 * `setEditingShapeFilter`. Une forme sans texte (`Shape.text` absent, ou `.text` vide) n'a
 * simplement pas de feature ici — pas la peine d'afficher un label vide.
 *
 * `zoom` : zoom COURANT de la carte au moment de l'appel (pas un zoom de référence fixe, voir git
 * blame pour la version précédente) — `size` (feature-level) est `fitTextSizePx(...) *
 * FIT_SAFETY_MARGIN * (ShapeText.size ?? 1)`, calculé sur la boîte de la forme À CE ZOOM
 * (`shapeBoundingBoxPx`), donc déjà exprimé dans l'unité que MapLibre va réellement utiliser pour
 * `text-size` (voir `SHAPE_TEXT_SIZE_EXPRESSION`, un simple passthrough `["get","size"]`, sans mise
 * à l'échelle supplémentaire). La boîte passée à `fitTextSizePx` est elle-même réduite de
 * `2 * haloWidthPx` sur chaque axe (voir `TEXT_HALO_WIDTH_PX`) : le halo s'ajoute AUTOUR du texte une
 * fois dimensionné, donc doit avoir sa place réservée en amont, sinon c'est LUI qui déborderait de la
 * forme. Un texte ne peut donc plus déborder de sa forme par construction (tant que
 * `ShapeText.size <= 1`, la convention documentée) — plus besoin de masquer un texte trop grand
 * comme avant (ancien schéma en px absolus). Cette valeur DOIT rester recalculée à chaque changement
 * de zoom pour rester correcte (voir `refreshShapesTextSize`, appelé sur `"moveend"`) — contrairement
 * à un zoom de référence fixe, elle n'est plus valable "à tout zoom" une fois calculée.
 *
 * Plafonnée à `MAX_SHAPE_TEXT_SIZE_PX` : un garde-fou contre la limite dure de MapLibre (255px
 * évalués, voir son commentaire) — rarement atteint en pratique puisque `widthPx`/`heightPx` sont
 * déjà les dimensions RÉELLEMENT affichées à l'écran (bornées par la taille du viewport), sauf forme
 * couvrant la quasi-totalité de l'écran avec un texte très court.
 *
 * `haloColor`/`haloWidth` approximent `ShapeText.bgColor` par un halo autour du texte : MapLibre GL
 * n'a pas de fond natif (rectangle) pour un label de `symbol` layer, et un halo reste lisible sur
 * n'importe quel fond sans construire un second visuel à synchroniser avec le texte.
 *
 * `hoveredShapeId` (voir `MapLibreAdapter.hoveredShapeId`) : la forme dont l'id correspond
 * (comparé en `string`, `hoveredShapeId` pouvant être `number` — même tolérance que
 * `queryShapeIdAtPoint`) affiche `Shape.hoverText` à la place de `Shape.text` si `hoverText.text`
 * est renseigné, remplaçant entièrement `text` (jamais fusionné champ par champ, même convention
 * que `hoverStyle`) — y compris quand `text` est absent (une forme peut n'avoir de texte qu'au
 * survol). Absent/`null`, ou forme sans `hoverText.text` : comportement inchangé, `Shape.text` seul.
 * Recalculé sur toute variation du survol (voir `refreshShapesTextForHover`), pas seulement sur un
 * changement de formes/zoom.
 */
/** Layout commun des layers de labels — `upright` : texte aligné sur l'écran, sinon posé au sol. */
function shapeTextLayout(upright: boolean): maplibregl.SymbolLayerSpecification["layout"] {
  return {
    "text-field": ["get", "text"],
    "text-font": SHAPE_TEXT_FONT_EXPRESSION,
    "text-size": SHAPE_TEXT_SIZE_EXPRESSION,
    "text-anchor": "center",
    "text-transform": ["get", "transform"],
    "text-justify": ["get", "justify"],
    // Désactive EN PRATIQUE le retour à la ligne automatique de MapLibre (10em par défaut) — voir
    // NO_TEXT_WRAP_MAX_WIDTH_EMS, `fitTextSizePx` ne mesure que les retours à la ligne EXPLICITES
    // (`\n`) du texte.
    "text-max-width": NO_TEXT_WRAP_MAX_WIDTH_EMS,
    "text-allow-overlap": true,
    "text-ignore-placement": true,
    ...(upright ? {} : { "text-rotation-alignment": "map" as const, "text-rotate": ["get", "rotate"] }),
  };
}

function shapesTextToGeoJSON(
  shapes: Shape[],
  map: MlMap,
  hoveredShapeId: string | number | null = null,
  themes: ShapeThemes = {},
  defaultRotationDeg = 0,
  hoverPreviewIds: ReadonlySet<string> = NO_HOVER_PREVIEW,
): GeoJSON.FeatureCollection {
  const zoom = map.getZoom();
  return {
    type: "FeatureCollection",
    features: shapes
      .map((shape) => {
        const isHovered = (hoveredShapeId != null && shape.id === String(hoveredShapeId)) || hoverPreviewIds.has(String(shape.id));
        // Le texte d'une forme liée à un thème en vient (voir `applyShapeTheme`) : ce chemin de rendu
        // est SÉPARÉ de `resolveShapeVisualProps` (qui ne traite que fond/bordure), il doit donc
        // résoudre le thème pour son propre compte.
        const themed = applyShapeTheme(shape, themes);
        const effectiveText = isHovered && themed.hoverText?.text ? themed.hoverText : themed.text;
        const rotationDeg = shapeTextRotationDeg(effectiveText, themed.text, !!effectiveText?.rotateWithMap, defaultRotationDeg);
        return { shape, effectiveText, rotationDeg };
      })
      .filter((entry): entry is { shape: Shape; effectiveText: ShapeText; rotationDeg: number } => !!entry.effectiveText?.text)
      .map(({ shape, effectiveText, rotationDeg }) => {
        const { widthPx, heightPx } = shapeBoundingBoxPx(shape.coordinates, shape.rotation, zoom, shape.shapeKind);
        const haloWidthPx = effectiveText.bgColor ? TEXT_HALO_WIDTH_PX : 0;
        const availableWidthPx = Math.max(0, widthPx - 2 * haloWidthPx);
        const availableHeightPx = Math.max(0, heightPx - 2 * haloWidthPx);
        const { maxLineWidthPx, totalHeightPx: measuredHeightPx } = measureTextLinesPx(effectiveText.text, effectiveText);
        const fitSizePx = fitTextSizePx(effectiveText.text, availableWidthPx, availableHeightPx, effectiveText);
        const sizeRatio = effectiveText.size ?? DEFAULT_SHAPE_TEXT_SIZE_RATIO;
        const size = Math.min(
          effectiveText.fixedSizePx ?? fitSizePx * FIT_SAFETY_MARGIN * sizeRatio,
          MAX_SHAPE_TEXT_SIZE_PX,
        );

        // `ShapeText.horizontalAlign`/`.verticalAlign` (défaut 0.5, voir leur commentaire dans
        // `types.ts`) : glissent le point d'ancrage du label (`text-anchor: center`, voir `init()`)
        // dans la marge laissée par `fitTextSizePx` sur chaque axe — la largeur/hauteur RÉELLEMENT
        // occupée par le texte au `size` final (déduite de `maxLineWidthPx`/`measuredHeightPx`,
        // mesurées au même `FIT_MEASURE_FONT_SIZE_PX`), pas la largeur/hauteur disponible. Sans
        // marge sur un axe (le texte l'occupe déjà entièrement, l'axe qui contraint le "contain"),
        // l'alignement n'a par construction aucun effet sur cet axe — indépendamment de l'autre.
        const alignX = effectiveText.horizontalAlign ?? 0.5;
        const alignY = effectiveText.verticalAlign ?? 0.5;
        const textWidthPx = maxLineWidthPx * (size / FIT_MEASURE_FONT_SIZE_PX);
        // `/ FIT_SAFETY_MARGIN` : `measuredHeightPx` (fontBoundingBoxAscent/Descent, voir
        // `measureTextLinesPx`) reste, empiriquement, en-deçà de la hauteur RÉELLEMENT rendue par
        // MapLibre une fois la police système substituée (mesuré ~15-20% d'écart, dans le même sens
        // que l'écart déjà documenté sur `measureTextLinesPx` lui-même) — un écart resté invisible
        // tant que le texte restait centré (`FIT_SAFETY_MARGIN` répartit sa marge des deux côtés à
        // parts égales, largement suffisant pour l'absorber), mais qui faisait déborder le bas de la
        // forme dès `verticalAlign` proche de 1 : l'alignement consommait ALORS la totalité de cette
        // marge d'un seul côté, ne laissant plus rien pour absorber l'écart. Gonfler `textHeightPx`
        // en amont du calcul de la marge disponible (`slackYPx`) restaure une marge résiduelle
        // garantie, quel que soit `verticalAlign`. `textWidthPx` n'a pas ce problème (l'avance des
        // glyphes mesurée par `canvas.measureText` est fiable, contrairement aux métriques
        // verticales de la police) : aucune correction équivalente nécessaire sur cet axe.
        const textHeightPx = measuredHeightPx * (size / FIT_MEASURE_FONT_SIZE_PX) / FIT_SAFETY_MARGIN;
        const slackXPx = Math.max(0, availableWidthPx - textWidthPx);
        const slackYPx = Math.max(0, availableHeightPx - textHeightPx);
        const offsetXPx = (alignX - 0.5) * slackXPx;
        const offsetYPx = (alignY - 0.5) * slackYPx;
        const upright = !!effectiveText.rotateWithMap;

        return {
          type: "Feature" as const,
          properties: {
            id: shape.id,
            text: effectiveText.text,
            size,
            color: effectiveText.color ?? DEFAULT_SHAPE_TEXT_COLOR,
            haloColor: effectiveText.bgColor ?? "rgba(0,0,0,0)",
            haloWidth: haloWidthPx,
            upright,
            fontStyle: effectiveText.fontStyle ?? "regular",
            transform: effectiveText.uppercase ? "uppercase" : "none",
            opacity: effectiveText.opacity ?? 1,
            justify: effectiveText.justify ?? "center",
            rotate: rotationDeg,
          },
          geometry: {
            // `shapeCenter` gère le cas cercle (`coordinates[0]`, pas le centroïde de ses 2 points
            // bruts) ; pour les autres formes, `centroid` attend un anneau SANS le point de
            // fermeture dupliqué (même convention que `editingState.points`/`selectionState.corners`
            // ailleurs dans ce fichier) : le passer tel quel pondérerait le premier sommet deux
            // fois, décalant le centre vers lui.
            type: "Point" as const,
            coordinates: groundTextAnchorPosition(shape, offsetXPx, offsetYPx, zoom),
          },
        };
      }),
  };
}

/**
 * `previewPoint` (ignoré si `closed`) ajoute un segment élastique du dernier sommet posé jusqu'au
 * curseur (potentiellement accroché) — jamais matérialisé comme sa propre feature `Point`, pour
 * rester visuellement distinct d'un sommet réellement posé.
 */
function draftToGeoJSON(
  points: LngLat[],
  options?: {
    closed?: boolean;
    previewPoint?: LngLat;
    closeHoverActive?: boolean;
    hideVertices?: boolean;
    /** Index du sommet qui grossit/s'aimante quand `closeHoverActive` est vrai — `0` (le 1er
     * sommet, celui qui referme un polygone) par défaut ; une ligne (`handleLineDrawingMove`) passe
     * le DERNIER sommet posé, puisqu'elle se termine sur lui plutôt que sur le premier. */
    closeVertexIndex?: number;
  },
): GeoJSON.FeatureCollection {
  const closeVertexIndex = options?.closeVertexIndex ?? 0;
  // `hideVertices` : un cercle passe ses 64 sommets d'approximation (`circlePointsPx`) par ce même
  // canal — les afficher un par un via `DRAFT_VERTICES_LAYER_ID` (pensé pour les 2-4 sommets d'un
  // rectangle/polygone) donnerait un contour "en pointillés de points" plutôt qu'un cercle net, voir
  // `handleCircleDrawingMove`.
  const features: GeoJSON.Feature[] = options?.hideVertices
    ? []
    : points.map((point, index) => ({
        type: "Feature",
        properties: {
          isFirst: index === 0,
          // Grossi encore plus dans `DRAFT_VERTICES_LAYER_ID` (voir plus bas) UNIQUEMENT pendant que le
          // curseur survole ce sommet à une distance qui clôturerait le polygone au clic
          // (`closeHoverActive`, voir `isNearClosingPoint`/`handlePolygonDrawingMove`) — pas dès que
          // `MIN_POINTS_TO_CLOSE` est atteint : sinon le sommet resterait gros en permanence dès le 3e
          // point posé, même loin du curseur, ce qui ne signale plus rien de précis.
          canClose: index === closeVertexIndex && !!options?.closeHoverActive,
        },
        geometry: { type: "Point", coordinates: point },
      }));

  const lineCoordinates = options?.closed
    ? [...points, points[0]!]
    : options?.previewPoint
      ? [...points, options.previewPoint]
      : points;

  if (lineCoordinates.length >= 2) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: lineCoordinates },
    });
  }

  return { type: "FeatureCollection", features };
}

/** Rectangle aligné sur l'écran (pas sur lng/lat), défini par deux coins opposés en pixels. */
function rectangleCornersPx(a: PixelPoint, b: PixelPoint): PixelPoint[] {
  return [
    { x: a.x, y: a.y },
    { x: b.x, y: a.y },
    { x: b.x, y: b.y },
    { x: a.x, y: b.y },
  ];
}

/** Nombre de sommets du polygone régulier approximant un cercle dessiné (voir `circlePointsPx`). */
const CIRCLE_SEGMENTS = 64;

/**
 * Cercle approximé par un polygone régulier, construit en espace écran (pas en lng/lat) à partir
 * d'un centre et d'un rayon en pixels — même logique que `rectangleCornersPx` : reste un vrai
 * cercle à l'écran même si la carte est pivotée (bearing).
 */
function circlePointsPx(centerPx: PixelPoint, radiusPx: number): PixelPoint[] {
  const points: PixelPoint[] = [];
  for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
    const angle = (2 * Math.PI * i) / CIRCLE_SEGMENTS;
    points.push({
      x: centerPx.x + radiusPx * Math.cos(angle),
      y: centerPx.y + radiusPx * Math.sin(angle),
    });
  }
  return points;
}

function centroidPx(points: PixelPoint[]): PixelPoint {
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/**
 * Test d'appartenance à un quadrilatère convexe (l'enveloppe du rectangle de sélection, tourné ou
 * non), tout en pixels écran — vrai si `point` est du même côté (produit vectoriel) de chacune de
 * ses 4 arêtes. Sert à savoir quelles formes tombent dans le rectangle (BESOINS.md §13, sur le
 * CENTRE de chaque forme — une forme à cheval dedans/dehors compte comme dehors) et si un clic
 * tombe dedans (pour ignorer le clic-en-dehors qui referme le rectangle).
 */
function isPointInConvexPolygonPx(point: PixelPoint, corners: PixelPoint[]): boolean {
  let sign = 0;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i]!;
    const b = corners[(i + 1) % corners.length]!;
    const cross = (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
    if (cross === 0) continue;
    const currentSign = cross > 0 ? 1 : -1;
    if (sign === 0) sign = currentSign;
    else if (sign !== currentSign) return false;
  }
  return true;
}

function selectionRectToGeoJSON(corners: LngLat[]): GeoJSON.FeatureCollection {
  if (corners.length !== 4) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: { type: "Polygon", coordinates: [[...corners, corners[0]!]] },
      },
    ],
  };
}

/** Une feature Polygon par forme surlignée (voir `MapLibreAdapter.highlightShapes`) — `shape.coordinates`
 * est déjà un anneau fermé (premier === dernier point, voir `Shape.coordinates`), pas besoin de le
 * refermer nous-mêmes ; sauf un cercle, développé en anneau via `circleRingLngLat` (voir
 * `shapesToGeoJSON`, même traitement). Géométrie du contour proprement dite (agrandie vers
 * l'extérieur, voir `outwardExpandedRingPx`) : construite séparément par `highlightOutlineToGeoJSON`,
 * pas ici — cette fonction alimente uniquement le fond (`HIGHLIGHT_FILL_LAYER_ID`), qui lui doit
 * rester calé exactement sur la silhouette réelle de la forme. */
function highlightShapesToGeoJSON(shapes: Shape[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: shapes.map((shape) => ({
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [
          shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates,
        ],
      },
    })),
  };
}

/** Une unique feature LineString pour le tracé d'itinéraire (voir `MapAdapter.showRoute`) —
 * `[]` produit une `FeatureCollection` vide (voir `clearRoute`), pas une `LineString` à 0 point
 * (invalide en GeoJSON). */
function routeToGeoJSON(coordinates: LngLat[]): GeoJSON.FeatureCollection {
  if (coordinates.length < 2) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates },
      },
    ],
  };
}

/**
 * Construit le `line-dasharray` (unités = largeur de trait, convention MapLibre) représentant le
 * motif tirets/espaces (`ROUTE_DASH_LENGTH`/`ROUTE_DASH_GAP`) décalé de `distance` le long du tracé
 * — voir `startRouteAnimation`. `line-dasharray` n'a pas de propriété "offset" native ; l'astuce
 * standard (reprise de l'exemple officiel MapLibre/Mapbox "animate a line") consiste à reconstruire
 * le tableau à chaque frame en répartissant le tiret/l'espace EN COURS entre un segment de tête
 * (ce qui reste avant que le prochain segment ne commence à `distance` 0) et un segment de queue,
 * de façon à ce que le motif rendu à `distance` corresponde exactement au motif infini
 * `[tiret, espace, tiret, espace, ...]` vu depuis le point `-distance` plutôt que depuis 0.
 *
 * `distance` est normalisée modulo `ROUTE_DASH_LENGTH + ROUTE_DASH_GAP` (le motif boucle) :
 * - Si le point `0` tombe dans un tiret (`t <= ROUTE_DASH_LENGTH`) : `[t, gap, dash - t, 0]` — tiret
 *   de tête déjà entamé (longueur `t`), espace plein, reste du tiret (`dash - t`), espace de queue nul.
 * - Sinon (le point `0` tombe dans un espace, `u = t - dash`) : `[0, u, dash, gap - u]` — tiret de
 *   tête nul (invisible), espace déjà entamé (`u`), puis un tiret plein et le reste de l'espace.
 *
 * TOUJOURS 4 éléments (ON, OFF, ON, OFF), jamais 3 — le premier cas complète explicitement avec un
 * espace de queue nul plutôt que d'omettre ce 4e élément (les deux sont mathématiquement équivalents
 * une fois tuilés le long du tracé). Constaté en testant : faire alterner la LONGUEUR du tableau
 * (3 puis 4 éléments) d'une frame à l'autre provoque un accroc visuel périodique sur tout le tracé —
 * probablement `line-dasharray` recalculant ses coordonnées de texture différemment selon la forme du
 * tableau. Garder un nombre d'éléments constant élimine ce changement de forme, indépendamment de
 * `ROUTE_DASH_PATTERNS` (qui bornait déjà le nombre de motifs DISTINCTS, un problème différent).
 */
function buildRouteDasharray(distance: number): number[] {
  const period = ROUTE_DASH_LENGTH + ROUTE_DASH_GAP;
  const t = ((distance % period) + period) % period;
  if (t <= ROUTE_DASH_LENGTH) return [t, ROUTE_DASH_GAP, ROUTE_DASH_LENGTH - t, 0];
  const u = t - ROUTE_DASH_LENGTH;
  return [0, u, ROUTE_DASH_LENGTH, ROUTE_DASH_GAP - u];
}

/** Les `ROUTE_DASH_STEPS` motifs `line-dasharray` d'un cycle complet, précalculés une seule fois
 * (voir le commentaire de `ROUTE_DASH_STEPS` pour pourquoi un nombre BORNÉ de motifs réutilisés,
 * plutôt qu'un calcul continu à partir du temps écoulé) — `startRouteAnimation` cycle sur ce tableau
 * plutôt que d'appeler `buildRouteDasharray` directement à chaque frame. */
const ROUTE_DASH_PATTERNS: number[][] = Array.from({ length: ROUTE_DASH_STEPS }, (_, i) =>
  buildRouteDasharray((i / ROUTE_DASH_STEPS) * (ROUTE_DASH_LENGTH + ROUTE_DASH_GAP)),
);

function editVerticesToGeoJSON(points: LngLat[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: points.map((point, index) => ({
      type: "Feature",
      properties: { vertexIndex: index },
      geometry: { type: "Point", coordinates: point },
    })),
  };
}

/** Voir `EDGE_RESIZE_SOURCE_ID` — une Feature `LineString` (la barre) par côté, vide si la forme en
 * édition n'est pas éligible (voir `computeEdgeResizeHandles`). `cursor` (calculé par
 * `resizeCursorForTangent`) est reporté en propriété pour que le `mouseenter`/`mousedown` de
 * `EDGE_RESIZE_LAYER_ID` sache quel curseur directionnel afficher pour CETTE barre précise. */
function edgeResizeHandlesToGeoJSON(
  handles: { edgeIndex: number; a: LngLat; b: LngLat; cursor: string }[],
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: handles.map(({ edgeIndex, a, b, cursor }) => ({
      type: "Feature",
      properties: { edgeIndex, cursor },
      geometry: { type: "LineString", coordinates: [a, b] },
    })),
  };
}

/**
 * Curseur de redimensionnement (`ns-resize`/`ew-resize`/`nwse-resize`/`nesw-resize`) le plus proche
 * de la direction perpendiculaire au côté représenté par la tangente écran `(dx, dy)` — c'est la
 * direction dans laquelle tirer la barre change réellement la dimension du rectangle (perpendiculaire
 * à son propre tracé), pas la direction de la barre elle-même. Calculé directement à partir des
 * pixels écran (pas du repère local dé-roté de `computeEdgeResizeScale`) : le curseur doit refléter
 * ce que l'utilisateur voit réellement à l'écran, y compris sous un bearing de carte non nul.
 */
function resizeCursorForTangent(dx: number, dy: number): string {
  const perpAngleDeg = (((Math.atan2(dy, dx) * 180) / Math.PI + 90) % 180 + 180) % 180;
  if (perpAngleDeg < 22.5 || perpAngleDeg >= 157.5) return "ew-resize";
  if (perpAngleDeg < 67.5) return "nwse-resize";
  if (perpAngleDeg < 112.5) return "ns-resize";
  return "nesw-resize";
}

/** Curseur de redimensionnement d'un coin de rectangle : dans l'axe de la diagonale qui part du coin
 * opposé, telle qu'elle est à l'écran. */
function resizeCursorForCorner(cornersPx: PixelPoint[], cornerIndex: number): string {
  const corner = cornersPx[cornerIndex]!;
  const opposite = cornersPx[(cornerIndex + 2) % 4]!;
  // `resizeCursorForTangent` vise la perpendiculaire de sa tangente : on lui passe celle de la diagonale.
  return resizeCursorForTangent(-(corner.y - opposite.y), corner.x - opposite.x);
}

/** Flèche circulaire des poignées de rotation (CSS n'en a pas), cerclée de blanc pour rester lisible
 * sur tout fond ; `grab` si le navigateur ne charge pas l'image. */
const ROTATE_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round">' +
    '<g stroke="white" stroke-width="4.5"><path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M17.5 3.5v4h-4"/></g>' +
    '<g stroke="black" stroke-width="2"><path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M17.5 3.5v4h-4"/></g>' +
    "</svg>",
)}") 12 12, grab`;

/** Voir `ADD_VERTEX_HOVER_SOURCE_ID` — au plus une feature (le sommet fantôme courant), aucune si
 * rien n'est survolé. */
function addVertexHoverToGeoJSON(position: LngLat | null): GeoJSON.FeatureCollection {
  if (!position) return { type: "FeatureCollection", features: [] };
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: position } }],
  };
}

function rotateHandleToGeoJSON(center: LngLat, handle: LngLat): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: [
      { type: "Feature", properties: {}, geometry: { type: "Point", coordinates: handle } },
      {
        type: "Feature",
        properties: {},
        geometry: { type: "LineString", coordinates: [center, handle] },
      },
    ],
  };
}

/**
 * Dessine une flèche de navigation (silhouette à 6 sommets — pointe fine, 2 "ailes" et une encoche
 * concave à l'arrière, cf. l'icône Material Design "navigation" et le repère visuel `cursor.png`
 * fourni à la racine du repo) pointant vers le haut par défaut, sur un canvas hors-écran. Dessinée
 * directement dans sa couleur finale (`color`, `colors.accent` — pas en SDF, même raisonnement que
 * `createRotateArrowIconImageData` : le rendu SDF, pensé pour des aplats simples, rendait la pointe
 * fine et l'encoche concave flous/crénelés une fois réduits à leur taille d'affichage). `size` (voir
 * `GEOLOCATION_HEADING_ICON_CANVAS_PX`) est volontairement bien plus grand que la taille d'affichage
 * finale (`GEOLOCATION_HEADING_ICON_PIXEL_RATIO`/`GEOLOCATION_HEADING_ICON_SIZE`), pour que MapLibre
 * la sous-échantillonne plutôt que de l'agrandir. Contrairement à l'ancien cône (ancré par sa pointe
 * via `icon-anchor: "bottom"`), cette forme est ancrée par son centre (`icon-anchor: "center"`, voir
 * `init()`) : son centre géométrique est déjà proche du centre visuel du glyphe, il n'y a pas de
 * sommet unique à aligner sur la position GPS.
 */
function createGeolocationHeadingIconImageData(size: number, color: string): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const scale = size / 24;
  // Coordonnées reprises de l'icône Material Design "navigation" (viewBox 24x24) : pointe en haut,
  // 2 sommets de base formant les "ailes", et 2 sommets internes formant l'encoche concave arrière.
  const points: Array<[number, number]> = [
    [12, 2],
    [4.5, 20.29],
    [5.21, 21],
    [12, 18],
    [18.79, 21],
    [19.5, 20.29],
  ];
  ctx.fillStyle = color;
  ctx.beginPath();
  points.forEach(([x, y], index) => {
    const px = x * scale;
    const py = y * scale;
    if (index === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  });
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

/**
 * Point représentant l'utilisateur sur la carte (voir BESOINS.md §15) : une unique feature Point,
 * portant le cap (`heading`, en `properties`) quand il est connu — c'est ce qui pilote `icon-rotate`
 * sur `GEOLOCATION_HEADING_LAYER_ID` (voir `init()`), filtré par `["has", "heading"]` pour que la
 * flèche ne s'affiche que si un cap a effectivement été résolu (GPS ou boussole) ; le point bleu
 * (`GEOLOCATION_DOT_LAYER_ID`, filtré par le complémentaire `["!", ["has", "heading"]]`) ne s'affiche
 * lui que tant qu'aucun cap n'est encore connu — jamais les deux en même temps.
 */
function geolocationToGeoJSON(position: LngLat, headingDeg: number | null): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: headingDeg != null ? { heading: headingDeg } : {},
        geometry: { type: "Point", coordinates: position },
      },
    ],
  };
}

/**
 * Dessine une flèche courbe (arc + tête de flèche, évoquant une rotation) sur un canvas hors-écran,
 * en blanc plein sur fond transparent — voir `ROTATE_HANDLE_ICON_ID`/`init()`. Dessinée directement
 * dans sa couleur finale (pas en SDF comme `createGeolocationHeadingIconImageData`) : celle-ci a besoin
 * de la recoloration data-driven qu'apporte le SDF (par cap), alors que cette flèche est toujours
 * blanche — et le rendu SDF, pensé pour des aplats simples, rendait ce tracé fin flou/crénelé une fois
 * réduit à sa taille d'affichage. `size` (voir `ROTATE_HANDLE_ICON_CANVAS_PX`) est volontairement
 * bien plus grand que la taille d'affichage finale (`ROTATE_HANDLE_ICON_SIZE`), pour que MapLibre
 * la sous-échantillonne plutôt que de l'agrandir.
 */
function createRotateArrowIconImageData(size: number): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const center = size / 2;
  const radius = size * 0.32;
  const lineWidth = size * 0.12;
  // Repère canvas : angle 0 = droite, sens horaire, axe Y vers le bas — un arc de -225° à 45°
  // (270° de balayage) laisse un quart de cercle d'ouverture, assez pour se lire comme une flèche
  // de rotation plutôt qu'un simple anneau.
  const startAngle = (-225 * Math.PI) / 180;
  const endAngle = (45 * Math.PI) / 180;

  ctx.lineCap = "round";
  ctx.strokeStyle = "#ffffff";
  ctx.fillStyle = "#ffffff";
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.arc(center, center, radius, startAngle, endAngle);
  ctx.stroke();

  // Tête de flèche à l'extrémité d'arrivée de l'arc (`endAngle`) : sa base recouvre le bout rond du
  // trait pour un raccord net, sa pointe prolonge la courbe dans le sens du balayage (la tangente à
  // `endAngle`, parcouru dans le sens croissant des angles, vaut `endAngle + 90°`).
  const baseX = center + radius * Math.cos(endAngle);
  const baseY = center + radius * Math.sin(endAngle);
  const tangentAngle = endAngle + Math.PI / 2;
  const arrowLength = size * 0.22;
  const arrowHalfWidth = size * 0.15;
  const apexX = baseX + arrowLength * Math.cos(tangentAngle);
  const apexY = baseY + arrowLength * Math.sin(tangentAngle);
  const perpAngle = tangentAngle + Math.PI / 2;
  ctx.beginPath();
  ctx.moveTo(apexX, apexY);
  ctx.lineTo(baseX + arrowHalfWidth * Math.cos(perpAngle), baseY + arrowHalfWidth * Math.sin(perpAngle));
  ctx.lineTo(baseX - arrowHalfWidth * Math.cos(perpAngle), baseY - arrowHalfWidth * Math.sin(perpAngle));
  ctx.closePath();
  ctx.fill();

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Dessine l'icône "déplacer" classique (croix à 4 branches, une tête de flèche à chaque extrémité)
 * sur un canvas hors-écran, en blanc plein sur fond transparent — voir `MOVE_HANDLE_ICON_ID`/`init()`.
 * Même raisonnement que `createRotateArrowIconImageData` (couleur fixe, pas de SDF ; `size` très
 * suréchantillonné par rapport à la taille d'affichage finale, voir `MOVE_HANDLE_ICON_CANVAS_PX`).
 */
function createMoveHandleIconImageData(size: number): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const center = size / 2;
  const armLength = size * 0.34;
  const arrowLength = size * 0.16;
  const arrowHalfWidth = size * 0.11;
  const shaftHalfWidth = size * 0.045;

  ctx.fillStyle = "#ffffff";
  // Croix centrale (2 barres pleines, horizontale et verticale) reliant les 4 têtes de flèche.
  ctx.fillRect(center - shaftHalfWidth, center - armLength, shaftHalfWidth * 2, armLength * 2);
  ctx.fillRect(center - armLength, center - shaftHalfWidth, armLength * 2, shaftHalfWidth * 2);

  // Une tête de flèche par direction cardinale (haut/bas/gauche/droite), pointe vers l'extérieur.
  const directions: Array<[number, number]> = [
    [0, -1],
    [0, 1],
    [-1, 0],
    [1, 0],
  ];
  for (const [dx, dy] of directions) {
    const tipX = center + dx * (armLength + arrowLength);
    const tipY = center + dy * (armLength + arrowLength);
    const baseX = center + dx * armLength;
    const baseY = center + dy * armLength;
    const perpX = -dy;
    const perpY = dx;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(baseX + perpX * arrowHalfWidth, baseY + perpY * arrowHalfWidth);
    ctx.lineTo(baseX - perpX * arrowHalfWidth, baseY - perpY * arrowHalfWidth);
    ctx.closePath();
    ctx.fill();
  }

  return ctx.getImageData(0, 0, size, size);
}

/** Tracé SVG (repère 20×20, pointe au coin haut-gauche, comme un curseur système classique) de la
 * flèche d'un curseur distant — voir `createRemoteCursorElement`. */
const REMOTE_CURSOR_POINTER_SVG_PATH = "M0,0 L0,14.4 L4.4,11.2 L7.6,18.4 L10,17.2 L6.8,10 L11.6,10 Z";

/**
 * Construit le `<div>` d'un curseur distant (voir `MapAdapter.setRemoteCursor`) : une flèche SVG
 * (`.toka-remote-cursor__pointer`) et une étiquette de nom pleine (`.toka-remote-cursor__label`,
 * `style.css`), toutes deux dans la couleur du curseur — voir le commentaire au-dessus de
 * `REMOTE_CURSOR_POINTER_SVG_PATH` pour pourquoi c'est du DOM/CSS plutôt qu'un rendu GL. Passé tel
 * quel à `new maplibregl.Marker({ element })` par `setRemoteCursor` ; son contenu est ensuite tenu à
 * jour en place par `updateRemoteCursorElement` plutôt que d'être recréé à chaque mise à jour.
 */
function createRemoteCursorElement(cursor: RemoteCursor): HTMLElement {
  const container = document.createElement("div");
  container.className = "toka-remote-cursor";

  const pointer = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  pointer.setAttribute("class", "toka-remote-cursor__pointer");
  pointer.setAttribute("width", "20");
  pointer.setAttribute("height", "20");
  pointer.setAttribute("viewBox", "0 0 20 20");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", REMOTE_CURSOR_POINTER_SVG_PATH);
  pointer.appendChild(path);
  container.appendChild(pointer);

  const label = document.createElement("div");
  label.className = "toka-remote-cursor__label";
  container.appendChild(label);

  updateRemoteCursorElement(container, cursor);
  return container;
}

/** Met à jour en place le contenu (couleur/nom) d'un `<div>` construit par `createRemoteCursorElement`
 * — évite de recréer l'élément (et le `maplibregl.Marker` qui le porte) à chaque appel de
 * `setRemoteCursor` pour un `id` déjà affiché. Masque aussi tout le `<div>` (point + nom) quand
 * `cursor.pointerVisible === false` (souris hors de la carte, voir `"map:pointer-left"`) : sa
 * `position` est alors périmée et n'a plus de raison de rester affichée —
 * `RemoteCursor.selectedShapeId`/le contour de sélection distante (`buildRemoteSelectionGeoJSON`)
 * n'est PAS affecté par ce filtre. */
function updateRemoteCursorElement(container: HTMLElement, cursor: RemoteCursor): void {
  const path = container.querySelector("path");
  if (path) path.setAttribute("fill", cursor.color);
  const label = container.querySelector<HTMLElement>(".toka-remote-cursor__label");
  if (label) {
    label.textContent = cursor.userName;
    label.style.background = cursor.color;
  }
  container.style.display = cursor.pointerVisible === false ? "none" : "";
}

/** Nombre de segments par quart de cercle (voir `roundedRectRingPx`) — suffisant pour un rayon de
 * quelques pixels comme `REMOTE_SELECTION_LABEL_CORNER_RADIUS_PX`, une facette visible nécessiterait
 * un rayon bien plus grand que ce que cette pastille utilise jamais. */
const ROUNDED_RECT_SEGMENTS_PER_CORNER = 8;

/**
 * Anneau (fermé — premier point répété en dernier, convention `Polygon` déjà utilisée par
 * `outwardExpandedRingPx`/`shapesToGeoJSON`) d'un rectangle à coins arrondis, en espace écran —
 * utilisé pour la pastille de fond d'une étiquette de sélection distante (voir
 * `REMOTE_SELECTION_LABEL_BG_SOURCE_ID`/`buildRemoteSelectionGeoJSON`), construite ainsi (plutôt
 * qu'une icône SDF `icon-text-fit`) pour rester dans le même paradigme "polygone pixel puis
 * unprojeté" que le reste des indicateurs de cet adaptateur. `radiusPx` est plafonné à la moitié du
 * plus petit côté, pour ne jamais produire un rayon plus grand que le rectangle lui-même.
 */
function roundedRectRingPx(center: PixelPoint, halfWidthPx: number, halfHeightPx: number, radiusPx: number): PixelPoint[] {
  const r = Math.min(radiusPx, halfWidthPx, halfHeightPx);
  // Centre de l'arc de chaque coin, et angle de départ (convention écran : 0 = droite, croissant
  // dans le sens horaire puisque l'axe Y pointe vers le bas) — parcouru haut-droit → bas-droit →
  // bas-gauche → haut-gauche, un sens de parcours cohérent (peu importe lequel pour un `fill`).
  const corners = [
    { cx: center.x + halfWidthPx - r, cy: center.y - halfHeightPx + r, startAngle: -Math.PI / 2 },
    { cx: center.x + halfWidthPx - r, cy: center.y + halfHeightPx - r, startAngle: 0 },
    { cx: center.x - halfWidthPx + r, cy: center.y + halfHeightPx - r, startAngle: Math.PI / 2 },
    { cx: center.x - halfWidthPx + r, cy: center.y - halfHeightPx + r, startAngle: Math.PI },
  ];
  const ring: PixelPoint[] = [];
  for (const { cx, cy, startAngle } of corners) {
    for (let i = 0; i <= ROUNDED_RECT_SEGMENTS_PER_CORNER; i++) {
      const angle = startAngle + (i / ROUNDED_RECT_SEGMENTS_PER_CORNER) * (Math.PI / 2);
      ring.push({ x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
    }
  }
  ring.push(ring[0]!);
  return ring;
}

/** Résout `cursor.selectedShapeId` en une `Shape` de `lastShapes` — `undefined` si absent ou si la
 * forme n'existe plus (ex. supprimée par son auteur entre-temps). */
function resolveSelectedShape(cursor: RemoteCursor, lastShapes: Shape[]): Shape | undefined {
  return cursor.selectedShapeId ? lastShapes.find((s) => s.id === cursor.selectedShapeId) : undefined;
}

/**
 * Reconstruit, pour chaque curseur distant dont `selectedShapeId` résout à une forme de `lastShapes` :
 * `outlines` — un contour dédié (voir `REMOTE_SELECTION_OUTLINE_SOURCE_ID`), même construction que
 * `updateHighlightOutlineSource` (anneau agrandi vers l'extérieur en espace écran via `map.project`/
 * `unproject`) ; `labelBackgrounds` — la pastille arrondie (voir `roundedRectRingPx`) sous le nom,
 * dimensionnée au texte via `measureTextLinesPx` (mesuré à `FIT_MEASURE_FONT_SIZE_PX`, mis à l'échelle
 * de `REMOTE_SELECTION_LABEL_TEXT_SIZE_PX`) ; `labels` — le nom (+ `REMOTE_CURSOR_EDITING_SUFFIX`) en
 * blanc, centré sur cette pastille. L'ensemble est ancré juste au-dessus du contour (haut de sa boîte
 * englobante ÉCRAN, PAS le centre de la forme : y ancrer donnerait l'impression trompeuse d'un clic
 * réel à cet endroit), à `REMOTE_SELECTION_LABEL_GAP_PX` de marge. Tout est calculé ensemble pour ne projeter/déprojeter
 * l'anneau qu'une seule fois par curseur. Ids inconnus ou `selectedShapeId` absent : aucune feature
 * pour ce curseur, dans aucune des trois collections.
 */
function buildRemoteSelectionGeoJSON(
  cursors: RemoteCursor[],
  lastShapes: Shape[],
  map: maplibregl.Map,
): { outlines: GeoJSON.FeatureCollection; labelBackgrounds: GeoJSON.FeatureCollection; labels: GeoJSON.FeatureCollection } {
  const outlines: GeoJSON.Feature[] = [];
  const labelBackgrounds: GeoJSON.Feature[] = [];
  const labels: GeoJSON.Feature[] = [];
  const textSizeScale = REMOTE_SELECTION_LABEL_TEXT_SIZE_PX / FIT_MEASURE_FONT_SIZE_PX;
  for (const cursor of cursors) {
    const shape = resolveSelectedShape(cursor, lastShapes);
    if (!shape) continue;
    const ring = shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates;
    const ringPx = ring.map((c) => map.project(c));
    const expandedPx = outwardExpandedRingPx(ringPx, REMOTE_SELECTION_OUTLINE_MARGIN_PX);
    const expandedLngLat = expandedPx.map((p): LngLat => {
      const ll = map.unproject(p as Point);
      return [ll.lng, ll.lat];
    });
    outlines.push({
      type: "Feature",
      properties: { color: cursor.color },
      geometry: { type: "Polygon", coordinates: [expandedLngLat] },
    });

    const topPx = expandedPx.reduce((top, p) => (p.y < top.y ? p : top));
    const xs = expandedPx.map((p) => p.x);
    const bottomCenterPx = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: topPx.y - REMOTE_SELECTION_LABEL_GAP_PX };
    const userName = `${cursor.userName}${REMOTE_CURSOR_EDITING_SUFFIX}`;
    const { maxLineWidthPx, totalHeightPx } = measureTextLinesPx(userName);
    const halfWidthPx = (maxLineWidthPx * textSizeScale) / 2 + REMOTE_SELECTION_LABEL_PADDING_X_PX;
    const halfHeightPx = (totalHeightPx * textSizeScale) / 2 + REMOTE_SELECTION_LABEL_PADDING_Y_PX;
    // `bottomCenterPx` est le bas de la pastille (juste au-dessus du contour) : son centre remonte
    // donc d'une demi-hauteur par rapport à ce point.
    const centerPx = { x: bottomCenterPx.x, y: bottomCenterPx.y - halfHeightPx };

    const bgRingLngLat = roundedRectRingPx(centerPx, halfWidthPx, halfHeightPx, REMOTE_SELECTION_LABEL_CORNER_RADIUS_PX).map(
      (p): LngLat => {
        const ll = map.unproject(p as Point);
        return [ll.lng, ll.lat];
      },
    );
    labelBackgrounds.push({
      type: "Feature",
      properties: { color: cursor.color },
      geometry: { type: "Polygon", coordinates: [bgRingLngLat] },
    });

    const centerLngLat = map.unproject(centerPx as Point);
    labels.push({
      type: "Feature",
      properties: { userName },
      geometry: { type: "Point", coordinates: [centerLngLat.lng, centerLngLat.lat] },
    });
  }
  return {
    outlines: { type: "FeatureCollection", features: outlines },
    labelBackgrounds: { type: "FeatureCollection", features: labelBackgrounds },
    labels: { type: "FeatureCollection", features: labels },
  };
}

interface DrawingState {
  points: LngLat[];
  /** Voir `DrawTriggerOptions.skipSave` — capturé au `startDrawingPolygon()`, reporté sur l'évènement `shape:created` émis à la finalisation. */
  skipSave: boolean;
  /** Voir `DrawTriggerOptions.skipHistory` — même capture/report que `skipSave` ci-dessus. */
  skipHistory: boolean;
  /** Voir `DrawTriggerOptions.skipConfirm` — même capture/report que `skipSave` ci-dessus. */
  skipConfirm: boolean;
  /** Voir `StartDrawingOptions.skipTutorial` — capturé au `startDrawingPolygon()`, détermine si
   * `"drawing:tutorial-step"`/`"drawing:tutorial-ended"` sont émis pour cette session de dessin
   * précise. */
  skipTutorial: boolean;
  /** Vrai pendant que le curseur survole le 1er sommet à une distance qui clôturerait le polygone
   * au clic (voir `isNearClosingPoint`/`handlePolygonDrawingMove`) — recalculé à chaque
   * `mousemove`, lu par `draftToGeoJSON` (`canClose`) pour grossir ce sommet uniquement à ce
   * moment-là, pas dès que `MIN_POINTS_TO_CLOSE` est atteint. */
  closeHoverActive: boolean;
}

interface RectDrawingState {
  /** 1er coin cliqué (en pixels écran), ou null tant qu'aucun clic n'a eu lieu. Le rectangle est
   * construit en espace écran, pas en lng/lat, pour rester un vrai rectangle à l'écran même si
   * la carte est pivotée (bearing) — même logique que la rotation de forme, voir plus haut. */
  anchorPx: PixelPoint | null;
  /** Voir `DrawTriggerOptions.skipSave` — capturé au `startDrawingRectangle()`, reporté sur l'évènement `shape:created` émis à la finalisation. */
  skipSave: boolean;
  /** Voir `DrawTriggerOptions.skipHistory` — même capture/report que `skipSave` ci-dessus. */
  skipHistory: boolean;
  /** Voir `DrawTriggerOptions.skipConfirm` — même capture/report que `skipSave` ci-dessus. */
  skipConfirm: boolean;
  /** Voir `StartDrawingOptions.skipTutorial` — capturé au `startDrawingRectangle()`, détermine si
   * `"drawing:tutorial-step"`/`"drawing:tutorial-ended"` sont émis pour cette session de dessin
   * précise. */
  skipTutorial: boolean;
}

/**
 * Session de dessin d'une image (BESOINS.md, `startDrawingImage`) — même geste en 2 clics que
 * `RectDrawingState`, mais le second coin est contraint par `ratio` (largeur/hauteur de l'image)
 * plutôt que suivre librement le curseur (voir `imageCornersFromScreen`). `width`/`height` sont
 * toujours déjà résolus à cet instant (voir `DrawAdapter.startDrawingImage`).
 */
interface ImageDrawingState {
  anchorPx: PixelPoint | null;
  src: string;
  /** `width / height` — sert à contraindre le rectangle dessiné (voir `imageCornersFromScreen`). */
  ratio: number;
  width: number;
  height: number;
  scale?: number;
  opacity?: number;
  rotation?: number;
  svg?: boolean;
  skipSave: boolean;
  skipHistory: boolean;
  skipConfirm: boolean;
  skipTutorial: boolean;
}

/**
 * Session de dessin d'un cercle (`startDrawingCircle`) — même geste en 2 clics que
 * `RectDrawingState` : le premier clic pose le centre (`anchorPx`), le second définit le rayon
 * (distance écran jusqu'à ce point) — voir `circlePointsFromScreen`.
 */
interface CircleDrawingState {
  /** Centre cliqué (en pixels écran), ou null tant qu'aucun clic n'a eu lieu. */
  anchorPx: PixelPoint | null;
  /** Voir `DrawTriggerOptions.skipSave` — capturé au `startDrawingCircle()`, reporté sur l'évènement `shape:created` émis à la finalisation. */
  skipSave: boolean;
  /** Voir `DrawTriggerOptions.skipHistory` — même capture/report que `skipSave` ci-dessus. */
  skipHistory: boolean;
  /** Voir `DrawTriggerOptions.skipConfirm` — même capture/report que `skipSave` ci-dessus. */
  skipConfirm: boolean;
  /** Voir `StartDrawingOptions.skipTutorial` — capturé au `startDrawingCircle()`, détermine si
   * `"drawing:tutorial-step"`/`"drawing:tutorial-ended"` sont émis pour cette session de dessin
   * précise. */
  skipTutorial: boolean;
}

interface EditingState {
  shapeId: string;
  /** Sommets uniques de l'anneau (sans le point de clôture dupliqué). */
  points: LngLat[];
  /** Voir `DrawTriggerOptions.skipSave` — capturé à `enableEditMode()`, reporté sur tous les
   * évènements `shape:vertex-moved`/`shape:rotated`/`shape:moved` de cette session d'édition. */
  skipSave: boolean;
  /** Voir `DrawTriggerOptions.skipHistory` — même capture/report que `skipSave` ci-dessus. */
  skipHistory: boolean;
  /** Voir `DrawTriggerOptions.skipConfirm` — même capture/report que `skipSave` ci-dessus. */
  skipConfirm: boolean;
}

/**
 * Session d'extension d'une ligne existante en cours d'édition (double-clic sur son 1er ou son
 * dernier sommet — voir `handleShapeDblClick`) : même geste qu'un dessin de ligne neuf
 * (`DrawingState`/`startDrawingLine`, un clic par sommet, clôturé par un clic proche du dernier
 * sommet posé, voir `isNearLastPoint`), mais les sommets posés ici sont fusionnés dans
 * `EditingState.points` à la fin (voir `finalizeExtendLine`) plutôt que de créer une nouvelle
 * forme. `points[0]` est toujours le sommet d'ancrage déjà existant sur la ligne (jamais un
 * nouveau sommet) ; `end` indique de quel côté de `editingState.points` (son 1er élément, ou son
 * dernier) cette extension la prolonge.
 */
interface ExtendingLineState {
  shapeId: string;
  end: "start" | "end";
  points: LngLat[];
  closeHoverActive: boolean;
  skipSave: boolean;
  skipHistory: boolean;
  skipConfirm: boolean;
}

/**
 * Redimensionnement contraint d'une forme rectangle/image individuelle par drag de sommet (voir
 * `MapLibreAdapter`'s handler `mousedown` sur `EDIT_VERTICES_LAYER_ID`) — même principe que
 * `SelectionResizingState`/`computeSelectionResizeScale` (défaire la rotation propre de la forme
 * pour travailler dans son repère local), appliqué à une forme seule plutôt qu'à un groupe. `null`
 * tant que la forme éditée n'est pas un rectangle à 4 sommets propres : dans ce cas, le drag de
 * sommet reste le déplacement libre historique (`MoveVertexCommand`).
 */
interface VertexResizingState {
  /** Index (0-3) du sommet déplacé — même ordre que `EditingState.points`. */
  cornerIndex: number;
  /** Sommets de la forme au début du drag, en pixels écran. */
  originalCornersPx: PixelPoint[];
  /** Rotation propre de la forme (`Shape.rotation`) au début du drag. */
  rotation: number;
  /** Vrai si `Shape.shapeKind === "image"` — force alors `scaleY = scaleX` pour garder le ratio
   * courant de la forme (voir `applyVertexResize`). */
  lockRatio: boolean;
}

/**
 * Redimensionnement d'un SEUL côté d'un rectangle en édition, par drag d'une des barres de
 * `EDGE_RESIZE_LAYER_ID` — même principe de repère local que `VertexResizingState`, mais un seul
 * axe change (le côté opposé reste fixe) au lieu des 2 dimensions à la fois. Restreint à
 * `shapeKind === "rectangle"` (voir `updateEditVisuals`/le handler `mousedown` de
 * `EDGE_RESIZE_LAYER_ID`) : contrairement au coin, jamais proposé pour une forme-image (étirer un
 * seul côté casserait délibérément son ratio, une décision produit différente et plus large que ce
 * que ce mécanisme couvre).
 */
interface EdgeResizingState {
  /** Index (0-3) de l'arête tirée — entre les sommets edgeIndex et (edgeIndex+1)%4, même ordre que
   * `EditingState.points`. */
  edgeIndex: number;
  /** Sommets de la forme au début du drag, en pixels écran. */
  originalCornersPx: PixelPoint[];
  /** Rotation propre de la forme (`Shape.rotation`) au début du drag. */
  rotation: number;
  /** Sommets au début du drag, en LngLat — pour le garde-fou `hasMoved` à la fin du geste (voir
   * `handleEdgeResizeEnd`), même rôle que `draggingVertexOriginalPoints` pour le drag de sommet. */
  originalPoints: LngLat[];
}

interface RotatingState {
  shapeId: string;
  centerPx: PixelPoint;
  originalPointsPx: PixelPoint[];
  startAngleDeg: number;
  startRotation: number;
  /** Rotation courante (mise à jour à chaque mousemove), lue au mouseup. */
  currentRotation: number;
}

interface MovingState {
  shapeId: string;
  startCursorPx: PixelPoint;
  /** Sommets au début du drag (avant translation) — le delta écran leur est appliqué à chaque mousemove. */
  originalPoints: LngLat[];
}

/** Clic-droit-glisser (ou Ctrl+glisser hors `editMode`) en cours, pour la rotation de la caméra —
 * voir `handleMapRotateDragStart`, qui remplace le `dragRotate` natif de MapLibre par un geste "à la
 * OpenLayers" (angle autour du centre de l'écran, pas un simple delta horizontal en pixels). */
interface MapRotateState {
  /** Centre du conteneur de la carte, en pixels écran — le pivot autour duquel l'angle du curseur
   * est mesuré (voir `angleDeg`), pas le centre géographique courant (les deux coïncident toujours
   * à l'écran, mais celui-ci reste valable même si la vue est repositionnée pendant le drag, ce qui
   * n'arrive normalement pas puisque `dragPan` est désactivé pendant ce geste). */
  centerPx: PixelPoint;
  startAngleDeg: number;
  startBearing: number;
}

// --- Sélection multiple (BESOINS.md §13) ---

/** Ctrl+glisser en cours, avant que le rectangle ne soit finalisé (voir `handleSelectionDrawEnd`). */
interface SelectionDrawState {
  anchorPx: PixelPoint;
}

interface SelectionState {
  /** 4 coins du rectangle, dans le même ordre que `rectangleCornersPx` — mis à jour après chaque
   * redimensionnement/rotation/déplacement pour refléter l'état courant. */
  corners: LngLat[];
  /** Rotation cumulée du rectangle depuis sa création, en degrés — indépendante de la rotation
   * propre de chaque forme contenue (portée par `Shape.rotation`). */
  rotation: number;
  /** Ids des formes à l'intérieur au moment de la création du rectangle. Pas recalculé
   * automatiquement : redimensionner/tourner/déplacer le rectangle ne fait pas entrer/sortir de
   * formes, seul un nouveau Ctrl+glisser (après avoir fermé celui-ci) peut changer la sélection.
   * Seule exception : `removeFromSelection` en retire explicitement une forme dont le `onSave` a
   * échoué (voir son commentaire). */
  shapeIds: string[];
}

interface SelectionResizingState {
  /** Index (0-3) du coin déplacé — même ordre que `SelectionState.corners`. */
  cornerIndex: number;
  /** Coins du rectangle au début du drag, en pixels écran. */
  originalCornersPx: PixelPoint[];
  /** Rotation du rectangle au début du drag (voir `computeSelectionResizeScale`). */
  rotation: number;
  /** Formes sélectionnées telles qu'elles étaient au début du drag — sert de base à l'aperçu live
   * (voir `handleSelectionResizeMove`), jamais mutée pendant le drag. */
  originalShapes: Shape[];
}

interface SelectionRotatingState {
  centerPx: PixelPoint;
  startAngleDeg: number;
  originalCornersPx: PixelPoint[];
  /** Delta de rotation courant (non normalisé), mis à jour à chaque mousemove, lu au mouseup. */
  currentDeltaDeg: number;
  /** Formes sélectionnées telles qu'elles étaient au début du drag — sert de base à l'aperçu live
   * (voir `handleSelectionRotateMove`), jamais mutée pendant le drag. */
  originalShapes: Shape[];
}

interface SelectionMovingState {
  startCursorPx: PixelPoint;
  originalCornersPx: PixelPoint[];
  /** Formes sélectionnées telles qu'elles étaient au début du drag — sert de base à l'aperçu live
   * (voir `handleSelectionMoveMove`), jamais mutée pendant le drag. */
  originalShapes: Shape[];
}

/** Voir `MapLibreAdapter.lastEmittedMapView` — égalité par valeur, pas par référence. */
function mapViewStatesEqual(a: MapViewState, b: MapViewState): boolean {
  return (
    a.center[0] === b.center[0] &&
    a.center[1] === b.center[1] &&
    a.zoom === b.zoom &&
    a.zoomMin === b.zoomMin &&
    a.zoomMax === b.zoomMax &&
    a.rotationDeg === b.rotationDeg
  );
}

/** Voir `MapLibreAdapter.setDefaultShapeStyle`/`setDefaultShapeHoverStyle` — égalité par valeur,
 * pas par référence, pour ne pas émettre `"shape-defaults:changed"` (`MapAdapterEvent`) quand
 * `SetConfigurationCommand` réapplique une valeur qui n'a en réalité pas changé (ex. seule la vue
 * caméra a changé, voir `applyConfigurationState`). */
function shapeStylesEqual(a: ShapeStyle | undefined, b: ShapeStyle | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.bgColor === b.bgColor && a.borderColor === b.borderColor && a.borderWidth === b.borderWidth;
}

/**
 * Voir `MapLibreAdapter.setThemes` — égalité par valeur, pour ne redessiner que sur un vrai
 * changement. Comparateur dédié plutôt que `shapeStylesEqual`, qui ignore `borderDashed` (sans
 * conséquence pour le style par défaut, dont il est le seul appelant, mais un thème doit être
 * comparé sur la totalité de `ShapeStyle`).
 */
function fullShapeStylesEqual(a: ShapeStyle | undefined, b: ShapeStyle | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.bgColor === b.bgColor &&
    a.borderColor === b.borderColor &&
    a.borderWidth === b.borderWidth &&
    a.borderDashed === b.borderDashed
  );
}

/** Voir `shapeThemesEqual` — les 2 seuls champs de texte qu'un thème porte. */
function textStylesEqual(a: ShapeTextStyle | undefined, b: ShapeTextStyle | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.color === b.color && a.bgColor === b.bgColor && a.fixedSizePx === b.fixedSizePx;
}

/** Voir `MapLibreAdapter.setThemes` — égalité par valeur de la table entière (ids et contenus). */
function shapeThemesEqual(a: ShapeThemes, b: ShapeThemes): boolean {
  if (a === b) return true;

  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;

  return aKeys.every((key) => {
    const themeA = a[key];
    const themeB = b[key];
    if (!themeB) return false;
    return (
      fullShapeStylesEqual(themeA!.style, themeB.style) &&
      fullShapeStylesEqual(themeA!.hoverStyle, themeB.hoverStyle) &&
      textStylesEqual(themeA!.text, themeB.text) &&
      textStylesEqual(themeA!.hoverText, themeB.hoverText) &&
      themeA!.height === themeB.height &&
      themeA!.elevation === themeB.elevation &&
      themeA!.hoverHeight === themeB.hoverHeight &&
      themeA!.hoverElevation === themeB.hoverElevation &&
      themeA!.minZoomOffset === themeB.minZoomOffset
    );
  });
}

/**
 * Sources dont TOUS les calques sont masqués le temps d'une capture d'image (voir
 * `MapLibreAdapter.captureImage`) : poignées d'édition, sélection, brouillon de dessin, guides
 * d'aimantation, surlignage, itinéraire, position de l'utilisateur, sélections des autres
 * utilisateurs, debug du calcul d'itinéraire. Tout ce qui répond à un geste en cours ou désigne
 * quelque chose à quelqu'un — jamais le contenu du document lui-même.
 *
 * Par source et non par id de calque : chacune n'existe que pour ça, et une source en porte souvent
 * plusieurs (contour + points, fond + libellé) — un calque ajouté plus tard à l'une d'elles est ainsi
 * couvert sans avoir à penser à l'inscrire ici. Les calques de survol/halo des formes font exception
 * et se listent un à un (voir `interactionLayerIds`) : ils partagent la source des formes.
 */
const INTERACTION_SOURCE_IDS = [
  DRAFT_SOURCE_ID,
  EDIT_SOURCE_ID,
  EDGE_RESIZE_SOURCE_ID,
  ADD_VERTEX_HOVER_SOURCE_ID,
  ROTATE_SOURCE_ID,
  MOVE_SOURCE_ID,
  SNAP_SOURCE_ID,
  SELECTION_SOURCE_ID,
  SELECTION_HANDLES_SOURCE_ID,
  SELECTION_ROTATE_SOURCE_ID,
  HIGHLIGHT_SOURCE_ID,
  HIGHLIGHT_OUTLINE_SOURCE_ID,
  ROUTE_SOURCE_ID,
  GEOLOCATION_SOURCE_ID,
  REMOTE_SELECTION_OUTLINE_SOURCE_ID,
  REMOTE_SELECTION_LABEL_SOURCE_ID,
  REMOTE_SELECTION_LABEL_BG_SOURCE_ID,
  PATHFINDING_DEBUG_BLOCKED_SOURCE_ID,
  PATHFINDING_DEBUG_CHEAP_SOURCE_ID,
];

/** Fond des captures en JPEG quand le document n'en définit aucun — voir `captureImage`. */
const OPAQUE_CAPTURE_BACKGROUND = "#ffffff";

/** Voir `MapLibreAdapter.captureImage`. */
export interface CaptureImageOptions {
  /** Format de sortie. Par défaut : `"image/png"` — sans perte, et il garde la transparence (utile
   * sans fond de tuiles, où la carte ne peint rien sous les formes). En `"image/jpeg"`, qui ne la
   * connaît pas, ce qui n'a pas été peint reçoit la couleur de fond du document, ou du blanc. */
  type?: "image/png" | "image/jpeg" | "image/webp";
  /** Qualité 0-1 des formats avec perte (`jpeg`/`webp`), ignorée en PNG. */
  quality?: number;
  /** Pixels de sortie par pixel CSS. Par défaut : celui du rendu courant — au-delà, on étirerait des
   * pixels que la carte n'a pas dessinés. */
  pixelRatio?: number;
  /** Bornes de la taille de sortie, en pixels CSS, ratio conservé : l'image est réduite pour y
   * tenir, jamais agrandie. Omises : la taille de la carte à l'écran. */
  maxWidth?: number;
  maxHeight?: number;
  /** Attendre que les tuiles de la vue courante soient toutes chargées avant de capturer (défaut :
   * `true`). `false` capture immédiatement ce qui est à l'écran, trous compris. */
  waitForTiles?: boolean;
  /** Garder les calques de `INTERACTION_SOURCE_IDS` (sélection, poignées, surlignage...). Par
   * défaut `false` : une capture montre la carte, pas l'état de l'outil. */
  includeInteractionOverlays?: boolean;
}

/** Voir `MapLibreAdapter.captureImage`. */
export interface CaptureImageResult {
  blob: Blob;
  /** Taille réelle de l'image produite, en pixels (taille CSS × `pixelRatio`). */
  width: number;
  height: number;
  /**
   * Formes dont l'image SVG n'a pas pu être dessinée, son hôte ne l'ayant pas servie en CORS (voir
   * `loadImageForCanvas`) — la capture est complète à ceci près. Vide dans le cas normal.
   */
  skippedImageShapeIds: string[];
}

/**
 * Implémentation MapLibre GL des interfaces MapAdapter/DrawAdapter.
 * Voir BESOINS.md §3, §5, §10. MapLibre ne fournit pas d'outil de dessin/
 * rotation de forme nativement : c'est codé ici à la main. Le calcul de
 * rotation se fait en espace écran (project/unproject) pour rester correct
 * même si la carte elle-même est pivotée.
 */
export class MapLibreAdapter implements MapAdapter, DrawAdapter {
  private map: MlMap | null = null;
  /** Conteneur DOM passé à `init()` — conservé (en plus d'être observé par `resizeObserver`) pour y
   * insérer `backgroundMap` au bon endroit (voir `updateBackgroundMode`/`BackgroundMap`). */
  private rootContainer: HTMLElement | null = null;
  /** Suit le conteneur DOM passé à `init()` pour appeler `map.resize()` sur tout changement de sa
   * taille (ex. une sidebar qui pousse la carte en flex) — MapLibre ne suit nativement que le
   * "resize" de `window` (`trackResize`), pas celui de son propre conteneur. */
  private resizeObserver: ResizeObserver | null = null;
  /** Non-null tant que le mode "3 couches" (carte de fond secondaire, voir `BackgroundMap`) est actif
   * — créée/détruite dynamiquement par `updateBackgroundMode` à chaque bascule de la condition
   * d'activation (au moins un SVG affiché ET `tileLayerName !== "unset"`). */
  private backgroundMap: BackgroundMap | null = null;
  /** Ce que la carte PRINCIPALE peint réellement comme fond en ce moment — distinct de
   * `this.tileLayerName` (la valeur LOGIQUE/configurée, lue par `getTileLayerName()`) : tant que le
   * mode 3-couches est actif, la principale est forcée à `"unset"` réel même si `this.tileLayerName`
   * ne l'est pas (voir `updateBackgroundMode`). */
  private primaryPaintedTileLayerName: TileLayerName = "unset";
  private listeners = new Set<MapAdapterEventListener>();
  private drawingState: DrawingState | null = null;
  private rectDrawingState: RectDrawingState | null = null;
  private imageDrawingState: ImageDrawingState | null = null;
  private circleDrawingState: CircleDrawingState | null = null;
  /** Session de dessin d'une ligne (`startDrawingLine`) — même forme que `DrawingState` (un clic par
   * sommet), réutilisée telle quelle : `closeHoverActive` reflète ici la proximité du DERNIER sommet
   * posé (pas le premier, voir `isNearLastPoint`), une ligne ne se refermant jamais. */
  private lineDrawingState: DrawingState | null = null;
  private editingState: EditingState | null = null;
  /** Non-null pendant l'extension d'une ligne en cours d'édition (voir `ExtendingLineState`) —
   * coexiste avec `editingState` (jamais avec les autres sessions de dessin, voir les resets dans
   * `startDrawing*`/`cancelDrawing`). */
  private extendingLineState: ExtendingLineState | null = null;
  /** Étiquette « segment · total » qui suit le curseur pendant le tracé d'une ligne. */
  private measureLabel: HTMLDivElement | null = null;
  private readonly showDrawingMeasures: boolean;
  /** Voir le commentaire de `handleShapeDblClick` : dernier id de forme trouvé par
   * `handleShapeClick`, avec son horodatage (`performance.now()`) — permet au double-clic de
   * retrouver la forme visée même si le 1er clic a fait bouger `#map` avant le 2e. */
  private lastClickedShapeId: string | null = null;
  private lastClickedShapeIdAt = 0;
  private draggingVertexIndex: number | null = null;
  /** Copie de `editingState.points` prise au `mousedown` du sommet — permet à `handleVertexDragEnd`
   * de détecter un simple clic (aucun `mousemove` entre-temps) et de ne pas émettre `shape:vertex-moved`/
   * `shape:scaled` pour un geste qui n'a rien déplacé. */
  private draggingVertexOriginalPoints: LngLat[] | null = null;
  private vertexResizingState: VertexResizingState | null = null;
  private edgeResizingState: EdgeResizingState | null = null;
  private rotatingState: RotatingState | null = null;
  private movingState: MovingState | null = null;
  private pendingMouseMove: MapMouseEvent | null = null;
  private pendingMouseMoveFrame: number | null = null;
  private lastMouseMove: MapMouseEvent | null = null;
  private snappingEnabled = false;
  private constrainEnabled = false;
  /** Sommet fantôme actuellement affiché au survol d'une arête (voir `handleAddVertexHoverMove`) —
   * `null` hors survol éligible (rectangle/cercle exclus, un autre geste d'édition déjà en cours, ou
   * curseur trop près d'un sommet réel/trop loin de toute arête). `edgeIndex` est l'index (dans
   * `EditingState.points`) du sommet PRÉCÉDENT le point survolé — le nouveau sommet s'insère donc à
   * `edgeIndex + 1`. */
  private addVertexHoverState: { shapeId: string; edgeIndex: number; position: LngLat } | null = null;
  private mapRotateState: MapRotateState | null = null;
  private selectionDrawState: SelectionDrawState | null = null;
  private selectionState: SelectionState | null = null;
  private selectionResizingState: SelectionResizingState | null = null;
  private selectionRotatingState: SelectionRotatingState | null = null;
  private selectionMovingState: SelectionMovingState | null = null;
  /** Derniers coins calculés pendant le tracé du rectangle de sélection (voir
   * `handleSelectionDrawMove`/`handleSelectionDrawEnd`) — un champ dédié plutôt que de réutiliser
   * `updateRectDraftSource` (dessin de rectangle-FORME classique, `DRAFT_SOURCE_ID`) : ce tracé
   * alimente directement `SELECTION_SOURCE_ID` (remplissage blanc, voir
   * `updateSelectionFillSource`) plutôt que les layers `DRAFT_*` (`colors.draft`) partagés avec le
   * dessin de forme — les deux tracés restent mutuellement exclusifs (voir le garde `mousedown` dans
   * `init()`) mais n'ont pas la même couleur pendant le glissé. */
  private selectionDraftCorners: LngLat[] = [];
  /** Dernier `shapeIds` émis via `"selection:changed"` pendant le tracé du rectangle de sélection
   * (voir `handleSelectionDrawMove`) — évite de ré-émettre à chaque `mousemove` (potentiellement
   * plusieurs par frame) tant que l'ensemble de formes à l'intérieur n'a pas réellement changé. */
  private lastLiveSelectionShapeIds: string[] = [];
  /** Dernière vue émise via `"map-view:changed"` (voir `init()`'s handler `"moveend"`) — un seul
   * changement "logique" (ex. `setMapView`, qui appelle `setCenter`/`setZoomBounds`/`setZoom`/
   * `setRotation` séparément) peut déclencher PLUSIEURS `"moveend"` natifs successifs, chacun lu une
   * fois tous ces appels synchrones déjà retombés (donc déjà avec le même état final) : sans ce
   * garde-fou, l'évènement serait émis plusieurs fois de suite avec un contenu identique. */
  private lastEmittedMapView: MapViewState | null = null;
  /** `true` pendant qu'un `requestAnimationFrame` est déjà programmé pour appeler
   * `refreshShapesTextSize` (voir le handler `"zoom"` dans `init()`) — coalesce les multiples
   * évènements `"zoom"` d'une même frame (un geste de zoom en émet plusieurs) en UN SEUL recalcul par
   * frame, plutôt qu'un par évènement. */
  private shapesTextSizeRefreshScheduled = false;
  /** Actif pendant `startEditingMapView`/avant `endEditingMapView`/`cancelEditingMapView` — `before`
   * est la vue capturée à l'entrée dans le mode, à laquelle `cancelEditingMapView` revient ;
   * `pitchBounds` les bornes de pitch à restaurer à la sortie (voir `restorePitchBounds`) ;
   * `allowCancel` reflète `StartEditingMapViewOptions.allowCancel`. */
  private mapViewEditState: {
    before: { center: LngLat; zoom: number; rotationDeg: number };
    pitchBounds: { min: number; max: number };
    allowCancel: boolean;
  } | null = null;
  /** Dernières formes reçues via renderShapes(), pour retrouver une forme par id. */
  private lastShapes: Shape[] = [];
  /** `lastShapes` privé des formes masquées par `Shape.minZoomOffset` au dernier rendu. */
  private lastZoomVisibleShapes: Shape[] = [];
  private framing: MapFraming | undefined;
  private contentPointsCache: MercatorPoint[] | null = null;
  private framingResizeListener: (() => void) | null = null;
  /** Limite que la caméra ne doit jamais laisser voir au-delà, hors mode édition (voir `installLimitConstraint`). */
  private framingLimit: FramingRect | null = null;
  private limitConstraintInstalled = false;
  /** Suspension en cours (voir `holdFramingConstraints`) — `null` hors suspension. Un jeton plutôt
   * qu'un booléen : seule la levée de la DERNIÈRE suspension doit compter. */
  private framingHold: symbol | null = null;
  /** Bornes de zoom demandées pendant une suspension, posées à sa levée. */
  private heldZoomBounds: { min?: number; max?: number } | null = null;
  private zoomFloor: number | null = null;
  /** Conteneur DOM superposé à la carte, accueillant les `<img>` des formes avec `Shape.image`. */
  private imageOverlayContainer: HTMLDivElement | null = null;
  /** shapeId -> élément `<img>` affichant son image, plaqué sur la forme via une transform CSS. */
  private imageOverlayElements = new Map<string, HTMLImageElement>();
  /** Formes-image SVG, parmi les dernières reçues via renderShapes() (les rasters ont leur propre
   * candidate list, voir `rasterImageShapeCandidates`). */
  private imageShapeCandidates: (Shape & { image: ShapeImage })[] = [];
  /** Vrai pendant un pan/zoom actif (entre `movestart` et `moveend`) : voir `setImageOverlaysCompositing`. */
  private imageOverlaysCompositing = false;
  /** Formes-image raster, parmi les dernières reçues via renderShapes() — voir `syncRasterShapeImages`. */
  private rasterImageShapeCandidates: (Shape & { image: ShapeImage })[] = [];
  /** shapeId -> source/layer MapLibre de son image raster montée — voir `upsertRasterShapeImage`. */
  private rasterImageLayers = new Map<string, RasterShapeImageLayer>();
  /** Voir `roundedRasterSrc` : `radius|src` -> `blob:` arrondie (ou `src` d'origine après un échec). */
  private roundedImageSrcs = new Map<string, string>();
  private roundedSourceImages = new Map<string, Promise<HTMLImageElement | null>>();
  /** `src` -> dernier arrondi dessiné, affiché le temps que le suivant soit prêt. */
  private lastRoundedImageSrcs = new Map<string, string>();
  private roundedImageTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** `Shape.image.src` (original, PAS `resolvedSrc` — le redimensionnement CDN préserve le ratio) ->
   * dimensions réelles (px) sondées via `probeImageDimensions` — sert de ratio "contain" pour le
   * chemin RASTER quand `trustedImageDimensions` n'a rien d'exploitable (pas d'accès synchrone aux
   * dimensions réelles avant chargement pour ce chemin, contrairement au SVG qui lit
   * `img.naturalWidth`/`.naturalHeight` directement une fois son `<img>` chargé — voir `imageQuadFor`). */
  private probedImageDimensions = new Map<string, { width: number; height: number }>();
  /** `Shape.image.src` actuellement en cours de sondage, pour ne jamais lancer deux fois la même
   * requête tant qu'elle n'a pas résolu. */
  private pendingImageDimensionSrcs = new Set<string>();
  /** `Shape.image.src` dont le sondage a définitivement échoué — mémorisé pour ne pas retenter à
   * chaque rendu (une URL cassée le reste tant que la donnée elle-même ne change pas). */
  private failedImageDimensionSrcs = new Set<string>();
  private readonly resolveImageUrl?: ImageUrlResolver;
  private readonly minImageOverlayScreenPx: number;
  /** Zoom de la vue de départ quand le document n'a pas de cadrage — voir `setStartZoom`. */
  private startZoom: number | null = null;
  /** Ids des formes masquées par `Shape.minZoomOffset` au dernier rendu, pour ne redessiner que si
   * cet ensemble change — voir `refreshZoomVisibility`. */
  private zoomHiddenShapeKey = "";
  /** En mode édition, les formes sous leur seuil au dernier rendu, affichées estompées. */
  private underZoomShapeIds = new Set<string>();
  private readonly snapThresholds: SnapThresholds;
  private readonly editMode: boolean;
  private allowPitch: boolean;
  private defaultShapeTheme?: ShapeStyle;
  /** Voir `MapLibreAdapterOptions.themes`/`setThemes`. */
  private themes: ShapeThemes = {};
  /** Voir `MapLibreAdapterOptions.defaultThemeId`/`setDefaultThemeId`. */
  private defaultThemeId?: string;
  private tileLayerName: TileLayerName;
  private backgroundColor: string | undefined;
  /**
   * Désactivée par défaut, jamais auto-démarrée depuis `init()`/le constructeur (contrairement à
   * `tileLayerName` ci-dessus) : démarrer `navigator.geolocation.watchPosition` avant qu'un
   * consommateur (`createTokaMap`) ait eu la chance d'enregistrer son propre `on(...)` risquerait de
   * manquer le tout premier `"geolocation:changed"`/`"geolocation:visibility"` (la réponse du
   * navigateur est asynchrone, mais peut arriver avant qu'un appelant lointain dans une fonction async
   * — ex. après un `await` réseau — n'ait atteint son `mapAdapter.on(...)`). Toujours démarrée via un
   * appel explicite à `setGeolocationEnabled(true)`, dont le consommateur maîtrise le moment.
   */
  private geolocationEnabled = false;
  private geolocationWatchId: number | null = null;
  private geolocationOrientationListener: ((event: DeviceOrientationEvent) => void) | null = null;
  private userPosition: LngLat | null = null;
  /** Appelants de `waitForUserLocation` encore en attente — vidés au premier relevé, ou quand le
   * repérage s'arrête (auquel cas ils reçoivent `null` plutôt que d'attendre leur délai pour rien). */
  private userLocationWaiters: ((position: LngLat | null) => void)[] = [];
  /** Évite d'empiler une demande ponctuelle par appelant en attente : une seule suffit, son résultat
   * les sert tous (voir `waitForUserLocation`). */
  private userLocationRequestPending = false;
  private userHeading: number | null = null;
  /** Dédoublonne `"geolocation:visibility"` (voir `updateGeolocationVisibility`) — `null` avant la
   * toute première évaluation, pour que le premier appel émette toujours (même si `false`). */
  private geolocationMarkerVisible: boolean | null = null;
  /** Calques BRUTS du style courant : `applyOsmOptions` en dérive ce qui est peint, eux permettent
   * de tout recalculer sans retour réseau. */
  private backgroundStyleLayers: LayerSpecification[] = [];
  /** Pas forcément `this.osmOptions.style` : les deux divergent le temps du `fetch`. */
  private loadedOsmStyle: OsmStyleName = "light";
  /** Une réponse dont la génération n'est plus la courante est ignorée : un style lent demandé puis
   * abandonné ne doit pas écraser le choix suivant à son arrivée. */
  private osmStyleGeneration = 0;
  /** `fetch` de style en cours — attendu par `waitUntilIdle`. */
  private pendingOsmStyle: Promise<void> | null = null;
  /** À repasser tel quel à `removeBackgroundTileLayer` : retirer avec un tableau recalculé depuis
   * laisserait les anciens calques en place. */
  private paintedOsmLayers: LayerSpecification[] = [];
  private osmOptions: OsmBackgroundOptions | undefined;
  private readonly includeImages: boolean;
  private readonly includeIndirectImages: boolean;
  /** Id de la forme actuellement survolée (voir `editMode`) — pour effacer son état de survol au bon moment. */
  private hoveredShapeId: string | number | null = null;
  /** Formes mises en état de survol par `setHoveredShapes`, indépendamment de la souris. */
  private externallyHoveredShapeIds: string[] = [];
  /** Conteneur DOM de la barre d'outils flottante affichée au-dessus de la forme survolée (cadenas +
   * "..." — voir `showHoverToolbar`), monté une seule fois par instance (si `editMode`), au-dessus du
   * canvas (contrairement à `imageOverlayContainer`, monté DERRIÈRE). */
  private hoverToolbarContainer: HTMLDivElement | null = null;
  /** Id de la forme au-dessus de laquelle `hoverToolbarContainer` est actuellement affiché — peut
   * rester non-`null` momentanément après que le curseur ait quitté la forme elle-même, tant qu'il
   * reste sur la barre (voir `isPointerOverHoverToolbar`). */
  private hoverToolbarShapeId: string | number | null = null;
  /** Dernière position (haut-centre de la forme survolée, pixels relatifs au conteneur de la carte)
   * calculée pour `hoverToolbarContainer` — voir `updateHoverToolbarPosition`. Réutilisée telle
   * quelle comme `screenPosition` du `shape:contextmenu` émis par le bouton "...", pour rester
   * cohérent avec la position réellement affichée plutôt que de la recalculer différemment. */
  private hoverToolbarAnchorPx: PixelPoint | null = null;
  /** Rectangle (pixels relatifs au conteneur de la carte) couvrant le "couloir" vide entre le haut de
   * la bbox écran de `hoverToolbarShapeId` et le bas de `hoverToolbarContainer` — recalculé par
   * `updateHoverToolbarPosition` en même temps que `hoverToolbarAnchorPx`. Voir
   * `isPointInHoverToolbarCorridor` : tant que le curseur reste dans ce rectangle, on ignore ce que
   * `queryShapeIdAtPoint` détecte réellement à ce pixel (peut être une AUTRE forme, plus grande, dont
   * le fill layer déborde sous ce couloir) pour ne pas voler le survol/la barre en plein trajet vers
   * elle. La bbox est calculée à partir des sommets projetés réels de la forme (voir
   * `updateHoverToolbarPosition`), donc reste correcte même si la forme est tournée/penchée — pas
   * besoin que le couloir soit lui-même un rectangle aligné avec la forme, seulement avec l'écran. */
  private hoverToolbarCorridor: { minX: number; maxX: number; minY: number; maxY: number } | null = null;
  /** Bouton cadenas de `hoverToolbarContainer` — référencé pour rafraîchir son icône/titre selon
   * `Shape.locked` (voir `updateHoverToolbarLockUI`). */
  private hoverToolbarLockButton: HTMLButtonElement | null = null;
  /** Bouton "…" de `hoverToolbarContainer` — masqué quand `Shape.locked` (voir
   * `updateHoverToolbarLockUI`). */
  private hoverToolbarMenuButton: HTMLButtonElement | null = null;
  /** Vrai tant que le curseur survole la barre d'outils flottante elle-même (pas la forme) — évite que
   * `handleShapeMouseMove`/`handleShapeMouseOut` ne l'effacent alors que l'utilisateur essaie
   * justement de cliquer dessus (la barre flotte au-dessus de la forme, hors de son polygone). */
  private isPointerOverHoverToolbar = false;
  /** Minuteur en attente d'un `hideHoverToolbar()` différé — voir `scheduleHoverToolbarHide`. `null`
   * si aucun n'est en attente. */
  private hoverToolbarHideTimer: ReturnType<typeof setTimeout> | null = null;
  /** Ids ayant actuellement l'état de feature "selected" (voir `applySelectionFeatureState`) — pour
   * pouvoir l'effacer proprement quand la sélection change ou se ferme. */
  private selectedFeatureStateIds: string[] = [];
  /** `Shape.id` ayant actuellement l'état de feature "focused" (voir `setFocusedShape`) — distinct de
   * `selectedFeatureStateIds` (sélection multiple, BESOINS.md §13) : ce champ suit la sélection au
   * clic simple (une seule forme, pilotée par le consommateur, ex. `App.tsx` + sa Sidebar), qui n'a
   * jusqu'ici aucun retour visuel sur la carte elle-même. */
  private focusedShapeId: string | number | null = null;
  /** `Shape.id` (string) -> id numérique stable, assigné à la première rencontre — voir le
   * commentaire de `shapesToGeoJSON` sur pourquoi le feature-state a besoin d'un id numérique. Doit
   * rester stable d'un rendu à l'autre (une nouvelle valeur à chaque `setData` casserait le feature-
   * state en cours), d'où la table plutôt qu'un hash recalculé. */
  private readonly shapeNumericIds = new Map<string | number, number>();
  private nextNumericShapeId = 1;
  /** Une entrée par valeur distincte de `Shape.layer` actuellement en jeu — voir `ensureLayerBand`/
   * `syncLayerBands`, appelé depuis `renderShapes`. */
  private shapeLayerBands = new Map<number | null, ShapeLayerBand>();
  /** Miroir de `shapeLayerBands` (`fillLayerId` + `lineHitLayerId` de chaque bande — les deux
   * surfaces cliquables, voir leurs commentaires), lu par `queryShapeIdAtPoint` — recalculé via
   * `updateFillLayerIdsCache` seulement quand l'ensemble des bandes change, pas à chaque rendu. */
  private currentFillLayerIds: string[] = [];
  /** Ids actuellement exclus de la source principale le temps d'une édition (voir
   * `setEditingShapeFilter`) — mémorisé pour pouvoir réappliquer la même exclusion à une bande
   * nouvellement créée en cours d'édition. */
  private currentEditingFilterIds: string[] | null = null;
  /** Voir `setHoverPreviewShapes`. */
  private hoverPreviewShapeIds: ReadonlySet<string> = NO_HOVER_PREVIEW;
  /** Dernier contenu de la source d'édition, pour la redessiner quand l'aperçu de survol change. */
  private lastEditingShapes: { shapes: Shape[]; selected: boolean } | null = null;
  /** Ids actuellement surlignés via `highlightShapes` — voir `updateHighlightSource`, resynchronisé
   * à chaque `renderShapes` pour rester correct si une de ces formes est déplacée/éditée/supprimée
   * pendant qu'elle est surlignée. */
  private highlightedShapeIds: string[] = [];
  /** Non-`null` tant que l'animation de pulsation (voir `startHighlightAnimation`) tourne — évite
   * d'en programmer une deuxième en parallèle, et sert à l'arrêter (`cancelAnimationFrame`). */
  private highlightAnimationFrame: number | null = null;
  /** Horodatage (`performance.now()`) du premier frame de l'animation en cours — sert de référence
   * de phase pour la pulsation, remis à `null` à chaque arrêt (`stopHighlightAnimation`) pour que la
   * pulsation reparte toujours depuis le début à chaque nouvel appel à `highlightShapes`. */
  private highlightAnimationStartTime: number | null = null;
  private shapeFadeInStartTimes = new Map<string, number>();
  private shapeFadeInFrame: number | null = null;
  /** Marge (px écran) du dernier contour de surbrillance calculé (voir `updateHighlightOutlineSource`)
   * — piloté par `startHighlightAnimation` à chaque frame, mais aussi lu en dehors de l'animation
   * (`updateHighlightSource`, ex. quand une forme surlignée est déplacée) pour resynchroniser le
   * contour sans attendre le prochain frame de pulsation. Initialisé à la marge de repos (largeur
   * minimale / 2, même relation que l'ancien `line-offset`). */
  private highlightOutlineMarginPx = HIGHLIGHT_OUTLINE_WIDTH_MIN / 2;
  /** Curseurs distants actuellement affichés (voir `setRemoteCursor`/`removeRemoteCursor`), par id —
   * l'état affiché (couleur/nom/position/`selectedShapeId`) est porté directement ici plutôt que
   * résolu depuis `this.lastShapes` (un curseur distant n'est pas une `Shape`), et sert notamment à
   * `buildRemoteSelectionGeoJSON`/`hasRemoteSelections`. Le `maplibregl.Marker` du point+nom lui-même
   * (voir `remoteCursorMarkers` ci-dessous) porte redondamment sa propre couleur/nom/position dans son
   * élément DOM — les deux Maps sont tenues à jour ensemble par `setRemoteCursor`/`removeRemoteCursor`. */
  private remoteCursors: Map<string, RemoteCursor> = new Map();
  /** Un `maplibregl.Marker` par curseur distant affiché — voir `createRemoteCursorElement`/
   * `updateRemoteCursorElement`. */
  private remoteCursorMarkers: Map<string, maplibregl.Marker> = new Map();
  /** Non-`null` tant que `startRemoteSelectionAnimation` tourne (voir `hasRemoteSelections`) — même
   * rôle que `highlightAnimationFrame`, pour le contour de sélection distante. */
  private remoteSelectionAnimationFrame: number | null = null;
  /** Non-`null` tant que l'animation des pointillés du tracé d'itinéraire (voir
   * `startRouteAnimation`) tourne — même rôle que `highlightAnimationFrame`. */
  private routeAnimationFrame: number | null = null;
  /** Horodatage (`performance.now()`) du premier frame de l'animation en cours — remis à `null` à
   * chaque arrêt (`stopRouteAnimation`) pour que le motif reparte toujours depuis le début à chaque
   * nouvel appel à `showRoute`. */
  private routeAnimationStartTime: number | null = null;
  /** Index dans `ROUTE_DASH_PATTERNS` posé au dernier `setPaintProperty` — évite un appel redondant
   * tant que l'étape du cycle n'a pas changé (voir `startRouteAnimation`). */
  private lastRouteDashStepIndex: number | null = null;
  /** Voir `MapLibreAdapterOptions.colors`/`DEFAULT_COLORS` — figé à la construction (pas de setter,
   * comme `defaultShapeTheme`) : ces couleurs sont posées une fois pour toutes dans les `paint` créés
   * par `init()`, jamais recalculées à partir d'un feature-state/d'une expression data-driven. */
  private readonly colors: Required<TokaMapColors>;

  constructor(options: MapLibreAdapterOptions = {}) {
    this.resolveImageUrl = options.resolveImageUrl;
    this.minImageOverlayScreenPx = options.minImageOverlayScreenPx ?? DEFAULT_MIN_IMAGE_OVERLAY_SCREEN_PX;
    this.snapThresholds = { ...DEFAULT_SNAP_THRESHOLDS, ...options.snapThresholds };
    this.editMode = options.editMode ?? false;
    this.allowPitch = options.defaultAllowPitch ?? false;
    this.defaultShapeTheme = options.defaultShapeTheme;
    this.themes = options.themes ?? {};
    this.defaultThemeId = options.defaultThemeId;
    this.tileLayerName = options.defaultTileLayerName ?? "unset";
    this.osmOptions = options.defaultOsmOptions;
    this.backgroundColor = options.defaultBackgroundColor;
    this.includeImages = options.includeImages ?? true;
    this.includeIndirectImages = options.includeIndirectImages ?? true;
    this.showDrawingMeasures = options.showDrawingMeasures ?? false;
    this.colors = { ...DEFAULT_COLORS, ...options.colors };
  }

  /** Voir `MapLibreAdapterOptions.includeImages`/`includeIndirectImages` — factorisé, utilisé par
   * `syncShapeImages`/`syncRasterShapeImages` (candidature) et `upsertShapeImageOverlay`/
   * `upsertRasterShapeImage` (montage individuel, ex. pendant une édition en direct). */
  private shouldRenderShapeImage(shape: Shape): boolean {
    return this.includeImages && (this.includeIndirectImages || shape.shapeKind === "image");
  }

  async init(container: HTMLElement): Promise<void> {
    this.rootContainer = container;
    if (this.backgroundColor !== undefined) container.style.backgroundColor = this.backgroundColor;
    this.map = new maplibregl.Map({
      container,
      style: DEFAULT_STYLE,
      center: [2.5137, 48.9614], // Paris Nord Villepinte
      zoom: 16,
      bearing: 0,
      attributionControl: false,
      // Sur un écran haute densité (Retina/4K, devicePixelRatio 2-3), MapLibre rend par défaut à
      // cette résolution : 4 à 9x plus de pixels à calculer par frame. On plafonne ce ratio —
      // légèrement moins net sur ces écrans, mais un levier de fluidité important au zoom/pan.
      pixelRatio: Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO),
      // Mode 3D (inclinaison de la caméra, "pitch") : bloqué par défaut (`this.allowPitch === false`,
      // voir `defaultAllowPitch`/`setAllowPitch`), la carte reste alors toujours vue du dessus.
      // `maxPitch: 0` bloque le pitch au niveau du `Transform` lui-même, donc pour TOUTE source
      // (clic-droit-glisser natif, tactile, clavier, appel programmatique `setPitch`), pas seulement
      // le geste souris — même mécanisme que le blocage temporaire déjà utilisé par
      // `startEditingMapView` (voir son commentaire). Avec `allowPitch` actif, on repasse à
      // `MAX_PITCH_DEG` (le plafond par défaut de MapLibre) — `setAllowPitch` répète ce même calcul
      // via `map.setMaxPitch(...)` pour un changement en cours de session.
      maxPitch: this.allowPitch ? MAX_PITCH_DEG : 0,
      // Le geste natif "clic-droit-glisser tourne/incline la caméra" de MapLibre (`dragRotate`) est
      // désactivé par défaut au profit d'une implémentation maison bearing-seul (voir
      // `handleMapRotateDragStart` plus bas) : le natif dérive la rotation d'un simple delta
      // horizontal en pixels (`bearing += dx * 0.8`), ce qui ne correspond à aucune notion
      // géométrique cohérente (le point sous le curseur ne "suit" pas le geste) — contrairement à
      // OpenLayers, dont le `DragRotate` tourne la carte de l'angle balayé par le curseur autour du
      // centre de l'écran, comme si on tournait un volant. Avec `allowPitch: true`, ce raisonnement
      // ne s'applique plus : on VEUT le comportement natif (bearing + pitch), donc `dragRotate` est
      // réactivé et `isMapRotateTrigger` neutralise le handler maison (voir son commentaire) — sauf
      // pour Ctrl+clic-gauche-glisser en `editMode`, réservé au rectangle de sélection multiple (voir
      // le `mousedown` correspondant plus bas, qui désactive/réactive `dragRotate` ponctuellement
      // autour de ce seul geste).
      dragRotate: this.allowPitch,
      // En `editMode`, le double-clic est déjà un raccourci vers `enableEditMode` sur une forme
      // (voir `handleShapeDblClick`, qui appelle `preventDefault()` dans CE cas précis). Mais un
      // double-clic sur du vide (aucune forme sous le curseur) n'a rien à `preventDefault` et
      // déclenchait donc encore le zoom natif de MapLibre — non désiré en édition, où un double-clic
      // est un geste courant (ex. essayer de sélectionner une forme et rater légèrement). `this.editMode`
      // est figé au constructeur (jamais togglé dynamiquement, voir son commentaire plus bas), donc
      // le désactiver ici une fois pour toutes est suffisant, pas besoin d'un handler `dblclick`
      // séparé pour l'annuler cas par cas.
      doubleClickZoom: !this.editMode,
      // Shift est réservé au magnétisme (voir `setSnappingEnabled`), tenu pendant un glisser de
      // sommet ou de forme : le zoom natif Shift+glisser sur une boîte s'y superposerait.
      boxZoom: false,
    });

    await new Promise<void>((resolve) => this.map!.on("load", () => resolve()));

    // Tous les calques du fond de carte (`positron` : fond uni, routes, bâtiments, eau, zones,
    // libellés...) sont retirés — pas seulement les `symbol` (ex-raison : leur placement/anti-
    // collision recalculé à chaque zoom, seul coût notable). Nouvelle raison, plus large : le
    // `<div>` d'images SVG vit maintenant DERRIÈRE le canvas (voir son commentaire plus bas) — une
    // vraie image SVG (pas seulement un cas de test, voir BESOINS.md) serait sinon masquée par
    // n'importe quel calque de fond qui la recouvre géographiquement (route, bâtiment, eau...),
    // sans aucun rapport avec `Shape.layer`. Un canvas entièrement transparent laisse ce `<div>`
    // visible en toute circonstance ; seules nos propres formes/images (raster, dans ce même canvas)
    // et images SVG (derrière) doivent rester visibles, jamais un fond de carte réel non pertinent
    // pour un plan de salon/exposition — SAUF si `tileLayerName` demande explicitement un fond (voir
    // `addBackgroundTileLayer` plus bas). Conservés dans `backgroundStyleLayers` avant suppression :
    // c'est ce que `tileLayerName: "osm"` réaffiche, sans re-télécharger un second style.
    this.backgroundStyleLayers = captureAndStripStyleLayers(this.map);
    this.loadedOsmStyle = "light";

    // Fond de carte initial (voir `TileLayerName`) : appliqué avant toute autre source/calque, pour
    // qu'il reste toujours en dessous des formes (voir `addBackgroundTileLayer`'s usage de `beforeId`,
    // sans effet ici puisque la carte est encore entièrement vide à ce stade).
    this.addBackgroundTileLayer(this.tileLayerName);
    this.primaryPaintedTileLayerName = this.tileLayerName;

    // `DEFAULT_STYLE` reste le style chargé par MapLibre (il apporte sources/polices/sprite) : un
    // autre style n'en remplace que les calques, une fois téléchargé.
    this.selectOsmStyle(resolveOsmOptions(this.osmOptions).style);

    this.map.addSource(SHAPES_SOURCE_ID, {
      type: "geojson",
      data: shapesToGeoJSON([], this.editMode, this.colors),
    });
    // Source dédiée aux cercles (voir `CIRCLE_SOURCE_ID`) — alimentée en parallèle de
    // `SHAPES_SOURCE_ID` à chaque `renderShapes`, jamais seule.
    this.map.addSource(CIRCLE_SOURCE_ID, {
      type: "geojson",
      data: circleShapesToGeoJSON([], this.editMode, this.colors),
    });
    this.map.addSource(LINE_WALL_SOURCE_ID, { type: "geojson", data: lineWallsToGeoJSON([], this.editMode, this.colors) });
    // Pas de layers fill/outline statiques ici : ils sont créés à la demande, une bande par valeur
    // de `Shape.layer` en jeu — voir `ensureLayerBand`, appelé depuis `syncLayerBands`/`renderShapes`.
    // `EDITING_SHAPE_FILL_LAYER_ID` (créé juste après) sert d'ancre fixe : la bande la plus haute
    // s'insère toujours juste en dessous de lui, reproduisant l'ordre statique d'origine (formes
    // sous l'aperçu d'édition/dessin/rotation/sélection).

    this.map.addSource(EDITING_SHAPE_SOURCE_ID, {
      type: "geojson",
      data: shapesToGeoJSON([], this.editMode, this.colors),
    });
    // Anneau purement extérieur (voir `HALO_LINE_WIDTH_EXPRESSION`/`HALO_LINE_OFFSET_EXPRESSION` et
    // le commentaire de `SELECTED_SHAPE_HALO_MARGIN_PX`) — jamais à l'intérieur de la forme, y
    // compris pour un fond non parfaitement opaque.
    this.map.addLayer({
      id: EDITING_SHAPE_SELECTED_HALO_LAYER_ID,
      type: "line",
      source: EDITING_SHAPE_SOURCE_ID,
      paint: {
        "line-color": this.colors.accent,
        "line-width": HALO_LINE_WIDTH_EXPRESSION,
        "line-offset": HALO_LINE_OFFSET_EXPRESSION,
        "line-opacity": ["case", ["boolean", ["get", "selected"], false], 1, 0],
      },
    });
    this.map.addLayer({
      id: SHAPES_EXTRUSION_LAYER_ID,
      type: "fill-extrusion",
      source: SHAPES_SOURCE_ID,
      filter: SHAPES_EXTRUSION_FILTER,
      layout: { visibility: this.allowPitch ? "visible" : "none" },
      paint: {
        "fill-extrusion-color": [
          "case",
          ["boolean", ["feature-state", "hovered"], false],
          ["get", "hoverBgColor"],
          ["get", "bgColor"],
        ],
        "fill-extrusion-height": EXTRUSION_HEIGHT_EXPRESSION,
        "fill-extrusion-base": EXTRUSION_BASE_EXPRESSION,
        "fill-extrusion-opacity": SHAPE_EXTRUSION_OPACITY,
      },
    });
    this.map.addLayer({
      id: LINE_WALL_LAYER_ID,
      type: "fill-extrusion",
      source: LINE_WALL_SOURCE_ID,
      layout: { visibility: this.allowPitch ? "visible" : "none" },
      paint: {
        "fill-extrusion-color": ["case", ["boolean", ["feature-state", "hovered"], false], ["get", "hoverColor"], ["get", "color"]],
        "fill-extrusion-height": EXTRUSION_HEIGHT_EXPRESSION,
        "fill-extrusion-base": EXTRUSION_BASE_EXPRESSION,
        "fill-extrusion-opacity": SHAPE_EXTRUSION_OPACITY,
      },
    });
    this.map.addLayer({
      id: EDITING_SHAPE_FILL_LAYER_ID,
      type: "fill",
      source: EDITING_SHAPE_SOURCE_ID,
      filter: NOT_LINE_FILTER,
      paint: { "fill-color": ["get", "bgColor"] },
    });
    this.placeBuildingLayer();
    this.map.addLayer({
      id: EDITING_SHAPE_OUTLINE_LAYER_ID,
      type: "line",
      source: EDITING_SHAPE_SOURCE_ID,
      filter: ["all", NOT_DEFAULT_BORDER_FILTER, NOT_BORDER_DASHED_FILTER] as maplibregl.FilterSpecification,
      paint: {
        "line-color": ["get", "borderColor"],
        "line-width": EDITING_BORDER_WIDTH_EXPRESSION,
      },
    });
    this.map.addLayer({
      id: EDITING_SHAPE_OUTLINE_DASHED_LAYER_ID,
      type: "line",
      source: EDITING_SHAPE_SOURCE_ID,
      filter: ["all", NOT_DEFAULT_BORDER_FILTER, BORDER_DASHED_FILTER] as maplibregl.FilterSpecification,
      paint: {
        "line-color": ["get", "borderColor"],
        "line-width": EDITING_BORDER_WIDTH_EXPRESSION,
        "line-dasharray": EDIT_MODE_DEFAULT_BORDER_DASHARRAY,
      },
    });
    this.map.addLayer({
      id: EDITING_SHAPE_OUTLINE_DEFAULT_LAYER_ID,
      type: "line",
      source: EDITING_SHAPE_SOURCE_ID,
      filter: DEFAULT_BORDER_FILTER,
      paint: {
        "line-color": ["get", "borderColor"],
        "line-width": EDITING_BORDER_WIDTH_EXPRESSION,
        "line-dasharray": EDIT_MODE_DEFAULT_BORDER_DASHARRAY,
      },
    });
    // Contour de modification : toujours actif pour toute forme de cette source, pas de condition
    // (contrairement à `SHAPES_HOVER_OUTLINE_LAYER_PREFIX`, qui bascule sur un feature-state) — voir
    // EDITING_SHAPE_MODIFY_OUTLINE_LAYER_ID.
    this.map.addLayer({
      id: EDITING_SHAPE_MODIFY_OUTLINE_LAYER_ID,
      type: "line",
      source: EDITING_SHAPE_SOURCE_ID,
      paint: {
        "line-color": this.colors.accent,
        "line-width": SHAPE_HOVER_OUTLINE_WIDTH,
        "line-dasharray": EDIT_MODE_DEFAULT_BORDER_DASHARRAY,
      },
    });

    // Labels (Shape.text) : au-dessus de toutes les formes/bandes (voir le commentaire de
    // SHAPES_TEXT_SOURCE_ID) — layer unique, pas de bandes par Shape.layer comme pour les formes
    // elles-mêmes (un texte reste lisible au-dessus de n'importe quel calque).
    this.map.addSource(SHAPES_TEXT_SOURCE_ID, {
      type: "geojson",
      data: shapesTextToGeoJSON([], this.map),
    });
    for (const upright of [false, true]) {
      this.map.addLayer({
        id: upright ? SHAPES_TEXT_UPRIGHT_LAYER_ID : SHAPES_TEXT_LAYER_ID,
        type: "symbol",
        source: SHAPES_TEXT_SOURCE_ID,
        filter: upright ? UPRIGHT_TEXT_FILTER : GROUND_TEXT_FILTER,
        layout: shapeTextLayout(upright),
        paint: {
          "text-color": ["get", "color"],
          "text-halo-color": ["get", "haloColor"],
          "text-halo-width": ["get", "haloWidth"],
          "text-opacity": ["*", ["get", "opacity"], SHAPE_OPACITY_EXPRESSION],
        },
      });
    }

    this.map.addSource(EDITING_SHAPE_TEXT_SOURCE_ID, {
      type: "geojson",
      data: shapesTextToGeoJSON([], this.map),
    });
    for (const upright of [false, true]) {
      this.map.addLayer({
        id: upright ? EDITING_SHAPE_TEXT_UPRIGHT_LAYER_ID : EDITING_SHAPE_TEXT_LAYER_ID,
        type: "symbol",
        source: EDITING_SHAPE_TEXT_SOURCE_ID,
        filter: upright ? UPRIGHT_TEXT_FILTER : GROUND_TEXT_FILTER,
        layout: shapeTextLayout(upright),
        paint: {
          "text-color": ["get", "color"],
          "text-halo-color": ["get", "haloColor"],
          "text-halo-width": ["get", "haloWidth"],
          "text-opacity": ["get", "opacity"],
        },
      });
    }

    this.map.addSource(DRAFT_SOURCE_ID, {
      type: "geojson",
      data: draftToGeoJSON([]),
    });
    this.map.addLayer({
      id: DRAFT_LINE_LAYER_ID,
      type: "line",
      source: DRAFT_SOURCE_ID,
      filter: ["==", ["geometry-type"], "LineString"],
      // Trait PLEIN (pas de `line-dasharray`) : le pointillé (`EDIT_MODE_DEFAULT_BORDER_DASHARRAY`)
      // est réservé au style de repli d'une forme PERSISTÉE sans couleur définie (voir
      // `outlineDefaultLayerId`) — l'aperçu live pendant le tracé n'est jamais concerné, quelle que
      // soit la forme en cours de dessin (polygone/rectangle/cercle/ligne).
      paint: { "line-color": this.colors.draft, "line-width": 2 },
    });
    this.map.addLayer({
      id: DRAFT_VERTICES_LAYER_ID,
      type: "circle",
      source: DRAFT_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        // `canClose` (voir `draftToGeoJSON`) : le 1er sommet, une fois assez de sommets posés pour
        // qu'un clic dessus clôture le polygone, ressort nettement plus qu'un simple "1er sommet"
        // (`isFirst`, cf. rectangle où ce dernier n'a pas ce même geste de clôture).
        "circle-radius": ["case", ["get", "canClose"], 10, ["get", "isFirst"], 6, 4],
        "circle-color": this.colors.draft,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": ["case", ["get", "canClose"], 2, 1],
      },
    });

    this.map.addSource(EDIT_SOURCE_ID, {
      type: "geojson",
      data: editVerticesToGeoJSON([]),
    });
    this.map.addLayer({
      id: EDIT_VERTICES_LAYER_ID,
      type: "circle",
      source: EDIT_SOURCE_ID,
      paint: {
        "circle-radius": 6,
        "circle-color": this.colors.accentSecondary,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });

    this.map.addSource(EDGE_RESIZE_SOURCE_ID, {
      type: "geojson",
      data: edgeResizeHandlesToGeoJSON([]),
    });
    this.map.addLayer({
      id: EDGE_RESIZE_LAYER_ID,
      type: "line",
      source: EDGE_RESIZE_SOURCE_ID,
      layout: { "line-cap": "round" },
      // Même couleur que EDIT_VERTICES_LAYER_ID/ADD_VERTEX_HOVER_LAYER_ID (famille "poignées
      // d'édition") — purement visuel, toute l'interaction passe par EDGE_RESIZE_HIT_LAYER_ID
      // ci-dessous (voir son commentaire).
      paint: {
        "line-color": this.colors.accentSecondary,
        "line-width": 6,
      },
    });
    this.map.addLayer({
      id: EDGE_RESIZE_HIT_LAYER_ID,
      type: "line",
      source: EDGE_RESIZE_SOURCE_ID,
      paint: { "line-width": EDGE_RESIZE_HIT_WIDTH_PX, "line-opacity": 0 },
    });

    this.map.addSource(ADD_VERTEX_HOVER_SOURCE_ID, {
      type: "geojson",
      data: addVertexHoverToGeoJSON(null),
    });
    this.map.addLayer({
      id: ADD_VERTEX_HOVER_LAYER_ID,
      type: "circle",
      source: ADD_VERTEX_HOVER_SOURCE_ID,
      // Anneau creux (même contour que EDIT_VERTICES_LAYER_ID, fond transparent comme
      // SNAP_INDICATOR_LAYER_ID) : se distingue visuellement d'un sommet réel (plein) tout en restant
      // dans la même famille de couleur.
      paint: {
        "circle-radius": 6,
        "circle-color": "transparent",
        "circle-stroke-color": this.colors.accentSecondary,
        "circle-stroke-width": 2,
      },
    });

    this.map.addSource(ROTATE_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: ROTATE_LINE_LAYER_ID,
      type: "line",
      source: ROTATE_SOURCE_ID,
      filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": this.colors.rotate, "line-width": 1, "line-dasharray": [1, 2] },
    });
    this.map.addLayer({
      id: ROTATE_HANDLE_LAYER_ID,
      type: "circle",
      source: ROTATE_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-radius": 9,
        "circle-color": this.colors.rotate,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });
    this.map.addImage(ROTATE_HANDLE_ICON_ID, createRotateArrowIconImageData(ROTATE_HANDLE_ICON_CANVAS_PX), {
      pixelRatio: ROTATE_HANDLE_ICON_PIXEL_RATIO,
    });
    this.map.addLayer({
      id: ROTATE_HANDLE_ICON_LAYER_ID,
      type: "symbol",
      source: ROTATE_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      layout: {
        "icon-image": ROTATE_HANDLE_ICON_ID,
        "icon-size": ROTATE_HANDLE_ICON_SIZE,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });

    this.map.addSource(MOVE_SOURCE_ID, {
      type: "geojson",
      data: addVertexHoverToGeoJSON(null),
    });
    this.map.addLayer({
      id: MOVE_HANDLE_LAYER_ID,
      type: "circle",
      source: MOVE_SOURCE_ID,
      paint: {
        "circle-radius": 9,
        "circle-color": this.colors.rotate,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });
    this.map.addImage(MOVE_HANDLE_ICON_ID, createMoveHandleIconImageData(MOVE_HANDLE_ICON_CANVAS_PX), {
      pixelRatio: MOVE_HANDLE_ICON_PIXEL_RATIO,
    });
    this.map.addLayer({
      id: MOVE_HANDLE_ICON_LAYER_ID,
      type: "symbol",
      source: MOVE_SOURCE_ID,
      layout: {
        "icon-image": MOVE_HANDLE_ICON_ID,
        "icon-size": MOVE_HANDLE_ICON_SIZE,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });

    this.map.addSource(SNAP_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: SNAP_GUIDE_LINE_LAYER_ID,
      type: "line",
      source: SNAP_SOURCE_ID,
      filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": this.colors.accentSecondary, "line-width": 1, "line-dasharray": [3, 2] },
    });
    this.map.addLayer({
      id: SNAP_INDICATOR_LAYER_ID,
      type: "circle",
      source: SNAP_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-radius": 5,
        "circle-color": "transparent",
        "circle-stroke-color": this.colors.accentSecondary,
        "circle-stroke-width": 2,
      },
    });

    // Géolocalisation (BESOINS.md §15) : source vide tant que `setGeolocationEnabled(true)` n'a pas
    // reçu de première position (voir `updateGeolocationSource`) — même principe que ROTATE_SOURCE_ID
    // ci-dessus (source toujours présente, alimentée au fil de l'eau plutôt qu'ajoutée/retirée).
    this.map.addSource(GEOLOCATION_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addImage(
      GEOLOCATION_HEADING_ICON_ID,
      createGeolocationHeadingIconImageData(GEOLOCATION_HEADING_ICON_CANVAS_PX, this.colors.accent),
      { pixelRatio: GEOLOCATION_HEADING_ICON_PIXEL_RATIO },
    );
    this.map.addLayer({
      id: GEOLOCATION_HEADING_LAYER_ID,
      type: "symbol",
      source: GEOLOCATION_SOURCE_ID,
      // Ne s'affiche que si un cap est connu (voir `geolocationToGeoJSON`) — sinon rien à orienter.
      filter: ["has", "heading"],
      layout: {
        "icon-image": GEOLOCATION_HEADING_ICON_ID,
        "icon-size": GEOLOCATION_HEADING_ICON_SIZE,
        "icon-anchor": "center",
        // "map" : la flèche tourne avec le repère carte (nord géographique), pas avec l'écran — donc
        // reste orientée vers le cap réel même si l'utilisateur pivote la vue (bearing) ou pitche.
        "icon-rotation-alignment": "map",
        "icon-rotate": ["get", "heading"],
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
      paint: { "icon-opacity": 0.9 },
    });
    this.map.addLayer({
      id: GEOLOCATION_DOT_LAYER_ID,
      type: "circle",
      source: GEOLOCATION_SOURCE_ID,
      // Complémentaire du filtre de GEOLOCATION_HEADING_LAYER_ID ci-dessus : les deux ne s'affichent
      // jamais en même temps, un simple point tant qu'aucun cap n'est connu, la flèche une fois qu'il
      // l'est (voir `geolocationToGeoJSON`).
      filter: ["!", ["has", "heading"]],
      paint: {
        "circle-radius": 7,
        "circle-color": this.colors.accent,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });

    // Sélection multiple (BESOINS.md §13) : rectangle de sélection, ses poignées de redimensionnement
    // (coins) et sa poignée de rotation — même vocabulaire de layers que l'édition d'une forme seule.
    this.map.addSource(SELECTION_SOURCE_ID, {
      type: "geojson",
      data: selectionRectToGeoJSON([]),
    });
    this.map.addLayer({
      id: SELECTION_FILL_LAYER_ID,
      type: "fill",
      source: SELECTION_SOURCE_ID,
      // Opacité par défaut 0 (repos) : basculée à `SELECTION_FILL_OPACITY_DRAGGING` pendant un
      // glissé actif, voir `setSelectionInteracting`.
      paint: { "fill-color": SELECTION_RECT_FILL_COLOR, "fill-opacity": 0 },
    });
    this.map.addLayer({
      id: SELECTION_OUTLINE_LAYER_ID,
      type: "line",
      source: SELECTION_SOURCE_ID,
      // Trait PLEIN (pas de `line-dasharray`) — TOUJOURS visible (`line-opacity: 1` fixe, pas
      // data-driven) : seule `line-color` change selon l'état, voir `setSelectionInteracting`.
      paint: { "line-color": this.colors.accent, "line-width": 2, "line-opacity": 1 },
    });

    this.map.addSource(SELECTION_HANDLES_SOURCE_ID, {
      type: "geojson",
      data: editVerticesToGeoJSON([]),
    });
    this.map.addLayer({
      id: SELECTION_HANDLES_LAYER_ID,
      type: "circle",
      source: SELECTION_HANDLES_SOURCE_ID,
      paint: {
        "circle-radius": 6,
        "circle-color": this.colors.accent,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });

    this.map.addSource(SELECTION_ROTATE_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: SELECTION_ROTATE_LINE_LAYER_ID,
      type: "line",
      source: SELECTION_ROTATE_SOURCE_ID,
      filter: ["==", ["geometry-type"], "LineString"],
      paint: { "line-color": this.colors.accent, "line-width": 1, "line-dasharray": [1, 2] },
    });
    this.map.addLayer({
      id: SELECTION_ROTATE_HANDLE_LAYER_ID,
      type: "circle",
      source: SELECTION_ROTATE_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-radius": 9,
        "circle-color": this.colors.accent,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    });
    this.map.addLayer({
      id: SELECTION_ROTATE_HANDLE_ICON_LAYER_ID,
      type: "symbol",
      source: SELECTION_ROTATE_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      layout: {
        "icon-image": ROTATE_HANDLE_ICON_ID,
        "icon-size": ROTATE_HANDLE_ICON_SIZE,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });

    // Surbrillance ponctuelle (voir `highlightShapes`) : ajoutée en dernier, donc au-dessus de tout
    // le reste (y compris la sélection multiple ci-dessus) — les largeurs/opacités de départ
    // n'importent pas, `startHighlightAnimation` les pilote dès le premier frame.
    this.map.addSource(HIGHLIGHT_SOURCE_ID, {
      type: "geojson",
      data: highlightShapesToGeoJSON([]),
    });
    this.map.addLayer({
      id: HIGHLIGHT_FILL_LAYER_ID,
      type: "fill",
      source: HIGHLIGHT_SOURCE_ID,
      paint: { "fill-color": this.colors.highlight, "fill-opacity": HIGHLIGHT_FILL_OPACITY_MIN },
    });
    // Source dédiée pour le contour (voir `HIGHLIGHT_OUTLINE_SOURCE_ID`/`updateHighlightOutlineSource`) :
    // sa géométrie est un anneau agrandi vers l'extérieur (`outwardExpandedRingPx`), pas la silhouette
    // exacte de la forme comme `HIGHLIGHT_SOURCE_ID` — donc pas de `line-offset` à piloter ici.
    this.map.addSource(HIGHLIGHT_OUTLINE_SOURCE_ID, {
      type: "geojson",
      data: highlightShapesToGeoJSON([]),
    });
    this.map.addLayer({
      id: HIGHLIGHT_OUTLINE_LAYER_ID,
      type: "line",
      source: HIGHLIGHT_OUTLINE_SOURCE_ID,
      paint: {
        "line-color": this.colors.highlight,
        "line-width": HIGHLIGHT_OUTLINE_WIDTH_MIN,
      },
    });

    // Tracé d'itinéraire (voir `showRoute`/`clearRoute`) : ajouté juste après la surbrillance, donc
    // au-dessus des formes/de la sélection mais sous les curseurs distants ci-dessous.
    this.map.addSource(ROUTE_SOURCE_ID, {
      type: "geojson",
      data: routeToGeoJSON([]),
    });
    this.map.addLayer({
      id: ROUTE_LINE_LAYER_ID,
      type: "line",
      source: ROUTE_SOURCE_ID,
      // `line-cap: "butt"` (PAS `"round"`, contrairement au reste de l'adapter) — un bout arrondi
      // dépasse la longueur exacte du tiret d'un rayon égal à la moitié de `ROUTE_LINE_WIDTH` de
      // CHAQUE côté. Avec `ROUTE_DASH_GAP` du même ordre de grandeur que la largeur du trait, ce
      // dépassement peut couvrir tout l'espace : selon l'alignement sous-pixel exact (qui change en
      // continu pendant l'animation), deux tirets consécutifs se touchent/fusionnent visuellement par
      // moments puis se séparent — constaté en testant, décrit comme le tracé qui "se rejoint puis
      // redémarre" en boucle. Un bout franc ne dépasse jamais la longueur exacte du tiret : l'espace
      // reste toujours pleinement visible, quel que soit l'alignement au moment donné.
      layout: { "line-cap": "butt", "line-join": "round" },
      // `line-dasharray` posé ici à la 1ère étape précalculée pour que le tracé ait déjà un aspect
      // correct avant même le premier frame de `startRouteAnimation` (déclenchée par `showRoute`,
      // jamais par `init()` lui-même) — voir `ROUTE_DASH_PATTERNS`.
      paint: {
        "line-color": this.colors.accentSecondary,
        "line-width": ROUTE_LINE_WIDTH,
        "line-dasharray": ROUTE_DASH_PATTERNS[0],
      },
    });

    // Aperçu de debug de la grille de pathfinding (voir `showPathfindingDebugGrid`/`clearPathfindingDebugGrid`)
    // — purement diagnostic, ajouté juste après le tracé d'itinéraire, jamais interactif.
    this.map.addSource(PATHFINDING_DEBUG_BLOCKED_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: PATHFINDING_DEBUG_BLOCKED_LAYER_ID,
      type: "fill",
      source: PATHFINDING_DEBUG_BLOCKED_SOURCE_ID,
      paint: {
        "fill-color": this.colors.danger,
        "fill-opacity": 0.35,
      },
    });
    this.map.addSource(PATHFINDING_DEBUG_CHEAP_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    // Dégradé jaune (rabais faible) -> bleu marine foncé (rabais fort, voir `pathPriorityWeight`) +
    // contour noir — jamais de rouge/orange, réservé à `PATHFINDING_DEBUG_BLOCKED_LAYER_ID`
    // (`colors.danger`) : un dégradé qui l'aurait recoupé aurait rendu les deux calques ambigus l'un
    // par rapport à l'autre. Bleu marine plutôt qu'un bleu vif : reste distinct à la fois de
    // `colors.accent` (boutons/sélection de l'éditeur) et d'une ligne attractive elle-même bleue
    // (une cellule à prix réduit RECOUVRE presque toujours une portion de la ligne qui l'a créée,
    // donc son fond se retrouve peint PAR-DESSUS la couleur de cette ligne, là où ça compte le plus
    // — constaté en testant avec une ligne bleu vif : un bleu de dégradé trop proche l'aurait rendue
    // indiscernable de son propre survol).
    this.map.addLayer({
      id: PATHFINDING_DEBUG_CHEAP_LAYER_ID,
      type: "fill",
      source: PATHFINDING_DEBUG_CHEAP_SOURCE_ID,
      paint: {
        "fill-color": ["interpolate", ["linear"], ["get", "weight"], 0, "#1a237e", 1, "#fff9c4"],
        "fill-opacity": 0.7,
      },
    });
    this.map.addLayer({
      id: PATHFINDING_DEBUG_CHEAP_OUTLINE_LAYER_ID,
      type: "line",
      source: PATHFINDING_DEBUG_CHEAP_SOURCE_ID,
      paint: { "line-color": "#000000", "line-width": 1, "line-opacity": 0.6 },
    });

    // Contour de sélection distante (voir `RemoteCursor.selectedShapeId`/`REMOTE_SELECTION_OUTLINE_SOURCE_ID`)
    // : ajouté juste avant les curseurs distants, donc au-dessus de la surbrillance mais sous l'icône/
    // le nom du curseur lui-même (qui doit rester le plus visible, voir plus bas).
    this.map.addSource(REMOTE_SELECTION_OUTLINE_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: REMOTE_SELECTION_OUTLINE_LAYER_ID,
      type: "line",
      source: REMOTE_SELECTION_OUTLINE_SOURCE_ID,
      layout: { "line-join": "round" },
      paint: {
        "line-color": ["get", "color"],
        "line-width": REMOTE_SELECTION_OUTLINE_WIDTH_PX,
      },
    });
    // Étiquette de sélection distante (voir `REMOTE_SELECTION_LABEL_*`/`buildRemoteSelectionGeoJSON`)
    // — une pastille arrondie (`fill`, teintée par curseur) sous un nom en blanc fixe (`symbol`,
    // `text-anchor: "center"` : la position déjà calculée EST le centre de la pastille, contrairement
    // au curseur réel dont `icon-anchor`/`text-anchor` visent `"top-left"` depuis un point de souris).
    this.map.addSource(REMOTE_SELECTION_LABEL_BG_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: REMOTE_SELECTION_LABEL_BG_LAYER_ID,
      type: "fill",
      source: REMOTE_SELECTION_LABEL_BG_SOURCE_ID,
      paint: { "fill-color": ["get", "color"] },
    });
    this.map.addSource(REMOTE_SELECTION_LABEL_SOURCE_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: REMOTE_SELECTION_LABEL_LAYER_ID,
      type: "symbol",
      source: REMOTE_SELECTION_LABEL_SOURCE_ID,
      layout: {
        "text-field": ["get", "userName"],
        "text-font": SHAPE_TEXT_FONT,
        "text-size": REMOTE_SELECTION_LABEL_TEXT_SIZE_PX,
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#ffffff",
      },
    });

    // click/contextmenu/survol sur les formes : génériques (pas filtrés par layer), interrogent
    // eux-mêmes `queryRenderedFeatures` avec la liste courante de bandes (voir
    // `queryShapeIdAtPoint`/`updateFillLayerIdsCache`) — enregistrés ICI, AVANT
    // `handleDrawingClick`/`handleRectDrawingClick` juste en dessous, pour préserver leur ordre de
    // dispatch relatif (voir le commentaire de `updateFillLayerIdsCache` : un enregistrement plus
    // tardif casserait le garde `if (this.drawingState ...)` sur le clic qui clôture un dessin).
    this.map.on("click", this.handleShapeClick);
    this.map.on("contextmenu", this.handleShapeContextMenu);
    // Curseur "pointer" au survol d'une forme cliquable : générique et enregistré
    // inconditionnellement, comme les deux ci-dessus, puisque le clic gauche sélectionne une forme
    // aussi bien en vue qu'en édition (voir `handleShapeHoverCursor`).
    this.map.on("mousemove", this.handleShapeHoverCursor);
    this.map.on("mouseout", this.handleShapeHoverCursorOut);
    // Voir "map:pointer-left" (MapAdapterEvent) — inconditionnel comme les 2 listeners juste au-dessus :
    // le consommateur (curseurs distants collaboratifs) n'est pas propre à `editMode`.
    this.map.on("mouseout", () => this.emit({ type: "map:pointer-left" }));
    // `handleShapeMouseMove`/`handleShapeMouseOut` : désormais enregistrés INCONDITIONNELLEMENT (ne
    // l'étaient qu'en `editMode` avant) — `Shape.hoverText`/`Shape.hoverStyle` sont du CONTENU (voir
    // `resolveShapeVisualProps`), pas une simple aide à l'édition, et doivent donc pouvoir s'afficher
    // au survol même en visualisation seule (ex. une info-bulle configurée par le consommateur). Ce
    // que ces deux gardent malgré tout réservé à `editMode` : le highlight bleu PAR DÉFAUT (voir
    // `resolveShapeVisualProps`'s `defaultHoverBgColor`/`defaultHoverBorderColor`) et le contour
    // pointillé dédié (`band.hoverOutlineLayerId`) — deux aides purement visuelles à l'édition, sans
    // rapport avec `hoverText`/`hoverStyle` explicites. La barre d'outils flottante
    // (`showHoverToolbar`) n'a pas besoin d'un garde équivalent ici : elle s'auto-neutralise déjà
    // (`hoverToolbarContainer` reste `null` hors `editMode`, jamais monté).
    this.map.on("mousemove", this.handleShapeMouseMove);
    this.map.on("mouseout", this.handleShapeMouseOut);
    if (this.editMode) {
      // Double-clic pour entrer en mode édition : seulement enregistré en `editMode` — hors lui, le
      // zoom natif au double-clic de MapLibre reste intact.
      this.map.on("dblclick", this.handleShapeDblClick);
    }

    this.map.on("click", (event: MapMouseEvent) => this.handleDrawingClick(event));
    this.map.on("click", (event: MapMouseEvent) => this.handleRectDrawingClick(event));
    this.map.on("click", (event: MapMouseEvent) => this.handleCircleDrawingClick(event));
    this.map.on("click", (event: MapMouseEvent) => this.handleLineDrawingClick(event));
    this.map.on("click", (event: MapMouseEvent) => this.handleImageDrawingClick(event));
    this.map.on("click", (event: MapMouseEvent) => this.handleExtendLineClick(event));

    this.map.on("mousedown", EDIT_VERTICES_LAYER_ID, (event: MapLayerMouseEvent) => {
      const vertexIndex = event.features?.[0]?.properties?.vertexIndex;
      if (vertexIndex == null || !this.map || !this.editingState || this.extendingLineState) return;
      this.claimHandleGesture(event);
      this.draggingVertexIndex = Number(vertexIndex);
      this.draggingVertexOriginalPoints = [...this.editingState.points];

      // Une forme rectangle/image à 4 sommets propres se redimensionne sans se déformer (angles
      // droits garantis, ratio conservé si `shapeKind === "image"`) — voir `VertexResizingState`/
      // `applyVertexResize`. Sinon (polygone libre, ou rectangle/image dégénéré à un nombre de
      // sommets différent de 4) : comportement historique, ce sommet se déplace librement.
      const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
      this.vertexResizingState =
        (shape?.shapeKind === "rectangle" || shape?.shapeKind === "image") && this.editingState.points.length === 4
          ? (() => {
              const originalCornersPx = this.editingState!.points.map((p) => this.map!.project(p));
              return {
                cornerIndex: this.draggingVertexIndex,
                originalCornersPx,
                // Rotation ÉCRAN réelle du rectangle, dérivée directement du côté 0→1 déjà projeté
                // (jamais une diagonale, voir `rectangleCornersPx`) — PAS `shape.rotation +
                // map.getBearing()` : `Shape.rotation` n'est qu'un delta accumulé, valide seulement
                // relativement au bearing actif lors du dernier dessin/geste de rotation (jamais
                // stocké sur la forme), donc ne se recombine pas simplement avec le bearing courant.
                // Lire l'angle sur les pixels déjà projetés est toujours exact, quel que soit ce
                // bearing de référence implicite.
                rotation:
                  (Math.atan2(
                    originalCornersPx[1]!.y - originalCornersPx[0]!.y,
                    originalCornersPx[1]!.x - originalCornersPx[0]!.x,
                  ) *
                    180) /
                  Math.PI,
                lockRatio: shape.shapeKind === "image",
              };
            })()
          : null;

      this.map.dragPan.disable();
      this.map.getCanvas().style.cursor = this.vertexResizingState
        ? resizeCursorForCorner(this.vertexResizingState.originalCornersPx, this.vertexResizingState.cornerIndex)
        : "grabbing";
    });
    this.map.on("mouseenter", EDIT_VERTICES_LAYER_ID, (event: MapLayerMouseEvent) => {
      const vertexIndex = event.features?.[0]?.properties?.vertexIndex;
      if (this.map && this.draggingVertexIndex == null && vertexIndex != null) {
        this.map.getCanvas().style.cursor = this.vertexHoverCursor(Number(vertexIndex));
      }
    });
    this.map.on("mouseleave", EDIT_VERTICES_LAYER_ID, () => {
      if (this.map && this.draggingVertexIndex == null) this.map.getCanvas().style.cursor = "";
    });

    // Poignée de redimensionnement d'un côté de rectangle (voir `EdgeResizingState`) — même priorité
    // que EDIT_VERTICES_LAYER_ID/ROTATE_HANDLE_LAYER_ID juste au-dessus : la barre n'est de toute
    // façon affichée (voir `updateEditVisuals`) que pour une forme déjà éligible. Toute l'interaction
    // passe par `EDGE_RESIZE_HIT_LAYER_ID` (zone plus généreuse que la barre visible elle-même, voir
    // son commentaire), jamais `EDGE_RESIZE_LAYER_ID` directement. Le curseur affiché
    // (properties.cursor, voir `resizeCursorForTangent`/`edgeResizeHandlesToGeoJSON`) est directionnel
    // — `ns-resize`/`ew-resize`/diagonales selon l'orientation ÉCRAN réelle de CE côté précis, pas un
    // simple `grab` générique. Un `mouseenter` d'une autre couche (le remplissage de la forme,
    // enregistré plus bas) peut retomber sur ce même point et regagner le curseur juste après —
    // `updateHandleHoverCursor`, appelé en tout dernier dans le dispatcher `mousemove` central,
    // corrige ça en ayant systématiquement le dernier mot (voir son commentaire).
    this.map.on("mousedown", EDGE_RESIZE_HIT_LAYER_ID, (event: MapLayerMouseEvent) => {
      const edgeIndex = event.features?.[0]?.properties?.edgeIndex;
      const cursor = event.features?.[0]?.properties?.cursor;
      if (edgeIndex == null || !this.map || !this.editingState || this.extendingLineState) return;
      const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
      if (!shape || shape.shapeKind !== "rectangle" || this.editingState.points.length !== 4) return;

      this.claimHandleGesture(event);
      const originalCornersPx = this.editingState.points.map((p) => this.map!.project(p));
      this.edgeResizingState = {
        edgeIndex: Number(edgeIndex),
        originalCornersPx,
        // Même technique que VertexResizingState ci-dessus : angle lu directement sur le côté 0→1
        // déjà projeté à l'écran, jamais recombiné depuis `shape.rotation`/`map.getBearing()`.
        rotation:
          (Math.atan2(
            originalCornersPx[1]!.y - originalCornersPx[0]!.y,
            originalCornersPx[1]!.x - originalCornersPx[0]!.x,
          ) *
            180) /
          Math.PI,
        originalPoints: [...this.editingState.points],
      };
      this.map.dragPan.disable();
      this.map.getCanvas().style.cursor = typeof cursor === "string" ? cursor : "grabbing";
    });
    this.map.on("mouseenter", EDGE_RESIZE_HIT_LAYER_ID, (event: MapLayerMouseEvent) => {
      const cursor = event.features?.[0]?.properties?.cursor;
      if (this.map && !this.edgeResizingState) {
        this.map.getCanvas().style.cursor = typeof cursor === "string" ? cursor : "grab";
      }
    });
    this.map.on("mouseleave", EDGE_RESIZE_HIT_LAYER_ID, () => {
      if (this.map && !this.edgeResizingState) this.map.getCanvas().style.cursor = "";
    });

    this.map.on("mousedown", ROTATE_HANDLE_LAYER_ID, (event: MapLayerMouseEvent) =>
      this.handleRotateDragStart(event),
    );
    this.map.on("mouseenter", ROTATE_HANDLE_LAYER_ID, () => {
      if (this.map && !this.rotatingState) this.map.getCanvas().style.cursor = ROTATE_CURSOR;
    });
    this.map.on("mouseleave", ROTATE_HANDLE_LAYER_ID, () => {
      if (this.map && !this.rotatingState) this.map.getCanvas().style.cursor = "";
    });

    // Ajout de sommet au survol d'une arête (voir `handleAddVertexHoverMove`) : enregistré ici, avant
    // le déplacement de la forme entière ci-dessous, pour la même raison que EDIT_VERTICES_LAYER_ID/
    // ROTATE_HANDLE_LAYER_ID juste au-dessus — cliquer sur le sommet fantôme insère un sommet plutôt
    // que de démarrer une translation de toute la forme.
    this.map.on("mousedown", ADD_VERTEX_HOVER_LAYER_ID, () => this.handleAddVertexInsert());
    this.map.on("mouseenter", ADD_VERTEX_HOVER_LAYER_ID, () => {
      if (this.map && this.draggingVertexIndex == null) this.map.getCanvas().style.cursor = "copy";
    });
    this.map.on("mouseleave", ADD_VERTEX_HOVER_LAYER_ID, () => {
      if (this.map && this.draggingVertexIndex == null) this.map.getCanvas().style.cursor = "";
    });

    // Déplacement de la forme entière (translation) : glisser son remplissage/contour pendant
    // l'édition, OU directement sa poignée de déplacement dédiée (MOVE_HANDLE_LAYER_ID, posée au
    // centre de la forme — voir son commentaire) : les deux déclenchent exactement le même geste,
    // la poignée n'est qu'une cible fiable supplémentaire, utile notamment pour une ligne dont le
    // tracé réel est fin. `mousedown` sur EDIT_VERTICES_LAYER_ID/ROTATE_HANDLE_LAYER_ID/
    // ADD_VERTEX_HOVER_LAYER_ID est enregistré avant (donc traité avant, voir
    // `handleMoveShapeDragStart`) : cliquer pile sur un sommet (réel ou fantôme) ou la poignée de
    // rotation démarre CE geste-là, pas un déplacement de la forme entière.
    for (const layerId of [
      EDITING_SHAPE_FILL_LAYER_ID,
      EDITING_SHAPE_OUTLINE_LAYER_ID,
      EDITING_SHAPE_OUTLINE_DASHED_LAYER_ID,
      EDITING_SHAPE_OUTLINE_DEFAULT_LAYER_ID,
      MOVE_HANDLE_LAYER_ID,
    ]) {
      this.map.on("mousedown", layerId, (event: MapLayerMouseEvent) => this.handleMoveShapeDragStart(event));
      this.map.on("mouseenter", layerId, () => {
        if (
          this.map &&
          !this.movingState &&
          this.draggingVertexIndex == null &&
          !this.edgeResizingState &&
          !this.rotatingState
        ) {
          this.map.getCanvas().style.cursor = "move";
        }
      });
      this.map.on("mouseleave", layerId, () => {
        if (this.map && !this.movingState && !this.edgeResizingState) this.map.getCanvas().style.cursor = "";
      });
    }

    // --- Sélection multiple (BESOINS.md §13) ---

    if (this.editMode) {
      // Ctrl+glisser et clic-droit-glisser étaient auparavant TOUS LES DEUX liés par MapLibre au
      // même handler natif `dragRotate` (incliner/pivoter la caméra), ce qui empêchait de les
      // activer/désactiver indépendamment selon le déclencheur (bouton droit vs Ctrl+bouton gauche)
      // — hors `allowPitch`, `dragRotate` est désactivé une fois pour toutes à la construction (voir
      // `dragRotate: false` dans `init()`) et remplacé par `handleMapRotateDragStart`, qui distingue
      // lui-même les deux gestes (bouton droit : toujours la rotation ; Ctrl+bouton gauche :
      // seulement hors `editMode`, ici réservé au rectangle de sélection ci-dessous).
      //
      // Avec `allowPitch: true`, `dragRotate` natif est réactivé (voir `init()`) pour que le
      // clic-droit-glisser bénéficie du pitch — mais MapLibre lie ce même `dragRotate` natif à
      // Ctrl+bouton-gauche AUSSI, sans moyen de le restreindre au seul bouton droit. On désactive
      // donc `dragRotate` ponctuellement pour la durée de CE geste précis (Ctrl+clic-gauche-glisser
      // en `editMode`) afin qu'il ne fasse jamais tourner/incliner la caméra à la place du rectangle
      // de sélection — réactivé au relâchement, voir `handleSelectionDrawEnd`.
      this.map.on("mousedown", (event: MapMouseEvent) => {
        if (!(event.originalEvent.ctrlKey || event.originalEvent.metaKey)) return;
        if (
          this.drawingState ||
          this.rectDrawingState ||
          this.imageDrawingState ||
          this.circleDrawingState ||
          this.editingState ||
          this.selectionState
        )
          return;
        this.selectionDrawState = { anchorPx: { x: event.point.x, y: event.point.y } };
        this.lastLiveSelectionShapeIds = [];
        this.map!.dragPan.disable();
        if (this.allowPitch) this.map!.dragRotate.disable();
        this.setSelectionInteracting(true);
      });

      // Poignées de redimensionnement (coins) : même priorité que EDIT_VERTICES_LAYER_ID/
      // ROTATE_HANDLE_LAYER_ID pour la forme seule (voir plus haut) — enregistrées avant le
      // remplissage du rectangle, donc traitées avant lui pour le même clic.
      this.map.on("mousedown", SELECTION_HANDLES_LAYER_ID, (event: MapLayerMouseEvent) => {
        const cornerIndex = event.features?.[0]?.properties?.vertexIndex;
        if (cornerIndex == null || !this.selectionState || !this.map) return;
        this.claimHandleGesture(event);
        const originalShapes = this.currentSelectionShapes();
        this.selectionResizingState = {
          cornerIndex: Number(cornerIndex),
          originalCornersPx: this.selectionState.corners.map((c) => this.map!.project(c)),
          rotation: this.selectionState.rotation,
          originalShapes,
        };
        // Même mécanisme que pour un déplacement de groupe (voir `handleSelectionMoveMove`/
        // `SELECTION_FILL_LAYER_ID` plus bas) : les formes passent dans la source légère dédiée à
        // l'édition, la source principale n'est plus retouchée tant que le drag est en cours.
        this.setEditingShapeFilter(this.selectionState.shapeIds);
        this.updateEditingShapeSource(originalShapes, true);
        this.map.dragPan.disable();
        this.map.getCanvas().style.cursor = "nwse-resize";
        this.setSelectionInteracting(true);
      });
      this.map.on("mouseenter", SELECTION_HANDLES_LAYER_ID, () => {
        if (this.map && !this.selectionResizingState) this.map.getCanvas().style.cursor = "nwse-resize";
      });
      this.map.on("mouseleave", SELECTION_HANDLES_LAYER_ID, () => {
        if (this.map && !this.selectionResizingState) this.map.getCanvas().style.cursor = "";
      });

      this.map.on("mousedown", SELECTION_ROTATE_HANDLE_LAYER_ID, (event: MapLayerMouseEvent) => {
        if (!this.selectionState || !this.map) return;
        this.claimHandleGesture(event);
        const centerPx = this.map.project(centroid(this.selectionState.corners));
        const originalShapes = this.currentSelectionShapes();
        this.selectionRotatingState = {
          centerPx,
          startAngleDeg: angleDeg(centerPx, event.point),
          originalCornersPx: this.selectionState.corners.map((c) => this.map!.project(c)),
          currentDeltaDeg: 0,
          originalShapes,
        };
        // Même mécanisme que pour un déplacement/redimensionnement de groupe (voir
        // `handleSelectionMoveMove`/`handleSelectionResizeMove`) : les formes passent dans la source
        // légère dédiée à l'édition, la source principale n'est plus retouchée tant que le drag est
        // en cours.
        this.setEditingShapeFilter(this.selectionState.shapeIds);
        this.updateEditingShapeSource(originalShapes, true);
        this.map.dragPan.disable();
        this.map.getCanvas().style.cursor = "grabbing";
        this.setSelectionInteracting(true);
      });
      this.map.on("mouseenter", SELECTION_ROTATE_HANDLE_LAYER_ID, () => {
        if (this.map && !this.selectionRotatingState) this.map.getCanvas().style.cursor = ROTATE_CURSOR;
      });
      this.map.on("mouseleave", SELECTION_ROTATE_HANDLE_LAYER_ID, () => {
        if (this.map && !this.selectionRotatingState) this.map.getCanvas().style.cursor = "";
      });

      this.map.on("mousedown", SELECTION_FILL_LAYER_ID, (event: MapLayerMouseEvent) => {
        // Un Ctrl/Cmd+clic sur une forme à l'intérieur du rectangle a sa propre sémantique
        // (ajouter/retirer CETTE forme de la sélection, voir `handleShapeClick`/
        // `toggleShapeInSelection`) — sans ce garde, il démarrait ici un déplacement de groupe
        // (`selectionMovingState`), qui émettait ensuite un `shape:moved` no-op (mêmes coordonnées,
        // aucun glissé) pour CHAQUE forme sélectionnée au relâchement, déclenchant un `onSave` pour
        // rien et empêchant le clic natif qui suit de retirer la forme proprement.
        if (event.originalEvent.ctrlKey || event.originalEvent.metaKey) return;
        if (this.selectionResizingState || this.selectionRotatingState || !this.selectionState || !this.map) return;
        this.claimHandleGesture(event);
        const originalShapes = this.currentSelectionShapes();
        this.selectionMovingState = {
          startCursorPx: { x: event.point.x, y: event.point.y },
          originalCornersPx: this.selectionState.corners.map((c) => this.map!.project(c)),
          originalShapes,
        };
        // Les formes sélectionnées passent dans la source légère dédiée à l'édition (voir
        // `enableEditMode`) : la source principale (~1190 formes) ne sera plus retouchée par un
        // setData() tant que le drag est en cours, seule cette petite source l'est à chaque
        // mousemove (voir `handleSelectionMoveMove`).
        this.setEditingShapeFilter(this.selectionState.shapeIds);
        this.updateEditingShapeSource(originalShapes, true);
        this.map.dragPan.disable();
        this.map.getCanvas().style.cursor = "grabbing";
        this.setSelectionInteracting(true);
      });
      this.map.on("mouseenter", SELECTION_FILL_LAYER_ID, () => {
        if (this.map && !this.selectionMovingState && !this.selectionResizingState && !this.selectionRotatingState) {
          this.map.getCanvas().style.cursor = "move";
        }
      });
      this.map.on("mouseleave", SELECTION_FILL_LAYER_ID, () => {
        if (this.map && !this.selectionMovingState) this.map.getCanvas().style.cursor = "";
      });

      // Clic en dehors du rectangle de sélection (pas sur lui ni ses poignées, voir plus haut où ces
      // mousedown-là sont gérés séparément) : referme la sélection, les formes restent telles quelles.
      // Un clic tenu avec Ctrl/Cmd est ignoré ici : il a sa propre sémantique (ajouter/retirer une
      // forme, voir `handleShapeClick`/`toggleShapeInSelection`) et ne doit jamais refermer la
      // sélection, y compris quand il tombe hors du rectangle actuel (le cas le plus courant en
      // ajoutant une forme qui n'y était pas encore).
      this.map.on("click", (event: MapMouseEvent) => {
        if (!this.selectionState || !this.map) return;
        if (event.originalEvent.ctrlKey || event.originalEvent.metaKey) return;
        const cornersPx = this.selectionState.corners.map((c) => this.map!.project(c));
        if (!isPointInConvexPolygonPx({ x: event.point.x, y: event.point.y }, cornersPx)) {
          this.clearSelection();
        }
      });
    }

    // --- Rotation de la carte (clic-droit-glisser, ou Ctrl+glisser hors `editMode`) ---
    // Enregistrée hors du bloc `if (this.editMode)` ci-dessus : le clic-droit-glisser doit tourner
    // la caméra dans les deux modes (voir `handleMapRotateDragStart`, qui filtre lui-même les
    // gestes concernés par bouton/Ctrl/mode).
    this.map.on("mousedown", (event: MapMouseEvent) => this.handleMapRotateDragStart(event));

    // Une souris peut émettre plusieurs mousemove par frame : chacun déclencherait des setData que le
    // worker traiterait tous, jusqu'à prendre du retard. On n'applique que le dernier, une fois par
    // frame, et on le vide avant tout mousedown/mouseup pour que ceux-ci partent de la vraie position.
    this.map.getCanvasContainer().addEventListener("mousedown", () => this.flushPendingMouseMove(), { capture: true });
    this.map.on("mousemove", (event: MapMouseEvent) => {
      this.pendingMouseMove = event;
      this.lastMouseMove = event;
      this.setSnappingEnabled(event.originalEvent.shiftKey);
      this.setConstrainEnabled(event.originalEvent.altKey);
      this.pendingMouseMoveFrame ??= requestAnimationFrame(() => this.flushPendingMouseMove());
    });
    this.map.on("mouseout", () => {
      this.lastMouseMove = null;
    });
    this.map.on("mouseup", () => {
      this.flushPendingMouseMove();
      this.handleVertexDragEnd();
      this.handleEdgeResizeEnd();
      this.handleRotateDragEnd();
      this.handleMapRotateDragEnd();
      this.handleMoveShapeDragEnd();
      this.handleSelectionDrawEnd();
      this.handleSelectionResizeEnd();
      this.handleSelectionRotateEnd();
      this.handleSelectionMoveEnd();
    });

    // Les images de forme SVG (Shape.image) restent des <img> DOM plutôt que des textures
    // MapLibre, pour garder un rendu vectoriel natif du navigateur (net à tout zoom, jamais
    // rasterisé) — mais DERRIÈRE le canvas (`beforebegin`), pas devant : les images raster (le cas
    // réel, photos CDN) vivent elles comme des layers MapLibre, dans le canvas, avec un empilement
    // unifié aux formes piloté par `Shape.layer` (voir ShapeLayerBand/BESOINS.md §4/§8) — un SVG
    // reste donc toujours en dessous de TOUTES les formes (et du fond de carte lui-même), plutôt que
    // de participer à cet ordre unifié. Accepté : voir BESOINS.md pour la justification.
    const canvasContainer = this.map.getCanvasContainer();
    const overlay = document.createElement("div");
    overlay.className = "shape-image-overlay"; // sélecteur de test (e2e/image-overlay.spec.ts)
    overlay.style.position = "absolute";
    overlay.style.inset = "0";
    overlay.style.overflow = "hidden";
    overlay.style.pointerEvents = "none";
    canvasContainer.insertAdjacentElement("beforebegin", overlay);
    this.imageOverlayContainer = overlay;

    // Barre d'outils flottante (cadenas + "...", voir `showHoverToolbar`) : seulement en `editMode`,
    // même garde que `handleShapeMouseMove`/`handleShapeMouseOut` juste au-dessus, dont elle dépend
    // entièrement pour savoir quand s'afficher. Montée en enfant DIRECT de `canvasContainer`, APRÈS
    // le canvas (contrairement à `overlay` ci-dessus, inséré `beforebegin`) : elle doit rester
    // cliquable par-dessus tout le rendu de la carte, pas en dessous.
    if (this.editMode) {
      this.hoverToolbarContainer = this.createHoverToolbarElement();
      canvasContainer.appendChild(this.hoverToolbarContainer);
    }

    // Pendant un pan/zoom actif, on promeut les <img> sur leur propre calque GPU
    // (`will-change: transform`) pour rester fluide : juste une texture qu'on glisse/redimensionne,
    // sans repeindre le SVG à chaque frame. On l'enlève une fois la carte stabilisée pour que le
    // rendu final soit net (voir `applyImageOverlayTransform` : sans calque dédié, `matrix()` sur
    // du SVG reste nette à tout zoom — c'est justement ce qui coûte cher à chaque frame en mouvement).
    this.map.on("movestart", () => this.setImageOverlaysCompositing(true));
    this.map.on("move", () => {
      this.updateImageOverlayTransforms();
      this.updateRotateWithMapRasterImages();
      this.updateHoverToolbarPosition();
      this.updateGeolocationVisibility();
      // Un seul point d'ancrage suffit à synchroniser `backgroundMap` (voir `updateBackgroundMode`) :
      // `"move"` se déclenche pour TOUT changement de caméra, natif ou programmatique (`setCenter`/
      // `setZoom`/`setBearing`/`setPitch`, `easeTo`/`fitBounds`/`panBy`, et la rotation maison via
      // `handleMapRotateDragMove`) — aucune boucle possible, `backgroundMap` n'étant jamais interactif.
      // `padding` est inclus : sans lui, `ensureShapeVisible` (qui pose un padding sur `this.map`
      // pour garder une forme visible malgré la sidebar) désynchroniserait visuellement les formes du
      // fond OSM peint par `backgroundMap` — `getCenter()` renvoie le point centré dans la zone
      // *rognée* par ce padding, pas le centre géométrique réel du conteneur.
      this.backgroundMap?.syncCamera({
        center: this.getCenter(),
        zoom: this.map!.getZoom(),
        bearing: this.map!.getBearing(),
        pitch: this.map!.getPitch(),
        padding: this.map!.getPadding(),
      });
    });
    // `"zoom"` (continu, à chaque frame d'un geste de zoom — contrairement à `"moveend"` ci-dessous,
    // qui n'attend pas la fin du geste) : sans ça, un texte gardait sa taille du DÉBUT du zoom jusqu'à
    // ce que la caméra se stabilise, débordant visiblement de sa forme (qui, elle, se redessine en
    // continu) pendant tout le geste — voir `scheduleShapesTextSizeRefresh`.
    this.map.on("zoom", () => this.scheduleShapesTextSizeRefresh());
    this.map.on("moveend", () => {
      this.setImageOverlaysCompositing(false);
      this.refreshZoomVisibility();
      // Monte/démonte les <img> (SVG) et les layers image raster en fonction de la vue une fois le
      // déplacement terminé (pas à chaque frame) : avec ~80 images (dont certaines de plusieurs Mo),
      // n'en garder que les formes visibles évite de payer leur coût réseau/mémoire/compositing
      // hors écran. Les deux chemins ont besoin de cet appel : un zoom/pan seul (sans changement de
      // forme, donc sans passage par `renderShapes`) peut faire entrer une image dans le cadrage ou
      // lui faire dépasser le seuil de taille.
      this.updateImageOverlayMounts();
      this.updateRasterImageMounts();
      // Les tailles de `SHAPES_TEXT_SOURCE_ID` sont exprimées au zoom courant (voir
      // `shapesTextToGeoJSON`) : un zoom/pan seul (sans passage par `renderShapes`) les périme,
      // recalculées ici une fois la caméra stabilisée (voir `refreshShapesTextSize`).
      this.refreshShapesTextSize();
      // `"map-view:changed"` (voir `MapAdapterEvent`) : quelle qu'en soit la cause (geste natif,
      // `setMapView`, `resetView`, undo/redo...), MapLibre déclenche toujours `"moveend"` une fois la
      // caméra stabilisée, même pour un changement déclenché par programmation (`setCenter`/`setZoom`/
      // `setBearing`) — un seul point d'émission suffit donc pour toutes les sources. Dédupliqué contre
      // `lastEmittedMapView` : voir son commentaire (plusieurs `"moveend"` natifs successifs peuvent
      // correspondre au même changement logique, déjà entièrement appliqué).
      const view = this.getMapViewState();
      if (!this.lastEmittedMapView || !mapViewStatesEqual(this.lastEmittedMapView, view)) {
        this.lastEmittedMapView = view;
        this.emit({ type: "map-view:changed", view });
      }
    });

    window.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      if (this.drawingState || this.rectDrawingState || this.imageDrawingState || this.circleDrawingState || this.lineDrawingState) this.cancelDrawing();
      if (this.extendingLineState) this.cancelExtendLine();
      else if (this.editingState) this.disableEditMode(this.editingState.shapeId);
      if (this.selectionState) this.clearSelection();
      if (this.mapViewEditState) this.cancelEditingMapView();
    });
    window.addEventListener("keydown", this.handleSnapModifierKey);
    window.addEventListener("keyup", this.handleSnapModifierKey);
    window.addEventListener("blur", this.handleWindowBlur);

    // Voir le commentaire de `resizeObserver` : un changement de taille du conteneur qui ne passe
    // pas par un "resize" de `window` (ex. `.sidebar` apparaissant/disparaissant en flex à côté de
    // lui) laisserait sinon le canvas avec une résolution/projection périmée par rapport à sa boîte
    // CSS réelle — désynchronisant durablement le rendu et la détection de clic des vraies positions
    // écran.
    this.resizeObserver = new ResizeObserver(() => {
      this.map?.resize();
      this.backgroundMap?.resize();
    });
    this.resizeObserver.observe(container);
  }

  /**
   * Résout une fois que la carte n'a plus aucune tuile/ressource en attente pour la vue courante
   * (event MapLibre "idle") — contrairement à `init()`, dont la Promise résout sur "load" (le style
   * de base chargé, mais AVANT que `createTokaMap` n'applique `tileLayerName`/les formes initiales,
   * voir son commentaire). Utilisée par `createTokaMap` pour ne résoudre sa propre Promise qu'une
   * fois la carte réellement peinte à l'écran, plutôt que de rendre l'instance disponible pendant
   * que le fond de carte/les images sont encore en train de charger (flash visible à l'affichage
   * initial ou au changement de carte). Attend aussi `backgroundMap` (mode "3 couches", voir
   * `updateBackgroundMode`) quand actif — les deux cartes chargent leurs tuiles indépendamment.
   */
  /**
   * Capture la carte telle qu'elle est affichée, et rend l'image.
   *
   * Ne capture QUE la carte : le fond, les images SVG des formes et les formes elles-mêmes. Rien de
   * l'interface — boutons flottants, barre de survol, fiche entité, curseurs des autres
   * utilisateurs — parce que rien de tout ça n'est peint par les cartes : ce sont des éléments DOM
   * voisins, qu'une capture composée des seuls canvas laisse dehors sans avoir à les masquer. Ce qui
   * DOIT être masqué, ce sont les calques qui vivent dans le canvas sans appartenir au document —
   * sélection, poignées, guides (voir `INTERACTION_SOURCE_IDS`/`includeInteractionOverlays`) : ils
   * disparaissent le temps de la capture, et sont rétablis même en cas d'échec. Le rendu correspondant
   * étant réellement peint à l'écran, une sélection affichée peut clignoter l'espace d'une frame.
   *
   * Les trois couches sont recomposées dans l'ordre où elles s'empilent à l'écran (voir CLAUDE.md,
   * "Carte de fond secondaire") : carte de fond, puis les `<img>` SVG, puis la carte principale. Une
   * capture prise pendant un déplacement de la carte peut donc mélanger deux frames.
   */
  async captureImage(options: CaptureImageOptions = {}): Promise<CaptureImageResult> {
    if (!this.map) throw new Error("[captureImage] carte non initialisée");
    const map = this.map;

    // Avant tout le reste : une tuile encore en vol laisserait un trou gris dans l'image.
    if (options.waitForTiles !== false) await this.waitUntilIdle();

    const cssWidth = map.getCanvas().clientWidth;
    const cssHeight = map.getCanvas().clientHeight;
    if (!cssWidth || !cssHeight) throw new Error("[captureImage] la carte n'a aucune taille à l'écran");

    const fitted = fitWithin(cssWidth, cssHeight, options.maxWidth, options.maxHeight);
    const pixelRatio = options.pixelRatio ?? Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const width = Math.max(1, Math.round(fitted.width * pixelRatio));
    const height = Math.max(1, Math.round(fitted.height * pixelRatio));

    const target = document.createElement("canvas");
    target.width = width;
    target.height = height;
    const ctx = target.getContext("2d");
    if (!ctx) throw new Error("[captureImage] contexte 2d indisponible");

    const restoreLayers = options.includeInteractionOverlays ? null : this.hideInteractionLayers();
    const type = options.type ?? "image/png";

    try {
      // `backgroundColor` est posée sur le conteneur DOM, pas peinte par une carte (voir
      // `setBackgroundColor`) : sans ce remplissage, elle manquerait partout où rien d'autre ne peint.
      //
      // Non définie, l'image reste transparente à ces endroits — sauf en JPEG, qui ne connaît pas la
      // transparence et encode en NOIR ce qui n'a pas été peint : sous un fond `unset`, toute la
      // capture serait noire. On lui donne donc du blanc, faute de mieux.
      const background = this.backgroundColor ?? (type === "image/jpeg" ? OPAQUE_CAPTURE_BACKGROUND : undefined);

      if (background !== undefined) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, width, height);
      }

      // Sans 2e carte, c'est la principale qui peint le fond : il est déjà dans son canvas.
      if (this.backgroundMap) await this.backgroundMap.drawInto(ctx, width, height);

      const skippedImageShapeIds = await drawOverlayImagesInto(ctx, this.imageOverlayElements, width / cssWidth);

      await drawMapCanvasInto(map, ctx, width, height);

      return {
        blob: await canvasToBlob(target, type, options.quality),
        width,
        height,
        skippedImageShapeIds,
      };
    } finally {
      restoreLayers?.();
    }
  }

  /** Calques peints dans le canvas qui relèvent de l'outil et non du document — voir
   * `INTERACTION_SOURCE_IDS` pour ce qui les réunit. Le survol et les halos de sélection des formes
   * s'ajoutent bande par bande : ils partagent la source des formes, donc ne peuvent pas se
   * reconnaître à elle. */
  private interactionLayerIds(): string[] {
    const map = this.map;
    if (!map) return [];

    const fromSources = (map.getStyle()?.layers ?? [])
      .filter((layer) => INTERACTION_SOURCE_IDS.includes((layer as { source?: string }).source ?? ""))
      .map((layer) => layer.id);

    const fromBands = [...this.shapeLayerBands.values()].flatMap((band) => [
      band.hoverOutlineLayerId,
      band.haloLayerId,
      band.circleHaloLayerId,
    ]);

    return [
      ...fromSources,
      ...fromBands,
      EDITING_SHAPE_SELECTED_HALO_LAYER_ID,
      EDITING_SHAPE_MODIFY_OUTLINE_LAYER_ID,
    ];
  }

  /** Masque les calques d'interaction et rend de quoi les rétablir — un calque déjà masqué est
   * laissé tel quel, pour ne pas le rendre visible en le "rétablissant". */
  private hideInteractionLayers(): () => void {
    const map = this.map;
    if (!map) return () => {};

    const hidden = new Set<string>();
    for (const id of this.interactionLayerIds()) {
      if (!map.getLayer(id)) continue;
      if (map.getLayoutProperty(id, "visibility") === "none") continue;
      map.setLayoutProperty(id, "visibility", "none");
      hidden.add(id);
    }

    return () => {
      for (const id of hidden) {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", "visible");
      }
    };
  }

  async waitUntilIdle(): Promise<void> {
    // Attendu AVANT "idle" : le style repeint à son arrivée, donc un "idle" observé pendant qu'il est
    // encore en vol ne dit rien du rendu final.
    await this.pendingOsmStyle;
    await Promise.all([
      waitForMapIdle(this.map!),
      this.backgroundMap?.waitUntilIdle() ?? Promise.resolve(),
    ]);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.handleSnapModifierKey);
    window.removeEventListener("keyup", this.handleSnapModifierKey);
    window.removeEventListener("blur", this.handleWindowBlur);
    this.stopGeolocationTracking();
    this.stopHighlightAnimation();
    this.stopRemoteSelectionAnimation();
    this.stopRouteAnimation();
    this.stopShapesFadeIn();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.backgroundMap?.destroy();
    this.backgroundMap = null;
    this.rootContainer = null;
    this.map?.remove();
    this.map = null;
    this.imageOverlayContainer?.remove();
    this.imageOverlayContainer = null;
    this.imageOverlayElements.clear();
    this.cancelHoverToolbarHide();
    this.hoverToolbarContainer?.remove();
    this.hoverToolbarContainer = null;
    this.hoverToolbarShapeId = null;
    // Les sources/layers MapLibre (bandes de calques, images raster) sont détruits avec `this.map`
    // ci-dessus : seul le bookkeeping JS a besoin d'être nettoyé ici.
    this.rasterImageLayers.clear();
    this.probedImageDimensions.clear();
    this.pendingImageDimensionSrcs.clear();
    this.failedImageDimensionSrcs.clear();
    this.shapeLayerBands.clear();
    this.currentFillLayerIds = [];
    this.shapeNumericIds.clear();
    this.listeners.clear();
  }

  /**
   * Retire une forme de la sélection multiple courante (BESOINS.md §13) si elle en fait partie —
   * ne fait rien sinon (pas de sélection active, ou forme qui n'y est pas). Appelé par
   * `createTokaMap` quand le `onSave` d'une forme échoue pendant qu'elle est dans une sélection :
   * `ShapeSaveTracker` la ramène à sa position confirmée, mais rien ne la sort automatiquement du
   * groupe — sans cet appel, elle resterait affichée en surbrillance "selected" alors qu'elle n'a en
   * réalité pas suivi le reste du déplacement/redimensionnement/rotation. Referme entièrement la
   * sélection si c'était la dernière forme restante (un rectangle vide n'a plus de sens).
   */
  removeFromSelection(shapeId: string): void {
    if (!this.selectionState || !this.selectionState.shapeIds.includes(shapeId)) return;
    const shapeIds = this.selectionState.shapeIds.filter((id) => id !== shapeId);
    if (shapeIds.length === 0) {
      this.clearSelection();
      return;
    }
    this.selectionState.shapeIds = shapeIds;
    this.applySelectionFeatureState(shapeIds);
    this.updateSelectionVisuals();
    this.emit({ type: "selection:changed", shapeIds });
  }

  /**
   * Compose une sélection multiple (BESOINS.md §13) à partir d'une liste de formes, sans geste de
   * l'utilisateur — pour qu'un consommateur puisse désigner un ensemble calculé ailleurs (toutes les
   * formes d'un thème, d'un type de point d'intérêt...) et le rendre manipulable comme s'il avait
   * été tracé au rectangle.
   *
   * REMPLACE la sélection courante plutôt que de s'y ajouter : l'appelant part d'un ensemble qu'il a
   * déterminé lui-même, il n'a aucune raison d'hériter d'une sélection antérieure dont il ne sait
   * rien. Pour ajouter, il lui suffit d'inclure l'existant dans la liste qu'il passe.
   *
   * Le rectangle est construit comme le fait `toggleShapeInSelection` en partant de zéro : bornes de
   * la première forme, puis `expandSelectionCorners` pour chacune des suivantes. Rotation nulle, un
   * ensemble calculé n'ayant pas d'orientation propre.
   *
   * Les formes verrouillées et les identifiants inconnus sont ignorés (même exclusion que
   * `shapeIdsInsideCornersPx`) ; une liste qui n'en laisse aucune referme la sélection, un rectangle
   * vide n'ayant pas de sens.
   */
  selectShapes(shapeIds: string[]): void {
    if (!this.map) return;

    const shapes = shapeIds
      .map((id) => this.lastShapes.find((s) => s.id === id))
      .filter((shape): shape is Shape => !!shape && !shape.locked);

    if (!shapes.length) {
      this.clearSelection();
      return;
    }

    const corners = shapes
      .slice(1)
      .reduce((acc, shape) => this.expandSelectionCorners(acc, 0, shape), this.boundingCornersForShapePx(shapes[0]!));

    this.selectionState = { corners, rotation: 0, shapeIds: shapes.map((shape) => shape.id) };

    this.applySelectionFeatureState(this.selectionState.shapeIds);
    this.updateSelectionVisuals();
    this.emit({ type: "selection:changed", shapeIds: this.selectionState.shapeIds });
  }

  /**
   * Ctrl+clic sur une forme (voir `handleShapeClick`) : l'ajoute à la sélection multiple courante si
   * elle n'y est pas déjà, l'en retire sinon (bascule) — sans avoir à tracer un rectangle. Une forme
   * verrouillée n'est jamais éligible (même exclusion que `shapeIdsInsideCornersPx`).
   *
   * En ajout, le rectangle de sélection (`selectionState.corners`) est agrandi pour englober aussi
   * la nouvelle forme — sans quoi ses poignées de redimensionnement/rotation resteraient dessinées
   * en dehors d'une partie des formes qu'elles sont censées manipuler. `expandSelectionCorners`
   * défait/refait la rotation cumulée de la sélection (même principe que `computeSelectionResizeScale`)
   * pour que ce calcul reste correct même si le groupe a déjà été pivoté. En retrait
   * (`removeFromSelection`), le rectangle n'est volontairement PAS rétréci — même convention déjà en
   * place là-bas (un rectangle qui ne colle pas exactement aux formes qu'il contient est déjà le cas
   * normal pour un rectangle tracé à la main).
   *
   * Un premier Ctrl+clic (aucun `selectionState` encore actif) démarre la sélection à partir de la
   * forme actuellement "focused" (`focusedShapeId`, clic simple précédent — voir `setFocusedShape`)
   * quand il y en a une différente de `shapeId`, PAS juste de `shapeId` seule : sans ça, un clic
   * simple sur A puis un Ctrl+clic sur B ne sélectionnait que B, la forme cliquée juste avant
   * disparaissant silencieusement de la sélection au lieu de s'y ajouter.
   */
  private toggleShapeInSelection(shapeId: string): void {
    if (!this.map) return;
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (!shape || shape.locked) return;

    if (this.selectionState?.shapeIds.includes(shapeId)) {
      this.removeFromSelection(shapeId);
      return;
    }

    if (!this.selectionState && this.focusedShapeId != null && String(this.focusedShapeId) !== shapeId) {
      const focusedShape = this.lastShapes.find((s) => s.id === String(this.focusedShapeId));
      if (focusedShape && !focusedShape.locked) {
        this.selectionState = { corners: this.boundingCornersForShapePx(focusedShape), rotation: 0, shapeIds: [String(this.focusedShapeId)] };
      }
    }

    this.selectionState = this.selectionState
      ? {
          corners: this.expandSelectionCorners(this.selectionState.corners, this.selectionState.rotation, shape),
          rotation: this.selectionState.rotation,
          shapeIds: [...this.selectionState.shapeIds, shapeId],
        }
      : { corners: this.boundingCornersForShapePx(shape), rotation: 0, shapeIds: [shapeId] };

    this.applySelectionFeatureState(this.selectionState.shapeIds);
    this.updateSelectionVisuals();
    this.emit({ type: "selection:changed", shapeIds: this.selectionState.shapeIds });
  }

  /** Rectangle (non pivoté) englobant les sommets d'une forme, en espace écran puis reprojeté —
   * sert de rectangle de sélection initial quand `toggleShapeInSelection` en crée un à partir d'une
   * seule forme (aucun Ctrl+glisser n'a eu lieu pour en fournir un). */
  private boundingCornersForShapePx(shape: Shape): LngLat[] {
    if (!this.map) return [];
    const pointsPx = shapeVertices(shape).map((v) => this.map!.project(v));
    const minX = Math.min(...pointsPx.map((p) => p.x));
    const maxX = Math.max(...pointsPx.map((p) => p.x));
    const minY = Math.min(...pointsPx.map((p) => p.y));
    const maxY = Math.max(...pointsPx.map((p) => p.y));
    return this.rectangleCornersFromScreen({ x: minX, y: minY }, { x: maxX, y: maxY });
  }

  /**
   * Agrandit un rectangle de sélection existant pour qu'il englobe aussi les sommets de `extraShape`
   * — dans le repère LOCAL du rectangle (défait sa rotation cumulée `rotation` autour de son centre,
   * même principe que `computeSelectionResizeScale`), pour que le rectangle résultant reste un vrai
   * rectangle aligné sur ses propres côtés, pas un parallélogramme quelconque, même si le groupe a
   * déjà été pivoté avant cet ajout.
   */
  private expandSelectionCorners(corners: LngLat[], rotation: number, extraShape: Shape): LngLat[] {
    if (!this.map) return corners;
    const centerPx = this.map.project(centroid(corners));
    const toLocal = (p: PixelPoint) => rotatePointPx(p, centerPx, -rotation);
    const localCornersPx = corners.map((c) => toLocal(this.map!.project(c)));
    const localExtraPx = shapeVertices(extraShape).map((v) => toLocal(this.map!.project(v)));
    const allLocalPx = [...localCornersPx, ...localExtraPx];
    const minX = Math.min(...allLocalPx.map((p) => p.x));
    const maxX = Math.max(...allLocalPx.map((p) => p.x));
    const minY = Math.min(...allLocalPx.map((p) => p.y));
    const maxY = Math.max(...allLocalPx.map((p) => p.y));
    return rectangleCornersPx({ x: minX, y: minY }, { x: maxX, y: maxY }).map((p) => {
      const rotated = rotatePointPx(p, centerPx, rotation);
      const ll = this.map!.unproject(rotated as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
  }

  // --- Accesseurs réservés aux tests e2e (Playwright) ---

  /** @internal Uniquement pour les tests. */
  getMapForTests(): MlMap | null {
    return this.map;
  }

  /** @internal Uniquement pour les tests — instance MapLibre du fond "3 couches" (voir
   * `updateBackgroundMode`), ou `null` si ce mode n'est pas actif. */
  getBackgroundMapForTests(): MlMap | null {
    return this.backgroundMap?.getMapForTests() ?? null;
  }

  /** @internal Sommets uniques de la forme en cours d'édition, ou null si aucune édition en cours. */
  getEditingPointsForTests(): LngLat[] | null {
    return this.editingState?.points ?? null;
  }

  /** @internal Position (lngLat) courante de la poignée de rotation, ou null hors édition — ou
   * pour un cercle, qui n'en a pas (voir `updateEditVisuals`). */
  getRotateHandleForTests(): LngLat | null {
    if (!this.editingState) return null;
    const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    if (shape?.shapeKind === "circle") return null;
    const center = centroid(this.editingState.points);
    return this.computeRotateHandlePosition(this.editingState.points, center);
  }

  /** @internal Position (lngLat) courante de la poignée de déplacement, ou null hors édition —
   * présente sur TOUTES les formes (contrairement à `getRotateHandleForTests`, voir
   * `computeMoveHandleCenter`). */
  getMoveHandleForTests(): LngLat | null {
    if (!this.editingState || this.editingState.points.length === 0) return null;
    const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    return this.computeMoveHandleCenter(shape, this.editingState.points);
  }

  /** @internal Les 4 barres de redimensionnement de côté (voir `EDGE_RESIZE_LAYER_ID`), ou null
   * hors édition/pour une forme non éligible (voir `computeEdgeResizeHandles`/`updateEditVisuals` :
   * seulement `shapeKind === "rectangle"` à 4 sommets). */
  getEdgeResizeHandlesForTests(): { edgeIndex: number; a: LngLat; b: LngLat; cursor: string }[] | null {
    if (!this.editingState) return null;
    const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    if (shape?.shapeKind !== "rectangle" || this.editingState.points.length !== 4) return null;
    return this.computeEdgeResizeHandles(this.editingState.points);
  }

  /** @internal Rectangle de sélection courant (BESOINS.md §13), ou null si aucun. */
  getSelectionForTests(): { corners: LngLat[]; rotation: number; shapeIds: string[] } | null {
    return this.selectionState;
  }

  /** @internal Position (lngLat) courante de la poignée de rotation du rectangle de sélection. */
  getSelectionRotateHandleForTests(): LngLat | null {
    if (!this.selectionState) return null;
    const center = centroid(this.selectionState.corners);
    return this.computeRotateHandlePosition(this.selectionState.corners, center);
  }

  /** MapLibre `bearing` est anti-horaire depuis le nord ; l'interface `MapAdapter` est horaire (boussole) — on inverse le signe ici. */
  setRotation(degrees: number): void {
    this.map?.setBearing(-degrees);
  }

  getRotation(): number {
    return this.map ? -this.map.getBearing() : 0;
  }

  setZoom(zoom: number): void {
    this.map?.setZoom(zoom);
  }

  getZoom(): number {
    return this.map?.getZoom() ?? 16; // valeur par défaut du constructeur, voir init()
  }

  /** `easeTo` plutôt que `map.zoomIn()/zoomOut()`, dont le pas est figé à 1. MapLibre borne lui-même
   * la destination aux min/max posés par `setZoomBounds`. */
  zoomBy(delta: number): void {
    this.map?.easeTo({ zoom: this.map.getZoom() + delta, duration: ZOOM_STEP_DURATION_MS });
  }

  /**
   * Rejoint une vue en volant plutôt qu'en sautant — même destination que `setCenter`/`setZoom`/
   * `setRotation` appelés ensemble, mais le trajet reste à l'écran. Sert au passage d'un document à
   * l'autre (voir `TokaMapInstance.loadDocument`) : le déplacement dit où l'on est allé, ce qu'un
   * saut ne dit pas.
   *
   * Ne bloque pas : l'animation se poursuit après le retour. Un champ omis est laissé tel quel.
   */
  animateToView(
    view: { center?: LngLat; zoom?: number; rotationDeg?: number; pitchDeg?: number },
    durationMs = VIEW_ANIMATION_DURATION_MS,
  ): void {
    this.map?.easeTo({
      ...(view.pitchDeg !== undefined ? { pitch: view.pitchDeg } : {}),
      ...(view.center !== undefined ? { center: view.center } : {}),
      ...(view.zoom !== undefined ? { zoom: view.zoom } : {}),
      // Convention "boussole" inversée, comme `setRotation`.
      ...(view.rotationDeg !== undefined ? { bearing: -view.rotationDeg } : {}),
      duration: durationMs,
    });
  }

  setZoomBounds(min?: number, max?: number): void {
    if (!this.map) return;
    if (this.framingHold) {
      this.heldZoomBounds = { min, max };
      return;
    }
    // MapLibre refuse un minimum au-dessus du maximum en place : passer d'un document borné bas à un
    // document qui exige un zoom plus fort doit relever le plafond d'abord.
    if (min !== undefined && min > this.map.getMaxZoom()) {
      this.map.setMaxZoom(max);
      this.map.setMinZoom(min);
    } else {
      this.map.setMinZoom(min);
      this.map.setMaxZoom(max);
    }
  }

  getZoomBounds(): { min: number; max: number } {
    if (!this.map) return { min: 0, max: 22 }; // valeurs par défaut natives de MapLibre
    return { min: this.map.getMinZoom(), max: this.map.getMaxZoom() };
  }

  getFraming(): MapFraming | undefined {
    return this.framing;
  }

  setFraming(framing: MapFraming | undefined): void {
    const previousDefaultRotation = this.defaultTextRotationDeg();
    this.framing = framing;
    this.contentPointsCache = null;
    this.applyFramingConstraints();
    // La rotation par défaut des textes et images vient de la vue de départ, connue parfois après le
    // premier dessin : `refreshZoomVisibility` ne redessinerait pas.
    if (this.defaultTextRotationDeg() !== previousDefaultRotation) this.renderShapes(this.lastShapes);
    else this.refreshZoomVisibility();
    this.emit({ type: "framing:changed", framing });
  }

  setZoomFloor(zoom: number | null): void {
    this.zoomFloor = zoom;
    this.applyFramingConstraints();
  }

  getFramingGeometry(): FramingGeometry | null {
    return this.framing ? resolveFramingGeometry(this.framing, this.contentPoints()) : null;
  }

  showFramingRect(which: "start" | "limit", padding: EdgeInsets = {}): boolean {
    const geometry = this.getFramingGeometry();
    const rect = which === "start" ? geometry?.startRect : geometry?.limitRect;
    if (!rect || !this.map || !this.framing) return false;

    const insets = { top: padding.top ?? 0, right: padding.right ?? 0, bottom: padding.bottom ?? 0, left: padding.left ?? 0 };
    const { width, height } = this.viewportSize();
    const zoom = fitZoom(rect, {
      width: Math.max(1, width - insets.left - insets.right),
      height: Math.max(1, height - insets.top - insets.bottom),
    });

    const targetZoom = Math.min(zoom, this.map.getMaxZoom());
    const metersPerPixel = mercatorMetersPerPixel(targetZoom);
    const bearing = (-this.framing.rotationDeg * Math.PI) / 180;
    const right: MercatorPoint = [Math.cos(bearing), -Math.sin(bearing)];
    const up: MercatorPoint = [Math.sin(bearing), Math.cos(bearing)];
    const dx = ((insets.left - insets.right) / 2) * metersPerPixel;
    const dy = ((insets.top - insets.bottom) / 2) * metersPerPixel;

    this.map.easeTo({
      center: mercatorToLngLat([
        rect.center[0] - right[0] * dx + up[0] * dy,
        rect.center[1] - right[1] * dx + up[1] * dy,
      ]),
      zoom: targetZoom,
      bearing: -this.framing.rotationDeg,
      duration: VIEW_ANIMATION_DURATION_MS,
    });

    return true;
  }

  getContainerElement(): HTMLElement | null {
    return this.map?.getContainer() ?? null;
  }

  private defaultImageRotationDeg(shape: Shape): number {
    return defaultImageRotationDeg(shape, this.defaultTextRotationDeg());
  }

  /** Rotation d'un texte qui n'en fixe pas (`ShapeText.rotation`) : le cap de la vue de départ. */
  private defaultTextRotationDeg(): number {
    return startViewBearingDeg(this.getStartCamera()?.rotationDeg ?? 0);
  }

  getStartCamera(viewport?: { width: number; height: number }): FramingCamera | null {
    if (!this.framing) return null;

    const content = this.contentPoints();
    const startRect = resolveStartRect(this.framing, content);
    if (!startRect) return null;

    const limitRect = resolveLimitRect(this.framing, content, startRect);

    return resolveStartCamera(this.framing, startRect, viewport ?? this.viewportSize(), limitRect);
  }

  showStartView(options: { animate?: boolean; withPitch?: boolean } = {}): boolean {
    const camera = this.getStartCamera();
    if (!camera || !this.map) return false;

    const pitch = options.withPitch && this.allowPitch ? camera.pitchDeg : 0;
    const target = { center: camera.center, zoom: camera.zoom, bearing: -camera.rotationDeg, pitch };

    if (options.animate) {
      this.map.easeTo({ ...target, zoom: this.zoomFittingPitchedStart(target), duration: VIEW_ANIMATION_DURATION_MS });
    } else {
      this.map.jumpTo(target);
      if (pitch) this.map.jumpTo({ zoom: this.zoomFittingPitchedStart(target) });
    }

    return true;
  }

  projectToScreen(lngLat: LngLat): { x: number; y: number } {
    const point = this.map!.project(lngLat);
    return { x: point.x, y: point.y };
  }

  screenToLngLat(x: number, y: number): LngLat {
    const lngLat = this.map!.unproject([x, y]);
    return [lngLat.lng, lngLat.lat];
  }

  onCameraMove(listener: () => void): () => void {
    this.map?.on("move", listener);
    return () => {
      this.map?.off("move", listener);
    };
  }

  private viewportSize(): { width: number; height: number } {
    const canvas = this.map?.getContainer();
    return { width: canvas?.clientWidth || 1280, height: canvas?.clientHeight || 800 };
  }

  private contentPoints(): MercatorPoint[] {
    if (!this.contentPointsCache) this.contentPointsCache = shapesExtentPoints(this.lastShapes);
    return this.contentPointsCache;
  }

  private zoomFittingPitchedStart(target: { center: LngLat; zoom: number; bearing: number; pitch: number }): number {
    if (!this.map || !this.framing || !target.pitch) return target.zoom;

    const startRect = resolveStartRect(this.framing, this.contentPoints());
    if (!startRect) return target.zoom;

    const corners = rectCorners(startRect, this.framing.rotationDeg).map((point) => mercatorToLngLat(point));
    const { width, height } = this.viewportSize();
    const previous = { center: this.map.getCenter(), zoom: this.map.getZoom(), bearing: this.map.getBearing(), pitch: this.map.getPitch() };
    let zoom = target.zoom;
    // Sauts d'essai, pour mesurer : la limite les déplacerait et fausserait la mesure. Elle
    // s'appliquera au vrai déplacement vers le zoom trouvé.
    const limit = this.framingLimit;
    this.framingLimit = null;

    try {
      for (let attempt = 0; attempt < 6; attempt++) {
        this.map.jumpTo({ ...target, zoom });
        const projected = corners.map((corner) => this.map!.project(corner));
        const overflow = Math.max(
          ...projected.map((p) => Math.max(
            Math.abs(p.x - width / 2) / (width / 2 / 1.05),
            Math.abs(p.y - height / 2) / (height / 2 / 1.05),
          )),
        );
        if (!Number.isFinite(overflow) || overflow <= 1.001) break;
        zoom -= Math.log2(overflow);
      }

      this.map.jumpTo(previous);
    } finally {
      this.framingLimit = limit;
    }

    return zoom;
  }

  /**
   * Suspend les contraintes de cadrage le temps d'une transition animée vers un autre document : au
   * départ du vol, la caméra est encore sur le document sortant, donc hors des limites de l'entrant —
   * les appliquer la ferait sauter (`jumpTo`, qui interrompt le vol) ou la plaquerait contre la
   * limite à chaque image. Les bornes de zoom sont suspendues pour la même raison : celles du
   * sortant peuvent interdire la vue de l'entrant, et celles de l'entrant feraient sauter le zoom au
   * départ. Levée par `releaseFramingConstraintsAfterMove`.
   */
  holdFramingConstraints(): void {
    this.framingHold = Symbol("framing-hold");
    this.framingLimit = null;
    this.map?.setMinZoom(0);
    this.map?.setMaxZoom(FRAMING_MAX_ZOOM);
  }

  /** Réapplique les contraintes de cadrage une fois le déplacement en cours terminé — tout de suite
   * si la caméra est immobile. Sans effet si une autre suspension a été posée entre-temps. */
  releaseFramingConstraintsAfterMove(): void {
    const hold = this.framingHold;
    const release = () => {
      if (this.framingHold !== hold) return;
      this.framingHold = null;
      if (this.heldZoomBounds) {
        const { min, max } = this.heldZoomBounds;
        this.heldZoomBounds = null;
        this.setZoomBounds(min, max);
      }
      this.applyFramingConstraints();
    };

    if (this.map?.isMoving()) this.map.once("moveend", release);
    else release();
  }

  private applyFramingConstraints(): void {
    if (!this.map) return;

    this.framingLimit = null;

    if (this.framingHold) return;

    if (!this.framing) {
      if (!this.editMode) this.map.setMaxBounds(null);
      return;
    }

    const content = this.contentPoints();
    const startRect = resolveStartRect(this.framing, content);
    const maxZoom = resolveMaxZoom(this.framing, startRect);
    const limitRect = this.editMode ? null : resolveLimitRect(this.framing, content, startRect);
    // Hors édition, le dézoom s'arrête quand la limite recouvre tout juste l'écran : jamais au-delà.
    // Incliné, l'écran voit plus loin et il faut zoomer davantage (voir `installLimitConstraint`) :
    // le zoom max doit le permettre jusqu'à l'inclinaison la plus forte.
    const limitZoom = limitRect ? coverZoom(limitRect, this.viewportSize()) : 0;
    const pitchedLimitZoom = limitRect && this.allowPitch
      ? limitZoomFloor(limitRect, this.framing.rotationDeg, { rotationDeg: this.framing.rotationDeg, pitchDeg: FRAMING_MAX_PITCH_DEG }, this.viewportSize(), this.map.transform.fov)
      : limitZoom;

    const ceiling = Math.max(maxZoom ?? FRAMING_MAX_ZOOM, pitchedLimitZoom);
    const floor = Math.min(ceiling, this.editMode && this.zoomFloor !== null ? this.zoomFloor : limitZoom);
    const zoom = this.map.getZoom();

    if (zoom < floor || zoom > ceiling) this.map.jumpTo({ zoom: Math.min(ceiling, Math.max(floor, zoom)) });

    this.map.setMinZoom(0);
    this.map.setMaxZoom(ceiling);
    this.map.setMinZoom(floor);

    if (this.editMode) return;

    if (!this.framingResizeListener) {
      this.framingResizeListener = () => this.applyFramingConstraints();
      this.map.on("resize", this.framingResizeListener);
    }

    this.map.setMaxBounds(null);
    this.framingLimit = limitRect;
    this.installLimitConstraint();

    // Seulement si la caméra sort de la nouvelle limite : un `jumpTo` émet `"moveend"`, qui peut
    // relancer `renderShapes` (voir `refreshZoomVisibility`), lequel nous rappelle — sauter sans
    // raison bouclerait sans fin.
    if (limitRect) {
      const current = this.map.getCenter();
      const { center, zoom: constrainedZoom } = this.map.transform.getConstrained(current, this.map.getZoom());
      const moved = Math.abs(center.lng - current.lng) > 1e-9
        || Math.abs(center.lat - current.lat) > 1e-9
        || Math.abs(constrainedZoom - this.map.getZoom()) > 1e-9;

      if (moved) this.map.jumpTo({ center, zoom: constrainedZoom });
    }
  }

  /**
   * Garde la caméra dans `framingLimit`, à chaque mouvement (geste, animation, `jumpTo`). Les
   * `maxBounds` de MapLibre ne conviennent pas : une boîte en longitude/latitude, qui ignore
   * l'orientation, laisse voir au-delà d'une limite tournée. On se branche donc au même endroit
   * qu'eux, `Transform.getConstrained` (MapLibre 4 n'expose pas d'option pour ça), en ajoutant notre
   * contrainte à la sienne.
   */
  private installLimitConstraint(): void {
    if (!this.map || this.limitConstraintInstalled) return;

    const transform = this.map.transform;
    const native = transform.getConstrained.bind(transform);

    transform.getConstrained = (lngLat, zoom) => {
      const constrained = native(lngLat, zoom);
      if (!this.framingLimit || !this.framing || this.editMode) return constrained;

      const { center, zoom: limitedZoom } = constrainViewToLimit(
        this.framingLimit,
        this.framing.rotationDeg,
        {
          center: lngLatToMercator([constrained.center.lng, constrained.center.lat]),
          zoom: constrained.zoom,
          rotationDeg: -transform.bearing,
          pitchDeg: transform.pitch,
        },
        { width: transform.width, height: transform.height },
        transform.fov,
      );
      const [lng, lat] = mercatorToLngLat(center);

      return { center: new maplibregl.LngLat(lng, lat), zoom: limitedZoom };
    };

    // Incliner ou tourner seul ne repasse pas par `getConstrained` (MapLibre ne contraint qu'au
    // changement de centre ou de zoom) : on la réapplique, sur le transform directement plutôt que
    // par `jumpTo`, qui interromprait le geste ou l'animation en cours.
    const reconstrain = () => {
      if (!this.map || !this.framingLimit || this.editMode) return;

      const { center, zoom } = transform.getConstrained(transform.center, transform.zoom);
      const moved = Math.abs(center.lng - transform.center.lng) > 1e-9
        || Math.abs(center.lat - transform.center.lat) > 1e-9
        || Math.abs(zoom - transform.zoom) > 1e-9;
      if (!moved) return;

      transform.zoom = zoom;
      transform.center = center;
      this.map.triggerRepaint();
    };

    this.map.on("pitch", reconstrain);
    this.map.on("rotate", reconstrain);
    this.limitConstraintInstalled = true;
  }

  setCenter(center: LngLat): void {
    this.map?.setCenter(center);
  }

  getCenter(): LngLat {
    if (!this.map) return [2.5137, 48.9614]; // valeur par défaut du constructeur, voir init()
    const center = this.map.getCenter();
    return [center.lng, center.lat];
  }

  ensureShapeVisible(shapeId: string, avoid: EdgeInsets): void {
    if (!this.map) return;
    // Recherche par id parmi les dernières formes reçues via `renderShapes` — même mécanisme que
    // les autres méthodes qui retrouvent une forme depuis un `shapeId` (voir `this.lastShapes`).
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (!shape) return;

    // Centre de la forme (voir `shapeCenter`), projeté en pixels écran relatifs au conteneur de la
    // carte (`this.map.project`, jamais relatifs à la page — voir CLAUDE.md "E2e gotcha").
    const center = shapeCenter(shape);
    const centerPx = this.map.project(center);

    // `avoid` délimite une zone à éviter sur un ou plusieurs bords (ex. la largeur d'une sidebar
    // qui réduit d'autant l'espace effectivement visible) — bord omis : marge nulle.
    const { top = 0, right = 0, bottom = 0, left = 0 } = avoid;
    const { clientWidth, clientHeight } = this.map.getContainer();

    // Le centroïde est-il déjà en dehors des marges ? Si oui, rien à faire : on ne veut pas
    // recentrer la caméra à chaque clic, seulement quand c'est nécessaire pour ne pas laisser la
    // forme cachée/coincée contre le bord.
    const stillVisible =
      centerPx.x >= left && centerPx.x <= clientWidth - right && centerPx.y >= top && centerPx.y <= clientHeight - bottom;
    if (stillVisible) return;

    // Anime la caméra pour replacer le centroïde au centre de la zone effectivement visible une
    // fois les marges retirées (`padding` de MapLibre, natif — pas un calcul de décalage en pixels
    // fait à la main), ce qui reste correct quels que soient le zoom ou la rotation courants.
    // Ce `padding` reste posé sur la caméra pour tout déplacement ultérieur (natif MapLibre — voir
    // `clearVisibilityAvoid`) : c'est ce qui permet à `backgroundMap` de rester synchronisé (voir le
    // handler `"move"` de `init()`) plutôt que de désynchroniser le fond OSM des formes.
    this.map.easeTo({ center, padding: { top, right, bottom, left } });
  }

  /** Voir `MapAdapter.clearVisibilityAvoid`. */
  clearVisibilityAvoid(): void {
    if (!this.map) return;
    const current = this.map.getPadding();
    if (!current.top && !current.right && !current.bottom && !current.left) return;
    this.map.easeTo({ padding: { top: 0, right: 0, bottom: 0, left: 0 } });
  }

  /**
   * Surligne (contour + fond pulsants, voir `HIGHLIGHT_*`) l'ensemble des formes de `shapeIds`, pour
   * les repérer facilement sur la carte (ex. résultat d'une recherche côté consommateur) — remplace
   * entièrement tout surlignage précédent. `[]` efface le surlignage courant et arrête l'animation.
   * Ids inconnus (absents de `this.lastShapes`) : silencieusement ignorés, pas d'erreur.
   *
   * Ne recentre PAS la caméra sur les formes surlignées par défaut — voir
   * `HighlightShapesOptions.zoomToFit`/`ensureVisible`, ou combiner avec `ensureShapeVisible`/
   * `setCenter` séparément si besoin.
   */
  highlightShapes(shapeIds: string[], options?: HighlightShapesOptions): void {
    this.highlightedShapeIds = shapeIds;
    const shapes = this.updateHighlightSource();
    if (shapeIds.length > 0) {
      this.startHighlightAnimation();
    } else {
      this.stopHighlightAnimation();
    }
    if (options?.zoomToFit) this.zoomToFitShapes(shapes, options.rotationDeg);
    // Après `zoomToFit` (qui ne connaît rien d'une éventuelle sidebar) : ne s'applique qu'à la
    // première forme, voir `HighlightShapesOptions.ensureVisible`.
    if (options?.ensureVisible && shapeIds.length > 0) this.ensureShapeVisible(shapeIds[0]!, options.ensureVisible);
  }

  /** Reconstruit `HIGHLIGHT_SOURCE_ID` à partir de `this.highlightedShapeIds` et `this.lastShapes` —
   * appelé depuis `highlightShapes` ET `renderShapes` (pour rester correct si une forme surlignée est
   * déplacée/éditée/supprimée pendant qu'elle l'est). Retourne les formes résolues (ids inconnus
   * filtrés), réutilisées par `highlightShapes` pour `zoomToFit` sans refaire la même recherche. */
  private updateHighlightSource(): Shape[] {
    const shapes = this.highlightedShapeIds
      .map((id) => this.lastShapes.find((s) => s.id === id))
      .filter((shape): shape is Shape => shape !== undefined);
    const source = this.map?.getSource(HIGHLIGHT_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(highlightShapesToGeoJSON(shapes));
    }
    this.updateHighlightOutlineSource(shapes);
    return shapes;
  }

  /** Reconstruit `HIGHLIGHT_OUTLINE_SOURCE_ID` — un anneau agrandi vers l'extérieur de
   * `this.highlightOutlineMarginPx` pixels (voir `outwardExpandedRingPx`) pour chaque forme, calculé
   * en espace écran via `project`/`unproject` (comme le reste de l'édition interactive de cet adapter,
   * voir CLAUDE.md) : reste donc correct quels que soient le zoom/bearing/pitch courants, sans se
   * soucier du sens de parcours de l'anneau source ni de la projection Web Mercator. */
  private updateHighlightOutlineSource(shapes: Shape[]): void {
    const map = this.map;
    if (!map) return;
    const source = map.getSource(HIGHLIGHT_OUTLINE_SOURCE_ID);
    if (!source || source.type !== "geojson") return;
    const geojson: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: shapes.map((shape) => {
        const ring = shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates;
        const ringPx = ring.map((c) => map.project(c));
        const expandedPx = outwardExpandedRingPx(ringPx, this.highlightOutlineMarginPx);
        const expandedLngLat = expandedPx.map((p): LngLat => {
          const ll = map.unproject(p as Point);
          return [ll.lng, ll.lat];
        });
        return {
          type: "Feature",
          properties: {},
          geometry: { type: "Polygon", coordinates: [expandedLngLat] },
        };
      }),
    };
    (source as maplibregl.GeoJSONSource).setData(geojson);
  }

  /**
   * Affiche le tracé d'un itinéraire calculé (voir `core/pathfinding.ts`'s `RouteResult.coordinates`,
   * `createTokaMap.ts`) — remplace entièrement tout tracé précédent, `[]`/1 seul point n'affiche
   * rien (voir `routeToGeoJSON`). Ne recentre pas la caméra (même convention que `highlightShapes`) :
   * à combiner avec `highlightShapes`/`ensureShapeVisible` côté appelant si besoin.
   */
  showRoute(coordinates: LngLat[]): void {
    const source = this.map?.getSource(ROUTE_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(routeToGeoJSON(coordinates));
    }
    if (coordinates.length >= 2) this.startRouteAnimation();
    else this.stopRouteAnimation();
  }

  /** Efface le tracé affiché par `showRoute` — no-op si aucun n'était affiché. */
  clearRoute(): void {
    this.showRoute([]);
  }

  /** Voir `MapAdapter.showPathfindingDebugGrid`. */
  showPathfindingDebugGrid(grid: { blockedCells: LngLat[][]; cheapCells: { ring: LngLat[]; weight: number }[] }): void {
    const blockedSource = this.map?.getSource(PATHFINDING_DEBUG_BLOCKED_SOURCE_ID);
    if (blockedSource && blockedSource.type === "geojson") {
      (blockedSource as maplibregl.GeoJSONSource).setData({
        type: "FeatureCollection",
        features: grid.blockedCells.map((ring) => ({
          type: "Feature",
          properties: {},
          geometry: { type: "Polygon", coordinates: [ring] },
        })),
      });
    }
    const cheapSource = this.map?.getSource(PATHFINDING_DEBUG_CHEAP_SOURCE_ID);
    if (cheapSource && cheapSource.type === "geojson") {
      (cheapSource as maplibregl.GeoJSONSource).setData({
        type: "FeatureCollection",
        features: grid.cheapCells.map(({ ring, weight }) => ({
          type: "Feature",
          properties: { weight },
          geometry: { type: "Polygon", coordinates: [ring] },
        })),
      });
    }
  }

  /** Efface l'aperçu affiché par `showPathfindingDebugGrid` — no-op si aucun n'était affiché. */
  clearPathfindingDebugGrid(): void {
    this.showPathfindingDebugGrid({ blockedCells: [], cheapCells: [] });
  }

  /** Voir `MapAdapter.getViewportBounds`. */
  getViewportBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const bounds = this.map!.getBounds();
    return { minX: bounds.getWest(), minY: bounds.getSouth(), maxX: bounds.getEast(), maxY: bounds.getNorth() };
  }

  /**
   * Position actuelle de l'utilisateur (voir BESOINS.md §15, `setGeolocationEnabled`) — `null` si la
   * géolocalisation est désactivée ou qu'aucune position n'est encore connue (ex. juste après
   * activation, avant la première réponse du navigateur). Sert notamment au mode itinéraire (voir
   * `TokaMapInstance.startRouteMode`) pour proposer la position utilisateur comme point de départ.
   */
  getUserLocation(): LngLat | null {
    return this.userPosition;
  }

  /** Voir `MapAdapter.waitForUserLocation`. */
  waitForUserLocation(timeoutMs = USER_LOCATION_WAIT_MS): Promise<LngLat | null> {
    if (this.userPosition) return Promise.resolve(this.userPosition);
    // Repérage désactivé : rien n'est en route, inutile de faire patienter qui que ce soit.
    if (!this.geolocationEnabled) return Promise.resolve(null);

    return new Promise((resolve) => {
      const settle = (position: LngLat | null) => {
        clearTimeout(timer);
        this.userLocationWaiters = this.userLocationWaiters.filter((waiter) => waiter !== settle);
        resolve(position);
      };
      const timer = setTimeout(() => settle(null), timeoutMs);

      this.userLocationWaiters.push(settle);
      this.requestUserLocationOnce(timeoutMs);
    });
  }

  /**
   * Demande ponctuelle de position, en parallèle du suivi continu déjà en cours. Celui-ci est réglé
   * pour la précision (`enableHighAccuracy`, voir `startGeolocationTracking`) : il peut mettre des
   * dizaines de secondes à livrer son PREMIER relevé, quand le navigateur en a souvent déjà un en
   * cache qu'il suffit de réclamer. La première des deux réponses sert tout le monde, via
   * `handleGeolocationPosition`.
   *
   * Un échec n'est pas rapporté : le suivi continu reste en cours et peut encore répondre, et
   * l'appelant a déjà son propre délai (voir `waitForUserLocation`).
   */
  private requestUserLocationOnce(timeoutMs: number): void {
    if (this.userLocationRequestPending || typeof navigator === "undefined" || !navigator.geolocation) return;

    this.userLocationRequestPending = true;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocationRequestPending = false;
        this.handleGeolocationPosition(position);
      },
      () => {
        this.userLocationRequestPending = false;
      },
      { enableHighAccuracy: false, maximumAge: USER_LOCATION_MAX_AGE_MS, timeout: timeoutMs },
    );
  }

  /** Répond à `waitForUserLocation` — au premier relevé, ou à l'arrêt du repérage (`null`). */
  private flushUserLocationWaiters(position: LngLat | null): void {
    const waiters = this.userLocationWaiters;
    this.userLocationWaiters = [];
    for (const waiter of waiters) waiter(position);
  }

  setRemoteCursor(cursor: RemoteCursor): void {
    this.remoteCursors.set(cursor.id, cursor);
    const existing = this.remoteCursorMarkers.get(cursor.id);
    if (existing) {
      updateRemoteCursorElement(existing.getElement(), cursor);
      existing.setLngLat(cursor.position);
    } else {
      const marker = new maplibregl.Marker({ element: createRemoteCursorElement(cursor), anchor: "top-left" })
        .setLngLat(cursor.position)
        .addTo(this.map!);
      this.remoteCursorMarkers.set(cursor.id, marker);
    }
    this.syncRemoteSelectionAnimation();
  }

  removeRemoteCursor(id: string): void {
    if (!this.remoteCursors.delete(id)) return;
    const marker = this.remoteCursorMarkers.get(id);
    if (marker) {
      marker.remove();
      this.remoteCursorMarkers.delete(id);
    }
    this.syncRemoteSelectionAnimation();
  }

  /** `true` si au moins un curseur distant a une `selectedShapeId` — condition d'activation de
   * `startRemoteSelectionAnimation`/`updateRemoteSelectionSources` (voir leurs commentaires). */
  private hasRemoteSelections(): boolean {
    return [...this.remoteCursors.values()].some((cursor) => cursor.selectedShapeId !== undefined);
  }

  /** Démarre ou arrête `startRemoteSelectionAnimation`/`stopRemoteSelectionAnimation` selon
   * `hasRemoteSelections()` — appelé après chaque changement de `this.remoteCursors`. Rafraîchit aussi
   * `updateRemoteSelectionSources()` immédiatement (plutôt que d'attendre la prochaine frame de la
   * boucle) : sans ça, une désélection (dernier curseur distant sélectionné qui repasse à `undefined`)
   * arrête la boucle sans jamais repousser un contour/étiquette vides, laissant le contour de la forme
   * précédemment sélectionnée affiché indéfiniment chez les autres utilisateurs. */
  private syncRemoteSelectionAnimation(): void {
    this.updateRemoteSelectionSources();
    if (this.hasRemoteSelections()) this.startRemoteSelectionAnimation();
    else this.stopRemoteSelectionAnimation();
  }

  /** Reconstruit `REMOTE_SELECTION_OUTLINE_SOURCE_ID`, `REMOTE_SELECTION_LABEL_BG_SOURCE_ID` ET
   * `REMOTE_SELECTION_LABEL_SOURCE_ID` à partir de `this.remoteCursors` et `this.lastShapes` — voir
   * `buildRemoteSelectionGeoJSON`. */
  private updateRemoteSelectionSources(): void {
    const map = this.map;
    if (!map) return;
    const outlineSource = map.getSource(REMOTE_SELECTION_OUTLINE_SOURCE_ID);
    const labelBgSource = map.getSource(REMOTE_SELECTION_LABEL_BG_SOURCE_ID);
    const labelSource = map.getSource(REMOTE_SELECTION_LABEL_SOURCE_ID);
    if (
      !outlineSource || outlineSource.type !== "geojson" ||
      !labelBgSource || labelBgSource.type !== "geojson" ||
      !labelSource || labelSource.type !== "geojson"
    ) return;
    const { outlines, labelBackgrounds, labels } = buildRemoteSelectionGeoJSON(
      [...this.remoteCursors.values()],
      this.lastShapes,
      map,
    );
    (outlineSource as maplibregl.GeoJSONSource).setData(outlines);
    (labelBgSource as maplibregl.GeoJSONSource).setData(labelBackgrounds);
    (labelSource as maplibregl.GeoJSONSource).setData(labels);
  }

  /**
   * Boucle `requestAnimationFrame` tant qu'au moins une sélection distante est active (voir
   * `hasRemoteSelections`) — même raison que `startHighlightAnimation` : le contour et l'étiquette
   * sont calculés en espace écran (`map.project`/`unproject`), ils doivent donc être recalculés à
   * chaque frame pour rester corrects pendant un pan/zoom, pas seulement sur `"move"`. Pas de
   * pulsation ici (contrairement à `startHighlightAnimation`) — voir `REMOTE_SELECTION_OUTLINE_SOURCE_ID`.
   */
  private startRemoteSelectionAnimation(): void {
    if (this.remoteSelectionAnimationFrame !== null) return;
    const tick = () => {
      this.updateRemoteSelectionSources();
      this.remoteSelectionAnimationFrame = requestAnimationFrame(tick);
    };
    this.remoteSelectionAnimationFrame = requestAnimationFrame(tick);
  }

  private stopRemoteSelectionAnimation(): void {
    if (this.remoteSelectionAnimationFrame !== null) cancelAnimationFrame(this.remoteSelectionAnimationFrame);
    this.remoteSelectionAnimationFrame = null;
  }

  /**
   * `HighlightShapesOptions.zoomToFit` : centre la caméra sur la boîte englobante de `shapes` et
   * zoome au maximum tant qu'elle reste entièrement visible (moins `HIGHLIGHT_FIT_PADDING_PX`, pour
   * ne pas coller une forme au bord de l'écran) — délégué entièrement à `Map#fitBounds` de MapLibre,
   * qui calcule lui-même le zoom maximal possible sans faire sortir la boîte englobante de l'écran,
   * en respectant les bornes de zoom déjà configurées sur la carte (`setZoomBounds`). Le pitch
   * courant est préservé nativement par `fitBounds`, mais PAS le bearing : sans `bearing` explicite
   * dans les options, MapLibre le réinitialise silencieusement à 0 (voir `cameraForBounds`) — on
   * précise donc toujours un bearing, soit celui fourni par `rotationDeg` (converti dans la
   * convention MapLibre, voir `setRotation`), soit à défaut le bearing courant, pour ne jamais faire
   * tourner la carte de façon inattendue. No-op si `shapes` est vide (rien à cadrer).
   */
  private zoomToFitShapes(shapes: Shape[], rotationDeg?: number): void {
    if (!this.map || shapes.length === 0) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const shape of shapes) {
      const points =
        shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates;
      for (const point of points) bounds.extend(point);
    }
    const bearing = rotationDeg != null ? -rotationDeg : this.map.getBearing();
    this.map.fitBounds(bounds, { padding: HIGHLIGHT_FIT_PADDING_PX, duration: HIGHLIGHT_FIT_DURATION_MS, bearing });
  }

  /**
   * Anime en boucle le contour et le fond de surbrillance, en phase, sur un cycle de
   * `HIGHLIGHT_PULSE_PERIOD_MS`, tant qu'au moins une forme est surlignée. No-op si déjà en cours.
   *
   * La courbe brute (sinusoïde 0..1) est passée à la puissance `HIGHLIGHT_PULSE_SHARPNESS` (< 1) :
   * ça tasse le pulse près de ses extrêmes et l'étire à mi-course, ce qui le fait "rebondir" (montée/
   * descente franches, repos bref aux bornes) plutôt qu'onduler doucement — plus voyant à volume
   * d'amplitude égal.
   *
   * Le fond (`HIGHLIGHT_FILL_LAYER_ID`) reste un simple `setPaintProperty` par frame. Le contour, lui,
   * recalcule sa géométrie à chaque frame (`updateHighlightOutlineSource`, voir son commentaire pour
   * pourquoi un anneau agrandi plutôt qu'un `line-offset`) — plus coûteux qu'un `setPaintProperty`,
   * mais négligeable en pratique : il n'y a typiquement qu'une poignée de formes surlignées à la fois.
   */
  private startHighlightAnimation(): void {
    if (this.highlightAnimationFrame !== null) return;
    const tick = (time: number) => {
      if (this.highlightAnimationStartTime === null) this.highlightAnimationStartTime = time;
      const elapsed = time - this.highlightAnimationStartTime;
      const phase = (elapsed % HIGHLIGHT_PULSE_PERIOD_MS) / HIGHLIGHT_PULSE_PERIOD_MS;
      const raw = (Math.sin(phase * Math.PI * 2) + 1) / 2; // 0..1
      const pulse = Math.pow(raw, HIGHLIGHT_PULSE_SHARPNESS);
      if (this.map) {
        const outlineWidth =
          HIGHLIGHT_OUTLINE_WIDTH_MIN + pulse * (HIGHLIGHT_OUTLINE_WIDTH_MAX - HIGHLIGHT_OUTLINE_WIDTH_MIN);
        this.map.setPaintProperty(HIGHLIGHT_OUTLINE_LAYER_ID, "line-width", outlineWidth);
        // Même relation que l'ancien `line-offset = outlineWidth / 2` : le bord INTÉRIEUR du trait
        // reste collé au contour de la forme, qui grossit donc uniquement vers l'extérieur.
        this.highlightOutlineMarginPx = outlineWidth / 2;
        const shapes = this.highlightedShapeIds
          .map((id) => this.lastShapes.find((s) => s.id === id))
          .filter((shape): shape is Shape => shape !== undefined);
        this.updateHighlightOutlineSource(shapes);
        this.map.setPaintProperty(
          HIGHLIGHT_FILL_LAYER_ID,
          "fill-opacity",
          HIGHLIGHT_FILL_OPACITY_MIN + pulse * (HIGHLIGHT_FILL_OPACITY_MAX - HIGHLIGHT_FILL_OPACITY_MIN),
        );
      }
      this.highlightAnimationFrame = requestAnimationFrame(tick);
    };
    this.highlightAnimationFrame = requestAnimationFrame(tick);
  }

  private stopHighlightAnimation(): void {
    if (this.highlightAnimationFrame !== null) cancelAnimationFrame(this.highlightAnimationFrame);
    this.highlightAnimationFrame = null;
    this.highlightAnimationStartTime = null;
  }

  /**
   * Fait défiler en boucle le motif de pointillés du tracé d'itinéraire affiché ("marching ants" —
   * voir `ROUTE_DASH_PATTERNS`), à une vitesse d'un cycle du motif toutes les `ROUTE_DASH_CYCLE_MS`.
   * No-op si déjà en cours — démarrée/arrêtée depuis `showRoute` selon que celui-ci reçoit un tracé
   * d'au moins 2 points ou pas.
   *
   * Cycle sur les `ROUTE_DASH_PATTERNS` PRÉCALCULÉS plutôt que d'appeler `buildRouteDasharray`
   * directement avec la distance exacte écoulée (voir le commentaire de `ROUTE_DASH_STEPS`) — et
   * `setPaintProperty` n'est appelé que quand l'étape change réellement (`lastRouteDashStepIndex`),
   * pas à chaque frame de `requestAnimationFrame`, qui ne sert ici qu'à mesurer le temps écoulé.
   */
  private startRouteAnimation(): void {
    if (this.routeAnimationFrame !== null) return;
    const tick = (time: number) => {
      if (this.routeAnimationStartTime === null) this.routeAnimationStartTime = time;
      const elapsed = time - this.routeAnimationStartTime;
      const cycleFraction = (elapsed % ROUTE_DASH_CYCLE_MS) / ROUTE_DASH_CYCLE_MS;
      const stepIndex = Math.floor(cycleFraction * ROUTE_DASH_PATTERNS.length) % ROUTE_DASH_PATTERNS.length;
      if (stepIndex !== this.lastRouteDashStepIndex) {
        this.lastRouteDashStepIndex = stepIndex;
        this.map?.setPaintProperty(ROUTE_LINE_LAYER_ID, "line-dasharray", ROUTE_DASH_PATTERNS[stepIndex]);
      }
      this.routeAnimationFrame = requestAnimationFrame(tick);
    };
    this.routeAnimationFrame = requestAnimationFrame(tick);
  }

  private stopRouteAnimation(): void {
    if (this.routeAnimationFrame !== null) cancelAnimationFrame(this.routeAnimationFrame);
    this.routeAnimationFrame = null;
    this.routeAnimationStartTime = null;
    this.lastRouteDashStepIndex = null;
  }

  /** Voir `"map-view:changed"` (`MapAdapterEvent`) — simple lecture groupée des getters ci-dessus. */
  private getMapViewState(): MapViewState {
    const { min: zoomMin, max: zoomMax } = this.getZoomBounds();
    return {
      center: this.getCenter(),
      zoom: this.getZoom(),
      zoomMin,
      zoomMax,
      rotationDeg: this.getRotation(),
    };
  }

  /** Inclinaison 3D (pitch) de la caméra, en degrés — voir `startEditingMapView` pour son blocage
   * pendant l'édition de la vue par défaut, et `createTokaMap`'s `resetView` qui la remet à 0. */
  setPitch(pitch: number): void {
    this.map?.setPitch(pitch);
  }

  getPitch(): number {
    return this.map?.getPitch() ?? 0;
  }

  /**
   * Décale la caméra de `dx`/`dy` pixels écran (`Map#panBy`, MapLibre) — utilisé par les flèches de
   * déplacement cliquables du mode d'édition de la vue par défaut (voir `ui/MapViewCrosshair`,
   * `createTokaMap.ts`), en plus du glisser natif. Volontairement PAS undoable/tracké (comme le
   * glisser/la molette pendant ce mode) : un simple raccourci pour le même geste de navigation.
   */
  panByScreenPixels(dx: number, dy: number): void {
    this.map?.panBy([dx, dy], { duration: 250 });
  }

  /**
   * Convertit une position écran (pixels relatifs à `#map`, ex. `MapAdapterEvent.screenPosition`) en
   * lng/lat — utilisé par `createTokaMap` pour coller une forme copiée exactement là où le clic droit
   * a ouvert le menu contextuel (voir `"shape:contextmenu"`/`"map:contextmenu"`). Retourne `[0, 0]`
   * si la carte n'est pas encore initialisée (ne devrait pas arriver en pratique : cette méthode n'a
   * de sens qu'une fois `init()` résolu, comme le reste de l'adapter).
   */
  screenPositionToLngLat(screenPosition: { x: number; y: number }): LngLat {
    if (!this.map) return [0, 0];
    const ll = this.map.unproject(screenPosition as Point);
    return [ll.lng, ll.lat];
  }

  /** Voir `MapLibreAdapterOptions.defaultShapeTheme` — simple accesseur, pas de logique propre. */
  getDefaultShapeStyle(): ShapeStyle | undefined {
    return this.defaultShapeTheme;
  }

  /**
   * Change `MapLibreAdapterOptions.defaultShapeTheme` après coup — sans ça, seule la valeur passée
   * à la construction compte, pour toute la durée de vie de l'instance. Ne s'applique qu'aux
   * PROCHAINES formes dessinées (`finalizeDrawing`/`finalizeRectDrawing` la lisent au moment de la
   * création) ; sans effet sur les formes déjà créées.
   *
   * Émet `"shape-defaults:changed"` (`MapAdapterEvent`) SI la valeur change réellement (voir
   * `shapeStylesEqual`) — no-op silencieux sinon, pour qu'un `SetConfigurationCommand` qui ne
   * touche que la vue caméra (`applyConfigurationState` rappelle ce setter avec la même valeur)
   * n'émette jamais cet évènement à tort.
   */
  setDefaultShapeStyle(style: ShapeStyle | undefined): void {
    if (shapeStylesEqual(this.defaultShapeTheme, style)) return;
    this.defaultShapeTheme = style;
    this.emit({
      type: "shape-defaults:changed",
      defaultShapeStyle: this.defaultShapeTheme,
    });
  }

  /** Voir `MapAdapter.getThemes` — simple accesseur, pas de logique propre. */
  getThemes(): ShapeThemes {
    return this.themes;
  }

  /**
   * Voir `MapAdapter.setThemes`. Contrairement à `setDefaultShapeStyle`, redessine les formes déjà
   * créées (`renderShapes(this.lastShapes)`) : c'est tout l'intérêt du lien par `Shape.themeId`,
   * une modification de thème s'applique sans qu'aucune forme ne soit modifiée ni persistée.
   *
   * Émet `"themes:changed"` SI la table change réellement (égalité par valeur, voir
   * `shapeThemesEqual`) — no-op silencieux sinon, pour qu'un appelant qui repousse la même table à
   * chaque rendu ne déclenche ni évènement ni redessin.
   */
  setThemes(themes: ShapeThemes): void {
    if (shapeThemesEqual(this.themes, themes)) return;
    this.themes = themes;
    this.renderShapes(this.lastShapes);
    this.emit({ type: "themes:changed", themes: this.themes });
  }

  /** Voir `MapAdapter.getDefaultThemeId` — simple accesseur, pas de logique propre. */
  getDefaultThemeId(): string | undefined {
    return this.defaultThemeId;
  }

  /**
   * Voir `MapAdapter.setDefaultThemeId`. Aucun redessin ni évènement, contrairement à `setThemes` :
   * seules les PROCHAINES formes dessinées lisent cette valeur (au moment de leur création), les
   * formes existantes ne sont pas concernées.
   */
  setDefaultThemeId(themeId: string | undefined): void {
    this.defaultThemeId = themeId;
  }

  /** Id du calque actuellement le plus bas du style (`undefined` si la carte n'a encore aucun
   * calque) — sert de `beforeId` à `map.addLayer` pour qu'un fond de carte ajouté après coup
   * (`addBackgroundTileLayer`) s'insère toujours SOUS les formes/calques déjà présents, plutôt que
   * de les recouvrir. */
  private getBottomLayerId(): string | undefined {
    return this.map?.getStyle().layers?.[0]?.id;
  }

  /** Retire le fond actuellement peint par la carte PRINCIPALE (`this.primaryPaintedTileLayerName` —
   * PAS forcément `this.tileLayerName`, voir `updateBackgroundMode` : les deux divergent tant que le
   * mode 3-couches est actif, la principale restant alors forcée à `"unset"` réel). Délègue à
   * `backgroundTileLayer.ts` (logique partagée avec `BackgroundMap`). No-op pour `"unset"`. */
  private removeBackgroundTileLayer(): void {
    if (!this.map) return;
    removeBackgroundTileLayer(this.map, this.primaryPaintedTileLayerName, this.paintedOsmLayers);
  }

  /** Affiche `name` sur la carte PRINCIPALE, sous le calque le plus bas déjà présent (`getBottomLayerId`,
   * pour rester sous les formes) — délègue à `backgroundTileLayer.ts`. No-op pour `"unset"`. */
  private addBackgroundTileLayer(name: TileLayerName): void {
    if (!this.map) return;
    const layers = this.buildOsmLayers();
    addBackgroundTileLayer(this.map, name, layers, this.getBottomLayerId());
    this.paintedOsmLayers = layers;
    this.placeBuildingLayer();
  }

  /**
   * En vue inclinée, les bâtiments passent au-dessus des formes plates pour les masquer selon
   * l'angle : MapLibre ne teste la profondeur qu'entre couches 3D, une couche plate peinte après
   * les bâtiments les recouvre toujours.
   */
  private placeBuildingLayer(): void {
    if (!this.map?.getLayer(OSM_BUILDING_3D_LAYER_ID) || !this.map.getLayer(SHAPES_EXTRUSION_LAYER_ID)) return;
    if (this.allowPitch) {
      this.map.moveLayer(OSM_BUILDING_3D_LAYER_ID, EDITING_SHAPE_FILL_LAYER_ID);
      return;
    }
    const backgroundLayerIds = new Set(this.paintedOsmLayers.map((layer) => layer.id));
    const firstShapeLayerId = this.map.getLayersOrder().find((id) => !backgroundLayerIds.has(id));
    this.map.moveLayer(OSM_BUILDING_3D_LAYER_ID, firstShapeLayerId);
  }

  private buildOsmLayers(): LayerSpecification[] {
    return applyOsmOptions(this.backgroundStyleLayers, this.osmOptions);
  }

  /** Repeint sur celle des deux cartes qui peint réellement (voir `updateBackgroundMode`). No-op sous
   * un autre fond, `addBackgroundTileLayer` recalculant les calques au retour sur `"osm"`. */
  private repaintOsmBackground(): void {
    if (this.tileLayerName !== "osm") return;

    if (this.backgroundMap) {
      this.backgroundMap.setOsmLayers(this.buildOsmLayers());
      return;
    }

    if (this.primaryPaintedTileLayerName !== "osm") return;
    this.removeBackgroundTileLayer();
    this.addBackgroundTileLayer("osm");
  }

  /**
   * Reste `void` et non `async`, pour préserver le contrat synchrone de `MapAdapter.setOsmOptions` :
   * c'est `pendingOsmStyle` qui permet à `waitUntilIdle` de s'y raccrocher. Un style injoignable
   * laisse le fond courant en place plutôt que de vider la carte.
   */
  private selectOsmStyle(style: OsmStyleName): void {
    // Incrémentée AVANT la sortie anticipée : revenir au style déjà chargé pendant qu'un autre se
    // télécharge (sombre demandé, puis clair aussitôt — deux cartes enchaînées) doit périmer ce
    // téléchargement, sans quoi le sombre serait peint à son arrivée par-dessus le choix du clair.
    const generation = ++this.osmStyleGeneration;

    if (this.loadedOsmStyle === style) {
      this.pendingOsmStyle = null;
      // Les autres options ont pu changer avec le style, alors que les calques peints sont toujours
      // ceux du style chargé : seul ce repeint les y applique.
      this.repaintOsmBackground();
      return;
    }

    this.pendingOsmStyle = fetchOsmStyleLayers(style)
      .then((layers) => {
        if (generation !== this.osmStyleGeneration) return;
        this.backgroundStyleLayers = layers;
        this.loadedOsmStyle = style;
        this.repaintOsmBackground();
      })
      .catch((error: unknown) => {
        console.warn(`[tokamap] style de fond "${style}" non chargé, le fond courant est conservé`, error);
      })
      .finally(() => {
        if (generation === this.osmStyleGeneration) this.pendingOsmStyle = null;
      });
  }

  /** Voir `TileLayerName`/`MapLibreAdapterOptions.defaultTileLayerName` — simple accesseur, pas de
   * logique propre. */
  getTileLayerName(): TileLayerName {
    return this.tileLayerName;
  }

  /**
   * Change le fond de carte affiché — voir `removeBackgroundTileLayer`/`addBackgroundTileLayer` pour
   * le détail par valeur. Émet `"tile-layer:changed"` (`MapAdapterEvent`) SI la valeur change
   * réellement, même convention que `setDefaultShapeStyle` (no-op silencieux sinon, pour qu'un
   * `SetConfigurationCommand` qui ne touche que la vue caméra/le style par défaut ne déclenche jamais
   * cet évènement à tort).
   */
  setTileLayerName(tileLayerName: TileLayerName): void {
    if (this.tileLayerName === tileLayerName) return;
    if (!this.backgroundMap) {
      // Mode 3-couches inactif : comportement historique inchangé, la principale peint elle-même
      // le fond. Si l'appel ci-dessous à `updateBackgroundMode()` active le mode (SVG déjà présents),
      // il reprendra ce fond fraîchement posé pour le transférer sur `backgroundMap`.
      this.removeBackgroundTileLayer();
      this.tileLayerName = tileLayerName;
      this.addBackgroundTileLayer(tileLayerName);
      this.primaryPaintedTileLayerName = tileLayerName;
    } else {
      // Mode déjà actif : la principale reste "unset" réel, inchangée — voir `updateBackgroundMode`.
      this.tileLayerName = tileLayerName;
    }
    this.updateBackgroundMode();
    this.emit({ type: "tile-layer:changed", tileLayerName: this.tileLayerName });
  }

  /** Telles que renseignées, pas résolues : c'est ce que la configuration persiste. */
  getOsmOptions(): OsmBackgroundOptions | undefined {
    return this.osmOptions;
  }

  /**
   * REMPLACE, jamais de fusion à ce niveau — voir `MapAdapter.setOsmOptions`. Sortie silencieuse si
   * les valeurs EFFECTIVES ne changent pas (`{}` et `{ style: "light" }` désignent le même rendu),
   * même convention que `setTileLayerName`.
   */
  setOsmOptions(osmOptions: OsmBackgroundOptions | undefined): void {
    const previous = resolveOsmOptions(this.osmOptions);
    const next = resolveOsmOptions(osmOptions);
    const changed = (Object.keys(next) as (keyof typeof next)[]).some((key) => previous[key] !== next[key]);
    if (!changed) return;

    this.osmOptions = osmOptions;

    if (previous.style !== next.style) {
      this.selectOsmStyle(next.style);
    } else {
      this.repaintOsmBackground();
    }

    this.emit({ type: "osm-options:changed", osmOptions: this.osmOptions });
  }

  /** Voir `MapLibreAdapterOptions.defaultBackgroundColor` — simple accesseur, pas de logique propre. */
  getBackgroundColor(): string | undefined {
    return this.backgroundColor;
  }

  /**
   * Change la couleur de fond du conteneur DOM de la carte — `style.backgroundColor` posé directement
   * sur `this.rootContainer` (le conteneur passé à `init()`), PAS un fond peint dans le canvas comme
   * `setTileLayerName`. Émet `"background-color:changed"` (`MapAdapterEvent`) SI la valeur change
   * réellement, même convention que `setTileLayerName` (no-op silencieux sinon).
   */
  setBackgroundColor(backgroundColor: string | undefined): void {
    if (this.backgroundColor === backgroundColor) return;
    this.backgroundColor = backgroundColor;
    if (this.rootContainer) this.rootContainer.style.backgroundColor = backgroundColor ?? "";
    this.emit({ type: "background-color:changed", backgroundColor });
  }

  /**
   * Décide si le mode "3 couches" (carte de fond secondaire, voir `BackgroundMap`) doit être actif :
   * seulement si au moins un SVG est affiché (`imageShapeCandidates.length > 0`, recalculé par
   * `syncShapeImages` juste avant chaque appel depuis là) ET `this.tileLayerName !== "unset"`.
   * Appelée après tout changement d'un des deux facteurs (`syncShapeImages`, `setTileLayerName`).
   * Détruit/recrée `backgroundMap` à chaque bascule d'activation (rechargement de tuiles/à-coup
   * visuel accepté, voir le choix explicite fait pour cette fonctionnalité), mais se contente de
   * déléguer à `BackgroundMap.setTileLayerName` si le mode reste actif et que seule la valeur
   * affichée change (ex. `"osm"` → `"satellite"` pendant qu'un SVG reste affiché).
   */
  private updateBackgroundMode(): void {
    if (!this.map || !this.rootContainer) return;
    const shouldBeActive = this.imageShapeCandidates.length > 0 && this.tileLayerName !== "unset";

    if (shouldBeActive && !this.backgroundMap) {
      if (this.primaryPaintedTileLayerName !== "unset") {
        this.removeBackgroundTileLayer();
        this.primaryPaintedTileLayerName = "unset";
      }
      this.backgroundMap = new BackgroundMap(
        this.rootContainer,
        this.tileLayerName,
        this.buildOsmLayers(),
        {
          center: this.getCenter(),
          zoom: this.map.getZoom(),
          bearing: this.map.getBearing(),
          pitch: this.map.getPitch(),
          padding: this.map.getPadding(),
        },
        Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO),
      );
      return;
    }

    if (!shouldBeActive && this.backgroundMap) {
      this.backgroundMap.destroy();
      this.backgroundMap = null;
      if (this.tileLayerName !== "unset") {
        this.addBackgroundTileLayer(this.tileLayerName);
        this.primaryPaintedTileLayerName = this.tileLayerName;
      }
      return;
    }

    if (shouldBeActive && this.backgroundMap) {
      // Les calques d'abord : `setTileLayerName` peint, et doit le faire avec les calques à jour.
      this.backgroundMap.setOsmLayers(this.buildOsmLayers());
      this.backgroundMap.setTileLayerName(this.tileLayerName);
    }
  }

  // --- Géolocalisation (BESOINS.md §15) ---

  /** Voir `TokaMapConfig.geolocation` — simple accesseur, pas de logique propre. */
  getGeolocationEnabled(): boolean {
    return this.geolocationEnabled;
  }

  /**
   * Active/désactive le repérage de la position de l'utilisateur — même convention que
   * `setTileLayerName` (no-op silencieux si la valeur ne change pas réellement, émet
   * `"geolocation:changed"` sinon). Comme `setTileLayerName`, cette méthode d'adapter reste un
   * primitif "brut" (pas d'undo/redo, pas de `SetConfigurationCommand` ici) — c'est `createTokaMap`
   * qui la fait passer par `configurationSaveTracker.run(...)` pour en faire un champ undoable/
   * persisté (voir BESOINS.md §15).
   */
  setGeolocationEnabled(enabled: boolean): void {
    if (this.geolocationEnabled === enabled) return;
    this.geolocationEnabled = enabled;
    // Remis à `null` (plutôt que laissé à sa valeur précédente, éventuellement calculée pendant que
    // la géolocalisation était encore désactivée/sans position connue) : sans ça, si cette ancienne
    // valeur "par coïncidence" égale le résultat du tout premier calcul réel une fois activée,
    // `updateGeolocationVisibility` la jugerait inchangée et ne réémettrait jamais
    // `"geolocation:visibility"` — le bouton "recentrer" resterait alors caché à tort.
    this.geolocationMarkerVisible = null;
    if (enabled) {
      this.startGeolocationTracking();
    } else {
      this.stopGeolocationTracking();
    }
    this.emit({ type: "geolocation:changed", enabled });
  }

  centerOnUserLocation(): void {
    if (!this.map || !this.userPosition) return;
    this.map.easeTo({ center: this.userPosition });
  }

  // --- Inclinaison 3D (pitch) ---

  /** Voir `TokaMapConfig.defaultAllowPitch` — simple accesseur, pas de logique propre. */
  getAllowPitch(): boolean {
    return this.allowPitch;
  }

  /**
   * Autorise/bloque l'inclinaison 3D de la caméra en cours de session — même convention que
   * `setTileLayerName`/`setGeolocationEnabled` (no-op silencieux si la valeur ne change pas
   * réellement, émet `"allow-pitch:changed"` sinon). Comme elles, cette méthode d'adapter reste un
   * primitif "brut" (pas d'undo/redo, pas de `SetConfigurationCommand` ici) — c'est `createTokaMap`
   * qui la fait passer par `configurationSaveTracker.run(...)` pour en faire un champ undoable/
   * persisté.
   *
   * Répète, à l'identique, les deux effets déjà posés une fois pour toutes dans `init()` (voir son
   * commentaire) : `maxPitch` (au niveau du `Transform`, bloque/débloque le pitch pour TOUTE source)
   * et `dragRotate` (le geste natif clic-droit-glisser/Ctrl+clic-gauche-glisser). `isMapRotateTrigger`
   * lit `this.allowPitch` en direct à chaque geste, donc le handler maison (bearing seul) cède ou
   * reprend la main automatiquement sans autre changement nécessaire ici.
   */
  setAllowPitch(allowPitch: boolean): void {
    if (this.allowPitch === allowPitch) return;
    this.allowPitch = allowPitch;
    this.map?.setMaxPitch(allowPitch ? MAX_PITCH_DEG : 0);
    if (allowPitch) {
      this.map?.dragRotate.enable();
    } else {
      this.map?.dragRotate.disable();
    }
    for (const layerId of [SHAPES_EXTRUSION_LAYER_ID, LINE_WALL_LAYER_ID]) {
      if (this.map?.getLayer(layerId)) this.map.setLayoutProperty(layerId, "visibility", allowPitch ? "visible" : "none");
    }
    this.placeBuildingLayer();
    this.emit({ type: "allow-pitch:changed", allowPitch });
  }

  /**
   * Démarre `navigator.geolocation.watchPosition` (suivi continu, pas une position unique — le point
   * affiché doit rester à jour tant que l'utilisateur se déplace) ET, si disponible, un listener
   * boussole (`deviceorientationabsolute`, avec repli sur `deviceorientation` — voir
   * `handleDeviceOrientation`) pour le cap affiché. Absence de l'API, ou permission refusée : simple
   * avertissement console, pas d'erreur bloquante (voir BESOINS.md §15) — le point n'apparaît
   * simplement jamais.
   */
  private startGeolocationTracking(): void {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      console.warn("[MapLibreAdapter] Géolocalisation non disponible dans ce navigateur.");
      return;
    }
    this.geolocationWatchId = navigator.geolocation.watchPosition(
      (position) => this.handleGeolocationPosition(position),
      (error) => console.warn(`[MapLibreAdapter] Erreur de géolocalisation : ${error.message}`),
      { enableHighAccuracy: true },
    );
    if (typeof window !== "undefined" && "DeviceOrientationEvent" in window) {
      this.geolocationOrientationListener = (event) => this.handleDeviceOrientation(event);
      window.addEventListener("deviceorientationabsolute", this.geolocationOrientationListener as EventListener);
      window.addEventListener("deviceorientation", this.geolocationOrientationListener);
    }
  }

  private stopGeolocationTracking(): void {
    if (this.geolocationWatchId != null) {
      navigator.geolocation.clearWatch(this.geolocationWatchId);
      this.geolocationWatchId = null;
    }
    if (this.geolocationOrientationListener) {
      window.removeEventListener("deviceorientationabsolute", this.geolocationOrientationListener as EventListener);
      window.removeEventListener("deviceorientation", this.geolocationOrientationListener);
      this.geolocationOrientationListener = null;
    }
    this.userPosition = null;
    this.userHeading = null;
    this.updateGeolocationSource();
    this.updateGeolocationVisibility();
    this.flushUserLocationWaiters(null);
  }

  private handleGeolocationPosition(position: GeolocationPosition): void {
    this.userPosition = [position.coords.longitude, position.coords.latitude];
    // Repli sur le cap GPS (fiable seulement en mouvement) tant qu'aucun cap boussole n'est encore
    // connu (voir `handleDeviceOrientation`, qui prend le dessus dès qu'il émet — plus fiable à
    // l'arrêt) — jamais l'inverse, un cap boussole déjà connu ne doit pas être remplacé par un cap
    // GPS potentiellement `null`/erratique à faible vitesse.
    if (this.userHeading == null && position.coords.heading != null && !Number.isNaN(position.coords.heading)) {
      this.userHeading = position.coords.heading;
    }
    this.updateGeolocationSource();
    this.updateGeolocationVisibility();
    this.flushUserLocationWaiters(this.userPosition);
  }

  private handleDeviceOrientation(event: DeviceOrientationEvent): void {
    // `webkitCompassHeading` (Safari/iOS uniquement) est déjà un cap boussole absolu (0 = nord).
    // Sur les autres navigateurs, `deviceorientationabsolute` ET `deviceorientation` peuvent tous les
    // deux être émis en parallèle pour le même appareil (notamment Android/Chrome) — seul
    // `event.absolute === true` garantit que `alpha` est calé sur le nord magnétique ; un
    // `deviceorientation` non-absolu peut être relatif à l'orientation de l'appareil au moment du
    // chargement de la page, sans rapport avec le nord réel. Sans ce filtre, les deux évènements
    // s'entrelaçaient et écrasaient `userHeading` l'un après l'autre : le cap affiché clignotait
    // entre une valeur correcte et une valeur arbitraire/dérivante.
    const webkitHeading = (event as DeviceOrientationEvent & { webkitCompassHeading?: number })
      .webkitCompassHeading;
    if (webkitHeading == null && event.absolute !== true) return;
    if (webkitHeading == null && event.alpha == null) return;

    // `alpha` tourne dans le sens ANTI-horaire depuis le nord (d'où `360 - alpha` pour revenir à la
    // convention boussole horaire utilisée partout ailleurs par `MapAdapter`), mais cette formule
    // suppose une orientation d'écran "portrait primaire" — `screen.orientation.angle` compense la
    // rotation effective de l'écran (paysage, portrait inversé...) ; sans cette compensation, le cap
    // affiché se décale de 90°/180°/270° selon la façon dont l'utilisateur tient son téléphone.
    const screenAngle = typeof window !== "undefined" ? (window.screen?.orientation?.angle ?? 0) : 0;
    const heading = webkitHeading ?? (((360 - event.alpha! + screenAngle) % 360) + 360) % 360;
    if (Number.isNaN(heading)) return;
    this.userHeading = heading;
    this.updateGeolocationSource();
  }

  private updateGeolocationSource(): void {
    const source = this.map?.getSource(GEOLOCATION_SOURCE_ID);
    if (!source || source.type !== "geojson") return;
    (source as maplibregl.GeoJSONSource).setData(
      this.userPosition
        ? geolocationToGeoJSON(this.userPosition, this.userHeading)
        : { type: "FeatureCollection", features: [] },
    );
  }

  /**
   * Émet `"geolocation:visibility"` (voir `MapAdapterEvent`) quand le point de position utilisateur
   * entre ou sort de la zone actuellement visible à l'écran — dédupliqué (`geolocationMarkerVisible`),
   * même convention que `setTileLayerName`/`setDefaultShapeStyle`. Appelé à chaque déplacement de
   * caméra (`"move"`) ET à chaque nouvelle position connue (voir `handleGeolocationPosition`).
   *
   * No-op tant que la géolocalisation est désactivée — pas seulement parce que `visible` serait alors
   * toujours `false` (rien à afficher), mais pour éviter deux pièges : (1) émettre `{ visible: false
   * }` juste parce que c'est désactivé donnerait à tort l'impression au bouton "recentrer" (voir
   * `createTokaMap`) qu'il doit s'afficher (il doit rester caché, `"geolocation:changed"` s'en charge
   * déjà) ; (2) laisser `geolocationMarkerVisible` se mettre à jour pendant que c'est désactivé
   * "empoisonnerait" la dédupliction ci-dessous — si le calcul réel, une fois réactivé, tombe par
   * coïncidence sur la même valeur, le tout premier évènement ne serait alors jamais réémis (voir
   * aussi `setGeolocationEnabled`, qui remet `geolocationMarkerVisible` à `null` à l'activation).
   */
  private updateGeolocationVisibility(): void {
    if (!this.geolocationEnabled) return;
    const visible = this.userPosition != null && this.map != null && this.isLngLatOnScreen(this.userPosition);
    if (visible === this.geolocationMarkerVisible) return;
    this.geolocationMarkerVisible = visible;
    this.emit({ type: "geolocation:visibility", visible });
  }

  private isLngLatOnScreen(position: LngLat): boolean {
    if (!this.map) return false;
    const { x, y } = this.map.project(position);
    const canvas = this.map.getCanvas();
    return x >= 0 && x <= canvas.clientWidth && y >= 0 && y <= canvas.clientHeight;
  }

  renderShapes(shapes: Shape[]): void {
    // `Shape.image` est déjà résolu au chargement (par le loader concerné — voir BESOINS.md
    // §8.1/§8.2) : contrairement à une version antérieure de ce fichier, il n'y a plus ici aucune
    // priorité à arbitrer entre plusieurs sources d'image possibles (imageSrc direct/icon/entity),
    // ni de normalisation à faire — `shapes` est utilisé tel quel.
    const previousShapeIds = new Set(this.lastShapes.map((shape) => shape.id));
    const previouslyVisibleShapeIds = new Set(this.lastZoomVisibleShapes.map((shape) => shape.id));
    this.lastShapes = shapes;
    this.contentPointsCache = null;
    if (this.framing) this.applyFramingConstraints();
    // La forme actuellement survolée peut avoir changé de géométrie sans geste de caméra (ex. un
    // undo/redo pendant qu'elle est survolée) — `"move"` (voir `init`) ne suffit pas à couvrir ce cas.
    this.updateHoverToolbarPosition();

    if (this.editingState) {
      const editedShape = shapes.find((s) => s.id === this.editingState!.shapeId);
      if (!editedShape) {
        // La forme en cours d'édition a été supprimée ailleurs (ex. undo) : on nettoie les poignées.
        this.editingState = null;
        this.updateEditVisuals();
        this.setEditingShapeFilter(null);
        this.updateEditingShapeSource([]);
        this.emit({ type: "edit-mode:changed", shapeId: null });
      } else {
        // La forme a changé sous nos pieds pendant qu'on l'édite (ex. undo/redo déclenché sans
        // quitter le mode édition) : on resynchronise poignées et aperçu sur la donnée canonique,
        // sinon l'aperçu affiché reste figé sur l'ancienne position malgré l'annulation.
        this.editingState.points = shapeVertices(editedShape);
        this.updateEditVisuals();
        this.updateEditingShapeSource([editedShape]);
        if (editedShape.image) {
          this.repositionShapeImageDuringEdit(editedShape as Shape & { image: ShapeImage });
        }
      }
    }

    const zoomHiddenIds = this.zoomHiddenShapeIds(shapes);
    this.zoomHiddenShapeKey = [...zoomHiddenIds].join(",");
    this.underZoomShapeIds = this.editMode ? zoomHiddenIds : new Set();
    const visibleShapes = this.editMode ? shapes : shapes.filter((shape) => !zoomHiddenIds.has(shape.id));
    this.lastZoomVisibleShapes = visibleShapes;
    this.startShapesFadeIn(
      visibleShapes
        .map((shape) => shape.id)
        .filter((id) => previousShapeIds.has(id) && !previouslyVisibleShapeIds.has(id)),
    );

    this.syncLayerBands(visibleShapes);

    const source = this.map?.getSource(SHAPES_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(
        this.markUnderZoom(
          shapesToGeoJSON(visibleShapes, this.editMode, this.colors, false, (id) => this.numericFeatureId(id), this.themes, this.hoverPreviewShapeIds),
        ),
      );
    }

    const wallSource = this.map?.getSource(LINE_WALL_SOURCE_ID);
    if (wallSource && wallSource.type === "geojson") {
      (wallSource as maplibregl.GeoJSONSource).setData(
        lineWallsToGeoJSON(visibleShapes, this.editMode, this.colors, (id) => this.numericFeatureId(id), this.themes, this.hoverPreviewShapeIds),
      );
    }

    const circleSource = this.map?.getSource(CIRCLE_SOURCE_ID);
    if (circleSource && circleSource.type === "geojson") {
      (circleSource as maplibregl.GeoJSONSource).setData(
        this.markUnderZoom(
          circleShapesToGeoJSON(visibleShapes, this.editMode, this.colors, (id) => this.numericFeatureId(id), this.themes, this.hoverPreviewShapeIds),
        ),
      );
    }

    this.refreshShapesTextSize();

    const imageShapes = visibleShapes.map((shape) => this.withUnderZoomImageOpacity(shape));
    this.syncShapeImages(imageShapes);
    this.syncRasterShapeImages(imageShapes);

    if (this.highlightedShapeIds.length > 0) this.updateHighlightSource();
    // Rien à refaire ici pour les curseurs/sélections distants : le curseur ne dépend que de sa propre
    // `position`, jamais des formes ; le contour/l'étiquette de sélection distante, eux, se recalculent
    // déjà en continu via `startRemoteSelectionAnimation` tant qu'une sélection distante est active
    // (voir `RemoteCursor.selectedShapeId`).
  }

  /** Voir `MapAdapter.setStartZoom`. */
  setStartZoom(zoom: number): void {
    this.startZoom = zoom;
    this.refreshZoomVisibility();
  }

  /**
   * Vrai si la forme s'affiche au zoom courant. Son seuil (`Shape.minZoomOffset`, ou celui de son
   * thème) se compte à partir du zoom de la vue de départ SUR CET ÉCRAN : le cadre de départ
   * s'adapte à la taille de l'écran, et un même décalage montre donc la même portion du cadre
   * partout. En mode édition, une forme sous son seuil reste affichée, estompée, pour rester
   * sélectionnable.
   */
  private isShapeVisibleAtZoom(shape: Shape, zoom: number, referenceZoom: number): boolean {
    const offset = (shape.themeId ? this.themes[shape.themeId]?.minZoomOffset : undefined) ?? shape.minZoomOffset;
    return !offset || offset <= 0 || zoom >= referenceZoom + offset - MIN_ZOOM_VISIBILITY_EPSILON;
  }

  private zoomHiddenShapeIds(shapes: Shape[]): Set<string> {
    const referenceZoom = this.getStartCamera()?.zoom ?? this.startZoom;
    if (!this.map || referenceZoom == null) return new Set();

    const zoom = this.map.getZoom();
    return new Set(
      shapes.filter((shape) => !this.isShapeVisibleAtZoom(shape, zoom, referenceZoom)).map((shape) => shape.id),
    );
  }

  private refreshZoomVisibility(): void {
    const hiddenKey = [...this.zoomHiddenShapeIds(this.lastShapes)].join(",");
    if (hiddenKey !== this.zoomHiddenShapeKey) this.renderShapes(this.lastShapes);
  }

  private markUnderZoom(collection: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
    if (this.underZoomShapeIds.size === 0) return collection;
    for (const feature of collection.features) {
      if (feature.properties && this.underZoomShapeIds.has(String(feature.properties.id))) {
        feature.properties.underZoom = true;
      }
    }
    return collection;
  }

  private withUnderZoomImageOpacity(shape: Shape): Shape {
    if (!shape.image || !this.underZoomShapeIds.has(shape.id)) return shape;
    return { ...shape, image: { ...shape.image, opacity: (shape.image.opacity ?? 1) * UNDER_ZOOM_OPACITY } };
  }

  // --- Bandes de calques (Shape.layer) : un triplet de layers MapLibre par valeur distincte, pour
  // empiler formes ET images raster dans un ordre unifié (BESOINS.md §4/§8) — voir ShapeLayerBand.

  /**
   * Ajoute/retire les bandes en fonction des valeurs de `Shape.layer` réellement présentes parmi
   * `shapes`. Coût dominé par le scan de `shapes` (déjà fait ailleurs à chaque rendu, négligeable) —
   * `addLayer`/`removeLayer` (coûteux, recompile le style) ne sont appelés que si l'ensemble des
   * valeurs a changé, jamais à chaque frame.
   */
  private syncLayerBands(shapes: Shape[]): void {
    if (!this.map) return;
    const nextValues = new Set<number | null>(shapes.map((s) => s.layer ?? null));

    for (const value of this.shapeLayerBands.keys()) {
      if (!nextValues.has(value)) this.removeLayerBand(value);
    }
    let changed = false;
    for (const value of nextValues) {
      if (!this.shapeLayerBands.has(value)) {
        this.ensureLayerBand(value);
        changed = true;
      }
    }
    if (changed || this.shapeLayerBands.size !== this.currentFillLayerIds.length) {
      this.updateFillLayerIdsCache();
    }
  }

  /** Bandes triées par calque croissant, `null` (sans calque) toujours en dernier — même ordre que
   * `ShapeStore.getAll()`. */
  private sortedLayerBands(): ShapeLayerBand[] {
    return [...this.shapeLayerBands.values()].sort(
      (a, b) => (a.layerValue ?? Number.POSITIVE_INFINITY) - (b.layerValue ?? Number.POSITIVE_INFINITY),
    );
  }

  /** Id du layer directement au-dessus de l'emplacement d'une bande `layerValue` — le PREMIER layer
   * de la bande existante immédiatement supérieure (son halo, ajouté avant son fond, voir
   * `ensureLayerBand`), sinon `SHAPES_EXTRUSION_LAYER_ID`. Sert de `beforeId` à `addLayer` pour
   * insérer une bande/un layer d'image raster exactement à la bonne hauteur : viser le fond de la
   * bande supérieure glissait l'insertion entre son halo et son fond, et une image d'un calque
   * inférieur masquait alors le contour de sélection des formes du calque au-dessus. */
  private beforeIdAboveLayerValue(layerValue: number | null): string {
    const value = layerValue ?? Number.POSITIVE_INFINITY;
    const above = this.sortedLayerBands().find((b) => (b.layerValue ?? Number.POSITIVE_INFINITY) > value);
    return above?.haloLayerId ?? SHAPES_EXTRUSION_LAYER_ID;
  }

  private ensureLayerBand(layerValue: number | null): ShapeLayerBand {
    const existing = this.shapeLayerBands.get(layerValue);
    if (existing) return existing;

    const suffix = layerValue == null ? "none" : String(layerValue);
    const band: ShapeLayerBand = {
      layerValue,
      fillLayerId: `${SHAPES_FILL_LAYER_PREFIX}-${suffix}`,
      outlineLayerId: `${SHAPES_OUTLINE_LAYER_PREFIX}-${suffix}`,
      outlineDashedLayerId: `${SHAPES_OUTLINE_DASHED_LAYER_PREFIX}-${suffix}`,
      outlineDefaultLayerId: `${SHAPES_OUTLINE_DEFAULT_LAYER_PREFIX}-${suffix}`,
      hoverOutlineLayerId: `${SHAPES_HOVER_OUTLINE_LAYER_PREFIX}-${suffix}`,
      circleLayerId: `${SHAPES_CIRCLE_LAYER_PREFIX}-${suffix}`,
      lineHitLayerId: `${SHAPES_LINE_HIT_LAYER_PREFIX}-${suffix}`,
      haloLayerId: `${SHAPES_HALO_LAYER_PREFIX}-${suffix}`,
      circleHaloLayerId: `${SHAPES_CIRCLE_HALO_LAYER_PREFIX}-${suffix}`,
    };
    const valueFilter = bandLayerFilter(layerValue);
    const beforeId = this.beforeIdAboveLayerValue(layerValue);

    // Ordre d'insertion important : les `addLayer(..., beforeId)` successifs (même `beforeId`)
    // empilent halo (voir plus bas) < fill (fond) < outline (couleur réelle) < outline-default
    // (secours pointillé) < cercle natif au sein de la bande, tout en la plaçant entièrement sous
    // `beforeId` — voir le commentaire de `beforeIdAboveLayerValue`. Les 4 layers polygone (hors
    // halo) sont rendus INVISIBLES pour un cercle (`fill-opacity`/`line-opacity` à 0 via
    // `IS_NOT_CIRCLE_FILTER`) — son rendu visuel vient désormais du dernier layer, `circleLayerId`
    // (voir `CIRCLE_SOURCE_ID`) — mais restent sinon inchangés : toujours présents/interrogeables
    // via `queryRenderedFeatures` (picking/survol/sélection d'un cercle continuent de passer par
    // eux).
    //
    // Halo additif d'une forme "selected" (sélection multiple) OU "focused" (clic simple, voir
    // `SELECTED_SHAPE_HALO_MARGIN_PX`) : ajouté AVANT le fond (`band.fillLayerId` juste en dessous),
    // donc peint SOUS lui — ceinture ET bretelles avec `HALO_LINE_OFFSET_EXPRESSION` (qui pousse déjà
    // la bande entièrement hors de la forme) : rester sous le fond masque tout résidu qui déborderait
    // malgré tout côté intérieur. Invisible pour un cercle, comme les autres layers polygone de la
    // bande : son propre halo est `band.circleHaloLayerId` plus bas.
    this.map!.addLayer(
      {
        id: band.haloLayerId,
        type: "line",
        source: SHAPES_SOURCE_ID,
        filter: valueFilter,
        paint: {
          "line-color": this.colors.accent,
          "line-width": HALO_LINE_WIDTH_EXPRESSION,
          "line-offset": HALO_LINE_OFFSET_EXPRESSION,
          "line-opacity": [
            "case",
            [
              "all",
              ["!", IS_CIRCLE_FILTER],
              ["any", ["boolean", ["feature-state", "focused"], false], ["boolean", ["feature-state", "selected"], false]],
            ],
            1,
            0,
          ],
        },
      },
      beforeId,
    );
    this.map!.addLayer(
      {
        id: band.fillLayerId,
        type: "fill",
        source: SHAPES_SOURCE_ID,
        filter: ["all", valueFilter, NOT_LINE_FILTER] as maplibregl.FilterSpecification,
        // Le fond de survol (voir `editMode`) n'est jamais activé si le mode édition est
        // désactivé : rien ne bascule alors jamais l'état de feature "hovered" à true.
        paint: {
          "fill-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            ["get", "hoverBgColor"],
            ["get", "bgColor"],
          ],
          "fill-opacity": ["case", IS_CIRCLE_FILTER, 0, SHAPE_OPACITY_EXPRESSION],
        },
      },
      beforeId,
    );
    // Layer de picking invisible pour `shapeKind: "line"` (voir `ShapeLayerBand.lineHitLayerId`) —
    // son rendu visuel passe par `outlineLayerId`/`outlineDefaultLayerId` juste en dessous, ce
    // layer-ci n'existe que pour élargir sa zone cliquable (voir `LINE_HIT_WIDTH_PX`).
    this.map!.addLayer(
      {
        id: band.lineHitLayerId,
        type: "line",
        source: SHAPES_SOURCE_ID,
        filter: ["all", valueFilter, IS_LINE_FILTER] as maplibregl.FilterSpecification,
        paint: { "line-width": LINE_HIT_WIDTH_PX, "line-opacity": 0 },
      },
      beforeId,
    );
    this.map!.addLayer(
      {
        id: band.outlineLayerId,
        type: "line",
        source: SHAPES_SOURCE_ID,
        filter: ["all", valueFilter, NOT_DEFAULT_BORDER_FILTER, NOT_BORDER_DASHED_FILTER] as maplibregl.FilterSpecification,
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            ["get", "hoverBorderColor"],
            ["get", "borderColor"],
          ],
          "line-width": BORDER_WIDTH_EXPRESSION,
          "line-opacity": ["case", IS_CIRCLE_FILTER, 0, SHAPE_OPACITY_EXPRESSION],
        },
      },
      beforeId,
    );
    // Variante pointillée de `outlineLayerId` (voir `ShapeStyle.borderDashed`) — même paint à part
    // `line-dasharray`, fixe (`line-dasharray` non data-driven, voir `outlineDefaultLayerId`
    // ci-dessous pour la même contrainte).
    this.map!.addLayer(
      {
        id: band.outlineDashedLayerId,
        type: "line",
        source: SHAPES_SOURCE_ID,
        filter: ["all", valueFilter, NOT_DEFAULT_BORDER_FILTER, BORDER_DASHED_FILTER] as maplibregl.FilterSpecification,
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            ["get", "hoverBorderColor"],
            ["get", "borderColor"],
          ],
          "line-width": BORDER_WIDTH_EXPRESSION,
          "line-dasharray": EDIT_MODE_DEFAULT_BORDER_DASHARRAY,
          "line-opacity": ["case", IS_CIRCLE_FILTER, 0, SHAPE_OPACITY_EXPRESSION],
        },
      },
      beforeId,
    );
    this.map!.addLayer(
      {
        id: band.outlineDefaultLayerId,
        type: "line",
        source: SHAPES_SOURCE_ID,
        filter: ["all", valueFilter, DEFAULT_BORDER_FILTER] as maplibregl.FilterSpecification,
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            ["get", "hoverBorderColor"],
            ["get", "borderColor"],
          ],
          "line-width": BORDER_WIDTH_EXPRESSION,
          "line-dasharray": EDIT_MODE_DEFAULT_BORDER_DASHARRAY,
          "line-opacity": ["case", IS_CIRCLE_FILTER, 0, SHAPE_OPACITY_EXPRESSION],
        },
      },
      beforeId,
    );
    // Contour de survol dédié, toujours au-dessus des deux outlines ci-dessus : couleur/largeur/
    // dasharray fixes (voir SHAPE_HOVER_OUTLINE_WIDTH), seule `line-opacity` bascule sur le
    // feature-state "hovered" — reste donc visible même quand la forme garde sa propre bordure
    // inchangée au survol (voir shapesToGeoJSON), ET même quand elle est "focused"/"selected" (son
    // halo additif, voir `band.haloLayerId`, ne remplace plus ce contour de survol — les deux se
    // superposent sans problème, contrairement à l'ancien comportement où "focused" remplaçait la
    // bordure elle-même). Sans objet pour un cercle (`line-dasharray` non data-driven, voir
    // `resolveShapeVisualProps`) : toujours invisible pour lui. Également invisible pour une forme
    // verrouillée (`Shape.locked`, propriété `locked` — voir `shapesToGeoJSON`) : ce contour est
    // TOUJOURS notre indication générique fixe, jamais personnalisable via `hoverStyle`
    // (contrairement au fond/bordure propres de la forme, voir `resolveShapeVisualProps`), donc
    // entièrement supprimé plutôt qu'adapté pour elle.
    this.map!.addLayer(
      {
        id: band.hoverOutlineLayerId,
        type: "line",
        source: SHAPES_SOURCE_ID,
        filter: valueFilter,
        paint: {
          "line-color": this.colors.accent,
          "line-width": SHAPE_HOVER_OUTLINE_WIDTH,
          "line-dasharray": EDIT_MODE_DEFAULT_BORDER_DASHARRAY,
          // `this.editMode` figé à la construction (jamais un `["case", ...]` dynamique) : ce
          // contour est une aide à l'ÉDITION, jamais affiché en visualisation seule — même si
          // `handleShapeMouseMove` y bascule désormais aussi `feature-state "hovered"` à `true`
          // (voir son commentaire dans `init()`, nécessaire pour `hoverText`/`hoverStyle` explicites).
          "line-opacity": this.editMode
            ? [
                "case",
                ["any", IS_CIRCLE_FILTER, ["boolean", ["get", "locked"], false]],
                0,
                ["case", ["boolean", ["feature-state", "hovered"], false], 1, 0],
              ]
            : 0,
        },
      },
      beforeId,
    );
    // Halo additif d'un cercle "selected" (sélection multiple) OU "focused" (clic simple) — même
    // principe que `band.haloLayerId` ci-dessus, mais un cercle n'a pas de "trait large sous le
    // fond" possible (le fond ET la bordure sont un seul layer `circle` natif) — à la place, un
    // second cercle légèrement plus grand, peint AVANT (donc SOUS) le cercle réel juste en
    // dessous. Son fond reste transparent et seul son trait est peint : un disque plein transparaissait
    // à travers un cercle au fond translucide. `circle-stroke-width` se place ENTIÈREMENT en dehors
    // de `circle-radius` : rayon réel + `actualBorderWidthAt`, puis la marge en trait.
    this.map!.addLayer(
      {
        id: band.circleHaloLayerId,
        type: "circle",
        source: CIRCLE_SOURCE_ID,
        filter: valueFilter,
        paint: {
          "circle-radius": [
            "interpolate",
            ["exponential", 2],
            ["zoom"],
            0,
            ["+", ["get", "radiusPxAtZ0"], actualBorderWidthAt("Z0")],
            CIRCLE_RADIUS_MAX_ZOOM,
            ["+", ["get", "radiusPxAtZMax"], actualBorderWidthAt("ZMax")],
          ],
          "circle-opacity": 0,
          "circle-stroke-color": this.colors.accent,
          "circle-stroke-width": SELECTED_SHAPE_HALO_MARGIN_PX,
          "circle-stroke-opacity": [
            "case",
            ["any", ["boolean", ["feature-state", "focused"], false], ["boolean", ["feature-state", "selected"], false]],
            1,
            0,
          ],
        },
      },
      beforeId,
    );
    // Layer `circle` natif MapLibre (GPU, parfaitement rond à tout zoom) : rend le fond ET la
    // bordure d'un cercle en un seul layer (`circle-color`/`circle-stroke-*`, voir
    // `circleShapesToGeoJSON`) — même chaîne `hovered` que `outlineLayerId` ci-dessus pour
    // `circle-stroke-color`/`-width` (le halo additif "selected"/"focused" est désormais porté par
    // `band.circleHaloLayerId` ci-dessus, pas par ce layer), pas de `circle-stroke-dasharray`
    // (inexistant dans le spec MapLibre) : `resolveShapeVisualProps(..., forCircleLayer: true)`
    // compense en donnant à `hoverBorderColor`/`hoverBorderWidth` les mêmes valeurs fixes que le
    // contour de survol dédié ci-dessus, pour un survol toujours visible malgré l'absence de
    // pointillés.
    this.map!.addLayer(
      {
        id: band.circleLayerId,
        type: "circle",
        source: CIRCLE_SOURCE_ID,
        filter: valueFilter,
        paint: {
          "circle-radius": [
            "interpolate",
            ["exponential", 2],
            ["zoom"],
            0,
            ["get", "radiusPxAtZ0"],
            CIRCLE_RADIUS_MAX_ZOOM,
            ["get", "radiusPxAtZMax"],
          ],
          "circle-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            ["get", "hoverBgColor"],
            ["get", "bgColor"],
          ],
          "circle-stroke-color": [
            "case",
            ["boolean", ["feature-state", "hovered"], false],
            ["get", "hoverBorderColor"],
            ["get", "borderColor"],
          ],
          "circle-stroke-width": BORDER_WIDTH_EXPRESSION,
          "circle-opacity": SHAPE_OPACITY_EXPRESSION,
          "circle-stroke-opacity": SHAPE_OPACITY_EXPRESSION,
        },
      },
      beforeId,
    );

    this.shapeLayerBands.set(layerValue, band);
    // Réapplique l'exclusion d'édition en cours (voir setEditingShapeFilter) à cette bande neuve —
    // sinon une forme en cours d'édition qui migre vers une bande qui vient d'apparaître (rare,
    // ex. sélection multiple à cheval sur deux calques) réapparaîtrait dans la source principale.
    if (this.currentEditingFilterIds) this.applyEditingFilterToBand(band, this.currentEditingFilterIds);
    return band;
  }

  private removeLayerBand(layerValue: number | null): void {
    const band = this.shapeLayerBands.get(layerValue);
    if (!band || !this.map) return;
    this.map.removeLayer(band.haloLayerId);
    this.map.removeLayer(band.fillLayerId);
    this.map.removeLayer(band.outlineLayerId);
    this.map.removeLayer(band.outlineDashedLayerId);
    this.map.removeLayer(band.outlineDefaultLayerId);
    this.map.removeLayer(band.hoverOutlineLayerId);
    this.map.removeLayer(band.circleHaloLayerId);
    this.map.removeLayer(band.circleLayerId);
    this.map.removeLayer(band.lineHitLayerId);
    this.shapeLayerBands.delete(layerValue);
  }

  /**
   * `handleShapeClick`/`handleShapeContextMenu`/`handleShapeMouseMove` (voir plus bas) sont
   * enregistrés une seule fois, de façon générique (pas via le filtrage par layer de `map.on`), et
   * interrogent eux-mêmes `queryRenderedFeatures` avec la liste courante de `fillLayerId` à chaque
   * appel — ce cache est donc juste mis à jour ici, sans ré-enregistrer quoi que ce soit.
   *
   * Pourquoi pas `map.on(event, fillLayerIds, handler)` (le filtrage par layer natif de MapLibre) :
   * ça obligerait à ré-enregistrer (`off` puis `on`) à chaque ajout/retrait de bande, et le nouvel
   * enregistrement se retrouverait alors placé APRÈS `handleDrawingClick`/`handleRectDrawingClick`
   * (déjà enregistrés, eux, une fois pour toutes à `init()`) dans l'ordre de dispatch interne de
   * MapLibre — qui suit l'ordre d'enregistrement, y compris entre écouteurs génériques et filtrés
   * par layer. Un clic qui à la fois clôture un dessin ET tombe sur une forme existante (ex. clic de
   * clôture d'un nouveau polygone juste au-dessus d'une forme déjà là) serait alors géré par
   * `handleDrawingClick` (qui vide `drawingState`) AVANT `handleShapeClick`, cassant le garde-fou
   * `if (this.drawingState ...) return` de ce dernier — une régression réellement observée
   * (sélection intempestive de la forme sous-jacente, faisant apparaître la Sidebar en plein dessin,
   * décalant `#map` sur la page). Rester générique, enregistré une fois au bon endroit, évite tout
   * ça : l'ordre relatif ne bouge jamais.
   */
  private updateFillLayerIdsCache(): void {
    // `lineHitLayerId` en plus de `fillLayerId` : une forme `shapeKind: "line"` (rendue en
    // `LineString`, jamais captée par un layer `fill`) n'est cliquable/survolable que via son propre
    // layer de picking dédié (voir `ShapeLayerBand.lineHitLayerId`).
    this.currentFillLayerIds = this.sortedLayerBands().flatMap((b) => [b.fillLayerId, b.lineHitLayerId]);
  }

  /** Ids de layers de fond de bande actuellement montés — exposé pour les tests e2e (picking non
   * synchrone avec `setData()`, voir CLAUDE.md) : une forme fraîchement dessinée/éditée n'est
   * "queryable" via `queryRenderedFeatures` qu'une fois sa bande créée. */
  getShapeFillLayerIdsForTests(): string[] {
    return this.currentFillLayerIds;
  }

  /** Ids des layers de contour de survol dédiés (voir `SHAPES_HOVER_OUTLINE_LAYER_PREFIX`) —
   * exposé pour les tests e2e (vérifier qu'il devient visible au survol via `queryRenderedFeatures`,
   * voir edit-mode.spec.ts). Pas de cache dédié (contrairement à `currentFillLayerIds`) : lu
   * uniquement par les tests, jamais sur le chemin chaud du picking. */
  getShapeHoverOutlineLayerIdsForTests(): string[] {
    return this.sortedLayerBands().map((band) => band.hoverOutlineLayerId);
  }

  /** Id du layer MapLibre de l'image raster d'une forme, si montée — `null` sinon (SVG, pas encore
   * montée, hors viewport...). Exposé pour les tests e2e (vérifier l'empilement via
   * `map.getLayersOrder()`, voir layer-order.spec.ts). */
  getRasterImageLayerIdForTests(shapeId: string): string | null {
    return this.rasterImageLayers.get(shapeId)?.layerId ?? null;
  }

  /** Id du layer de fond de la bande d'une valeur de `Shape.layer` donnée, si elle existe
   * actuellement — `null` sinon. Exposé pour les tests e2e (vérifier l'empilement via
   * `map.getLayersOrder()`, voir layer-order.spec.ts). */
  getShapeFillLayerIdForLayerValueForTests(layerValue: number | null): string | null {
    return this.shapeLayerBands.get(layerValue)?.fillLayerId ?? null;
  }

  /** Id numérique stable utilisé comme `id` de Feature GeoJSON (donc comme clé de feature-state,
   * voir `numericFeatureId`) pour un `Shape.id` donné — exposé pour les tests e2e, qui n'ont sinon
   * aucun moyen d'interroger `map.getFeatureState()`/`setFeatureState()` pour une forme précise. */
  getNumericFeatureIdForTests(shapeId: string): number {
    return this.numericFeatureId(shapeId);
  }

  on(listener: MapAdapterEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: MapAdapterEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  // --- DrawAdapter (BESOINS.md §3, §5) ---

  /**
   * Referme une éventuelle session de dessin déjà en cours (rectangle, polygone, cercle OU image)
   * avant qu'elle soit remplacée/interrompue — voir `startDrawingPolygon`/`startDrawingRectangle`/
   * `startDrawingCircle` (redémarrage, ou bascule de l'une vers l'autre) et `cancelDrawing`. Émet
   * `"drawing:ended"` INCONDITIONNELLEMENT (voir `MapAdapterEvent`), et `"drawing:tutorial-ended"`
   * seulement si le tutoriel de cette session n'était pas déjà désactivé. Ces états ne sont jamais
   * actifs en même temps (chaque `startDrawing*` remet les autres à `null`), donc au plus une paire
   * émettra.
   */
  private endActiveDrawingSession(): void {
    if (this.drawingState) {
      if (!this.drawingState.skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
      this.emit({ type: "drawing:ended" });
    } else if (this.rectDrawingState) {
      if (!this.rectDrawingState.skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
      this.emit({ type: "drawing:ended" });
    } else if (this.circleDrawingState) {
      if (!this.circleDrawingState.skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
      this.emit({ type: "drawing:ended" });
    } else if (this.lineDrawingState) {
      if (!this.lineDrawingState.skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
      this.emit({ type: "drawing:ended" });
    } else if (this.imageDrawingState) {
      if (!this.imageDrawingState.skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
      this.emit({ type: "drawing:ended" });
      this.removeImageDrawingDraftPreview();
    }
  }

  startDrawingPolygon(options?: StartDrawingOptions): void {
    this.endActiveDrawingSession();
    this.forceCancelEditingMapView();
    const skipTutorial = options?.skipTutorial ?? false;
    this.rectDrawingState = null;
    this.imageDrawingState = null;
    this.circleDrawingState = null;
    this.lineDrawingState = null;
    this.extendingLineState = null;
    this.drawingState = {
      points: [],
      skipSave: options?.skipSave ?? false,
      skipHistory: options?.skipHistory ?? false,
      skipConfirm: options?.skipConfirm ?? false,
      skipTutorial,
      closeHoverActive: false,
    };
    this.updateDraftSource();
    if (this.map) this.map.getCanvas().style.cursor = "crosshair";
    this.emit({ type: "drawing:started", shapeKind: "polygon" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-step", shapeKind: "polygon", step: 1 });
  }

  startDrawingRectangle(options?: StartDrawingOptions): void {
    this.endActiveDrawingSession();
    this.forceCancelEditingMapView();
    const skipTutorial = options?.skipTutorial ?? false;
    this.drawingState = null;
    this.imageDrawingState = null;
    this.circleDrawingState = null;
    this.lineDrawingState = null;
    this.extendingLineState = null;
    this.rectDrawingState = {
      anchorPx: null,
      skipSave: options?.skipSave ?? false,
      skipHistory: options?.skipHistory ?? false,
      skipConfirm: options?.skipConfirm ?? false,
      skipTutorial,
    };
    this.updateDraftSource();
    if (this.map) this.map.getCanvas().style.cursor = "crosshair";
    this.emit({ type: "drawing:started", shapeKind: "rectangle" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-step", shapeKind: "rectangle", step: 1 });
  }

  /**
   * Démarre le dessin d'un cercle (2 clics : centre, puis point du bord — voir
   * `handleCircleDrawingClick`/`handleCircleDrawingMove`). Même geste que `startDrawingRectangle`,
   * mais le rayon (distance écran centre→curseur) détermine un polygone régulier (voir
   * `circlePointsPx`) plutôt que 4 coins.
   */
  startDrawingCircle(options?: StartDrawingOptions): void {
    this.endActiveDrawingSession();
    this.forceCancelEditingMapView();
    const skipTutorial = options?.skipTutorial ?? false;
    this.drawingState = null;
    this.rectDrawingState = null;
    this.imageDrawingState = null;
    this.lineDrawingState = null;
    this.extendingLineState = null;
    this.circleDrawingState = {
      anchorPx: null,
      skipSave: options?.skipSave ?? false,
      skipHistory: options?.skipHistory ?? false,
      skipConfirm: options?.skipConfirm ?? false,
      skipTutorial,
    };
    this.updateDraftSource();
    if (this.map) this.map.getCanvas().style.cursor = "crosshair";
    this.emit({ type: "drawing:started", shapeKind: "circle" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-step", shapeKind: "circle", step: 1 });
  }

  /**
   * Démarre le dessin d'une ligne (un clic par sommet, comme `startDrawingPolygon`) — mais SANS
   * fermeture en anneau : `finalizeLineDrawing` garde `coordinates` ouverte. Se termine par un clic
   * près du DERNIER sommet posé (`isNearLastPoint`, `MIN_POINTS_TO_CLOSE_LINE`), pas le premier —
   * une ligne n'a rien à "fermer" (voir `handleLineDrawingClick`).
   */
  startDrawingLine(options?: StartDrawingOptions): void {
    this.endActiveDrawingSession();
    this.forceCancelEditingMapView();
    const skipTutorial = options?.skipTutorial ?? false;
    this.rectDrawingState = null;
    this.imageDrawingState = null;
    this.circleDrawingState = null;
    this.lineDrawingState = {
      points: [],
      skipSave: options?.skipSave ?? false,
      skipHistory: options?.skipHistory ?? false,
      skipConfirm: options?.skipConfirm ?? false,
      skipTutorial,
      closeHoverActive: false,
    };
    this.updateDraftSource();
    if (this.map) this.map.getCanvas().style.cursor = "crosshair";
    this.emit({ type: "drawing:started", shapeKind: "line" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-step", shapeKind: "line", step: 1 });
  }

  /**
   * Démarre le dessin d'une image (2 clics : 1er coin, puis coin opposé — voir
   * `handleImageDrawingClick`/`handleImageDrawingMove`), sous contrainte du ratio `width`/`height`
   * (déjà résolus à cet appel, voir `DrawAdapter.startDrawingImage`). Même geste que
   * `startDrawingRectangle`, mais avec son propre `shapeKind: "image"` pour
   * `"drawing:started"`/`"drawing:tutorial-step"` (texte dédié, voir `ui/DrawTutorial`) — et un
   * aperçu live de l'image réelle pendant le drag (voir `handleImageDrawingMove`), pas seulement le
   * rectangle en pointillés du dessin de rectangle.
   */
  startDrawingImage(
    src: string,
    options: StartDrawingImageOptions & { width: number; height: number },
  ): void {
    this.endActiveDrawingSession();
    this.forceCancelEditingMapView();
    const skipTutorial = options.skipTutorial ?? false;
    this.drawingState = null;
    this.rectDrawingState = null;
    this.circleDrawingState = null;
    this.lineDrawingState = null;
    this.extendingLineState = null;
    this.imageDrawingState = {
      anchorPx: null,
      src,
      ratio: options.width / options.height,
      width: options.width,
      height: options.height,
      scale: options.scale,
      opacity: options.opacity,
      rotation: options.rotation,
      svg: options.svg,
      skipSave: options.skipSave ?? false,
      skipHistory: options.skipHistory ?? false,
      skipConfirm: options.skipConfirm ?? false,
      skipTutorial,
    };
    this.updateDraftSource();
    if (this.map) this.map.getCanvas().style.cursor = "crosshair";
    this.emit({ type: "drawing:started", shapeKind: "image" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-step", shapeKind: "image", step: 1 });
  }

  cancelDrawing(): void {
    this.endActiveDrawingSession();
    this.drawingState = null;
    this.rectDrawingState = null;
    this.imageDrawingState = null;
    this.circleDrawingState = null;
    this.lineDrawingState = null;
    this.extendingLineState = null;
    this.updateDraftSource();
    this.updateSnapGuidesSource(null);
    this.removeImageDrawingDraftPreview();
    if (this.map) this.map.getCanvas().style.cursor = "";
  }

  // --- Édition interactive de la vue par défaut (centre/zoom/rotation) ---

  /**
   * Démarre le mode d'édition interactive de la vue par défaut de la carte — `createTokaMap`
   * affiche alors une croix au centre de l'écran (voir `ui/MapViewCrosshair`) et les boutons
   * Enregistrer/(Annuler, voir `options.allowCancel`) (voir `ui/MapViewEditControls`), pendant que
   * l'utilisateur déplace/zoome/fait pivoter librement la carte avec les gestes natifs habituels
   * (aucun geste intercepté ici, contrairement au dessin) — "Enregistrer" (voir
   * `TokaMapInstance.confirmMapViewEdit`) lit alors simplement `getCenter()`/`getZoom()`/
   * `getRotation()` courants. Interrompt une éventuelle session de dessin en cours (symétrique de
   * `startDrawingPolygon`/`startDrawingRectangle`, qui interrompent ce mode-ci).
   *
   * Le pitch (inclinaison 3D) est bloqué à 0 pour toute la durée du mode (`setMinPitch`/
   * `setMaxPitch(0)`, restauré à la sortie, voir `restorePitchBounds`) — la rotation (bearing)
   * n'est, elle, pas affectée : MapLibre n'a pas de bornes équivalentes pour le bearing, et le
   * clamp de pitch s'applique au niveau du `Transform` lui-même (donc à toute source de pitch :
   * clic-droit-glisser, tactile, clavier, appel programmatique), pas seulement au geste de la
   * souris — pas besoin de désactiver `dragRotate`/`touchPitch`/`keyboard` séparément.
   */
  startEditingMapView(options?: StartEditingMapViewOptions): void {
    this.endActiveDrawingSession();
    const pitchBounds = { min: this.map?.getMinPitch() ?? 0, max: this.map?.getMaxPitch() ?? 60 };
    this.map?.setMaxPitch(0);
    this.map?.setMinPitch(0);
    const allowCancel = options?.allowCancel ?? true;
    this.mapViewEditState = {
      before: { center: this.getCenter(), zoom: this.getZoom(), rotationDeg: this.getRotation() },
      pitchBounds,
      allowCancel,
    };
    this.emit({ type: "map-view:edit-started", allowCancel });
  }

  private restorePitchBounds(pitchBounds: { min: number; max: number }): void {
    this.map?.setMaxPitch(pitchBounds.max);
    this.map?.setMinPitch(pitchBounds.min);
  }

  /**
   * "Annuler" : revient exactement à la vue capturée au `startEditingMapView()` (avant que
   * l'utilisateur ne déplace/zoome/fasse pivoter quoi que ce soit), puis referme le mode. Sans
   * effet si le mode n'est pas actif OU si `startEditingMapView({ allowCancel: false })` a
   * désactivé l'annulation pour cette session (voir `forceCancelEditingMapView` pour le chemin
   * interne qui, lui, l'ignore — utilisé quand un dessin interrompt ce mode).
   */
  cancelEditingMapView(): void {
    if (!this.mapViewEditState || !this.mapViewEditState.allowCancel) return;
    this.forceCancelEditingMapView();
  }

  /**
   * Referme le mode en revenant à la vue capturée à l'entrée, SANS tenir compte de
   * `allowCancel` — utilisé uniquement par `startDrawingPolygon`/`startDrawingRectangle` quand ce
   * mode est interrompu par le démarrage d'un dessin : ce n'est pas l'utilisateur qui "annule"
   * explicitement via le bouton/Échap (seuls chemins que `allowCancel: false` doit bloquer), c'est
   * un changement de mode déclenché par le consommateur.
   */
  private forceCancelEditingMapView(): void {
    if (!this.mapViewEditState) return;
    const { before, pitchBounds } = this.mapViewEditState;
    this.setCenter(before.center);
    this.setZoom(before.zoom);
    this.setRotation(before.rotationDeg);
    this.restorePitchBounds(pitchBounds);
    this.mapViewEditState = null;
    this.emit({ type: "map-view:edit-ended" });
  }

  /**
   * "Enregistrer" : referme le mode SANS toucher à la caméra (déjà exactement où l'utilisateur la
   * veut) — `createTokaMap` a la responsabilité d'avoir déjà confirmé la nouvelle vue (via
   * `TokaMapInstance.confirmMapViewEdit`/`setMapView`, undoable) avant d'appeler cette méthode.
   * Sans effet si le mode n'est pas actif.
   */
  endEditingMapView(): void {
    if (!this.mapViewEditState) return;
    this.restorePitchBounds(this.mapViewEditState.pitchBounds);
    this.mapViewEditState = null;
    this.emit({ type: "map-view:edit-ended" });
  }

  enableEditMode(shapeId: string, options?: DrawTriggerOptions): void {
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (!shape) return;
    // Le dernier point du ring == le premier (fermeture) : on ne garde que les sommets uniques
    // (voir `shapeVertices` — un cercle n'a pas ce point de clôture, ses 2 points sont déjà uniques).
    this.editingState = {
      shapeId,
      points: shapeVertices(shape),
      skipSave: options?.skipSave ?? false,
      skipHistory: options?.skipHistory ?? false,
      skipConfirm: options?.skipConfirm ?? false,
    };
    // Repli défensif si on passait d'une forme éditée à une autre sans `disableEditMode()`
    // intermédiaire (voir son commentaire ci-dessous) — un sommet fantôme de l'ancienne forme n'a
    // aucun sens sur la nouvelle.
    this.addVertexHoverState = null;
    this.updateAddVertexHoverSource();
    this.updateEditVisuals();
    // La forme éditée passe dans sa propre source légère : la source principale (~1190 formes)
    // ne sera plus retouchée par un setData() tant que le drag est en cours (voir updateShapesPreview).
    this.setEditingShapeFilter([shapeId]);
    this.updateEditingShapeSource([shape]);
    this.emit({ type: "edit-mode:changed", shapeId });
  }

  disableEditMode(_shapeId: string): void {
    const wasEditing = !!this.editingState;
    this.editingState = null;
    this.extendingLineState = null;
    this.draggingVertexIndex = null;
    this.draggingVertexOriginalPoints = null;
    this.vertexResizingState = null;
    this.rotatingState = null;
    this.movingState = null;
    this.addVertexHoverState = null;
    this.updateAddVertexHoverSource();
    this.updateEditVisuals();
    this.updateDraftSource();
    this.setEditingShapeFilter(null);
    this.updateEditingShapeSource([]);
    this.updateSnapGuidesSource(null);
    if (wasEditing) this.emit({ type: "edit-mode:changed", shapeId: null });
  }

  /** Dans cette implémentation, la poignée de rotation fait partie du mode édition. */
  enableRotateHandle(shapeId: string): void {
    this.enableEditMode(shapeId);
  }

  disableRotateHandle(shapeId: string): void {
    this.disableEditMode(shapeId);
  }

  // --- Magnétisme (snapping) : dessin et édition de sommet — voir src/adapters/maplibre/snapping.ts ---

  private readonly handleSnapModifierKey = (event: KeyboardEvent): void => {
    if (event.key === "Shift") this.setSnappingEnabled(event.type === "keydown");
    if (event.key === "Alt") {
      this.setConstrainEnabled(event.type === "keydown");
      // Sans quoi Firefox ouvre sa barre de menus au relâchement d'Alt, en plein tracé.
      if (this.isDrawingShape()) event.preventDefault();
    }
  };

  private readonly handleWindowBlur = (): void => {
    this.setSnappingEnabled(false);
    this.setConstrainEnabled(false);
  };

  /**
   * Le magnétisme ne s'applique que Shift enfoncé (voir `"snapping:changed"`). Au changement, le
   * dernier mouvement de souris est rejoué pour que l'accroche et les guides suivent la touche sans
   * attendre que le curseur bouge — tous les gestes se recalculent depuis leur ancre, un rejeu ne
   * décale donc rien.
   */
  private setSnappingEnabled(enabled: boolean): void {
    if (this.snappingEnabled === enabled) return;
    this.snappingEnabled = enabled;
    this.emit({ type: "snapping:changed", enabled });
    this.replayLastMouseMove();
  }

  /** Contrainte de tracé tenue par Alt (voir `"constrain:changed"`), rejouée comme le magnétisme. */
  private setConstrainEnabled(enabled: boolean): void {
    if (this.constrainEnabled === enabled) return;
    this.constrainEnabled = enabled;
    this.emit({ type: "constrain:changed", enabled });
    this.replayLastMouseMove();
  }

  private replayLastMouseMove(): void {
    if (!this.pendingMouseMove && this.lastMouseMove) {
      this.pendingMouseMove = this.lastMouseMove;
      this.flushPendingMouseMove();
    }
  }

  private isDrawingShape(): boolean {
    return !!(this.drawingState || this.rectDrawingState || this.lineDrawingState || this.extendingLineState);
  }

  /**
   * Prochain sommet d'un polygone ou d'une ligne en cours : contraint à 45° depuis le précédent
   * avec Alt, sinon aimanté comme d'habitude.
   */
  private draftVertexLngLat(event: MapMouseEvent, points: LngLat[], excludeShapeId: string | null): LngLat {
    const cursorPx = { x: event.point.x, y: event.point.y };
    const fromPx = this.lastDraftPointPx(points);
    if (!this.constrainEnabled || !fromPx) return this.resolveSnappedLngLat(cursorPx, excludeShapeId, fromPx);

    this.updateSnapGuidesSource(null);
    const ll = this.map!.unproject(constrainAnglePx(fromPx, cursorPx) as Point);
    return [ll.lng, ll.lat];
  }

  /** Coin opposé du rectangle en cours : carré avec Alt, sinon aimanté. */
  private rectCornerPx(event: MapMouseEvent, anchorPx: PixelPoint): PixelPoint {
    const cursorPx = { x: event.point.x, y: event.point.y };
    if (!this.constrainEnabled) return this.resolveSnappedPointPx(cursorPx, null);

    this.updateSnapGuidesSource(null);
    return constrainSquarePx(anchorPx, cursorPx);
  }

  /**
   * Sommets/arêtes candidats au snapping (en pixels écran), depuis les formes actuellement à
   * l'écran (`marginFraction: 0` — pas besoin de la marge utilisée pour les images, ici on ne
   * s'accroche que sur ce qui est visible). `excludeShapeId` retire la forme en cours d'édition de
   * ses propres candidats (voir `handleVertexDragMove`).
   */
  private snapCandidatesPx(excludeShapeId: string | null): SnapCandidates {
    if (!this.map) return { vertices: [], edges: [] };
    const container = this.map.getContainer();
    const width = container.clientWidth;
    const height = container.clientHeight;
    const isOnScreenPx = (p: PixelPoint) => p.x >= 0 && p.x <= width && p.y >= 0 && p.y <= height;

    const shapesPointsPx = this.lastShapes
      // Pré-filtre bon marché (lng/lat) avant de projeter — évite de projeter les sommets de
      // toutes les formes du jeu de données à chaque mousemove.
      .filter((shape) => shape.id !== excludeShapeId && this.isShapeNearViewport(shape, SNAP_CANDIDATE_PREFILTER_MARGIN))
      .map((shape) => shapeVertices(shape).map((point) => this.map!.project(point)))
      // Filtre précis en pixels : la boîte lng/lat ci-dessus ignore la rotation de la carte et peut
      // laisser passer des formes qui ne sont, en réalité, pas à l'écran — ne pas les proposer au
      // magnétisme.
      .filter((ringPx) => ringPx.some(isOnScreenPx));

    // Les sommets déjà posés du tracé en cours aimantent aussi : pour refermer un polygone d'équerre
    // avec son premier sommet, ou aligner un point sur un précédent.
    const draftPoints = this.drawingState?.points ?? this.lineDrawingState?.points ?? this.extendingLineState?.points ?? [];
    const draftPx = draftPoints.map((point) => this.map!.project(point));

    return mergeSnapCandidates(buildSnapCandidates(shapesPointsPx), draftSnapCandidates(draftPx));
  }

  /**
   * Résout le point accroché (pixels écran) pour un curseur donné, et met à jour les guides
   * affichés. `referencePointPx` (dernier sommet posé sur un tracé polygone/ligne EN COURS, voir
   * `snapping.ts`) n'est fourni que par `handleDrawingClick`/`handlePolygonDrawingMove`/
   * `handleLineDrawingClick`/`handleLineDrawingMove` — les autres appelants (coin de rectangle,
   * rayon de cercle, image, drag de sommet, déplacement de forme) l'omettent, comportement inchangé.
   */
  private resolveSnappedPointPx(
    cursorPx: PixelPoint,
    excludeShapeId: string | null,
    referencePointPx?: PixelPoint | null,
  ): PixelPoint {
    if (!this.snappingEnabled) {
      this.updateSnapGuidesSource(null);
      return cursorPx;
    }
    const result = resolveSnap(cursorPx, this.snapCandidatesPx(excludeShapeId), this.snapThresholds, referencePointPx);
    this.updateSnapGuidesSource(result);
    return result.point;
  }

  /** Comme `resolveSnappedPointPx`, mais reprojette le résultat en lng/lat. */
  private resolveSnappedLngLat(
    cursorPx: PixelPoint,
    excludeShapeId: string | null,
    referencePointPx?: PixelPoint | null,
  ): LngLat {
    const snappedPx = this.resolveSnappedPointPx(cursorPx, excludeShapeId, referencePointPx);
    const ll = this.map!.unproject(snappedPx as Point);
    return [ll.lng, ll.lat];
  }

  /** Sommet précédent d'un tracé polygone/ligne EN COURS, en pixels écran — `null` si `points` est
   * vide (1er sommet : rien à verrouiller). Utilisé comme `referencePointPx` ci-dessus. */
  private lastDraftPointPx(points: LngLat[]): PixelPoint | null {
    if (points.length === 0 || !this.map) return null;
    return this.map.project(points[points.length - 1]!);
  }

  /**
   * Résout un éventuel correctif de translation (pixels écran) pour un déplacement de forme
   * entière : contrairement à `resolveSnappedPointPx` (qui accroche le curseur lui-même, sans
   * rapport avec la géométrie de la forme déplacée — peu lisible, le curseur peut être n'importe
   * où dans son remplissage), on teste ici l'accroche de CHAQUE sommet de la forme (déjà translaté
   * par le déplacement brut) contre les autres formes, et on retient le correctif de plus petite
   * amplitude — celui qui demande le moins de "triche" par rapport au déplacement voulu par
   * l'utilisateur. Ce correctif est ensuite appliqué à toute la forme (translation rigide), donc la
   * guideline affichée correspond au sommet/à l'arête de la forme qui s'aligne réellement, pas au
   * curseur. Retourne `{ x: 0, y: 0 }` (et efface les guides) si aucun sommet n'accroche.
   */
  private resolveShapeMoveSnapDelta(translatedPointsPx: PixelPoint[], excludeShapeId: string): PixelPoint {
    if (!this.snappingEnabled) {
      this.updateSnapGuidesSource(null);
      return { x: 0, y: 0 };
    }
    const candidates = this.snapCandidatesPx(excludeShapeId);
    let best: { result: SnapResult; delta: PixelPoint; distance: number } | null = null;

    for (const p of translatedPointsPx) {
      const result = resolveSnap(p, candidates, this.snapThresholds);
      if (result.kind === "none" && !result.guides.x && !result.guides.y) continue;
      const delta = { x: result.point.x - p.x, y: result.point.y - p.y };
      const distance = Math.hypot(delta.x, delta.y);
      if (!best || distance < best.distance) best = { result, delta, distance };
    }

    this.updateSnapGuidesSource(best?.result ?? null);
    return best?.delta ?? { x: 0, y: 0 };
  }

  /** Affiche (ou efface, avec `null`) les guides d'alignement et l'indicateur d'accroche courants. */
  private updateSnapGuidesSource(result: SnapResult | null): void {
    const source = this.map?.getSource(SNAP_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(this.snapResultToGeoJSON(result));
    }
  }

  private snapResultToGeoJSON(result: SnapResult | null): GeoJSON.FeatureCollection {
    if (!this.map || !result) return { type: "FeatureCollection", features: [] };
    const map = this.map;
    const features: GeoJSON.Feature[] = [];

    if (result.kind !== "none") {
      const ll = map.unproject(result.point as Point);
      features.push({
        type: "Feature",
        properties: {},
        geometry: { type: "Point", coordinates: [ll.lng, ll.lat] },
      });
    }

    const container = map.getContainer();
    const width = container.clientWidth;
    const height = container.clientHeight;

    if (result.guides.x) {
      const x = result.guides.x.value;
      const top = map.unproject({ x, y: 0 } as Point);
      const bottom = map.unproject({ x, y: height } as Point);
      features.push({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [
            [top.lng, top.lat],
            [bottom.lng, bottom.lat],
          ],
        },
      });
    }
    if (result.guides.y) {
      const y = result.guides.y.value;
      const left = map.unproject({ x: 0, y } as Point);
      const right = map.unproject({ x: width, y } as Point);
      features.push({
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [
            [left.lng, left.lat],
            [right.lng, right.lat],
          ],
        },
      });
    }

    return { type: "FeatureCollection", features };
  }

  /** Id de la forme (s'il y en a une) sous `point`, parmi les bandes de calque actuelles — requête
   * manuelle plutôt que le filtrage par layer natif de `map.on(type, layerIds, cb)` : voir le
   * commentaire de `updateFillLayerIdsCache` pour le problème d'ordre de dispatch que ça évite. */
  private queryShapeIdAtPoint(point: PixelPoint): string | number | null {
    if (!this.map || this.currentFillLayerIds.length === 0) return null;
    const feature = this.map.queryRenderedFeatures(point as Point, {
      layers: [SHAPES_EXTRUSION_LAYER_ID, LINE_WALL_LAYER_ID, ...this.currentFillLayerIds],
    })[0];
    return feature?.properties?.id ?? null;
  }

  /**
   * click/contextmenu/survol sur les formes — class fields (pas des méthodes ordinaires) : purement
   * par cohérence avec le reste des gestionnaires d'événements de cette classe (tous des class
   * fields), pas pour une histoire d'identité de référence ici (ces trois-là ne sont enregistrés
   * qu'une seule fois, jamais désenregistrés — voir `updateFillLayerIdsCache`).
   */
  private handleShapeClick = (event: MapMouseEvent): void => {
    // En cours de dessin : on ignore complètement le clic (même principe que les gestes de dessin
    // eux-mêmes, voir plus bas). Une sélection multiple déjà affichée n'est PAS un motif d'ignorer le
    // clic en général : Ctrl+clic (juste en dessous) doit rester utilisable pour ajouter/retirer une
    // forme même pendant qu'un rectangle de sélection est déjà visible.
    if (this.drawingState || this.rectDrawingState || this.imageDrawingState || this.circleDrawingState || this.lineDrawingState || this.extendingLineState) return;

    // Ctrl+clic (ou Cmd+clic) sur une forme, en `editMode`, ajoute/retire cette forme de la
    // sélection multiple (BESOINS.md §13) — un raccourci vers la même fonctionnalité que Ctrl+glisser,
    // sans avoir à tracer un rectangle pour une seule forme supplémentaire. Entièrement pris en charge
    // ici, avant toute autre logique de clic : jamais aussi une ouverture de fiche/sidebar
    // (`"shape:click"`) pour le même geste. Hors édition d'une forme individuelle
    // (`this.editingState`, comme pour le reste de la sélection multiple — voir le `mousedown` Ctrl du
    // rectangle dans `init()`) et jamais sur une forme verrouillée (même exclusion que
    // `shapeIdsInsideCornersPx`).
    if (this.editMode && !this.editingState && (event.originalEvent.ctrlKey || event.originalEvent.metaKey)) {
      const shapeId = this.queryShapeIdAtPoint(event.point);
      if (shapeId != null) this.toggleShapeInSelection(String(shapeId));
      return;
    }

    // Pendant qu'une sélection multiple est affichée (voir BESOINS.md §13), un clic simple (sans
    // Ctrl, déjà traité ci-dessus) est soit consommé par le rectangle/ses poignées, soit un clic en
    // dehors qui le referme — jamais aussi une sélection individuelle de forme.
    if (this.selectionState) return;
    const shapeId = this.queryShapeIdAtPoint(event.point);
    // Mémorisé pour `handleShapeDblClick` (voir son commentaire) : le 1er clic d'un double-clic peut
    // sélectionner la forme et déclencher un effet de bord côté consommateur qui décale `#map` (ex.
    // l'exemple ouvre une sidebar qui rétrécit la carte, voir BESOINS.md/App.tsx `ensureShapeVisible`)
    // avant que le 2e clic n'arrive — sans ça, la recherche de forme du double-clic tomberait sur du
    // vide. Seulement écrasé sur un résultat trouvé : un clic qui retombe sur du vide juste après ne
    // doit pas effacer le souvenir du dernier clic qui, lui, a trouvé une forme.
    if (shapeId != null) {
      this.lastClickedShapeId = String(shapeId);
      this.lastClickedShapeIdAt = performance.now();
    }
    // Un clic qui ne retombe sur aucune des couches de la forme en cours d'édition (corps, contour,
    // sommets, poignée de rotation) referme le mode édition — que le clic tombe sur du vide ou sur
    // une AUTRE forme (`shapeId` non-null mais différente de `editingState.shapeId`, elle est de
    // toute façon exclue des couches ci-dessous pendant qu'elle est éditée, voir
    // `setEditingShapeFilter`). Un clic qui retombe sur la forme éditée elle-même (son corps, son
    // contour, un sommet, la poignée de rotation) la laisse ouverte.
    if (this.editingState && this.map) {
      const clickedEditingLayers = this.map.queryRenderedFeatures(event.point as Point, {
        layers: [
          EDITING_SHAPE_FILL_LAYER_ID,
          EDITING_SHAPE_OUTLINE_LAYER_ID,
          EDITING_SHAPE_OUTLINE_DASHED_LAYER_ID,
          EDITING_SHAPE_OUTLINE_DEFAULT_LAYER_ID,
          EDIT_VERTICES_LAYER_ID,
          ROTATE_HANDLE_LAYER_ID,
          MOVE_HANDLE_LAYER_ID,
        ],
      });
      if (clickedEditingLayers.length === 0) this.disableEditMode(this.editingState.shapeId);
    }
    // Une forme verrouillée n'est plus sélectionnable par clic gauche en `editMode` (même principe
    // que `handleShapeContextMenu`/`handleShapeDblClick`, qui l'excluent déjà de leurs gestes) : le
    // clic retombe sur `"map:click"`, comme s'il n'y avait rien sous le curseur — ça ferme une
    // sidebar/fiche déjà ouverte pour une autre forme plutôt que de rester silencieusement sans
    // effet. Hors `editMode`, la fiche entité (`TokaMapConfig.entityPanel`) reste accessible même
    // pour une forme verrouillée : `locked` protège l'édition, pas la simple consultation.
    const shape = shapeId != null ? this.lastShapes.find((s) => s.id === shapeId) : undefined;
    if (shapeId != null && !(this.editMode && shape?.locked)) {
      this.emit({ type: "shape:click", shapeId: String(shapeId) });
    } else {
      this.emit({
        type: "map:click",
        position: [event.lngLat.lng, event.lngLat.lat],
        screenPosition: { x: event.point.x, y: event.point.y },
      });
    }
  };

  private handleShapeContextMenu = (event: MapMouseEvent): void => {
    event.preventDefault();
    if (this.drawingState || this.rectDrawingState || this.imageDrawingState || this.circleDrawingState || this.lineDrawingState || this.extendingLineState) return;
    const screenPosition = { x: event.point.x, y: event.point.y };

    // Clic droit sur un sommet d'une forme EN ÉDITION : prioritaire sur tout le reste de cette
    // méthode (y compris la sélection multiple juste en dessous — un sommet n'existe que hors
    // sélection multiple, `EDIT_VERTICES_LAYER_ID` n'étant jamais affiché pendant elle).
    if (this.editingState && this.map) {
      const vertexFeature = this.map.queryRenderedFeatures(event.point as Point, { layers: [EDIT_VERTICES_LAYER_ID] })[0];
      const vertexIndex = vertexFeature?.properties?.vertexIndex;
      const editedShape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
      if (vertexIndex != null && editedShape && this.isVertexEditEligible(editedShape)) {
        this.emit({ type: "vertex:contextmenu", shapeId: this.editingState.shapeId, vertexIndex: Number(vertexIndex), screenPosition });
        return;
      }
    }

    // Clic droit à l'intérieur du rectangle de sélection multiple courant (BESOINS.md §13) :
    // prioritaire sur une forme sous le curseur — même principe que `handleShapeClick`, qui ignore
    // entièrement les formes individuelles tant qu'une sélection est affichée.
    if (this.selectionState && this.map) {
      const cornersPx = this.selectionState.corners.map((c) => this.map!.project(c));
      if (isPointInConvexPolygonPx(screenPosition, cornersPx)) {
        this.emit({ type: "selection:contextmenu", shapeIds: [...this.selectionState.shapeIds], screenPosition });
        return;
      }
    }

    const shapeId = this.queryShapeIdAtPoint(event.point);
    const shape = shapeId != null ? this.lastShapes.find((s) => s.id === shapeId) : undefined;
    // Une forme verrouillée (`Shape.locked`) n'a plus de menu contextuel "normal" du tout, ni par
    // clic droit ni par le bouton "…" de la barre d'outils flottante (voir
    // `createHoverToolbarElement`) — les deux seuls chemins vers lui. Comme un clic droit sur du
    // vide, elle retombe malgré tout sur `"map:contextmenu"` (menu réduit, voir `createTokaMap`) :
    // coller ne doit pas dépendre de retomber précisément sur une forme actionnable.
    if (shapeId == null || shape?.locked) {
      this.emit({ type: "map:contextmenu", screenPosition });
      return;
    }
    this.emit({ type: "shape:contextmenu", shapeId: String(shapeId), screenPosition });
  };

  /**
   * Seulement enregistré en `editMode` (voir `init()`) : double-clic pour entrer en mode édition,
   * un raccourci de plus vers `enableEditMode` à côté du menu contextuel ("Modifier"). Ignore les
   * mêmes cas que `handleShapeClick`/`handleShapeContextMenu` (dessin en cours, sélection multiple
   * affichée, forme verrouillée). L'appel à `event.preventDefault()` sur une forme touchée est
   * désormais redondant avec `doubleClickZoom: !this.editMode` (voir `init()`) — qui bloque le zoom
   * natif de MapLibre pour TOUT double-clic en édition, y compris sur du vide — mais reste inoffensif
   * à garder, en défense en profondeur si `editMode` devait un jour redevenir togglable dynamiquement.
   *
   * Un double-clic natif du navigateur émet `click` (1er clic), `click` (2e clic) PUIS `dblclick` —
   * pas un évènement synthétique unique. Le 1er `click` a donc déjà eu l'occasion de faire réagir un
   * consommateur (ex. l'exemple ouvre une sidebar sur `"shape:click"`, voir `handleShapeClick`), ce
   * qui peut décaler/rétrécir `#map` avant que le 2e clic physique n'arrive : `queryShapeIdAtPoint`
   * sur CE `dblclick` peut alors retomber sur du vide alors que le geste visait bien la forme. On
   * retombe donc sur `lastClickedShapeId` (posé par `handleShapeClick` sur un clic réussi, très
   * récemment — fenêtre de 500ms, la valeur par défaut du double-clic système) plutôt que de rater
   * le mode édition à cause d'un effet de bord hors du contrôle de cet adapter.
   */
  private handleShapeDblClick = (event: MapMouseEvent): void => {
    if (
      this.drawingState ||
      this.rectDrawingState ||
      this.imageDrawingState ||
      this.circleDrawingState ||
      this.lineDrawingState ||
      this.selectionState ||
      this.extendingLineState
    )
      return;

    // Double-clic sur le 1er/dernier sommet d'une LIGNE en cours d'édition : démarre une extension
    // (voir `startExtendLine`/`ExtendingLineState`) plutôt que de rouvrir inutilement le mode édition
    // sur la même forme via la logique `shapeId`/`enableEditMode` plus bas. Vérifié en premier,
    // prioritaire sur elle.
    if (this.editingState && this.map) {
      const vertexFeature = this.map.queryRenderedFeatures(event.point as Point, { layers: [EDIT_VERTICES_LAYER_ID] })[0];
      const vertexIndex = vertexFeature?.properties?.vertexIndex;
      if (vertexIndex != null) {
        const editedShape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
        const lastIndex = this.editingState.points.length - 1;
        const index = Number(vertexIndex);
        if (editedShape?.shapeKind === "line" && (index === 0 || index === lastIndex)) {
          event.preventDefault();
          this.startExtendLine(index === 0 ? "start" : "end");
          return;
        }
      }
    }

    let shapeId = this.queryShapeIdAtPoint(event.point);
    if (shapeId == null && this.lastClickedShapeId != null && performance.now() - this.lastClickedShapeIdAt < 500) {
      shapeId = this.lastClickedShapeId;
    }
    if (shapeId == null) return;
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (shape?.locked) return;
    event.preventDefault();
    this.emit({ type: "shape:dblclick", shapeId: String(shapeId) });
  };

  /**
   * Curseur "pointer" au survol d'une forme cliquable (`shapeId != null`) — générique comme
   * `handleShapeClick`, jamais désenregistré. Contrairement à celui-ci, une forme verrouillée
   * (`Shape.locked`) n'affiche JAMAIS le curseur pointer, y compris hors `editMode` où elle reste
   * cliquable (la fiche entité y reste accessible, voir `handleShapeClick`) : décision produit
   * explicite, `locked` doit visuellement décourager l'interaction au survol même là où le clic
   * passe encore.
   *
   * Hors `editMode`, le clic gauche ne fait rien de visible sur une forme sans `Shape.action` (pas
   * de fiche entité à ouvrir, voir `TokaMapConfig.entityPanel`/`getEntityPanelEntry`) : le curseur
   * ne doit donc passer à `pointer` que si `shape.action != null`, sans quoi il annoncerait une
   * interaction qui n'existe pas. En `editMode`, le clic sélectionne toujours la forme (sidebar,
   * indépendamment de `action`), donc ce filtre supplémentaire ne s'applique pas.
   *
   * Ne touche pas au curseur pendant un dessin/glissement en cours (poignées de sommet/rotation,
   * déplacement de forme ou de sélection…) : ces gestes gèrent déjà le leur via leurs propres
   * `mouseenter`/`mouseleave`/`mousedown` sur des layers dédiés (voir plus haut dans `init()`) —
   * un `mousemove` générique qui tournerait par-dessus écraserait `grab`/`grabbing`/`move`/
   * `nwse-resize` par `pointer`/`""` à chaque tick.
   */
  private handleShapeHoverCursor = (event: MapMouseEvent): void => {
    if (!this.map) return;
    if (
      this.drawingState ||
      this.rectDrawingState ||
      this.imageDrawingState ||
      this.circleDrawingState ||
      this.lineDrawingState ||
      this.selectionState ||
      this.selectionDrawState ||
      this.draggingVertexIndex != null ||
      this.rotatingState ||
      this.movingState ||
      this.selectionResizingState ||
      this.selectionRotatingState ||
      this.selectionMovingState ||
      this.extendingLineState
    ) {
      return;
    }
    // Pendant l'édition d'une forme, son propre corps/contour/sommets/poignée de rotation ont déjà
    // leur curseur dédié (`grab`/`move`, voir plus haut) : on ne les recouvre pas ici — même liste de
    // layers que le garde-fou équivalent de `handleShapeClick`.
    if (this.editingState) {
      const editingLayersHit = this.map.queryRenderedFeatures(event.point as Point, {
        layers: [
          EDITING_SHAPE_FILL_LAYER_ID,
          EDITING_SHAPE_OUTLINE_LAYER_ID,
          EDITING_SHAPE_OUTLINE_DASHED_LAYER_ID,
          EDITING_SHAPE_OUTLINE_DEFAULT_LAYER_ID,
          EDIT_VERTICES_LAYER_ID,
          ROTATE_HANDLE_LAYER_ID,
          MOVE_HANDLE_LAYER_ID,
        ],
      });
      if (editingLayersHit.length > 0) return;
    }
    const shapeId = this.queryShapeIdAtPoint(event.point);
    const shape = shapeId != null ? this.lastShapes.find((s) => s.id === shapeId) : undefined;
    const isClickable = shapeId != null && !shape?.locked && (this.editMode || shape?.action != null);
    this.map.getCanvas().style.cursor = isClickable ? "pointer" : "";
  };

  /** Le curseur quitte le canvas entièrement : plus aucun `mousemove` ne suivra pour remettre
   * `handleShapeHoverCursor` à `""`, donc on le fait ici — même garde que lui n'est pas nécessaire,
   * quitter le canvas met de toute façon fin à un éventuel survol de forme. */
  private handleShapeHoverCursorOut = (): void => {
    if (this.map) this.map.getCanvas().style.cursor = "";
  };

  /**
   * Générique (pas de filtrage par layer) : contrairement au `mousemove` filtré par layer d'origine
   * (qui ne se déclenchait que par-dessus une forme, avec un `mouseleave` dédié pour le cas "vient
   * d'en sortir"), celui-ci se déclenche partout sur la carte et recalcule à chaque fois s'il y a une
   * forme sous le curseur — recouvre donc aussi nativement le cas "vient de sortir d'une forme vers
   * une zone vide" (`shapeId` retombe à `null`), sans binding séparé. Seul le cas "le curseur quitte
   * le canvas entièrement" reste à part, voir `handleShapeMouseOut`.
   */
  private handleShapeMouseMove = (event: MapMouseEvent): void => {
    // Le curseur est sur la barre d'outils flottante elle-même (hors du polygone de la forme, voir
    // `showHoverToolbar`) : ne rien recalculer, sous peine de l'effacer alors que l'utilisateur essaie
    // justement de cliquer dessus.
    if (this.isPointerOverHoverToolbar) return;
    if (
      this.drawingState ||
      this.rectDrawingState ||
      this.imageDrawingState ||
      this.circleDrawingState ||
      this.lineDrawingState ||
      this.extendingLineState
    )
      return;
    const shapeId = this.queryShapeIdAtPoint(event.point);
    const shape = shapeId != null ? this.lastShapes.find((s) => s.id === shapeId) : undefined;
    // Le highlight bleu (fond + contour pointillé dédié, voir `resolveShapeVisualProps`) s'applique
    // désormais à TOUTE forme survolée, y compris une forme-image
    // (`shapeKind: "image"`) — demande explicite, alors qu'elle n'a jamais de fond/bordure
    // NORMAUX (`bgColor`/`borderColor` restent "transparent" pour elle, voir `shapesToGeoJSON`) :
    // seul son survol est concerné par ce changement, pas son état au repos. Volontairement
    // indépendant du couloir ci-dessous : une forme plus grande sous le couloir doit pouvoir se
    // surligner/redevenir cliquable normalement, seule la barre d'outils flottante reste "collée" à
    // la forme qui l'a ouverte.
    const nextHoveredShapeId = shape ? shapeId : null;

    // Couloir vide entre la forme qui a ouvert la barre d'outils flottante et la barre elle-même
    // (voir `hoverToolbarCorridor`) : une forme plus grande peut être détectée à ce pixel précis sans
    // que l'utilisateur ait quitté la forme active pour autant — il est simplement en chemin vers la
    // barre. Tant qu'il y reste, on NE TOUCHE PAS à la barre (ni bascule vers `shape`, ni masquage),
    // même si `shape` désigne une autre forme — seul le highlight ci-dessus doit suivre `shape`.
    if (this.hoverToolbarShapeId != null && this.isPointInHoverToolbarCorridor(event.point)) {
      this.cancelHoverToolbarHide();
    } else if (shape) {
      // Barre d'outils : contrairement au highlight ci-dessus (exclu pour les images directes),
      // s'affiche sur TOUTE forme survolée, images directes comprises (demande explicite) —
      // recalculée à CHAQUE tick tant qu'une forme est sous le curseur (pas seulement au changement de
      // forme survolée) : nécessaire pour annuler un masquage différé en cours (voir
      // `scheduleHoverToolbarHide`) si le curseur revient sur la même forme pendant sa fenêtre de
      // grâce — `showHoverToolbar`/`scheduleHoverToolbarHide` sont tous deux idempotents, ce n'est pas
      // du travail perdu.
      this.showHoverToolbar(shapeId!);
    } else {
      this.scheduleHoverToolbarHide();
    }

    if (nextHoveredShapeId === this.hoveredShapeId) return;

    this.clearHoveredShape();
    if (nextHoveredShapeId != null) {
      this.setHoveredShapeId(nextHoveredShapeId);
      this.setShapeFeatureState(nextHoveredShapeId, { hovered: true });
    }
    // Voir `refreshShapesTextForHover` : nécessaire en plus du feature-state ci-dessus, pour
    // `Shape.hoverText` — appelé seulement ici (le survol vient de changer, voir le early-return
    // ci-dessus), jamais à chaque `mousemove` brut.
    this.refreshShapesTextForHover();
  };

  /**
   * Le curseur quitte le canvas entièrement (plus aucun `mousemove` ne suivra) : `handleShapeMouseMove`
   * seul ne suffit pas à effacer le survol dans ce cas précis.
   *
   * Se déclenche AUSSI quand le curseur quitte le canvas pour entrer sur `hoverToolbarContainer`
   * (un `<div>` DOM séparé, visuellement au-dessus mais hors de l'arbre du canvas) — un cas qu'il ne
   * faut surtout pas traiter comme une sortie de survol, sous peine de cacher la barre juste avant
   * que l'utilisateur ne clique dessus. `isPointerOverHoverToolbar` (mis à jour par le `mouseenter`
   * de la barre) arrive TROP TARD pour ce cas précis : l'ordre standard des évènements navigateur est
   * "mouseout" de l'ancien élément PUIS "mouseenter" du nouveau, donc le flag n'est pas encore posé
   * ici — `event.relatedTarget` (l'élément que le curseur rejoint) est le seul signal fiable à ce
   * stade.
   */
  private handleShapeMouseOut = (event: MapMouseEvent): void => {
    const relatedTarget = event.originalEvent?.relatedTarget as Node | null;
    if (this.isPointerOverHoverToolbar || (relatedTarget && this.hoverToolbarContainer?.contains(relatedTarget))) {
      return;
    }
    const hadHoveredShape = this.hoveredShapeId != null;
    this.clearHoveredShape();
    this.scheduleHoverToolbarHide();
    if (hadHoveredShape) this.refreshShapesTextForHover();
  };

  /**
   * Barre d'outils flottante affichée au survol d'une forme en `editMode` (cadenas + "...") — créée
   * une seule fois par instance (voir `init`), montrée/repositionnée via `showHoverToolbar`/
   * `updateHoverToolbarPosition`, cachée via `hideHoverToolbar`. Le bouton cadenas bascule
   * `Shape.locked` (voir `SetShapeLockedCommand`/`TokaMapInstance.setShapeLocked`, réémis en
   * `"shape:lock-toggled"` — `createTokaMap` calcule la commande, l'adapter se contente de constater
   * l'état courant et de demander l'inverse) ; le bouton "..." ouvre le même menu que le clic droit
   * (`shape:contextmenu`, voir `createTokaMap.ts`), en réémettant exactement le même évènement — sauf
   * si la forme est verrouillée, où il reste masqué (voir `updateHoverToolbarLockUI`).
   */
  private createHoverToolbarElement(): HTMLDivElement {
    const container = document.createElement("div");
    container.className = "toka-hover-toolbar";
    container.style.display = "none";

    const lockButton = document.createElement("button");
    lockButton.type = "button";
    lockButton.className = "toka-hover-toolbar__button";
    lockButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.hoverToolbarShapeId == null) return;
      const shape = this.lastShapes.find((s) => s.id === this.hoverToolbarShapeId);
      if (!shape) return;
      this.emit({
        type: "shape:lock-toggled",
        shapeId: String(this.hoverToolbarShapeId),
        locked: !shape.locked,
      });
    });
    this.hoverToolbarLockButton = lockButton;

    const menuButton = document.createElement("button");
    menuButton.type = "button";
    menuButton.className = "toka-hover-toolbar__button";
    menuButton.appendChild(createIcon("more"));
    menuButton.title = "Menu";
    menuButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (this.hoverToolbarShapeId == null) return;
      const shape = this.lastShapes.find((s) => s.id === this.hoverToolbarShapeId);
      if (shape?.locked) return;
      this.emit({
        type: "shape:contextmenu",
        shapeId: String(this.hoverToolbarShapeId),
        screenPosition: this.hoverToolbarAnchorPx ?? { x: 0, y: 0 },
      });
    });
    this.hoverToolbarMenuButton = menuButton;

    container.appendChild(lockButton);
    container.appendChild(menuButton);

    container.addEventListener("mouseenter", () => {
      this.isPointerOverHoverToolbar = true;
      this.cancelHoverToolbarHide();
    });
    container.addEventListener("mouseleave", () => {
      this.isPointerOverHoverToolbar = false;
      const hadHoveredShape = this.hoveredShapeId != null;
      this.clearHoveredShape();
      this.hideHoverToolbar();
      if (hadHoveredShape) this.refreshShapesTextForHover();
    });

    return container;
  }

  /** Affiche `hoverToolbarContainer` au-dessus de `shapeId` et la positionne (voir
   * `updateHoverToolbarPosition`). */
  private showHoverToolbar(shapeId: string | number): void {
    if (!this.hoverToolbarContainer) return;
    this.cancelHoverToolbarHide();
    this.hoverToolbarShapeId = shapeId;
    this.hoverToolbarContainer.style.display = "flex";
    this.updateHoverToolbarPosition();
  }

  /** Cache `hoverToolbarContainer` — no-op tant que le curseur est encore dessus (voir
   * `isPointerOverHoverToolbar`). */
  private hideHoverToolbar(): void {
    this.cancelHoverToolbarHide();
    if (this.isPointerOverHoverToolbar || !this.hoverToolbarContainer) return;
    this.hoverToolbarShapeId = null;
    this.hoverToolbarAnchorPx = null;
    this.hoverToolbarCorridor = null;
    this.hoverToolbarContainer.style.display = "none";
  }

  /** Voir `hoverToolbarCorridor`. */
  private isPointInHoverToolbarCorridor(point: PixelPoint): boolean {
    const corridor = this.hoverToolbarCorridor;
    if (!corridor) return false;
    return point.x >= corridor.minX && point.x <= corridor.maxX && point.y >= corridor.minY && point.y <= corridor.maxY;
  }

  /**
   * Cache `hoverToolbarContainer` après `HOVER_TOOLBAR_HIDE_DELAY_MS` plutôt qu'immédiatement — voir
   * le commentaire de cette constante. Annulé par `cancelHoverToolbarHide` si le survol revient sur
   * la forme ou atteint la barre elle-même avant expiration.
   *
   * RÉARME le délai à chaque appel (plutôt que de laisser filer un premier minuteur déjà en cours) :
   * `handleShapeMouseMove` appelle ceci à CHAQUE `mousemove` reçu tant que le curseur reste au-dessus
   * d'une zone de carte vide (voir son commentaire) — donc à chaque déplacement, même minime, tant
   * que l'utilisateur progresse visiblement vers la barre. Un premier-appel-gagne laisserait le
   * minuteur initial expirer sans tenir compte d'une traversée plus lente que
   * `HOVER_TOOLBAR_HIDE_DELAY_MS` (trackpad, mouvement précis) alors même que le curseur est encore
   * en mouvement — la barre disparaîtrait avant que l'utilisateur ne l'atteigne. Avec le réarmement,
   * elle ne se cache que si le curseur s'immobilise (ou repart ailleurs) pendant tout ce délai.
   */
  private scheduleHoverToolbarHide(): void {
    this.cancelHoverToolbarHide();
    this.hoverToolbarHideTimer = setTimeout(() => {
      this.hoverToolbarHideTimer = null;
      this.hideHoverToolbar();
    }, HOVER_TOOLBAR_HIDE_DELAY_MS);
  }

  private cancelHoverToolbarHide(): void {
    if (this.hoverToolbarHideTimer == null) return;
    clearTimeout(this.hoverToolbarHideTimer);
    this.hoverToolbarHideTimer = null;
  }

  /**
   * Recalcule la position (haut-centre de la forme, en pixels relatifs au conteneur de la carte —
   * même système que `event.point`/`map.project()`) de la barre d'outils flottante actuellement
   * affichée — appelé à chaque survol changé, à chaque déplacement de la carte (`"move"`, voir
   * `init`) et à chaque `renderShapes` (la géométrie de la forme survolée peut changer sans geste de
   * caméra, ex. un undo/redo). Projette les sommets réels de la forme (pas juste son bbox lng/lat) :
   * reste correct même si la forme est tournée.
   */
  private updateHoverToolbarPosition(): void {
    if (!this.map || !this.hoverToolbarContainer || this.hoverToolbarShapeId == null) return;
    const shape = this.lastShapes.find((s) => s.id === this.hoverToolbarShapeId);
    if (!shape) {
      this.hideHoverToolbar();
      return;
    }
    this.updateHoverToolbarLockUI(shape);
    const pixels = shapeVertices(shape).map((point) => this.map!.project(point));
    const minX = Math.min(...pixels.map((p) => p.x));
    const maxX = Math.max(...pixels.map((p) => p.x));
    const minY = Math.min(...pixels.map((p) => p.y));
    const anchor = { x: (minX + maxX) / 2, y: minY - HOVER_TOOLBAR_GAP_PX };
    this.hoverToolbarAnchorPx = anchor;
    this.hoverToolbarContainer.style.left = `${anchor.x}px`;
    this.hoverToolbarContainer.style.top = `${anchor.y}px`;
    // `transform: translate(-50%, -100%)` (voir style.css) place `anchor` au bas-centre de la barre —
    // son rectangle réel s'étend donc de `anchor.x ± offsetWidth/2` en X et de `anchor.y -
    // offsetHeight` à `anchor.y` en Y. Le couloir couvre l'union de ce rectangle et de la bbox écran
    // de la forme (`minX`/`maxX`/`minY`), pour rester valide que la barre soit plus étroite ou plus
    // large que la forme elle-même.
    const toolbarHalfWidth = this.hoverToolbarContainer.offsetWidth / 2;
    const corridor = {
      minX: Math.min(minX, anchor.x - toolbarHalfWidth),
      maxX: Math.max(maxX, anchor.x + toolbarHalfWidth),
      minY: anchor.y - this.hoverToolbarContainer.offsetHeight,
      maxY: minY + HOVER_TOOLBAR_CORRIDOR_BOTTOM_MARGIN_PX,
    };
    this.hoverToolbarCorridor = corridor;
  }

  /**
   * Reflète `shape.locked` sur la barre d'outils flottante — icône/titre du cadenas, et visibilité
   * du bouton "…" (masqué quand verrouillée, seul chemin vers le menu contextuel restant alors le
   * clic droit, lui-même désactivé pour une forme verrouillée — voir `handleShapeContextMenu`).
   * Appelé à chaque `updateHoverToolbarPosition` : `locked` peut changer pendant que la barre est
   * affichée (l'utilisateur vient justement de cliquer sur le cadenas).
   */
  private updateHoverToolbarLockUI(shape: Shape): void {
    if (!this.hoverToolbarLockButton || !this.hoverToolbarMenuButton) return;
    const locked = !!shape.locked;
    this.hoverToolbarLockButton.replaceChildren(createIcon(locked ? "lock" : "unlock"));
    this.hoverToolbarLockButton.title = locked ? "Déverrouiller" : "Verrouiller";
    this.hoverToolbarMenuButton.style.display = locked ? "none" : "";
  }

  /** Efface l'état de survol (voir `editMode`) de la forme actuellement survolée, s'il y en a une. */
  private clearHoveredShape(): void {
    if (this.hoveredShapeId != null && this.map) {
      this.setShapeFeatureState(this.hoveredShapeId, { hovered: false });
    }
    this.setHoveredShapeId(null);
  }

  /**
   * Point d'écriture UNIQUE de `hoveredShapeId`, pour que `"shape:hovered"` (voir `MapAdapterEvent`)
   * reste toujours synchronisé avec lui, sans avoir à se souvenir d'émettre à chaque site d'appel.
   * `clearHoveredShape()` PUIS ce setter (voir `handleShapeMouseMove`) émettent donc chacun leur tour
   * lors d'un changement de forme survolée directement d'une forme à une autre — deux évènements
   * successifs (`null` puis la nouvelle forme) plutôt qu'un seul directement à la bonne valeur, jugé
   * sans conséquence pour un consommateur qui ne lit l'évènement qu'à un instant T donné (ex. au
   * relâchement d'un glisser-déposer) plutôt qu'à chaque évènement individuellement.
   */
  private setHoveredShapeId(id: string | number | null): void {
    this.hoveredShapeId = id;
    this.emit({ type: "shape:hovered", shapeId: id != null ? String(id) : null });
  }

  /** Id numérique stable associé à un `Shape.id` — voir le commentaire de `shapesToGeoJSON` sur
   * pourquoi le feature-state MapLibre a besoin d'un id numérique plutôt que du `string` naturel. */
  private numericFeatureId(shapeId: string | number): number {
    let numericId = this.shapeNumericIds.get(shapeId);
    if (numericId == null) {
      numericId = this.nextNumericShapeId++;
      this.shapeNumericIds.set(shapeId, numericId);
    }
    return numericId;
  }

  /**
   * Applique un feature-state (`hovered`/`selected`) à une forme sur TOUTES les sources qui
   * peuvent la porter — `SHAPES_SOURCE_ID` (toujours) et `CIRCLE_SOURCE_ID` (voir son commentaire :
   * un `setFeatureState` sur un id absent d'une source donnée est un no-op silencieux côté
   * MapLibre, donc appeler les deux inconditionnellement plutôt que de vérifier `shapeKind` ici
   * reste correct et évite au surcroît une recherche de forme à chaque appel).
   */
  private setShapeFeatureState(shapeId: string | number, state: Record<string, unknown>): void {
    if (!this.map) return;
    const id = this.numericFeatureId(shapeId);
    this.map.setFeatureState({ source: SHAPES_SOURCE_ID, id }, state);
    this.map.setFeatureState({ source: CIRCLE_SOURCE_ID, id }, state);
    this.map.setFeatureState({ source: LINE_WALL_SOURCE_ID, id }, state);
  }

  /** Bascule l'état de feature "selected" (mise en évidence visuelle, voir `colors.accent`)
   * sur les formes d'une sélection multiple — appelé uniquement à la création/fermeture du rectangle
   * (voir `handleSelectionDrawEnd`/`clearSelection`), pas à chaque frame de drag : l'ensemble des
   * formes sélectionnées ne change pas pendant un redimensionnement/rotation/déplacement (BESOINS.md §13). */
  private applySelectionFeatureState(shapeIds: string[]): void {
    if (!this.map) return;
    for (const id of this.selectedFeatureStateIds) {
      this.setShapeFeatureState(id, { selected: false });
    }
    this.selectedFeatureStateIds = shapeIds;
    for (const id of shapeIds) {
      this.setShapeFeatureState(id, { selected: true });
    }
  }

  /**
   * Bascule l'état de feature "focused" (contour dédié, même couleur que `colors.accent`
   * — voir les `"case"` de `ensureLayerBand`) sur la forme actuellement sélectionnée au clic simple
   * (par opposition à `applySelectionFeatureState`, pour la sélection multiple) — `null` efface le
   * contour courant sans en poser de nouveau. `MapAdapter` ne connaît toujours aucune notion produit
   * de "sélection" : c'est le consommateur (ex. `App.tsx`, dont la sélection pilote aussi l'affichage
   * de sa Sidebar et les raccourcis clavier Copier/Supprimer) qui décide quand appeler cette méthode,
   * exactement comme pour `highlightShapes`.
   *
   * No-op hors `editMode` — même logique que le survol (voir son commentaire dans `init()`) : rien ne
   * doit surligner de forme sur la carte en mode vue seule, où il n'y a de toute façon rien à éditer.
   */
  /**
   * Voir `MapAdapter.setHoverPreviewShapes`. Passe par les données plutôt que par le feature-state :
   * une forme sélectionnée est dessinée par la source d'édition, qui n'en a pas.
   */
  setHoverPreviewShapes(shapeIds: string[]): void {
    const next = new Set(shapeIds.map(String));
    if (next.size === this.hoverPreviewShapeIds.size && [...next].every(id => this.hoverPreviewShapeIds.has(id))) return;
    this.hoverPreviewShapeIds = next;
    this.renderShapes(this.lastShapes);
    if (this.lastEditingShapes) this.updateEditingShapeSource(this.lastEditingShapes.shapes, this.lastEditingShapes.selected);
  }

  setHoveredShapes(shapeIds: string[]): void {
    if (!this.editMode || !this.map) return;
    for (const id of this.externallyHoveredShapeIds) {
      if (!shapeIds.includes(id) && String(this.hoveredShapeId) !== id) this.setShapeFeatureState(id, { hovered: false });
    }
    this.externallyHoveredShapeIds = [...shapeIds];
    for (const id of shapeIds) this.setShapeFeatureState(id, { hovered: true });
  }

  setFocusedShape(shapeId: string | number | null): void {
    if (!this.editMode || this.focusedShapeId === shapeId) return;
    if (this.focusedShapeId != null) this.setShapeFeatureState(this.focusedShapeId, { focused: false });
    this.focusedShapeId = shapeId;
    if (shapeId != null) this.setShapeFeatureState(shapeId, { focused: true });
  }

  /**
   * Vrai si `screenPoint` (pixels écran) est assez proche de `points[0]` pour clôturer le polygone
   * — reste sur des coordonnées écran brutes (pas le résultat du snapping générique), utilisé à la
   * fois pour détecter le clic de fermeture (`handleDrawingClick`) et pour le survol/magnétisme
   * visuel avant ce clic (`handlePolygonDrawingMove`) : les deux doivent s'accorder sur le même
   * rayon, sans quoi le curseur pourrait sembler "accroché" à un endroit qui ne ferme pourtant pas
   * le polygone au clic (ou l'inverse).
   */
  private isNearClosingPoint(points: LngLat[], screenPoint: PixelPoint): boolean {
    if (!this.map || points.length < MIN_POINTS_TO_CLOSE) return false;
    const firstScreen = this.map.project(points[0]!);
    const distance = Math.hypot(firstScreen.x - screenPoint.x, firstScreen.y - screenPoint.y);
    return distance <= CLOSE_CLICK_RADIUS_PX;
  }

  /**
   * Équivalent de `isNearClosingPoint` pour une ligne (`startDrawingLine`) — teste la proximité du
   * DERNIER sommet posé, pas le premier : une ligne se termine sur lui, elle ne se referme jamais.
   * Même rayon (`CLOSE_CLICK_RADIUS_PX`), seuil minimum plus bas (`MIN_POINTS_TO_CLOSE_LINE`, 2 —
   * une ligne existe dès 2 points, contrairement au polygone qui en réclame 3 avant de pouvoir se
   * fermer).
   */
  private isNearLastPoint(points: LngLat[], screenPoint: PixelPoint): boolean {
    if (!this.map || points.length < MIN_POINTS_TO_CLOSE_LINE) return false;
    const lastScreen = this.map.project(points[points.length - 1]!);
    const distance = Math.hypot(lastScreen.x - screenPoint.x, lastScreen.y - screenPoint.y);
    return distance <= CLOSE_CLICK_RADIUS_PX;
  }

  private handleDrawingClick(event: MapMouseEvent): void {
    if (!this.drawingState || !this.map) return;

    const { points } = this.drawingState;

    if (this.isNearClosingPoint(points, event.point)) {
      this.finalizeDrawing();
      return;
    }

    // Étape 2 du tutoriel dès le 1er sommet posé (voir `StartDrawingOptions.skipTutorial`) — pas à
    // chaque clic suivant, seulement à la transition 0 -> 1 sommet.
    const wasEmpty = points.length === 0;
    points.push(this.draftVertexLngLat(event, points, null));
    this.drawingState.closeHoverActive = false;
    this.updateDraftSource();
    if (wasEmpty && !this.drawingState.skipTutorial) {
      this.emit({ type: "drawing:tutorial-step", shapeKind: "polygon", step: 2 });
    }
  }

  private finalizeDrawing(): void {
    if (!this.drawingState) return;
    const { points, skipSave, skipHistory, skipConfirm, skipTutorial } = this.drawingState;

    const shape: Shape = {
      id: generateId(),
      type: "polygon",
      shapeKind: "polygon",
      coordinates: [...points, points[0]!],
      rotation: 0,
      order: Date.now(),
      layer: DEFAULT_NEW_SHAPE_LAYER,
      style: this.defaultShapeTheme,
      themeId: this.defaultThemeId,
    };

    this.drawingState = null;
    this.updateDraftSource();
    this.updateSnapGuidesSource(null);
    if (this.map) this.map.getCanvas().style.cursor = "";

    this.emit({ type: "shape:created", shape, skipSave, skipHistory, skipConfirm });
    this.emit({ type: "drawing:ended" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
  }

  /**
   * `drawingState`/`lineDrawingState` sont mutuellement exclusifs (chaque `startDrawing*` remet les
   * autres à `null`) — lire l'un ou l'autre ici (au lieu de dupliquer cette méthode) couvre les deux
   * sans dupliquer `draftToGeoJSON`. `closeVertexIndex` bascule sur le DERNIER sommet posé pour une
   * ligne (voir `isNearLastPoint`), le premier (défaut) pour un polygone.
   */
  private updateDraftSource(previewPoint?: LngLat): void {
    const source = this.map?.getSource(DRAFT_SOURCE_ID);
    if (source && source.type === "geojson") {
      const points = this.drawingState?.points ?? this.lineDrawingState?.points ?? this.extendingLineState?.points ?? [];
      (source as maplibregl.GeoJSONSource).setData(
        draftToGeoJSON(points, {
          previewPoint,
          closeHoverActive:
            this.drawingState?.closeHoverActive ??
            this.lineDrawingState?.closeHoverActive ??
            this.extendingLineState?.closeHoverActive ??
            false,
          closeVertexIndex: this.lineDrawingState || this.extendingLineState ? Math.max(0, points.length - 1) : 0,
        }),
      );
    }

    const draftPoints = this.drawingState?.points ?? this.lineDrawingState?.points ?? this.extendingLineState?.points ?? [];
    this.setMeasureLabel(previewPoint ? draftMeasureText(draftPoints, previewPoint, !!this.drawingState) : null, previewPoint);
  }

  /** Étiquette de mesure près de `at` (le point qui suit le curseur) ; `null` la retire. */
  private setMeasureLabel(text: string | null, at?: LngLat): void {
    if (!this.showDrawingMeasures || !text || !at || !this.map) {
      this.measureLabel?.remove();
      this.measureLabel = null;
      return;
    }

    if (!this.measureLabel) {
      this.measureLabel = document.createElement("div");
      this.measureLabel.className = "toka-measure-label";
      this.map.getCanvasContainer().appendChild(this.measureLabel);
    }

    this.measureLabel.textContent = text;
    const { x, y } = this.map.project(at);
    this.measureLabel.style.transform = `translate(${Math.round(x + 14)}px, ${Math.round(y + 14)}px)`;
  }

  /**
   * Segment élastique (avec accroche) du dernier sommet posé jusqu'au curseur, pendant le dessin
   * d'un polygone. Magnétisme de fermeture : une fois assez de sommets posés, dès que le curseur
   * entre dans le rayon de fermeture (`isNearClosingPoint`), la ligne élastique rejoint exactement
   * `points[0]` (pas le point brut/snappé du curseur) — le 1er sommet grossit en même temps (voir
   * `closeHoverActive`, `draftToGeoJSON`), pour un vrai retour visuel d'aimantation plutôt qu'une
   * simple détection silencieuse au clic.
   */
  private handlePolygonDrawingMove(event: MapMouseEvent): void {
    if (!this.drawingState) return;

    const { points } = this.drawingState;
    const closeHoverActive = this.isNearClosingPoint(points, event.point);
    this.drawingState.closeHoverActive = closeHoverActive;

    const previewPoint = closeHoverActive
      ? points[0]
      : this.draftVertexLngLat(event, points, null);
    this.updateDraftSource(previewPoint);
  }

  // --- Dessin de ligne (un clic par sommet, comme un polygone, mais sans fermeture en anneau) ---

  private handleLineDrawingClick(event: MapMouseEvent): void {
    if (!this.lineDrawingState || !this.map) return;

    const { points } = this.lineDrawingState;

    if (this.isNearLastPoint(points, event.point)) {
      this.finalizeLineDrawing();
      return;
    }

    const wasEmpty = points.length === 0;
    points.push(this.draftVertexLngLat(event, points, null));
    this.lineDrawingState.closeHoverActive = false;
    this.updateDraftSource();
    if (wasEmpty && !this.lineDrawingState.skipTutorial) {
      this.emit({ type: "drawing:tutorial-step", shapeKind: "line", step: 2 });
    }
  }

  /** `coordinates: [...points]` — PAS `[...points, points[0]!]` : une ligne reste une polyligne
   * OUVERTE (voir `Shape.coordinates`), contrairement à `finalizeDrawing` (polygone). */
  private finalizeLineDrawing(): void {
    if (!this.lineDrawingState) return;
    const { points, skipSave, skipHistory, skipConfirm, skipTutorial } = this.lineDrawingState;

    const shape: Shape = {
      id: generateId(),
      type: "polygon",
      shapeKind: "line",
      coordinates: [...points],
      rotation: 0,
      order: Date.now(),
      layer: DEFAULT_NEW_SHAPE_LAYER,
      style: lineShapeStyle(this.defaultShapeTheme),
      themeId: this.defaultThemeId,
    };

    this.lineDrawingState = null;
    this.extendingLineState = null;
    this.updateDraftSource();
    this.updateSnapGuidesSource(null);
    if (this.map) this.map.getCanvas().style.cursor = "";

    this.emit({ type: "shape:created", shape, skipSave, skipHistory, skipConfirm });
    this.emit({ type: "drawing:ended" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
  }

  /** Segment élastique jusqu'au curseur, aimanté sur le DERNIER sommet posé une fois assez proche
   * pour terminer la ligne au clic — symétrique de `handlePolygonDrawingMove`, voir
   * `isNearLastPoint`. */
  private handleLineDrawingMove(event: MapMouseEvent): void {
    if (!this.lineDrawingState) return;

    const { points } = this.lineDrawingState;
    const closeHoverActive = this.isNearLastPoint(points, event.point);
    this.lineDrawingState.closeHoverActive = closeHoverActive;

    const previewPoint = closeHoverActive
      ? points[points.length - 1]
      : this.draftVertexLngLat(event, points, null);
    this.updateDraftSource(previewPoint);
  }

  // --- Extension d'une ligne déjà existante, en cours d'édition (voir `ExtendingLineState`) ---

  /**
   * Démarre une session d'extension depuis l'extrémité `end` de la ligne actuellement éditée —
   * appelé par `handleShapeDblClick` sur double-clic d'un sommet d'extrémité. `points[0]` est le
   * sommet d'ancrage (déjà existant, pas un nouveau sommet) : le premier clic réel de l'utilisateur
   * posera `points[1]`.
   */
  private startExtendLine(end: "start" | "end"): void {
    if (!this.editingState || !this.map) return;
    const anchor = end === "end" ? this.editingState.points[this.editingState.points.length - 1]! : this.editingState.points[0]!;
    this.extendingLineState = {
      shapeId: this.editingState.shapeId,
      end,
      points: [anchor],
      closeHoverActive: false,
      skipSave: this.editingState.skipSave,
      skipHistory: this.editingState.skipHistory,
      skipConfirm: this.editingState.skipConfirm,
    };
    this.updateDraftSource();
    this.map.getCanvas().style.cursor = "crosshair";
  }

  /** Symétrique de `handleLineDrawingClick`, mais opère sur `extendingLineState` — chaque clic
   * ajoute un point, sauf près du dernier posé (`isNearLastPoint`) qui fusionne la session dans
   * `editingState.points` (voir `finalizeExtendLine`) plutôt que de créer une nouvelle forme. */
  private handleExtendLineClick(event: MapMouseEvent): void {
    if (!this.extendingLineState || !this.map) return;

    const { points, shapeId } = this.extendingLineState;

    if (this.isNearLastPoint(points, event.point)) {
      this.finalizeExtendLine();
      return;
    }

    points.push(this.draftVertexLngLat(event, points, shapeId));
    this.extendingLineState.closeHoverActive = false;
    this.updateDraftSource();
  }

  /** Symétrique de `handleLineDrawingMove`, pour `extendingLineState`. */
  private handleExtendLineMove(event: MapMouseEvent): void {
    if (!this.extendingLineState) return;

    const { points, shapeId } = this.extendingLineState;
    const closeHoverActive = this.isNearLastPoint(points, event.point);
    this.extendingLineState.closeHoverActive = closeHoverActive;

    const previewPoint = closeHoverActive
      ? points[points.length - 1]
      : this.draftVertexLngLat(event, points, shapeId);
    this.updateDraftSource(previewPoint);
  }

  /**
   * Fusionne les sommets posés pendant l'extension dans `editingState.points` — l'ancrage
   * (`points[0]`, déjà présent sur la ligne) est exclu de la fusion, seuls les NOUVEAUX sommets sont
   * ajoutés. Prolonger depuis le premier sommet (`end: "start"`) les ajoute dans l'ordre INVERSE de
   * leur pose (le dernier posé, le plus loin de la ligne, doit se retrouver en tête du tableau) ;
   * prolonger depuis le dernier (`end: "end"`) les ajoute dans leur ordre de pose. Un seul
   * `shape:scaled` (undo absolu, comme `handleAddVertexInsert`) — toute l'extension forme une seule
   * entrée d'historique, quel que soit le nombre de points posés.
   */
  private finalizeExtendLine(): void {
    if (!this.extendingLineState || !this.editingState || !this.map) return;

    const { points, end } = this.extendingLineState;
    const newPoints = points.slice(1);
    const merged =
      end === "end" ? [...this.editingState.points, ...newPoints] : [...newPoints.slice().reverse(), ...this.editingState.points];

    this.editingState.points = merged;
    const { shapeId, skipSave, skipHistory, skipConfirm } = this.editingState;
    this.emit({ type: "shape:scaled", shapeId, coordinates: merged, skipSave, skipHistory, skipConfirm });

    this.extendingLineState = null;
    this.updateDraftSource();
    this.updateSnapGuidesSource(null);
    this.updateEditVisuals();
    this.updateShapesPreview();
    this.map.getCanvas().style.cursor = "";
  }

  /** Annule l'extension en cours sans toucher à `editingState` — appelé sur Échap
   * (voir `init()`), prioritaire sur la fermeture du mode édition lui-même. */
  private cancelExtendLine(): void {
    this.extendingLineState = null;
    this.updateDraftSource();
    this.updateSnapGuidesSource(null);
    if (this.map) this.map.getCanvas().style.cursor = "";
  }

  // --- Dessin de rectangle (2 clics : coin de départ puis coin opposé) ---

  private handleRectDrawingClick(event: MapMouseEvent): void {
    if (!this.rectDrawingState) return;

    if (!this.rectDrawingState.anchorPx) {
      this.rectDrawingState.anchorPx = this.resolveSnappedPointPx({ x: event.point.x, y: event.point.y }, null);
      if (!this.rectDrawingState.skipTutorial) {
        this.emit({ type: "drawing:tutorial-step", shapeKind: "rectangle", step: 2 });
      }
      return;
    }

    this.finalizeRectDrawing(this.rectCornerPx(event, this.rectDrawingState.anchorPx));
  }

  private handleRectDrawingMove(event: MapMouseEvent): void {
    if (!this.rectDrawingState?.anchorPx) return;
    const cornerPx = this.rectCornerPx(event, this.rectDrawingState.anchorPx);
    const corners = this.rectangleCornersFromScreen(this.rectDrawingState.anchorPx, cornerPx);
    this.updateRectDraftSource(corners);
    this.setMeasureLabel(rectangleMeasureText(corners), corners[2]);
  }

  private finalizeRectDrawing(secondCornerPx: PixelPoint): void {
    if (!this.rectDrawingState?.anchorPx) return;
    const corners = this.rectangleCornersFromScreen(this.rectDrawingState.anchorPx, secondCornerPx);
    const { skipSave, skipHistory, skipConfirm, skipTutorial } = this.rectDrawingState;

    const shape: Shape = {
      id: generateId(),
      type: "polygon",
      shapeKind: "rectangle",
      coordinates: [...corners, corners[0]!],
      rotation: 0,
      order: Date.now(),
      layer: DEFAULT_NEW_SHAPE_LAYER,
      style: this.defaultShapeTheme,
      themeId: this.defaultThemeId,
    };

    this.rectDrawingState = null;
    this.updateRectDraftSource([]);
    this.updateSnapGuidesSource(null);
    if (this.map) this.map.getCanvas().style.cursor = "";

    this.emit({ type: "shape:created", shape, skipSave, skipHistory, skipConfirm });
    this.emit({ type: "drawing:ended" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
  }

  /** Coins du rectangle en lng/lat, calculés en espace écran puis reprojetés (`unproject`). */
  private rectangleCornersFromScreen(aPx: PixelPoint, bPx: PixelPoint): LngLat[] {
    if (!this.map) return [];
    return rectangleCornersPx(aPx, bPx).map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
  }

  /** `hideVertices` — voir son commentaire sur `draftToGeoJSON`. */
  private updateRectDraftSource(corners: LngLat[], options?: { hideVertices?: boolean }): void {
    const source = this.map?.getSource(DRAFT_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(
        draftToGeoJSON(corners, { closed: true, hideVertices: options?.hideVertices }),
      );
    }
    // Fin ou annulation d'un tracé de rectangle ou de cercle : plus rien à mesurer.
    if (!corners.length) this.setMeasureLabel(null);
  }

  // --- Dessin de cercle (2 clics : centre puis point du bord — voir `CircleDrawingState`) ---

  private handleCircleDrawingClick(event: MapMouseEvent): void {
    if (!this.circleDrawingState) return;

    if (!this.circleDrawingState.anchorPx) {
      this.circleDrawingState.anchorPx = this.resolveSnappedPointPx(
        { x: event.point.x, y: event.point.y },
        null,
      );
      if (!this.circleDrawingState.skipTutorial) {
        this.emit({ type: "drawing:tutorial-step", shapeKind: "circle", step: 2 });
      }
      return;
    }

    this.finalizeCircleDrawing(this.resolveSnappedPointPx({ x: event.point.x, y: event.point.y }, null));
  }

  private handleCircleDrawingMove(event: MapMouseEvent): void {
    if (!this.circleDrawingState?.anchorPx) return;
    const edgePx = this.resolveSnappedPointPx({ x: event.point.x, y: event.point.y }, null);
    const points = this.circlePointsFromScreen(this.circleDrawingState.anchorPx, edgePx);
    this.updateRectDraftSource(points, { hideVertices: true });
    if (this.map) {
      const center = this.map.unproject(this.circleDrawingState.anchorPx as Point);
      const edge = this.map.unproject(edgePx as Point);
      this.setMeasureLabel(circleMeasureText([center.lng, center.lat], [edge.lng, edge.lat]), [edge.lng, edge.lat]);
    }
  }

  private finalizeCircleDrawing(edgePx: PixelPoint): void {
    if (!this.circleDrawingState?.anchorPx || !this.map) return;
    const centerLngLat = this.map.unproject(this.circleDrawingState.anchorPx as Point);
    const edgeLngLat = this.map.unproject(edgePx as Point);
    const { skipSave, skipHistory, skipConfirm, skipTutorial } = this.circleDrawingState;

    const shape: Shape = {
      id: generateId(),
      type: "polygon",
      shapeKind: "circle",
      // Exactement 2 points (voir `Shape.coordinates` : PAS un anneau fermé pour un cercle) — le
      // rendu développe cette paire en polygone régulier à l'affichage (`circleRingLngLat`), jamais
      // persisté ici.
      coordinates: [
        [centerLngLat.lng, centerLngLat.lat],
        [edgeLngLat.lng, edgeLngLat.lat],
      ],
      rotation: 0,
      order: Date.now(),
      layer: DEFAULT_NEW_SHAPE_LAYER,
      style: this.defaultShapeTheme,
      themeId: this.defaultThemeId,
    };

    this.circleDrawingState = null;
    this.updateRectDraftSource([]);
    this.updateSnapGuidesSource(null);
    if (this.map) this.map.getCanvas().style.cursor = "";

    this.emit({ type: "shape:created", shape, skipSave, skipHistory, skipConfirm });
    this.emit({ type: "drawing:ended" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
  }

  /** Sommets du polygone-cercle en lng/lat, calculés en espace écran puis reprojetés (`unproject`). */
  private circlePointsFromScreen(centerPx: PixelPoint, edgePx: PixelPoint): LngLat[] {
    if (!this.map) return [];
    const radiusPx = Math.hypot(edgePx.x - centerPx.x, edgePx.y - centerPx.y);
    return circlePointsPx(centerPx, radiusPx).map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
  }

  // --- Dessin d'image (2 clics, ratio contraint — voir `ImageDrawingState`) ---

  private handleImageDrawingClick(event: MapMouseEvent): void {
    if (!this.imageDrawingState) return;

    if (!this.imageDrawingState.anchorPx) {
      this.imageDrawingState.anchorPx = this.resolveSnappedPointPx({ x: event.point.x, y: event.point.y }, null);
      if (!this.imageDrawingState.skipTutorial) {
        this.emit({ type: "drawing:tutorial-step", shapeKind: "image", step: 2 });
      }
      return;
    }

    this.finalizeImageDrawing({ x: event.point.x, y: event.point.y });
  }

  private handleImageDrawingMove(event: MapMouseEvent): void {
    if (!this.imageDrawingState?.anchorPx) return;
    const { anchorPx, ratio, src, width, height, scale, opacity, rotation, svg } = this.imageDrawingState;
    const corners = this.imageCornersFromScreen(anchorPx, { x: event.point.x, y: event.point.y }, ratio);
    this.updateRectDraftSource(corners);
    // Aperçu live de l'image RÉELLE (pas seulement le rectangle en pointillés du dessin de
    // rectangle) — `repositionShapeImageDuringEdit` crée l'overlay/layer au premier appel puis le
    // repositionne, exactement comme pour une forme-image réelle en cours d'édition (voir son
    // commentaire) : pas besoin d'ajouter cette forme provisoire à `this.lastShapes`/au store.
    this.repositionShapeImageDuringEdit({
      id: IMAGE_DRAWING_DRAFT_ID,
      type: "polygon",
      shapeKind: "image",
      coordinates: [...corners, corners[0]!],
      rotation: 0,
      image: { src, width, height, scale, opacity, rotation, svg },
    });
  }

  private finalizeImageDrawing(secondCornerPx: PixelPoint): void {
    if (!this.imageDrawingState?.anchorPx) return;
    const {
      anchorPx,
      ratio,
      src,
      width,
      height,
      scale,
      opacity,
      rotation,
      svg,
      skipSave,
      skipHistory,
      skipConfirm,
      skipTutorial,
    } = this.imageDrawingState;
    const corners = this.imageCornersFromScreen(anchorPx, secondCornerPx, ratio);

    const shape: Shape = {
      id: generateId(),
      type: "polygon",
      shapeKind: "image",
      coordinates: [...corners, corners[0]!],
      rotation: 0,
      order: Date.now(),
      layer: DEFAULT_NEW_SHAPE_LAYER,
      style: this.defaultShapeTheme,
      themeId: this.defaultThemeId,
      image: { src, width, height, scale, opacity, rotation, svg },
      walkable: true,
    };

    this.imageDrawingState = null;
    this.updateRectDraftSource([]);
    this.updateSnapGuidesSource(null);
    this.removeImageDrawingDraftPreview();
    if (this.map) this.map.getCanvas().style.cursor = "";

    this.emit({ type: "shape:created", shape, skipSave, skipHistory, skipConfirm });
    this.emit({ type: "drawing:ended" });
    if (!skipTutorial) this.emit({ type: "drawing:tutorial-ended" });
  }

  /** Démonte l'aperçu live de `handleImageDrawingMove`, qu'il ait pris la forme d'un `<img>` SVG ou
   * d'un layer raster (les deux sont no-op si rien n'est monté sous cet id) — appelé à la fin de
   * toute session de dessin d'image, terminée (`finalizeImageDrawing`) ou interrompue
   * (`endActiveDrawingSession`, donc aussi `cancelDrawing`/Échap/changement de mode). */
  private removeImageDrawingDraftPreview(): void {
    this.removeShapeImageOverlay(IMAGE_DRAWING_DRAFT_ID);
    this.removeRasterShapeImage(IMAGE_DRAWING_DRAFT_ID);
  }

  /**
   * Coins (lng/lat) d'un rectangle ancré à `anchorPx`, dont le coin opposé est déduit de `cursorPx`
   * SOUS CONTRAINTE de `ratio` (largeur/hauteur de l'image) plutôt que suivre librement le curseur
   * (contrairement à `rectangleCornersFromScreen`, utilisé par le rectangle libre) — la distance
   * horizontale au curseur pilote la largeur, la hauteur en est déduite par `ratio`, chacune dans
   * la direction (signe) du cadran du curseur par rapport à `anchorPx`.
   */
  private imageCornersFromScreen(anchorPx: PixelPoint, cursorPx: PixelPoint, ratio: number): LngLat[] {
    if (!this.map) return [];
    const dx = cursorPx.x - anchorPx.x;
    const dy = cursorPx.y - anchorPx.y;
    const width = Math.max(Math.abs(dx), 1);
    const height = width / ratio;
    const oppositePx: PixelPoint = {
      x: anchorPx.x + (dx < 0 ? -width : width),
      y: anchorPx.y + (dy < 0 ? -height : height),
    };
    return rectangleCornersPx(anchorPx, oppositePx).map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
  }

  // --- Déplacement de sommet ---

  private handleVertexDragMove(event: MapMouseEvent): void {
    if (this.draggingVertexIndex == null || !this.editingState) return;

    if (this.vertexResizingState) {
      this.applyVertexResize(event);
      return;
    }

    // Exclut la forme éditée de ses propres candidats : s'accrocher sur un autre sommet du même
    // polygone risquerait d'effondrer une arête — l'accroche vise une *autre* forme.
    this.editingState.points[this.draggingVertexIndex] = this.resolveSnappedLngLat(
      { x: event.point.x, y: event.point.y },
      this.editingState.shapeId,
    );
    this.updateEditVisuals();
    this.updateShapesPreview();
  }

  /**
   * Redimensionnement contraint d'un rectangle/image par drag de sommet — même calcul que
   * `handleSelectionResizeMove` (`computeSelectionResizeScale`/`applySelectionResizeScale`), mais
   * dans le repère de la forme éditée (`VertexResizingState`) plutôt que celui de la sélection de
   * groupe. `lockRatio` (forme "image") force `scaleY = scaleX` : la largeur pilote, la hauteur
   * suit pour garder le ratio courant de la forme (voir `VertexResizingState`).
   */
  private applyVertexResize(event: MapMouseEvent): void {
    if (!this.vertexResizingState || !this.editingState || !this.map) return;
    const { cornerIndex, originalCornersPx, rotation, lockRatio } = this.vertexResizingState;
    // Accroche le coin déplacé sur les sommets/arêtes des AUTRES formes (même mécanisme que
    // `handleVertexDragMove` pour un polygone libre) — exclut la forme éditée pour la même raison.
    const cursorPx = this.resolveSnappedPointPx(
      { x: event.point.x, y: event.point.y },
      this.editingState.shapeId,
    );
    const scale = this.computeSelectionResizeScale(cornerIndex, originalCornersPx, rotation, cursorPx);
    if (!scale) return;

    const scaleY = lockRatio ? scale.scaleX : scale.scaleY;
    const newCornersPx = this.applySelectionResizeScale(
      originalCornersPx,
      scale.centerPx,
      scale.anchorPx,
      rotation,
      scale.scaleX,
      scaleY,
    );
    this.editingState.points = newCornersPx.map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
    this.updateEditVisuals();
    this.updateShapesPreview();
  }

  /** Miroir de `applyVertexResize` ci-dessus, pour un côté (voir `EdgeResizingState`) au lieu d'un
   * coin — même flux, `computeEdgeResizeScale` remplace `computeSelectionResizeScale`, pas de
   * `lockRatio` (un côté n'a jamais de ratio à préserver, cette poignée n'existe que pour les
   * rectangles, jamais les images — voir le `mousedown` de `EDGE_RESIZE_LAYER_ID`). */
  private applyEdgeResize(event: MapMouseEvent): void {
    if (!this.edgeResizingState || !this.editingState || !this.map) return;
    const { edgeIndex, originalCornersPx, rotation } = this.edgeResizingState;
    const cursorPx = this.resolveSnappedPointPx(
      { x: event.point.x, y: event.point.y },
      this.editingState.shapeId,
    );
    const scale = this.computeEdgeResizeScale(edgeIndex, originalCornersPx, rotation, cursorPx);
    if (!scale) return;

    const newCornersPx = this.applySelectionResizeScale(
      originalCornersPx,
      scale.centerPx,
      scale.anchorPx,
      rotation,
      scale.scaleX,
      scale.scaleY,
    );
    this.editingState.points = newCornersPx.map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
    this.updateEditVisuals();
    this.updateShapesPreview();
  }

  /**
   * Garantit que le curseur directionnel d'une barre de redimensionnement de côté reste affiché
   * tant que le curseur la survole réellement, même si un `mouseenter` d'une AUTRE couche (le
   * remplissage/contour de la forme, dont le `mouseenter` peut se déclencher sur ce même point et
   * poser `cursor: "move"`) s'est exécuté plus tôt dans le même tick `mousemove` — voir l'appel en
   * toute fin du dispatcher `mousemove` central. Sans ça, l'ordre d'enregistrement des listeners
   * (celui de `EDGE_RESIZE_HIT_LAYER_ID` puis celui, plus tardif, du remplissage) peut laisser le
   * remplissage "avoir le dernier mot" sur le curseur pour ce point précis, sans jamais affecter la
   * détection du `mousedown` lui-même (qui reste correcte) — d'où le symptôme observé : le
   * redimensionnement fonctionne, mais l'icône affichée ne change jamais. Ne fait rien tant qu'un
   * autre geste d'édition est en cours (le curseur appartient alors à ce geste-là).
   */
  private updateHandleHoverCursor(event: MapMouseEvent): void {
    if (
      !this.map ||
      !this.editingState ||
      this.edgeResizingState ||
      this.draggingVertexIndex != null ||
      this.rotatingState ||
      this.movingState
    ) {
      return;
    }
    // Même problème, et même correctif, pour les sommets et la poignée de rotation : par priorité,
    // celle des `mousedown` (rotation, puis sommet, puis côté).
    const at = (layerId: string) =>
      this.map!.getLayer(layerId) ? this.map!.queryRenderedFeatures(event.point as Point, { layers: [layerId] })[0] : undefined;

    if (at(ROTATE_HANDLE_LAYER_ID)) {
      this.map.getCanvas().style.cursor = ROTATE_CURSOR;
      return;
    }
    const vertexIndex = at(EDIT_VERTICES_LAYER_ID)?.properties?.vertexIndex;
    if (vertexIndex != null) {
      this.map.getCanvas().style.cursor = this.vertexHoverCursor(Number(vertexIndex));
      return;
    }
    const edge = at(EDGE_RESIZE_HIT_LAYER_ID);
    if (!edge) return;
    const cursor = edge.properties?.cursor;
    this.map.getCanvas().style.cursor = typeof cursor === "string" ? cursor : "grab";
  }

  /**
   * À appeler dès qu'une poignée d'édition prend la main au `mousedown` : en coupant `dragPan`,
   * l'adapter laisse sinon au navigateur son geste par défaut, qui glisse-dépose la sélection laissée
   * par le double-clic d'entrée en édition — l'écran se fige sous une image fantôme.
   */
  private claimHandleGesture(event: MapMouseEvent): void {
    event.originalEvent.preventDefault();
    window.getSelection()?.removeAllRanges();
  }

  /** Coin d'un rectangle ou d'une image : il redimensionne (voir `VertexResizingState`), d'où un
   * curseur de redimensionnement ; tout autre sommet se déplace librement. */
  private vertexHoverCursor(vertexIndex: number): string {
    if (!this.map || !this.editingState) return "grab";
    const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    const resizes = (shape?.shapeKind === "rectangle" || shape?.shapeKind === "image") && this.editingState.points.length === 4;
    return resizes ? resizeCursorForCorner(this.editingState.points.map((p) => this.map!.project(p)), vertexIndex) : "grab";
  }

  private handleVertexDragEnd(): void {
    if (this.draggingVertexIndex == null || !this.editingState || !this.map) return;

    const shapeId = this.editingState.shapeId;
    const { skipSave, skipHistory, skipConfirm } = this.editingState;
    const originalPoints = this.draggingVertexOriginalPoints;
    // Un simple clic (mousedown+mouseup sans mousemove) laisse `editingState.points` strictement
    // inchangé — voir `handleVertexDragMove`/`applyVertexResize`, les seuls endroits qui le mutent.
    // Sans ce garde-fou, un clic sur un sommet en mode édition déclencherait quand même
    // `shape:vertex-moved`/`shape:scaled`, donc une `Command` no-op exécutée et un `onSave` inutile.
    const hasMoved = !originalPoints || !arePointsEqual(this.editingState.points, originalPoints);

    if (this.vertexResizingState) {
      const points = this.editingState.points;
      const shape = this.lastShapes.find((s) => s.id === shapeId);
      // Une ligne (`shapeKind: "line"`) reste OUVERTE — comme un cercle, mais pour une raison
      // différente (voir `Shape.coordinates`) : pas de point de clôture à ré-ajouter.
      const coordinates = shape?.shapeKind === "circle" || shape?.shapeKind === "line" ? points : [...points, points[0]!];

      const { cornerIndex } = this.vertexResizingState;
      this.vertexResizingState = null;
      this.draggingVertexIndex = null;
      this.draggingVertexOriginalPoints = null;
      this.map.dragPan.enable();
      this.map.getCanvas().style.cursor = resizeCursorForCorner(points.map((p) => this.map!.project(p)), cornerIndex);
      this.updateSnapGuidesSource(null);

      if (!hasMoved) return;

      // Réutilise `shape:scaled`/`ScaleShapeCommand` (undo absolu, voir son commentaire dans
      // `commands.ts`) plutôt que `shape:vertex-moved`/`MoveVertexCommand` : tous les sommets ont
      // bougé ensemble (pas un seul), exactement comme un redimensionnement de groupe à une seule
      // forme.
      this.emit({ type: "shape:scaled", shapeId, coordinates, skipSave, skipHistory, skipConfirm });
      return;
    }

    const vertexIndex = this.draggingVertexIndex;
    const position = this.editingState.points[vertexIndex]!;
    const points = this.editingState.points;
    const shape = this.lastShapes.find((s) => s.id === shapeId);

    this.draggingVertexIndex = null;
    this.draggingVertexOriginalPoints = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = "grab";
    this.updateSnapGuidesSource(null);

    if (!hasMoved) return;

    // Rejoindre les 2 extrémités d'une ligne (glisser l'une sur l'autre) la referme en polygone —
    // symétrique de `isNearLastPoint`/`isNearClosingPoint` (même rayon, `CLOSE_CLICK_RADIUS_PX`),
    // mais au RELÂCHEMENT d'un drag de sommet existant plutôt qu'au clic d'un tracé en cours. `>= 4`
    // points (pas 3) : l'extrémité déplacée fusionne avec l'autre (voir `unique` plus bas), il doit
    // rester au moins 3 sommets UNIQUES après fusion pour un polygone valide.
    const isEndpoint = vertexIndex === 0 || vertexIndex === points.length - 1;
    if (shape?.shapeKind === "line" && isEndpoint && points.length >= 4 && this.map) {
      const otherIndex = vertexIndex === 0 ? points.length - 1 : 0;
      const draggedPx = this.map.project(points[vertexIndex]!);
      const otherPx = this.map.project(points[otherIndex]!);
      if (Math.hypot(draggedPx.x - otherPx.x, draggedPx.y - otherPx.y) <= CLOSE_CLICK_RADIUS_PX) {
        // Retire le sommet déplacé (fusionné avec l'autre extrémité, dont la position propre — plus
        // précise que celle, approximative, du relâchement — devient le point de jonction) : un
        // anneau représente le même polygone quel que soit le sommet par lequel on le fait démarrer,
        // donc simplement retirer l'extrémité fusionnée (sans réordonner le reste) suffit.
        const unique = vertexIndex === 0 ? points.slice(1) : points.slice(0, -1);
        const coordinates = [...unique, unique[0]!];
        this.disableEditMode(shapeId);
        this.emit({
          type: "shape:kind-converted",
          shapeId,
          shapeKind: "polygon",
          coordinates,
          style: shape.style,
          skipSave,
          skipHistory,
          skipConfirm,
        });
        return;
      }
    }

    this.emit({ type: "shape:vertex-moved", shapeId, vertexIndex, position, skipSave, skipHistory, skipConfirm });
  }

  /** Miroir de la branche `vertexResizingState` de `handleVertexDragEnd` ci-dessus, pour un côté —
   * toujours un rectangle fermé (contrairement à `handleVertexDragEnd`, pas besoin de distinguer
   * cercle/ligne : `EdgeResizingState` n'existe que pour `shapeKind === "rectangle"`, voir le
   * `mousedown` de `EDGE_RESIZE_LAYER_ID`). */
  private handleEdgeResizeEnd(): void {
    if (!this.edgeResizingState || !this.editingState || !this.map) return;

    const shapeId = this.editingState.shapeId;
    const { skipSave, skipHistory, skipConfirm } = this.editingState;
    const { edgeIndex } = this.edgeResizingState;
    const hasMoved = !arePointsEqual(this.editingState.points, this.edgeResizingState.originalPoints);
    const points = this.editingState.points;
    const coordinates = [...points, points[0]!];

    // Ré-affiche le curseur directionnel de CETTE barre (recalculé sur la position finale) plutôt
    // qu'un `grab` générique — le curseur reste sous la souris juste après le relâchement, mieux vaut
    // qu'il continue à refléter la direction de redimensionnement réelle qu'un retour à une icône
    // de "saisie" libre qui n'a pas de sens pour ce geste.
    const endedCursor = this.computeEdgeResizeHandles(points)[edgeIndex]?.cursor ?? "";
    this.edgeResizingState = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = endedCursor;
    this.updateSnapGuidesSource(null);

    if (!hasMoved) return;

    this.emit({ type: "shape:scaled", shapeId, coordinates, skipSave, skipHistory, skipConfirm });
  }

  // --- Ajout de sommet au survol d'une arête ---
  //
  // Un polygone/une ligne en édition affiche un sommet fantôme qui suit la souris quand elle survole
  // une de ses arêtes (ADD_VERTEX_HOVER_LAYER_ID) — cliquer dessus l'insère comme un vrai sommet,
  // aussitôt modifiable comme n'importe quel autre (voir `handleAddVertexInsert`, qui bascule
  // directement dans le drag de sommet normal). Rectangle et cercle ne sont pas concernés : un
  // rectangle perdrait ses angles droits garantis (`VertexResizingState` suppose 4 sommets propres),
  // et un cercle n'a pas d'arête au sens polygonal (juste `[centre, point de bord]`, voir
  // `Shape.coordinates`).

  /**
   * Calcule/affiche `addVertexHoverState` à chaque `mousemove` — purement une question d'affichage
   * (aucune mutation de `editingState.points` ici, contrairement à `handleVertexDragMove`) : le point
   * le plus proche du curseur sur n'importe quelle arête de la forme éditée, ignoré s'il est trop
   * loin (`ADD_VERTEX_HOVER_RADIUS_PX`) ou trop près d'un sommet réel existant
   * (`ADD_VERTEX_NEAR_VERTEX_RADIUS_PX` — le curseur vise alors ce sommet-là, pas un nouveau).
   */
  private handleAddVertexHoverMove(event: MapMouseEvent): void {
    if (!this.map) return;

    const hide = () => {
      if (this.addVertexHoverState) {
        this.addVertexHoverState = null;
        this.updateAddVertexHoverSource();
      }
    };

    // Aucun sommet fantôme pendant un autre geste d'édition déjà en cours (drag de sommet/rotation/
    // déplacement de la forme) : les deux ne doivent jamais coexister.
    if (
      !this.editingState ||
      this.draggingVertexIndex != null ||
      this.rotatingState ||
      this.movingState ||
      this.vertexResizingState ||
      this.extendingLineState
    ) {
      hide();
      return;
    }

    const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    const isLine = shape?.shapeKind === "line";
    const isEligible = shape != null && (isLine || shape.shapeKind === "polygon" || shape.shapeKind == null);
    if (!isEligible) {
      hide();
      return;
    }

    const points = this.editingState.points;
    if (points.length < (isLine ? 2 : 3)) {
      hide();
      return;
    }

    const pointsPx = points.map((p) => this.map!.project(p));
    const cursorPx: PixelPoint = { x: event.point.x, y: event.point.y };

    // Le centre de la forme (poignée de déplacement, voir MOVE_SOURCE_ID) tombe souvent PILE sur une
    // arête — le cas le plus courant est une ligne à 2 sommets : son centroïde est alors exactement
    // le milieu du segment. Sans cette exclusion, survoler/cliquer la poignée déclenchait le sommet
    // fantôme à la place (ADD_VERTEX_HOVER_LAYER_ID, enregistré avant MOVE_HANDLE_LAYER_ID dans
    // `init()`) : au lieu d'attraper la poignée, un clic y insérait un nouveau sommet.
    const moveHandlePx = this.map.project(this.computeMoveHandleCenter(shape, points));
    if (Math.hypot(cursorPx.x - moveHandlePx.x, cursorPx.y - moveHandlePx.y) < MOVE_HANDLE_HOVER_EXCLUSION_RADIUS_PX) {
      hide();
      return;
    }

    // Une ligne reste OUVERTE (pas d'arête entre le dernier et le premier point) — mêmes bornes que
    // `nearestEditEdgePointPx` aurait si elle existait séparément, inlinées ici (seul appelant).
    const segmentCount = isLine ? pointsPx.length - 1 : pointsPx.length;

    let bestIndex = -1;
    let bestPointPx: PixelPoint | null = null;
    let bestDistance = Infinity;
    for (let i = 0; i < segmentCount; i++) {
      const a = pointsPx[i]!;
      const b = pointsPx[(i + 1) % pointsPx.length]!;
      const candidate = nearestPointOnSegment(cursorPx, a, b);
      const d = Math.hypot(cursorPx.x - candidate.x, cursorPx.y - candidate.y);
      if (d < bestDistance) {
        bestDistance = d;
        bestPointPx = candidate;
        bestIndex = i;
      }
    }

    if (!bestPointPx || bestDistance > ADD_VERTEX_HOVER_RADIUS_PX) {
      hide();
      return;
    }

    const a = pointsPx[bestIndex]!;
    const b = pointsPx[(bestIndex + 1) % pointsPx.length]!;
    const distanceToA = Math.hypot(bestPointPx.x - a.x, bestPointPx.y - a.y);
    const distanceToB = Math.hypot(bestPointPx.x - b.x, bestPointPx.y - b.y);
    if (distanceToA < ADD_VERTEX_NEAR_VERTEX_RADIUS_PX || distanceToB < ADD_VERTEX_NEAR_VERTEX_RADIUS_PX) {
      hide();
      return;
    }

    const ll = this.map.unproject(bestPointPx as Point);
    this.addVertexHoverState = {
      shapeId: this.editingState.shapeId,
      edgeIndex: bestIndex,
      position: [ll.lng, ll.lat],
    };
    this.updateAddVertexHoverSource();
  }

  private updateAddVertexHoverSource(): void {
    const source = this.map?.getSource(ADD_VERTEX_HOVER_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(
        addVertexHoverToGeoJSON(this.addVertexHoverState?.position ?? null),
      );
    }
  }

  /**
   * `mousedown` sur le sommet fantôme (ADD_VERTEX_HOVER_LAYER_ID) : insère un vrai sommet à
   * `edgeIndex + 1` dans `editingState.points`, l'enregistre aussitôt (réutilise `shape:scaled`/
   * `ScaleShapeCommand` — même raisonnement que `VertexResizingState` plus haut : le tableau de
   * coordonnées est remplacé en bloc, `undo()` absolu, contrairement à `shape:vertex-moved`/
   * `MoveVertexCommand` qui ne conviendrait pas ici puisque tous les sommets suivants changent
   * d'index), PUIS bascule directement dans le drag de sommet normal (`draggingVertexIndex`) pour que
   * ce nouveau sommet reste tirable sans relâcher le clic — un simple clic sans glisser ensuite ne
   * ré-émet rien de plus (voir le garde-fou `hasMoved` de `handleVertexDragEnd`).
   */
  private handleAddVertexInsert(): void {
    if (!this.addVertexHoverState || !this.editingState || !this.map) return;
    if (this.addVertexHoverState.shapeId !== this.editingState.shapeId) return;
    const shape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    if (!shape) return;

    const { edgeIndex, position } = this.addVertexHoverState;
    const vertexIndex = edgeIndex + 1;
    const points = [...this.editingState.points];
    points.splice(vertexIndex, 0, position);
    this.editingState.points = points;

    const { shapeId, skipSave, skipHistory, skipConfirm } = this.editingState;
    const coordinates = shape.shapeKind === "line" ? points : [...points, points[0]!];
    this.emit({ type: "shape:scaled", shapeId, coordinates, skipSave, skipHistory, skipConfirm });

    this.addVertexHoverState = null;
    this.updateAddVertexHoverSource();
    this.updateEditVisuals();
    this.updateShapesPreview();

    this.draggingVertexIndex = vertexIndex;
    this.draggingVertexOriginalPoints = [...points];
    this.map.dragPan.disable();
    this.map.getCanvas().style.cursor = "grabbing";
  }

  /** Ligne/polygone (ou `shapeKind` absent, défaut polygone) uniquement — même éligibilité que
   * `handleAddVertexHoverMove` : un rectangle perdrait ses 4 coins propres (`VertexResizingState`),
   * un cercle n'a que 2 points sans notion de sommet "au sens polygonal". */
  private isVertexEditEligible(shape: Shape): boolean {
    return shape.shapeKind === "line" || shape.shapeKind === "polygon" || shape.shapeKind == null;
  }

  removeShapeVertex(shapeId: string, vertexIndex: number): void {
    if (!this.editingState || this.editingState.shapeId !== shapeId || !this.map) return;
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (!shape || !this.isVertexEditEligible(shape)) return;

    const points = [...this.editingState.points];
    if (vertexIndex < 0 || vertexIndex >= points.length) return;
    points.splice(vertexIndex, 1);
    this.editingState.points = points;

    const { skipSave, skipHistory, skipConfirm } = this.editingState;
    const coordinates = shape.shapeKind === "line" ? points : [...points, points[0]!];
    this.emit({ type: "shape:scaled", shapeId, coordinates, skipSave, skipHistory, skipConfirm });

    this.addVertexHoverState = null;
    this.updateAddVertexHoverSource();
    this.updateEditVisuals();
    this.updateShapesPreview();
  }

  /**
   * Ligne : coupe en 2 formes indépendantes au sommet `vertexIndex` — jamais une extrémité (rien à
   * couper d'un côté). `firstCoordinates`/`newShape.coordinates` se touchent au sommet coupé (inclus
   * des deux côtés), voir `"shape:line-split"`.
   *
   * Polygone : ouvre l'anneau AU sommet coupé, qui devient les 2 extrémités de la ligne résultante —
   * fait tourner le tableau de sommets pour commencer à `vertexIndex`, puis le referme en répétant ce
   * même sommet en dernière position (voir `"shape:kind-converted"`).
   *
   * Referme le mode édition dans les deux cas (`disableEditMode`) : la forme change de structure/
   * nature, une session d'édition ouverte sur ses anciens sommets n'a plus de sens après.
   */
  cutShapeAtVertex(shapeId: string, vertexIndex: number): void {
    if (!this.editingState || this.editingState.shapeId !== shapeId || !this.map) return;
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (!shape || !this.isVertexEditEligible(shape)) return;

    const points = this.editingState.points;
    const { skipSave, skipHistory, skipConfirm } = this.editingState;

    if (shape.shapeKind === "line") {
      if (vertexIndex <= 0 || vertexIndex >= points.length - 1) return;
      const firstCoordinates = points.slice(0, vertexIndex + 1);
      const newShape: Shape = { ...shape, id: generateId(), coordinates: points.slice(vertexIndex), order: Date.now() };
      this.disableEditMode(shapeId);
      this.emit({ type: "shape:line-split", shapeId, firstCoordinates, newShape, skipSave, skipHistory, skipConfirm });
      return;
    }

    const n = points.length;
    const rotated: LngLat[] = [];
    for (let i = 0; i < n; i++) rotated.push(points[(vertexIndex + i) % n]!);
    rotated.push(points[vertexIndex]!);
    const style = lineShapeStyle(shape.style);
    this.disableEditMode(shapeId);
    this.emit({ type: "shape:kind-converted", shapeId, shapeKind: "line", coordinates: rotated, style, skipSave, skipHistory, skipConfirm });
  }

  /** Équivalent programmatique du double-clic sur une extrémité de ligne (voir
   * `handleShapeDblClick`) — exposé pour le menu contextuel "Continuer la ligne", voir son
   * commentaire dans `adapters/types.ts`. */
  startExtendLineAtVertex(shapeId: string, vertexIndex: number): void {
    if (!this.editingState || this.editingState.shapeId !== shapeId || this.extendingLineState) return;
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    if (shape?.shapeKind !== "line") return;
    const lastIndex = this.editingState.points.length - 1;
    if (vertexIndex !== 0 && vertexIndex !== lastIndex) return;
    this.startExtendLine(vertexIndex === 0 ? "start" : "end");
  }

  // --- Rotation (BESOINS.md §5) ---

  private handleRotateDragStart(event: MapLayerMouseEvent): void {
    if (!this.editingState || !this.map || this.extendingLineState) return;

    const currentShape = this.lastShapes.find((s) => s.id === this.editingState!.shapeId);
    // Un cercle n'a pas de poignée de rotation (voir `updateEditVisuals`) : ce garde-fou ne devrait
    // normalement jamais se déclencher (aucune poignée à cliquer), gardé par prudence.
    if (currentShape?.shapeKind === "circle") return;

    this.claimHandleGesture(event);
    const center = centroid(this.editingState.points);
    const centerPx = this.map.project(center);
    const originalPointsPx = this.editingState.points.map((p) => this.map!.project(p));

    this.rotatingState = {
      shapeId: this.editingState.shapeId,
      centerPx,
      originalPointsPx,
      startAngleDeg: angleDeg(centerPx, event.point),
      startRotation: currentShape?.rotation ?? 0,
      currentRotation: currentShape?.rotation ?? 0,
    };
    this.map.dragPan.disable();
    this.map.getCanvas().style.cursor = ROTATE_CURSOR;
  }

  private handleRotateDragMove(event: MapMouseEvent): void {
    if (!this.rotatingState || !this.editingState || !this.map) return;

    const { centerPx, originalPointsPx, startAngleDeg, startRotation } = this.rotatingState;
    const deltaDeg = angleDeg(centerPx, event.point) - startAngleDeg;
    const rad = (deltaDeg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const rotatedPx: PixelPoint[] = originalPointsPx.map((p) => {
      const dx = p.x - centerPx.x;
      const dy = p.y - centerPx.y;
      return { x: centerPx.x + dx * cos - dy * sin, y: centerPx.y + dx * sin + dy * cos };
    });

    this.editingState.points = rotatedPx.map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
    this.rotatingState.currentRotation = startRotation + deltaDeg;

    this.updateEditVisuals();
    this.updateShapesPreview();
  }

  private handleRotateDragEnd(): void {
    if (!this.rotatingState || !this.editingState || !this.map) return;

    const { shapeId, currentRotation, startRotation } = this.rotatingState;
    const points = this.editingState.points;
    // Jamais un cercle ici (voir `handleRotateDragStart`, aucune poignée à cliquer pour lui) — mais
    // une ligne (`shapeKind: "line"`) A une poignée de rotation (contrairement au cercle) et reste
    // OUVERTE : pas de point de clôture à ré-ajouter pour elle.
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    const coordinates = shape?.shapeKind === "line" ? points : [...points, points[0]!];
    const { skipSave, skipHistory, skipConfirm } = this.editingState;
    // Un simple clic sur la poignée (mousedown+mouseup sans mousemove) laisse `currentRotation`
    // égal à `startRotation` — voir `handleRotateDragMove`, seul endroit qui le change. Sans ce
    // garde-fou, un tel clic déclencherait quand même `shape:rotated`, donc une `RotateShapeCommand`
    // no-op et un `onSave` inutile.
    const hasRotated = currentRotation !== startRotation;

    this.rotatingState = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = ROTATE_CURSOR;

    if (!hasRotated) return;

    this.emit({
      type: "shape:rotated",
      shapeId,
      rotation: normalizeDeg(currentRotation),
      coordinates,
      skipSave,
      skipHistory,
      skipConfirm,
    });
  }

  // --- Rotation de la carte (clic-droit-glisser, façon OpenLayers) ---

  /** Bouton droit : rotation dans les deux modes. Ctrl+bouton gauche : seulement hors `editMode`
   * (réservé au rectangle de sélection sinon, voir le `mousedown` ci-dessus dans `init()`).
   *
   * Avec `allowPitch: true`, ce handler maison (bearing seul) cède systématiquement la main au
   * `dragRotate` NATIF de MapLibre (bearing + pitch), réactivé dans ce cas dès `init()` — voir son
   * commentaire. Le seul cas encore intercepté à la main est Ctrl+clic-gauche-glisser en `editMode`
   * (réservé au rectangle de sélection multiple), géré ailleurs en désactivant/réactivant
   * ponctuellement `dragRotate` natif autour de ce seul geste. */
  private isMapRotateTrigger(native: MouseEvent): boolean {
    if (this.allowPitch) return false;
    if (native.button === 2) return true;
    return native.button === 0 && (native.ctrlKey || native.metaKey) && !this.editMode;
  }

  private handleMapRotateDragStart(event: MapMouseEvent): void {
    if (!this.map || !this.isMapRotateTrigger(event.originalEvent)) return;
    // Ne démarre pas par-dessus un autre geste déjà en cours (dessin, édition, sélection...) — même
    // liste de garde que celle utilisée par le `mousedown` du rectangle de sélection ci-dessus.
    if (
      this.drawingState ||
      this.rectDrawingState ||
      this.imageDrawingState ||
      this.circleDrawingState ||
      this.editingState ||
      this.selectionState
    )
      return;

    const container = this.map.getContainer();
    const centerPx: PixelPoint = { x: container.clientWidth / 2, y: container.clientHeight / 2 };
    this.mapRotateState = {
      centerPx,
      startAngleDeg: angleDeg(centerPx, event.point),
      startBearing: this.map.getBearing(),
    };
    this.map.dragPan.disable();
  }

  private handleMapRotateDragMove(event: MapMouseEvent): void {
    if (!this.mapRotateState || !this.map) return;

    const { centerPx, startAngleDeg, startBearing } = this.mapRotateState;
    // `angleDeg` mesure l'angle du curseur autour du centre de l'écran, dans le sens horaire (voir
    // sa définition). Le contenu de la carte doit tourner de ce même angle, dans le même sens, pour
    // que le point sous le curseur au début du geste y reste (comme si on saisissait la carte pour
    // la faire tourner, à la façon d'OpenLayers) — contrairement au `dragRotate` natif de MapLibre
    // (voir le commentaire sur `dragRotate: false` dans `init()`), purement linéaire en pixels et
    // sans cette propriété. `bearing` de MapLibre est orienté dans le sens inverse d'un angle écran
    // classique (`bearing` croissant tourne la caméra dans le sens horaire, ce qui fait tourner le
    // contenu affiché dans le sens ANTI-horaire) : on soustrait donc l'angle balayé par le curseur,
    // on ne l'ajoute pas.
    const deltaDeg = angleDeg(centerPx, event.point) - startAngleDeg;
    this.map.setBearing(startBearing - deltaDeg);
  }

  private handleMapRotateDragEnd(): void {
    if (!this.mapRotateState || !this.map) return;
    this.mapRotateState = null;
    this.map.dragPan.enable();
  }

  private flushPendingMouseMove(): void {
    if (this.pendingMouseMoveFrame != null) cancelAnimationFrame(this.pendingMouseMoveFrame);
    this.pendingMouseMoveFrame = null;
    const event = this.pendingMouseMove;
    this.pendingMouseMove = null;
    if (!event) return;
    this.handleVertexDragMove(event);
    this.applyEdgeResize(event);
    this.handleRotateDragMove(event);
    this.handleMapRotateDragMove(event);
    this.handleMoveShapeDragMove(event);
    this.handleAddVertexHoverMove(event);
    this.handleRectDrawingMove(event);
    this.handleCircleDrawingMove(event);
    this.handleImageDrawingMove(event);
    this.handlePolygonDrawingMove(event);
    this.handleLineDrawingMove(event);
    this.handleExtendLineMove(event);
    this.handleSelectionDrawMove(event);
    this.handleSelectionResizeMove(event);
    this.handleSelectionRotateMove(event);
    this.handleSelectionMoveMove(event);
    // Voir "map:pointer-moved" (MapAdapterEvent) : `event.lngLat` est déjà calculé par MapLibre
    // pour ce même évènement natif, aucun travail supplémentaire (pas de queryRenderedFeatures).
    this.emit({ type: "map:pointer-moved", position: [event.lngLat.lng, event.lngLat.lat] });
    // Appelé EN DERNIER, volontairement : un `mouseenter` sur une autre couche (le remplissage de
    // la forme, qui peut légitimement partager le même point que la barre) peut avoir regagné le
    // curseur plus haut dans ce même tick — voir son commentaire pour pourquoi ce n'est pas fiable
    // à corriger dans l'autre sens.
    this.updateHandleHoverCursor(event);
  }

  // --- Déplacement de la forme entière (translation) ---

  private unprojectLngLat(point: PixelPoint): LngLat {
    const lngLat = this.map!.unproject(point as Point);
    return [lngLat.lng, lngLat.lat];
  }

  private handleMoveShapeDragStart(event: MapLayerMouseEvent): void {
    if (!this.editingState || !this.map || this.extendingLineState) return;
    // Priorité aux poignées : un clic pile sur un sommet, une barre de redimensionnement de côté ou
    // la poignée de rotation ne doit pas aussi démarrer une translation de la forme entière (les
    // mousedown peuvent se déclencher pour le même clic, la forme/son contour étant sous ces
    // poignées).
    if (this.draggingVertexIndex != null || this.edgeResizingState || this.rotatingState) return;

    this.movingState = {
      shapeId: this.editingState.shapeId,
      startCursorPx: { x: event.point.x, y: event.point.y },
      originalPoints: [...this.editingState.points],
    };
    this.claimHandleGesture(event);
    this.map.dragPan.disable();
    this.map.getCanvas().style.cursor = "grabbing";
  }

  private handleMoveShapeDragMove(event: MapMouseEvent): void {
    if (!this.movingState || !this.editingState || !this.map) return;

    const { startCursorPx, originalPoints } = this.movingState;
    const translateTo = (cursorPx: PixelPoint) =>
      translateOnGround(originalPoints, this.unprojectLngLat(startCursorPx), this.unprojectLngLat(cursorPx));

    // Translation au sol, pas à l'écran : en vue inclinée, un même écart en pixels ne couvre pas la
    // même distance au premier plan et au fond, et déformerait la forme.
    const translatedPointsPx = translateTo(event.point).map((point) => this.map!.project(point));

    // Puis un éventuel correctif d'accroche (voir `resolveShapeMoveSnapDelta`) sur les sommets de
    // la forme elle-même, pas sur le curseur — la guideline suit alors le bord de la forme qui
    // s'aligne réellement.
    const snapDelta = this.resolveShapeMoveSnapDelta(translatedPointsPx, this.editingState.shapeId);

    this.editingState.points = translateTo({ x: event.point.x + snapDelta.x, y: event.point.y + snapDelta.y });

    this.updateEditVisuals();
    this.updateShapesPreview();
  }

  private handleMoveShapeDragEnd(): void {
    if (!this.movingState || !this.editingState || !this.map) return;

    const { shapeId, originalPoints } = this.movingState;
    const points = this.editingState.points;
    const shape = this.lastShapes.find((s) => s.id === shapeId);
    const coordinates = shape?.shapeKind === "circle" || shape?.shapeKind === "line" ? points : [...points, points[0]!];
    const { skipSave, skipHistory, skipConfirm } = this.editingState;
    // Un simple clic (mousedown+mouseup sans mousemove) laisse `editingState.points` strictement
    // inchangé — voir `handleMoveShapeDragMove`, seul endroit qui le mute. Sans ce garde-fou, un clic
    // sur le corps de la forme en mode édition déclencherait quand même `shape:moved`, donc une
    // `MoveShapeCommand` no-op et un `onSave` inutile.
    const hasMoved = !arePointsEqual(points, originalPoints);

    this.movingState = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = "move";
    this.updateSnapGuidesSource(null);

    if (!hasMoved) return;

    this.emit({ type: "shape:moved", shapeId, coordinates, skipSave, skipHistory, skipConfirm });
  }

  // --- Sélection multiple (BESOINS.md §13) ---

  /** Aperçu live du rectangle en cours de tracé (Ctrl+glisser) — même calcul géométrique que le
   * dessin de rectangle-forme (`rectangleCornersFromScreen`), mais alimente `SELECTION_SOURCE_ID`
   * (remplissage blanc, voir `updateSelectionFillSource`/`selectionDraftCorners`) plutôt que
   * `DRAFT_SOURCE_ID` (`colors.draft`, partagé avec le dessin de forme) : les deux tracés restent
   * mutuellement exclusifs mais n'ont pas la même couleur pendant le glissé, voir `setSelectionInteracting`. */
  /** Sur le CENTRE de chaque forme (pas toute sa surface) : une forme à cheval dedans/dehors compte
   * comme dehors, plutôt que de devoir décider d'un seuil de recouvrement arbitraire. Partagé entre
   * l'aperçu live pendant le tracé (`handleSelectionDrawMove`) et sa finalisation
   * (`handleSelectionDrawEnd`), qui doivent appliquer exactement le même critère. Une forme
   * verrouillée (`Shape.locked`) n'entre jamais dans une sélection multiple, comme elle n'ouvre déjà
   * jamais son menu contextuel (`handleShapeContextMenu`) — même principe étendu à ce geste. */
  private shapeIdsInsideCornersPx(cornersPx: PixelPoint[]): string[] {
    return this.lastShapes
      .filter((shape) => !shape.locked && isPointInConvexPolygonPx(this.map!.project(shapeCenter(shape)), cornersPx))
      .map((shape) => shape.id);
  }

  private handleSelectionDrawMove(event: MapMouseEvent): void {
    if (!this.selectionDrawState || !this.map) return;
    const corners = this.rectangleCornersFromScreen(this.selectionDrawState.anchorPx, {
      x: event.point.x,
      y: event.point.y,
    });
    this.selectionDraftCorners = corners;
    this.updateSelectionFillSource(corners);

    // Surbrillance live des formes actuellement à l'intérieur du rectangle en cours de tracé (avant
    // même que le Ctrl+glisser ne soit relâché) — même critère que `handleSelectionDrawEnd`, qui
    // applique la même sélection définitivement au relâchement.
    const cornersPx = corners.map((c) => this.map!.project(c));
    const shapeIds = this.shapeIdsInsideCornersPx(cornersPx);
    this.applySelectionFeatureState(shapeIds);

    // Émis en direct (pas seulement à `handleSelectionDrawEnd`) pour qu'un consommateur (ex.
    // `SelectionCount.tsx`) puisse refléter la composition de la sélection pendant le tracé, pas
    // seulement une fois le Ctrl+glisser relâché — dédupliqué (`lastLiveSelectionShapeIds`) pour ne
    // pas réémettre à chaque `mousemove` tant que l'ensemble de formes concernées n'a pas changé.
    if (!this.sameShapeIds(shapeIds, this.lastLiveSelectionShapeIds)) {
      this.lastLiveSelectionShapeIds = shapeIds;
      this.emit({ type: "selection:changed", shapeIds, interacting: true });
    }
  }

  private sameShapeIds(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, index) => id === b[index]);
  }

  private handleSelectionDrawEnd(): void {
    if (!this.selectionDrawState || !this.map) return;
    const corners = this.selectionDraftCorners;

    this.selectionDrawState = null;
    this.selectionDraftCorners = [];
    this.map.dragPan.enable();
    if (this.allowPitch) this.map.dragRotate.enable();
    this.setSelectionInteracting(false);

    if (corners.length !== 4) {
      this.updateSelectionFillSource([]); // rectangle resté dégénéré : efface l'aperçu blanc
      this.applySelectionFeatureState([]); // efface la surbrillance live
      return;
    }
    const cornersPx = corners.map((c) => this.map!.project(c));
    const shapeIds = this.shapeIdsInsideCornersPx(cornersPx);
    if (shapeIds.length === 0) {
      this.updateSelectionFillSource([]); // rien sélectionné : pas de rectangle persistant
      this.applySelectionFeatureState([]);
      return;
    }

    this.selectionState = { corners, rotation: 0, shapeIds };
    this.applySelectionFeatureState(shapeIds);
    this.updateSelectionVisuals();
    this.emit({ type: "selection:changed", shapeIds });
  }

  /**
   * Facteurs d'échelle pour amener le coin `cornerIndex` en `cursorPx`, dans le repère LOCAL du
   * rectangle (aligné sur ses propres côtés, pas l'écran — défait sa rotation cumulée `rotation`).
   * Travailler dans ce repère est ce qui permet à un redimensionnement de rester un vrai
   * redimensionnement (pas un cisaillement) même après avoir tourné la sélection — même principe
   * que la rotation de forme en espace écran (voir `handleRotateDragMove`). `null` si le rectangle
   * de départ est dégénéré (coin confondu avec son opposé, division par ~0 sinon).
   */
  private computeSelectionResizeScale(
    cornerIndex: number,
    originalCornersPx: PixelPoint[],
    rotation: number,
    cursorPx: PixelPoint,
  ): { anchorPx: PixelPoint; centerPx: PixelPoint; scaleX: number; scaleY: number } | null {
    const centerPx = centroidPx(originalCornersPx);
    const anchorPx = originalCornersPx[(cornerIndex + 2) % 4]!;
    const toLocal = (p: PixelPoint) => rotatePointPx(p, centerPx, -rotation);
    const localOrigin = toLocal(anchorPx);
    const localDraggedOriginal = toLocal(originalCornersPx[cornerIndex]!);
    const localCursor = toLocal(cursorPx);

    const width = localDraggedOriginal.x - localOrigin.x;
    const height = localDraggedOriginal.y - localOrigin.y;
    if (Math.abs(width) < 1 || Math.abs(height) < 1) return null;

    return {
      anchorPx,
      centerPx,
      scaleX: (localCursor.x - localOrigin.x) / width,
      scaleY: (localCursor.y - localOrigin.y) / height,
    };
  }

  /** Applique le résultat de `computeSelectionResizeScale`/`computeEdgeResizeScale` à une liste de
   * points écran quelconque — les coins du rectangle pour l'aperçu live, ou les sommets d'une forme
   * à la finalisation. `anchorPx` est le point fixe de la mise à l'échelle (le coin diagonalement
   * opposé pour un redimensionnement de coin, le milieu de l'arête opposée pour un côté). */
  private applySelectionResizeScale(
    points: PixelPoint[],
    centerPx: PixelPoint,
    anchorPx: PixelPoint,
    rotation: number,
    scaleX: number,
    scaleY: number,
  ): PixelPoint[] {
    const toLocal = (p: PixelPoint) => rotatePointPx(p, centerPx, -rotation);
    const localOrigin = toLocal(anchorPx);
    return points.map((p) => {
      const local = toLocal(p);
      const scaledLocal = {
        x: localOrigin.x + (local.x - localOrigin.x) * scaleX,
        y: localOrigin.y + (local.y - localOrigin.y) * scaleY,
      };
      return rotatePointPx(scaledLocal, centerPx, rotation);
    });
  }

  /**
   * Facteur d'échelle pour amener l'arête `edgeIndex` (entre les sommets `edgeIndex` et
   * `edgeIndex + 1`) en `cursorPx`, en gardant l'arête OPPOSÉE fixe — un seul axe change (l'autre
   * facteur d'échelle vaut 1), contrairement à `computeSelectionResizeScale` qui change les 2 axes
   * à la fois depuis un coin. Même repère local (dé-rotation par `rotation`) que cette dernière.
   * L'axe concerné (largeur ou hauteur) est déterminé dynamiquement en comparant, dans ce repère
   * local, les 2 sommets de l'arête tirée — pas supposé depuis la parité de `edgeIndex` — pour
   * rester correct quel que soit l'ordre de construction des 4 sommets du rectangle. `null` si le
   * rectangle de départ est dégénéré sur cet axe (arête confondue avec son opposée).
   */
  private computeEdgeResizeScale(
    edgeIndex: number,
    originalCornersPx: PixelPoint[],
    rotation: number,
    cursorPx: PixelPoint,
  ): { anchorPx: PixelPoint; centerPx: PixelPoint; scaleX: number; scaleY: number } | null {
    const centerPx = centroidPx(originalCornersPx);
    const toLocal = (p: PixelPoint) => rotatePointPx(p, centerPx, -rotation);

    const cornerA = originalCornersPx[edgeIndex]!;
    const cornerB = originalCornersPx[(edgeIndex + 1) % 4]!;
    const oppositeA = originalCornersPx[(edgeIndex + 2) % 4]!;
    const oppositeB = originalCornersPx[(edgeIndex + 3) % 4]!;
    const edgeMidpointPx = { x: (cornerA.x + cornerB.x) / 2, y: (cornerA.y + cornerB.y) / 2 };
    const anchorPx = { x: (oppositeA.x + oppositeB.x) / 2, y: (oppositeA.y + oppositeB.y) / 2 };

    const localA = toLocal(cornerA);
    const localB = toLocal(cornerB);
    const axisIsY = Math.abs(localA.y - localB.y) < Math.abs(localA.x - localB.x);

    const localAnchor = toLocal(anchorPx);
    const localEdgeMidOriginal = toLocal(edgeMidpointPx);
    const localCursor = toLocal(cursorPx);

    const delta = axisIsY ? localEdgeMidOriginal.y - localAnchor.y : localEdgeMidOriginal.x - localAnchor.x;
    if (Math.abs(delta) < 1) return null;
    const cursorDelta = axisIsY ? localCursor.y - localAnchor.y : localCursor.x - localAnchor.x;
    const factor = cursorDelta / delta;

    return { anchorPx, centerPx, scaleX: axisIsY ? 1 : factor, scaleY: axisIsY ? factor : 1 };
  }

  private handleSelectionResizeMove(event: MapMouseEvent): void {
    if (!this.selectionResizingState || !this.selectionState || !this.map) return;
    const { cornerIndex, originalCornersPx, rotation, originalShapes } = this.selectionResizingState;
    const scale = this.computeSelectionResizeScale(cornerIndex, originalCornersPx, rotation, {
      x: event.point.x,
      y: event.point.y,
    });
    if (!scale) return;

    const newCornersPx = this.applySelectionResizeScale(
      originalCornersPx,
      scale.centerPx,
      scale.anchorPx,
      rotation,
      scale.scaleX,
      scale.scaleY,
    );
    this.selectionState.corners = newCornersPx.map((p) => {
      const ll = this.map!.unproject(p as Point);
      return [ll.lng, ll.lat] as LngLat;
    });

    // Aperçu live des formes elles-mêmes (même principe que `handleSelectionMoveMove`) : aucun
    // évènement `shape:scaled`/`onSave` n'est émis ici, seulement au relâchement (`handleSelectionResizeEnd`).
    // Passé à `updateEditingShapeSource(..., true)` plus bas, qui pilote la bordure "sélectionné"
    // (voir `shapesToGeoJSON`) — ces formes suivent donc leur position live, pas figées sur leur
    // position de départ pendant le drag.
    const scaledShapes = originalShapes.map((shape) => ({
      ...shape,
      coordinates: this.applySelectionResizeScale(
        shape.coordinates.map(([lng, lat]) => this.map!.project([lng, lat])),
        scale.centerPx,
        scale.anchorPx,
        rotation,
        scale.scaleX,
        scale.scaleY,
      ).map((p) => {
        const ll = this.map!.unproject(p as Point);
        return [ll.lng, ll.lat] as LngLat;
      }),
    }));
    this.updateSelectionVisuals();
    this.updateEditingShapeSource(scaledShapes, true);
    for (const shape of scaledShapes) {
      if (shape.image) this.repositionShapeImageDuringEdit(shape as Shape & { image: ShapeImage });
    }
  }

  private handleSelectionResizeEnd(): void {
    if (!this.selectionResizingState || !this.selectionState || !this.map) return;
    const { cornerIndex, originalCornersPx, rotation } = this.selectionResizingState;
    const currentCornerPx = this.map.project(this.selectionState.corners[cornerIndex]!);
    const scale = this.computeSelectionResizeScale(cornerIndex, originalCornersPx, rotation, currentCornerPx);

    this.selectionResizingState = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = "";
    this.setSelectionInteracting(false);
    // Resté dégénéré tout du long, OU un simple clic sans glisser (le coin revient exactement à sa
    // position de départ, `scaleX`/`scaleY` valent alors 1) : rien n'a réellement changé, inutile
    // d'émettre un `"selection:scaled"`/`onSave` no-op. Les formes doivent tout de même réintégrer
    // la source principale (masquées au mousedown, voir `SELECTION_HANDLES_LAYER_ID`).
    if (!scale || (scale.scaleX === 1 && scale.scaleY === 1)) {
      this.setEditingShapeFilter(null);
      this.updateEditingShapeSource([]);
      return;
    }

    const scaledShapesForEvent: { shapeId: string; coordinates: LngLat[] }[] = [];
    this.forEachSelectedShape((shape) => {
      const pointsPx = shape.coordinates.map(([lng, lat]) => this.map!.project([lng, lat]));
      const scaledPx = this.applySelectionResizeScale(
        pointsPx,
        scale.centerPx,
        scale.anchorPx,
        rotation,
        scale.scaleX,
        scale.scaleY,
      );
      const coordinates: LngLat[] = scaledPx.map((p) => {
        const ll = this.map!.unproject(p as Point);
        return [ll.lng, ll.lat];
      });
      scaledShapesForEvent.push({ shapeId: shape.id, coordinates });
    });
    this.emit({ type: "selection:scaled", shapes: scaledShapesForEvent });

    // Voir le commentaire équivalent dans `handleSelectionMoveEnd` : le `emit` ci-dessus a déjà
    // déclenché, de façon synchrone, le `renderShapes()` qui porte la source principale à ses
    // positions finales — on peut donc la réafficher ici sans flash de retour à l'ancienne position.
    this.setEditingShapeFilter(null);
    this.updateEditingShapeSource([]);
  }

  private handleSelectionRotateMove(event: MapMouseEvent): void {
    if (!this.selectionRotatingState || !this.selectionState || !this.map) return;
    const { centerPx, originalCornersPx, startAngleDeg, originalShapes } = this.selectionRotatingState;
    const deltaDeg = angleDeg(centerPx, event.point) - startAngleDeg;

    this.selectionState.corners = originalCornersPx.map((p) => {
      const rotated = rotatePointPx(p, centerPx, deltaDeg);
      const ll = this.map!.unproject(rotated as Point);
      return [ll.lng, ll.lat] as LngLat;
    });
    this.selectionRotatingState.currentDeltaDeg = deltaDeg;

    // Aperçu live des formes elles-mêmes (même principe que `handleSelectionMoveMove`/
    // `handleSelectionResizeMove`) : aucun évènement `shape:rotated`/`onSave` n'est émis ici,
    // seulement au relâchement (`handleSelectionRotateEnd`).
    const rotatedShapes = originalShapes.map((shape) => ({
      ...shape,
      // Voir `shapeOwnImageRotationDeg` : pour un cercle, c'est `Shape.rotation` (pas ses
      // `coordinates`) qui pilote l'orientation de son image — sans la mettre à jour ici aussi,
      // l'aperçu live de son image resterait figé pendant tout le geste de rotation de groupe.
      rotation: normalizeDeg(shape.rotation + deltaDeg),
      coordinates: shape.coordinates.map(([lng, lat]) => {
        const px = this.map!.project([lng, lat]);
        const rotatedPx = rotatePointPx(px, centerPx, deltaDeg);
        const ll = this.map!.unproject(rotatedPx as Point);
        return [ll.lng, ll.lat] as LngLat;
      }),
    }));
    this.updateSelectionVisuals();
    this.updateEditingShapeSource(rotatedShapes, true);
    for (const shape of rotatedShapes) {
      if (shape.image) this.repositionShapeImageDuringEdit(shape as Shape & { image: ShapeImage });
    }
  }

  private handleSelectionRotateEnd(): void {
    if (!this.selectionRotatingState || !this.selectionState || !this.map) return;
    const { centerPx, currentDeltaDeg } = this.selectionRotatingState;

    this.selectionState.rotation = normalizeDeg(this.selectionState.rotation + currentDeltaDeg);
    this.selectionRotatingState = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = "grab";
    this.setSelectionInteracting(false);

    // Un simple clic sur la poignée (mousedown+mouseup sans glisser) laisse `currentDeltaDeg` à 0 :
    // rien n'a réellement tourné, inutile d'émettre un `"selection:rotated"`/`onSave` no-op.
    if (currentDeltaDeg === 0) {
      this.setEditingShapeFilter(null);
      this.updateEditingShapeSource([]);
      return;
    }

    const rotatedShapesForEvent: { shapeId: string; rotation: number; coordinates: LngLat[] }[] = [];
    this.forEachSelectedShape((shape) => {
      const coordinates: LngLat[] = shape.coordinates.map(([lng, lat]) => {
        const px = this.map!.project([lng, lat]);
        const rotatedPx = rotatePointPx(px, centerPx, currentDeltaDeg);
        const ll = this.map!.unproject(rotatedPx as Point);
        return [ll.lng, ll.lat];
      });
      rotatedShapesForEvent.push({
        shapeId: shape.id,
        rotation: normalizeDeg(shape.rotation + currentDeltaDeg),
        coordinates,
      });
    });
    this.emit({ type: "selection:rotated", shapes: rotatedShapesForEvent });

    // Voir le commentaire équivalent dans `handleSelectionMoveEnd`/`handleSelectionResizeEnd` : le
    // `emit` ci-dessus a déjà déclenché, de façon synchrone, le `renderShapes()` qui porte la
    // source principale à ses positions finales — on peut donc la réafficher ici sans flash de
    // retour à l'ancienne position.
    this.setEditingShapeFilter(null);
    this.updateEditingShapeSource([]);
  }

  private handleSelectionMoveMove(event: MapMouseEvent): void {
    if (!this.selectionMovingState || !this.selectionState || !this.map) return;
    const from = this.unprojectLngLat(this.selectionMovingState.startCursorPx);
    const to = this.unprojectLngLat(event.point);

    this.selectionState.corners = translateOnGround(
      this.selectionMovingState.originalCornersPx.map((p) => this.unprojectLngLat(p)),
      from,
      to,
    );

    // Aperçu live des formes elles-mêmes (pas seulement du rectangle) : voir la source légère
    // dédiée peuplée au mousedown (`setEditingShapeFilter`/`updateEditingShapeSource`) — aucun
    // évènement `shape:moved`/`onSave` n'est émis ici, seulement au relâchement (`handleSelectionMoveEnd`).
    const movedShapes = this.selectionMovingState.originalShapes.map((shape) => ({
      ...shape,
      coordinates: translateOnGround(shape.coordinates, from, to),
    }));
    this.updateSelectionVisuals();
    this.updateEditingShapeSource(movedShapes, true);
    for (const shape of movedShapes) {
      if (shape.image) this.repositionShapeImageDuringEdit(shape as Shape & { image: ShapeImage });
    }
  }

  private handleSelectionMoveEnd(): void {
    if (!this.selectionMovingState || !this.selectionState || !this.map) return;
    const { originalCornersPx } = this.selectionMovingState;
    const currentCorner0Px = this.map.project(this.selectionState.corners[0]!);
    const dx = currentCorner0Px.x - originalCornersPx[0]!.x;
    const dy = currentCorner0Px.y - originalCornersPx[0]!.y;

    this.selectionMovingState = null;
    this.map.dragPan.enable();
    this.map.getCanvas().style.cursor = "move";
    this.setSelectionInteracting(false);

    // Un simple clic (mousedown+mouseup sans glisser — ex. Ctrl+clic sur une forme du groupe pour la
    // retirer de la sélection, voir le garde `ctrlKey`/`metaKey` du `mousedown` plus haut, qui ne
    // couvre que le cas Ctrl ; ceci couvre aussi un clic sans Ctrl qui n'aurait pas bougé) laisse
    // dx/dy à 0 : rien n'a réellement bougé, inutile d'émettre un `"selection:moved"`/`onSave` no-op.
    if (dx === 0 && dy === 0) {
      this.setEditingShapeFilter(null);
      this.updateEditingShapeSource([]);
      return;
    }

    const from = this.unprojectLngLat(originalCornersPx[0]!);
    const to = this.selectionState.corners[0]!;
    const movedShapesForEvent: { shapeId: string; coordinates: LngLat[] }[] = [];
    this.forEachSelectedShape((shape) => {
      movedShapesForEvent.push({ shapeId: shape.id, coordinates: translateOnGround(shape.coordinates, from, to) });
    });
    this.emit({ type: "selection:moved", shapes: movedShapesForEvent });

    // Le `emit` ci-dessus a déjà déclenché, de façon synchrone, l'exécution de la commande et le
    // `renderShapes()` qui en découle (voir `ShapeStore.onChange`) : la source principale porte
    // donc déjà les positions finales quand on la réaffiche ici. En cas d'échec `onSave` ultérieur,
    // le rollback (BESOINS.md §7.1) réinjecte la position de départ dans le store — via le même
    // mécanisme `renderShapes()`, sans rien de spécifique à gérer ici.
    this.setEditingShapeFilter(null);
    this.updateEditingShapeSource([]);
  }

  /** Referme le rectangle de sélection (clic en dehors, Échap) — les formes restent telles quelles,
   * seule la sélection elle-même disparaît. */
  /**
   * Publique (contrairement au reste des sous-états de la sélection rectangle) : `createTokaMap`
   * l'appelle depuis `loadDocument`, où les formes sélectionnées cessent simplement d'exister.
   */
  clearSelection(): void {
    // Un Escape (ou un clic en dehors) peut interrompre un déplacement en cours : on referme aussi
    // l'aperçu live et on réintègre les formes à la source principale (idempotent sinon, voir
    // `setEditingShapeFilter`/`updateEditingShapeSource`).
    this.setEditingShapeFilter(null);
    this.updateEditingShapeSource([]);
    this.selectionState = null;
    this.selectionResizingState = null;
    this.selectionRotatingState = null;
    this.selectionMovingState = null;
    this.setSelectionInteracting(false);
    this.applySelectionFeatureState([]);
    this.updateSelectionVisuals();
    this.emit({ type: "selection:changed", shapeIds: [] });
  }

  private forEachSelectedShape(fn: (shape: Shape) => void): void {
    if (!this.selectionState) return;
    for (const shapeId of this.selectionState.shapeIds) {
      const shape = this.lastShapes.find((s) => s.id === shapeId);
      if (shape) fn(shape);
    }
  }

  /** Formes actuellement sélectionnées, telles que connues au moment de l'appel — capturées au début
   * d'un déplacement/redimensionnement de groupe pour servir de base fixe à son aperçu live. */
  private currentSelectionShapes(): Shape[] {
    if (!this.selectionState) return [];
    return this.selectionState.shapeIds
      .map((id) => this.lastShapes.find((s) => s.id === id))
      .filter((s): s is Shape => s != null);
  }

  /** Alimente juste `SELECTION_SOURCE_ID` (le remplissage blanc) à partir d'un jeu de coins
   * quelconque — factorisé pour être appelable pendant le TRACÉ initial du rectangle
   * (`handleSelectionDrawMove`/`handleSelectionDrawEnd`, avant même que `selectionState` existe),
   * séparément de `updateSelectionVisuals` (qui suppose `selectionState` et peuple aussi les
   * poignées/la ligne de rotation). */
  private updateSelectionFillSource(corners: LngLat[]): void {
    const rectSource = this.map?.getSource(SELECTION_SOURCE_ID);
    if (rectSource && rectSource.type === "geojson") {
      (rectSource as maplibregl.GeoJSONSource).setData(selectionRectToGeoJSON(corners));
    }
  }

  /** Bascule l'apparence du rectangle de sélection entre "en cours de glissé" (tracé initial,
   * redimensionnement, rotation, déplacement : remplissage blanc visible + bordure blanche) et "au
   * repos" (persistant avec poignées, entre deux glissés : pas de remplissage, bordure pointillée
   * bleue) — voir `SELECTION_FILL_LAYER_ID`/`SELECTION_OUTLINE_LAYER_ID`. La bordure reste TOUJOURS
   * visible dans les deux états, seule sa couleur change (blanche assortie au remplissage pendant le
   * glissé, bleue au repos comme les poignées/la ligne de rotation) — contrairement au remplissage,
   * qui lui apparaît/disparaît. Appelé au début/à la fin de chacun des 4 gestes
   * (`handleSelectionDraw*`/`handleSelectionResize*`/`handleSelectionRotate*`/
   * `handleSelectionMove*`), jamais en fonction de `selectionState` lui-même (qui n'existe pas
   * encore pendant le tracé initial). */
  private setSelectionInteracting(interacting: boolean): void {
    if (!this.map) return;
    this.map.setPaintProperty(
      SELECTION_FILL_LAYER_ID,
      "fill-opacity",
      interacting ? SELECTION_FILL_OPACITY_DRAGGING : 0,
    );
    this.map.setPaintProperty(
      SELECTION_OUTLINE_LAYER_ID,
      "line-color",
      interacting ? SELECTION_RECT_FILL_COLOR : this.colors.accent,
    );
  }

  private updateSelectionVisuals(): void {
    this.updateSelectionFillSource(this.selectionState?.corners ?? []);

    const handlesSource = this.map?.getSource(SELECTION_HANDLES_SOURCE_ID);
    if (handlesSource && handlesSource.type === "geojson") {
      (handlesSource as maplibregl.GeoJSONSource).setData(editVerticesToGeoJSON(this.selectionState?.corners ?? []));
    }

    const rotateSource = this.map?.getSource(SELECTION_ROTATE_SOURCE_ID);
    if (rotateSource && rotateSource.type === "geojson") {
      if (!this.selectionState) {
        (rotateSource as maplibregl.GeoJSONSource).setData({ type: "FeatureCollection", features: [] });
      } else {
        const center = centroid(this.selectionState.corners);
        // Générique (prend n'importe quel anneau de points) : réutilisée telle quelle pour le
        // rectangle de sélection, voir `computeRotateHandlePosition`.
        const handle = this.computeRotateHandlePosition(this.selectionState.corners, center);
        (rotateSource as maplibregl.GeoJSONSource).setData(rotateHandleToGeoJSON(center, handle));
      }
    }

  }

  private updateEditVisuals(): void {
    const shape = this.editingState && this.lastShapes.find((s) => s.id === this.editingState!.shapeId);

    const editSource = this.map?.getSource(EDIT_SOURCE_ID);
    if (editSource && editSource.type === "geojson") {
      const vertices = editVerticesToGeoJSON(this.editingState?.points ?? []);
      if (shape?.shapeKind === "circle") {
        vertices.features = vertices.features.filter((feature) => feature.properties?.vertexIndex !== 0);
      }
      (editSource as maplibregl.GeoJSONSource).setData(vertices);
    }

    const edgeResizeSource = this.map?.getSource(EDGE_RESIZE_SOURCE_ID);
    if (edgeResizeSource && edgeResizeSource.type === "geojson") {
      const isEligible = shape?.shapeKind === "rectangle" && this.editingState?.points.length === 4;
      (edgeResizeSource as maplibregl.GeoJSONSource).setData(
        edgeResizeHandlesToGeoJSON(
          this.editingState && isEligible ? this.computeEdgeResizeHandles(this.editingState.points) : [],
        ),
      );
    }

    const rotateSource = this.map?.getSource(ROTATE_SOURCE_ID);
    if (rotateSource && rotateSource.type === "geojson") {
      // Pas de poignée de rotation pour un cercle (voir `handleRotateDragStart`) : faire pivoter un
      // cercle ne change jamais son contour (invariant par rotation autour de son centre — voir
      // `circleRingLngLat`), une action jugée inutile/confuse à exposer dans l'UI d'édition.
      if (!this.editingState || this.editingState.points.length === 0 || shape?.shapeKind === "circle") {
        (rotateSource as maplibregl.GeoJSONSource).setData({
          type: "FeatureCollection",
          features: [],
        });
      } else {
        const center = centroid(this.editingState.points);
        const handle = this.computeRotateHandlePosition(this.editingState.points, center);
        (rotateSource as maplibregl.GeoJSONSource).setData(rotateHandleToGeoJSON(center, handle));
      }
    }

    // Poignée de déplacement (voir MOVE_SOURCE_ID) : sur TOUTES les formes, contrairement à la
    // rotation ci-dessus — même un cercle se déplace.
    const moveSource = this.map?.getSource(MOVE_SOURCE_ID);
    if (moveSource && moveSource.type === "geojson") {
      if (!this.editingState || this.editingState.points.length === 0) {
        (moveSource as maplibregl.GeoJSONSource).setData(addVertexHoverToGeoJSON(null));
      } else {
        const moveCenter = this.computeMoveHandleCenter(shape, this.editingState.points);
        (moveSource as maplibregl.GeoJSONSource).setData(addVertexHoverToGeoJSON(moveCenter));
      }
    }
  }

  /**
   * Les 4 barres de `EDGE_RESIZE_SOURCE_ID`, une par côté du rectangle `points` (4 sommets
   * uniques) — chacune centrée sur le milieu de son côté, longue de `EDGE_RESIZE_HANDLE_LENGTH_PX`
   * dans l'alignement de ce côté (calculé en pixels écran puis reprojeté en LngLat, comme
   * `computeRotateHandlePosition` : un offset en pixels figé en
   * LngLat au moment de l'appel, qui ne reste exact qu'au zoom courant — même limite déjà acceptée
   * pour ces 2 poignées, pas un problème introduit ici).
   */
  private computeEdgeResizeHandles(points: LngLat[]): { edgeIndex: number; a: LngLat; b: LngLat; cursor: string }[] {
    if (!this.map) return [];
    const pointsPx = points.map((p) => this.map!.project(p));
    const handles: { edgeIndex: number; a: LngLat; b: LngLat; cursor: string }[] = [];
    for (let edgeIndex = 0; edgeIndex < pointsPx.length; edgeIndex++) {
      const cornerA = pointsPx[edgeIndex]!;
      const cornerB = pointsPx[(edgeIndex + 1) % pointsPx.length]!;
      const midpointPx = { x: (cornerA.x + cornerB.x) / 2, y: (cornerA.y + cornerB.y) / 2 };
      const dx = cornerB.x - cornerA.x;
      const dy = cornerB.y - cornerA.y;
      const length = Math.hypot(dx, dy) || 1;
      const halfLength = EDGE_RESIZE_HANDLE_LENGTH_PX / 2;
      const tangent = { x: (dx / length) * halfLength, y: (dy / length) * halfLength };
      const aPx = { x: midpointPx.x - tangent.x, y: midpointPx.y - tangent.y };
      const bPx = { x: midpointPx.x + tangent.x, y: midpointPx.y + tangent.y };
      const a = this.map.unproject(aPx as Point);
      const b = this.map.unproject(bPx as Point);
      handles.push({ edgeIndex, a: [a.lng, a.lat], b: [b.lng, b.lat], cursor: resizeCursorForTangent(dx, dy) });
    }
    return handles;
  }

  /**
   * Centre de la poignée de déplacement pour la forme EN ÉDITION (voir MOVE_SOURCE_ID) — `points`
   * doit être `editingState.points` (les positions LIVE pendant un drag, pas `shape.coordinates`).
   * Pour un cercle, `points` vaut `[centre, point de bord]` (voir `shapeVertices`) : `points[0]`
   * directement serait son vrai centre (le centroïde des 2 points donnerait leur MILIEU, pas le
   * centre — même piège que `shapeCenter`) ; pour les autres, le centroïde de tous les
   * sommets (identique au centre utilisé pour la rotation). Partagée avec
   * `handleAddVertexHoverMove`, qui doit exclure cette même position de sa propre détection de
   * survol (voir MOVE_HANDLE_HOVER_EXCLUSION_RADIUS_PX).
   */
  private computeMoveHandleCenter(shape: Shape | null | undefined, points: LngLat[]): LngLat {
    if (shape?.shapeKind === "circle") {
      return points[0]!;
    }
    return centroid(points);
  }

  /** Place la poignée juste au-delà du 1er sommet, dans son alignement depuis le centre. */
  /**
   * `points[0]` sert de référence directionnelle (la poignée se place juste au-delà de ce point,
   * dans son alignement depuis `center`) — pour un cercle, appeler avec `[points[0]]` (son propre
   * centre) donnerait `firstPx === centerPx` (dx=dy=0), collapsant la poignée sur le centre :
   * les appelants doivent alors passer `[edgePoint]` (le point de bord, voir `Shape.coordinates`)
   * comme SEUL élément de `points`, pas `editingState.points` tel quel.
   */
  private computeRotateHandlePosition(points: LngLat[], center: LngLat): LngLat {
    if (!this.map) return center;
    const centerPx = this.map.project(center);
    const firstPx = this.map.project(points[0]!);
    const dx = firstPx.x - centerPx.x;
    const dy = firstPx.y - centerPx.y;
    const dist = Math.hypot(dx, dy) || 1;
    const scale = (dist + ROTATE_HANDLE_OFFSET_PX) / dist;
    const handlePx = { x: centerPx.x + dx * scale, y: centerPx.y + dy * scale };
    const handleLngLat = this.map.unproject(handlePx as Point);
    return [handleLngLat.lng, handleLngLat.lat];
  }

  /**
   * Reflète en direct, pendant le drag, la position/rotation du polygone affiché.
   * Ne touche que la source dédiée à la forme éditée (1 feature) — jamais la source
   * principale `shapes` (~1190 formes), pour rester fluide à chaque mousemove.
   */
  private updateShapesPreview(): void {
    if (!this.editingState) return;
    const { shapeId, points } = this.editingState;
    const original = this.lastShapes.find((shape) => shape.id === shapeId);
    if (!original) return;
    const closed = original.shapeKind === "circle" || original.shapeKind === "line" ? points : [...points, points[0]!];
    this.updateEditingShapeSource([{ ...original, coordinates: closed }]);
    if (original.image) {
      this.repositionShapeImageDuringEdit({ ...original, coordinates: closed, image: original.image });
    }
  }

  // --- Image plaquée sur une forme (Shape.image) ---
  //
  // Affichée comme un <img> DOM superposé à la carte (transformé en CSS pour suivre la forme),
  // plutôt que comme une texture MapLibre : pour du SVG, ça reste le rendu vectoriel natif du
  // navigateur — net à n'importe quel niveau de zoom, sans jamais rasteriser nous-mêmes l'image.

  /**
   * Ajoute/retire les `<img>` en fonction des formes-image SVG (les rasters vivent dans le canvas,
   * voir `syncRasterShapeImages`), et de la vue courante. Si `includeImages` est désactivé, aucune
   * forme n'est candidate : celles déjà montées sont démontées (voir la boucle plus bas), et aucune
   * nouvelle ne le sera. Si `includeIndirectImages` est désactivé, seules les formes
   * `shapeKind: "image"` restent candidates (voir `shouldRenderShapeImage`).
   */
  private syncShapeImages(shapes: Shape[]): void {
    this.imageShapeCandidates = shapes.filter(
      (shape): shape is Shape & { image: ShapeImage } =>
        !!shape.image?.src &&
        this.shouldRenderShapeImage(shape) &&
        isSvgShapeImage(shape.image.src, shape.image.svg),
    );
    const nextIds = new Set(this.imageShapeCandidates.map((shape) => shape.id));

    for (const shapeId of this.imageOverlayElements.keys()) {
      if (!nextIds.has(shapeId)) this.removeShapeImageOverlay(shapeId);
    }
    this.updateImageOverlayMounts();
    // `updateImageOverlayMounts` ne repositionne une image que lors d'un montage/démontage : une
    // forme qui reste montée (ex. reprend sa position d'origine après un rollback d'`onSave`, sans
    // sortir de la vue) ne serait sinon retransformée qu'au prochain événement `move` de la carte —
    // laissant son `<img>` visuellement figé jusqu'à un geste de l'utilisateur.
    this.updateImageOverlayTransforms();
    // `imageShapeCandidates` vient d'être recalculé ci-dessus : c'est le point où "au moins un SVG
    // est affiché" peut changer (apparition/disparition de la dernière forme-image SVG) — voir
    // `updateBackgroundMode`.
    this.updateBackgroundMode();
  }

  /**
   * Monte (crée l'`<img>`, déclenche son chargement) les formes-image qui viennent d'entrer dans
   * la vue (+ marge), et démonte celles qui en sont sorties. Certaines de ces images pèsent
   * plusieurs Mo : avec ~80 formes-image au total, ne garder montées que celles réellement (ou
   * bientôt) visibles évite de payer leur coût réseau/décodage/compositing en continu.
   */
  /**
   * Bascule toutes les images actuellement montées entre calque GPU dédié (fluide, moins net —
   * pendant un pan/zoom actif) et rendu direct (net, plus coûteux à chaque frame — une fois la
   * carte stabilisée). Voir `applyImageOverlayTransform` pour le compromis netteté/coût sous-jacent.
   */
  private setImageOverlaysCompositing(active: boolean): void {
    this.imageOverlaysCompositing = active;
    const willChange = active ? "transform" : "auto";
    for (const img of this.imageOverlayElements.values()) {
      img.style.willChange = willChange;
    }
  }

  private updateImageOverlayMounts(): void {
    if (!this.map) return;
    for (const shape of this.imageShapeCandidates) {
      const isMounted = this.imageOverlayElements.has(shape.id);
      const shouldBeMounted =
        this.isShapeNearViewport(shape, IMAGE_OVERLAY_VIEWPORT_MARGIN) && !this.isImageTooSmallToShow(shape);
      if (shouldBeMounted) {
        // Appelé aussi quand `isMounted` est déjà vrai (pas seulement au montage initial) :
        // `upsertShapeImageOverlay` est idempotent (voir son commentaire, "change sa src si l'URL a
        // changé") et c'est le seul appelant qui tourne à chaque `renderShapes()` — sans ça,
        // changer `Shape.image.src` d'une forme qui reste montée (sans quitter puis revenir dans la
        // vue) ne mettrait jamais à jour l'image affichée.
        this.upsertShapeImageOverlay(shape);
      } else if (isMounted) {
        this.removeShapeImageOverlay(shape.id);
      }
    }

    // Une image tout juste (re)montée est `appendChild`-ée en fin de conteneur, donc au-dessus de
    // toutes les autres — sans rapport avec son calque (`Shape.layer`). `imageShapeCandidates` est
    // déjà trié par calque (voir `ShapeStore.getAll`) : reproduire cet ordre dans le DOM à chaque
    // appel (pas seulement si le jeu d'images montées a changé) — une forme déjà montée dont
    // `Shape.layer` change seule (ex. "Monter"/"Descendre" du menu contextuel) doit elle aussi
    // remonter/redescendre visuellement parmi les autres images, sans entrer/sortir de la vue pour
    // autant. Déplacer un nœud déjà présent via `appendChild` le repositionne sans le recréer/
    // re-télécharger, et est un no-op bon marché quand l'ordre n'a pas changé.
    if (this.imageOverlayContainer) {
      for (const shape of this.imageShapeCandidates) {
        const img = this.imageOverlayElements.get(shape.id);
        if (img) this.imageOverlayContainer.appendChild(img);
      }
    }
  }

  /** Vrai si la forme est visible, ou à moins de `marginFraction` (fraction de la vue) de le devenir. */
  private isShapeNearViewport(shape: Shape, marginFraction: number): boolean {
    if (!this.map) return false;
    const view = this.map.getBounds();
    const marginLng = (view.getEast() - view.getWest()) * marginFraction;
    const marginLat = (view.getNorth() - view.getSouth()) * marginFraction;
    const { minLng, maxLng, minLat, maxLat } = boundsOf(shape.coordinates);
    return (
      maxLng >= view.getWest() - marginLng &&
      minLng <= view.getEast() + marginLng &&
      maxLat >= view.getSouth() - marginLat &&
      minLat <= view.getNorth() + marginLat
    );
  }

  /**
   * Quad sur lequel plaquer l'image RASTER effective d'une forme (voir `renderShapes`) — mode
   * "contain" toujours appliqué (voir CLAUDE.md), pas étiré sur les sommets réels de la forme comme
   * autrefois. Le ratio cible vient, par ordre de préférence :
   * 1. `Shape.image.width`/`.height` (voir `trustedImageDimensions`), si exploitables (`> 0`,
   *    finis) — évite le sondage asynchrone ci-dessous, et surtout le rendu complet en cascade
   *    qu'il déclenche une fois résolu (voir `probeImageDimensions`), potentiellement coûteux à
   *    l'échelle de nombreuses images distinctes. Validité minimale seulement : rien ne garantit que
   *    le RATIO fourni soit correct (un consommateur pourrait renseigner des valeurs plausibles mais
   *    fausses) — compromis assumé pour la performance, pas une nouvelle promesse de fiabilité totale.
   * 2. Sinon, `probedImageDimensions` (sondé de façon asynchrone via `probeImageDimensions` —
   *    MapLibre charge la texture d'une `ImageSource` lui-même, sans nous exposer ses dimensions
   *    réelles avant coup, contrairement au chemin SVG qui lit `img.naturalWidth`/`.naturalHeight`
   *    directement, voir `applyImageOverlayTransform`). Tant que ce sondage n'a pas résolu, retombe
   *    SANS DÉFORMER sur `shapeImageQuad` (étiré) pour cette forme, le temps qu'un nouveau rendu
   *    (déclenché par `probeImageDimensions` une fois résolu) applique le vrai ratio.
   *
   * `Shape.image.scale` (défaut 1) est appliqué DANS `containQuad` (pas via `scaleAndRotateQuadPx`,
   * réservée à `.rotation` — voir son commentaire) pour que `horizontalAlign`/`verticalAlign`
   * restent collés au bon bord quelle que soit la taille réduite de l'image.
   *
   * Calculé en pixels écran (`project`/`unproject`), pas directement en lng/lat : un ratio ou une
   * rotation calculés en lng/lat brut seraient faussés par la distorsion Mercator. Part de
   * `containReferenceQuadPx` (pas directement de `shapeImageQuad`) : les 4 sommets propres de la
   * forme si elle forme un vrai rectangle (pivoté ou non, hérite alors correctement de sa rotation),
   * sinon sa bounding box axis-aligned — jamais les sommets bruts d'un quadrilatère quelconque (type
   * trapèze), qui cisailleraient l'image (`containQuad` suppose un parallélogramme).
   *
   * Ne concerne QUE le chemin raster (voir `upsertRasterShapeImage`) : le chemin SVG applique son
   * propre "contain" avec les dimensions réelles une fois chargées, voir
   * `applyImageOverlayTransform`, qui n'appelle pas cette méthode pour son propre quad.
   */
  private imageQuadFor(shape: Shape): ImageQuad {
    if (!this.map || !shape.image?.src) return shapeImageQuad(shape);
    // Le chemin SVG applique son propre "contain" avec les dimensions RÉELLES du bitmap une fois
    // chargé (voir `applyImageOverlayTransform`, plus précis que `probedImageDimensions` ici) : pas
    // besoin (et refaire le travail en double, avec un ratio potentiellement légèrement différent,
    // risquerait de recontenir une seconde fois de façon incohérente).
    if (isSvgShapeImage(shape.image.src, shape.image.svg)) return shapeImageQuad(shape);
    const map = this.map;

    const dims = trustedImageDimensions(shape.image) || this.probedImageDimensions.get(shape.image.src);
    if (!dims) {
      this.probeImageDimensions(shape.image.src);
      return shapeImageQuad(shape);
    }

    const { project, unproject } = this.groundPlaneProjection();
    const quadPx = containReferenceQuadPx(shape, project);
    const fittedPx = scaleAndRotateQuadPx(
      containQuad(
        quadPx,
        dims.width,
        dims.height,
        shape.image.horizontalAlign ?? 0.5,
        shape.image.verticalAlign ?? 0.5,
        shape.image.scale ?? 1,
      ),
      1,
      effectiveImageRotationDeg(shape.image, map.getBearing(), this.defaultImageRotationDeg(shape)) + shapeOwnImageRotationDeg(shape, map.getBearing()),
    );

    const [f0, f1, f2, f3] = fittedPx;
    return [unproject(f0), unproject(f1), unproject(f2), unproject(f3)];
  }

  /**
   * `imageQuadFor` + anti-débordement TOUJOURS appliqué (voir `shrinkQuadToFitPolygonPx`, déjà
   * utilisé côté SVG dans `applyImageOverlayTransform`) — sans ça, une image (raster) plaquée sur un
   * triangle/polygone à main levée déborderait dans les coins "vides" de sa bounding box
   * (`containReferenceQuadPx` y retombe pour ces formes, voir son commentaire), et une image sur un
   * quadrilatère qui n'est PAS un vrai rectangle (ex. un trapèze, où `containReferenceQuadPx` retombe
   * aussi sur la bbox) déborderait de la même façon hors de sa vraie silhouette. No-op pour un vrai
   * rectangle (le contain y tient déjà par construction). Extrait dans sa propre méthode (plutôt que
   * dans `imageQuadFor`) parce que le chemin SVG fait déjà cet anti-débordement lui-même, à un autre
   * endroit (`applyImageOverlayTransform`, après son propre contain avec les dimensions réelles) —
   * seul le chemin raster (`upsertRasterShapeImage`) en a besoin ici.
   */
  private imageQuadForClippedToShape(shape: Shape): ImageQuad {
    if (!this.map) return this.imageQuadFor(shape);
    const { project, unproject } = this.groundPlaneProjection();

    const [q0, q1, q2, q3] = this.imageQuadFor(shape);
    const quadPx: [PixelPoint, PixelPoint, PixelPoint, PixelPoint] = [project(q0), project(q1), project(q2), project(q3)];
    const polygonPoints =
      shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates.slice(0, -1);
    const polygonPx = polygonPoints.map(project);
    const [f0, f1, f2, f3] = shrinkQuadToFitPolygonPx(quadPx, polygonPx);
    return [unproject(f0), unproject(f1), unproject(f2), unproject(f3)];
  }

  // Le quad d'une image raster est posé au sol : calculé en pixels écran sous inclinaison, la
  // perspective le déformerait. Le plan Mercator au zoom courant, c'est l'écran à plat.
  private groundPlaneProjection(): {
    project: (point: LngLat) => PixelPoint;
    unproject: (point: PixelPoint) => LngLat;
  } {
    const worldSize = 512 * Math.pow(2, this.map!.getZoom());
    return {
      project: ([lng, lat]) => {
        const { x, y } = maplibregl.MercatorCoordinate.fromLngLat({ lng, lat });
        return { x: x * worldSize, y: y * worldSize };
      },
      unproject: ({ x, y }) => {
        const { lng, lat } = new maplibregl.MercatorCoordinate(x / worldSize, y / worldSize).toLngLat();
        return [lng, lat];
      },
    };
  }

  /**
   * Sonde les dimensions réelles (px) de `src` (`Shape.image.src`) via un `Image()` DOM léger —
   * indépendant de MapLibre (dont l'`ImageSource` ne nous expose pas les dimensions réelles de la
   * texture chargée). Sondé sur une variante CDN volontairement PETITE (64px, via
   * `resolveImageUrl` si configuré) — jamais l'URL source telle quelle : un des rôles mêmes de
   * `resolveImageUrl` est d'éviter de télécharger un fichier source potentiellement énorme (voir son
   * commentaire, l'incident de crash iOS) ; ce sondage ne doit pas réintroduire ce risque, seul le
   * ratio largeur/hauteur nous intéresse ici, pas la netteté.
   *
   * Une fois résolu (succès ou échec), redéclenche un rendu complet (`renderShapes(this.lastShapes)`)
   * pour que `imageQuadFor` puisse immédiatement exploiter le nouveau ratio — sans ça, l'image
   * resterait étirée jusqu'au prochain changement de forme qui appellerait `renderShapes` de toute
   * façon.
   *
   * `imageQuadFor` n'appelle cette méthode QUE quand `trustedImageDimensions` n'a rien à offrir
   * (`width`/`height` absents/invalides sur `Shape.image`) : à l'échelle de nombreuses images
   * distinctes, chaque sondage résolu déclenche un rendu complet (ci-dessus) — potentiellement
   * autant de rendus en cascade au chargement initial si aucune n'a de dimensions fiables en amont.
   * Fournir des `image.width`/`.height` valides dans la donnée source évite ce coût entièrement pour
   * ces formes.
   */
  private probeImageDimensions(src: string): void {
    if (
      this.probedImageDimensions.has(src) ||
      this.pendingImageDimensionSrcs.has(src) ||
      this.failedImageDimensionSrcs.has(src)
    )
      return;

    this.pendingImageDimensionSrcs.add(src);
    const probeSrc = this.resolveImageUrl ? this.resolveImageUrl(src, { width: 64, height: 64 }) : src;
    const img = new Image();
    // Mode "cors" explicite — cette méthode ne sert QUE le chemin raster (voir `imageQuadFor`, qui
    // exclut déjà le SVG avant d'appeler ceci), et cette même `probeSrc` sera de toute façon
    // rechargée en mode "cors" par MapLibre lui-même juste après (`upsertRasterShapeImage`,
    // `map.addSource`, pour construire sa texture WebGL). Sans ce mode explicite ici, ce sondage se
    // ferait en "no-cors", et le cache HTTP du navigateur peut alors mal se comporter face à 2
    // requêtes concurrentes/successives vers la même URL avec des modes CORS différents — observé en
    // pratique comme un échec CORS intermittent sur une image dont le serveur a pourtant bien
    // `Access-Control-Allow-Origin` (voir `loadImageDimensions.ts`, même correctif, même raison).
    img.crossOrigin = "anonymous";
    img.onload = () => {
      this.pendingImageDimensionSrcs.delete(src);
      this.probedImageDimensions.set(src, { width: img.naturalWidth, height: img.naturalHeight });
      this.renderShapes(this.lastShapes);
    };
    img.onerror = () => {
      this.pendingImageDimensionSrcs.delete(src);
      this.failedImageDimensionSrcs.add(src);
      console.warn(`[Shape.image] échec du sondage des dimensions de "${src}":`, probeSrc);
    };
    img.src = probeSrc;
  }

  /** Taille actuelle (CSS px, zoom courant) du quadrilatère sur lequel une image de forme est plaquée. */
  private currentImageScreenSize(shape: Shape): ImageTargetSize {
    const quadPx = this.imageQuadFor(shape).map((point) => this.map!.project(point));
    const [q0, q1, q2] = [quadPx[0]!, quadPx[1]!, quadPx[2]!];
    return {
      width: Math.hypot(q1.x - q0.x, q1.y - q0.y),
      height: Math.hypot(q2.x - q1.x, q2.y - q1.y),
    };
  }

  /**
   * Taille (pixels device) nécessaire pour qu'une image de forme reste nette au zoom maximum
   * autorisé par la carte — inutile de demander plus : au-delà de `getMaxZoom()`, l'utilisateur
   * ne peut pas zoomer davantage pour "voir" les pixels en trop. La taille à l'écran croît en
   * 2^zoom (propriété de la projection Web Mercator), donc on part de la taille actuelle du
   * quadrilatère projeté et on l'extrapole jusqu'au zoom max, plutôt que de refaire les calculs
   * de projection mercator nous-mêmes.
   */
  private computeImageTargetSize(shape: Shape): ImageTargetSize {
    const { width: currentWidthPx, height: currentHeightPx } = this.currentImageScreenSize(shape);

    const zoomFactor = Math.pow(2, this.map!.getMaxZoom() - this.map!.getZoom());
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);

    return {
      width: Math.max(1, Math.ceil(currentWidthPx * zoomFactor * dpr)),
      height: Math.max(1, Math.ceil(currentHeightPx * zoomFactor * dpr)),
    };
  }

  /**
   * Vrai si la forme est actuellement trop petite à l'écran pour que son image vaille d'être montée.
   *
   * Court-circuite d'abord sur le quadrilatère BRUT de la forme (pas besoin des vraies dimensions de
   * l'image, donc ne déclenche jamais `probeImageDimensions`) : le mode "contain" ne peut jamais
   * dépasser ce quadrilatère (`containQuad` ne fait que le rétrécir, jamais le dépasser), donc s'il
   * est déjà sous le seuil, l'image contenue le sera forcément aussi — inutile de connaître ses
   * dimensions réelles pour le savoir. Sans ce court-circuit, `currentImageScreenSize` (via
   * `imageQuadFor`) déclencherait le sondage réseau même pour une forme trop petite pour jamais
   * afficher son image : ce garde-fou n'empêcherait alors la requête que dans le cas contraire (déjà
   * assez grande), pas dans le cas qu'il est censé filtrer.
   */
  private isImageTooSmallToShow(shape: Shape): boolean {
    if (!this.map) return true;
    const map = this.map;

    const minScreenPx = shape.image?.minScreenPx ?? this.minImageOverlayScreenPx;
    if (minScreenPx <= 0) return false;

    const [r0, r1, , r3] = containReferenceQuadPx(shape, (p) => map.project(p));
    const rawWidth = Math.hypot(r1.x - r0.x, r1.y - r0.y);
    const rawHeight = Math.hypot(r3.x - r0.x, r3.y - r0.y);
    if (Math.max(rawWidth, rawHeight) < minScreenPx) return true;

    const { width, height } = this.currentImageScreenSize(shape);
    return Math.max(width, height) < minScreenPx;
  }

  /**
   * Point d'entrée direct utilisé pendant l'édition d'une forme-image (hors de `syncShapeImages`/
   * `syncRasterShapeImages`, appelés seulement sur changement du store, voir `renderShapes`) : les
   * aperçus live de drag (déplacement/rotation/redimensionnement de sélection) doivent aussi garder
   * l'image d'une forme éditée à jour à chaque frame, qu'elle soit SVG ou raster — ces appelants ne
   * savent pas laquelle, donc le tri se fait ici plutôt que chez chacun d'eux.
   */
  private repositionShapeImageDuringEdit(shape: Shape & { image: ShapeImage }): void {
    this.upsertShapeImageOverlay(shape);
    this.upsertRasterShapeImage(shape);
  }

  /** Crée l'`<img>` d'une forme si besoin (ou change sa `src` si l'URL a changé), et la repositionne. */
  private upsertShapeImageOverlay(shape: Shape & { image: ShapeImage }): void {
    if (
      !this.shouldRenderShapeImage(shape) ||
      !this.imageOverlayContainer ||
      !this.map ||
      !isSvgShapeImage(shape.image.src, shape.image.svg)
    )
      return;

    const resolvedSrc = this.resolveImageUrl
      ? this.resolveImageUrl(shape.image.src, this.computeImageTargetSize(shape))
      : shape.image.src;

    let img = this.imageOverlayElements.get(shape.id);
    if (!img) {
      img = document.createElement("img");
      img.style.position = "absolute";
      img.style.top = "0";
      img.style.left = "0";
      img.style.transformOrigin = "0 0";
      img.style.visibility = "hidden"; // le temps de connaître sa taille naturelle (onload)
      img.style.opacity = "0"; // état de départ du fondu d'apparition, voir `onload` ci-dessous
      img.style.transition = `opacity ${SHAPE_FADE_IN_DURATION_MS}ms ease-out`;
      // `will-change: transform` uniquement pendant un mouvement actif (voir
      // `setImageOverlaysCompositing`) : ce calque coûte de la netteté au repos, mais évite de
      // repeindre le SVG à chaque frame pendant le pan/zoom, si l'image est créée en plein geste.
      img.style.willChange = this.imageOverlaysCompositing ? "transform" : "auto";
      img.decoding = "async";
      img.onload = () => {
        img!.style.visibility = "visible";
        this.applyImageOverlayTransform(img!, shape); // positionne l'image ; écrase aussi son opacity, réinitialisée juste après
        img!.style.opacity = "0";
        // Force un flush de style synchrone entre les 2 écritures d'`opacity` ci-dessus/ci-dessous :
        // sans lui, le navigateur compresse les deux dans le même commit de style (rien n'a encore
        // été peint entre elles) et la transition ne s'anime pas — même piège que celui déjà résolu
        // via `requestAnimationFrame` dans `upsertRasterShapeImage`, ici via une lecture de layout.
        void img!.offsetWidth;
        img!.style.opacity = String(shape.image?.opacity ?? 1);
      };
      img.onerror = () => {
        console.error(`[MapLibreAdapter] échec du chargement de l'image ${img!.src}`);
      };
      this.imageOverlayContainer.appendChild(img);
      this.imageOverlayElements.set(shape.id, img);
    }

    if (img.src !== resolvedSrc) {
      img.style.visibility = "hidden";
      img.src = resolvedSrc;
    } else if (img.complete && img.naturalWidth > 0) {
      this.applyImageOverlayTransform(img, shape);
    }
  }

  /** Repositionne/redimensionne/pivote un `<img>` pour qu'il recouvre exactement le quadrilatère de sa forme. */
  private applyImageOverlayTransform(img: HTMLImageElement, shape: Shape): void {
    if (!this.map) return;
    const map = this.map;
    const width = img.naturalWidth || 1;
    const height = img.naturalHeight || 1;
    // À plat (pitch ≈ 0, le cas normal de cet outil 2D), `matrix()` reste nette à tout zoom —
    // `matrix3d()` n'est nécessaire (et ne coûte du flou, cf. ci-dessus) que sous vraie perspective.
    const isFlat = map.getPitch() < FLAT_PITCH_THRESHOLD_DEG;

    let fittedQuad: [PixelPoint, PixelPoint, PixelPoint, PixelPoint];
    if (isFlat) {
      // `containQuad`/`containReferenceQuadPx` supposent un parallélogramme (p2 = p0+u+v) : vrai à
      // plat, mais faux sous perspective réelle (pitch), où le vrai p2 projeté diverge de cette
      // approximation — l'utiliser là décale/déforme l'image, voir la branche `else` ci-dessous.
      // Toujours appliqué (voir CLAUDE.md) : plus étiré par défaut. `containReferenceQuadPx` (pas
      // directement les sommets de la forme) : repart de sa bounding box si elle n'est pas un vrai
      // rectangle (ex. un trapèze), pour ne jamais cisailler l'image dessus.
      const quad = containReferenceQuadPx(shape, (p) => map.project(p));
      // `Shape.image.scale` : appliqué DANS `containQuad` (pas via `scaleAndRotateQuadPx`, réservée
      // à `.rotation` — voir le commentaire de `containQuad`), pour que `horizontalAlign`/
      // `verticalAlign` restent collés au bon bord quelle que soit la taille réduite de l'image.
      fittedQuad = scaleAndRotateQuadPx(
        containQuad(
          quad,
          width,
          height,
          shape.image?.horizontalAlign ?? 0.5,
          shape.image?.verticalAlign ?? 0.5,
          shape.image?.scale ?? 1,
        ),
        1,
        effectiveImageRotationDeg(shape.image, map.getBearing(), this.defaultImageRotationDeg(shape)) + shapeOwnImageRotationDeg(shape, map.getBearing()),
      );
    } else {
      // Sous pitch, pas d'anti-déformation ni de contain (limite acceptée, cf. ci-dessus) : on part
      // du quadrilatère réel de la forme tel quel.
      const [q0, q1, q2, q3] = shapeImageQuad(shape);
      fittedQuad = [map.project(q0), map.project(q1), map.project(q2), map.project(q3)];
    }

    // Anti-débordement : toujours appliqué (pas seulement pour les formes non-quadrilatères) — un
    // quadrilatère peut être un trapèze (pas un rectangle), auquel cas `containReferenceQuadPx` est
    // déjà reparti de sa bbox (plus grande que la silhouette réelle) : sans ce clip, l'image
    // déborderait dans les "coins" hors du vrai quadrilatère. No-op pour un vrai rectangle (le
    // contain y tient déjà par construction).
    const polygonPoints =
      shape.shapeKind === "circle" ? circleRingLngLat(shape.coordinates as [LngLat, LngLat]) : shape.coordinates.slice(0, -1);
    const polygonPx = polygonPoints.map((point) => map.project(point));
    fittedQuad = shrinkQuadToFitPolygonPx(fittedQuad, polygonPx);

    const radius = shape.image?.radius ?? 0;
    img.style.clipPath = radius >= 0.5 ? "circle(closest-side at center)" : "";
    img.style.borderRadius = radius > 0 && radius < 0.5 ? `${radius * Math.min(width, height)}px` : "";

    const matrix = isFlat ? quadMatrix2d(fittedQuad, width, height) : quadMatrix3d(fittedQuad, width, height);
    if (!matrix) return; // quadrilatère dégénéré (aire nulle)
    img.style.transform = matrix;
    img.style.opacity = String(shape.image?.opacity ?? 1);
  }

  /** Resynchronise les images actuellement montées avec la vue courante (pan/zoom/rotation). */
  private updateImageOverlayTransforms(): void {
    if (this.imageOverlayElements.size === 0) return;
    for (const [shapeId, img] of this.imageOverlayElements) {
      if (!img.complete || img.naturalWidth === 0) continue;
      const shape = this.imageShapeCandidates.find((s) => s.id === shapeId);
      if (shape) this.applyImageOverlayTransform(img, shape);
    }
  }

  private removeShapeImageOverlay(shapeId: string): void {
    this.imageOverlayElements.get(shapeId)?.remove();
    this.imageOverlayElements.delete(shapeId);
  }

  // --- Image plaquée sur une forme, cas RASTER (photo CDN) ---
  //
  // Contrairement au SVG (voir plus haut, `<img>` DOM derrière le canvas), une image raster vit
  // comme une source `image` + un layer `raster` MapLibre, DANS le canvas, aux côtés des formes —
  // ce qui lui donne un empilement unifié avec elles, piloté par `Shape.layer` (voir ShapeLayerBand,
  // BESOINS.md §4/§8). Une source `image` accepte nativement un quadrilatère arbitraire
  // (`shapeImageQuad`, même convention de coins que MapLibre attend), rotation comprise : pas besoin
  // ici des homographies CSS (`quadMatrix2d`/`quadMatrix3d`/`containQuad`) utilisées pour le SVG.

  /** Recalcule les formes candidates à une image raster (format + `includeImages`) — appelé
   * seulement quand les formes changent (`renderShapes`). Le montage/démontage lui-même est délégué
   * à `updateRasterImageMounts`, aussi appelé sur `moveend` (voir `init()`) : un zoom/pan seul, sans
   * changement de forme, peut faire entrer une image dans le cadrage ou la faire dépasser le seuil
   * de taille — il faut alors la monter même si `syncRasterShapeImages` ne s'exécute pas. */
  private syncRasterShapeImages(shapes: Shape[]): void {
    this.rasterImageShapeCandidates = shapes.filter(
      (shape): shape is Shape & { image: ShapeImage } =>
        !!shape.image?.src &&
        this.shouldRenderShapeImage(shape) &&
        !isSvgShapeImage(shape.image.src, shape.image.svg),
    );
    const nextIds = new Set(this.rasterImageShapeCandidates.map((shape) => shape.id));

    for (const shapeId of this.rasterImageLayers.keys()) {
      if (!nextIds.has(shapeId)) this.removeRasterShapeImage(shapeId);
    }
    this.updateRasterImageMounts();
  }

  /** Monte/démonte les images raster en fonction de la vue courante — même logique de
   * viewport-culling que le chemin SVG (`isShapeNearViewport`/`isImageTooSmallToShow`, génériques,
   * réutilisées telles quelles). Appelé à la fois quand les candidats changent
   * (`syncRasterShapeImages`) et sur `moveend` (voir `init()`), pour les mêmes raisons que
   * `updateImageOverlayMounts` côté SVG. */
  private updateRasterImageMounts(): void {
    for (const shape of this.rasterImageShapeCandidates) {
      const mounted = this.rasterImageLayers.has(shape.id);
      const shouldBeMounted =
        this.isShapeNearViewport(shape, IMAGE_OVERLAY_VIEWPORT_MARGIN) && !this.isImageTooSmallToShow(shape);
      if (shouldBeMounted) {
        // Monté ou non : `upsertRasterShapeImage` crée si besoin, sinon repositionne/recrée si le
        // calque ou l'URL cible a changé (voir son commentaire) — même esprit que le chemin SVG,
        // qui repositionne aussi les images déjà montées à chaque appel (voir `syncShapeImages`).
        this.upsertRasterShapeImage(shape);
      } else if (mounted) {
        this.removeRasterShapeImage(shape.id);
      }
    }
  }

  /**
   * Repositionne en continu (chaque frame de `"move"`, voir `init()`) les images raster dont
   * `image.rotateWithMap` est actif — leur quad en dépend directement (voir
   * `effectiveImageRotationDeg`), contrairement aux autres images raster, dont le repositionnement
   * suit gratuitement la reprojection native de MapLibre (`ImageSource`) sans notre intervention.
   * Sans ceci, ces images resteraient figées pendant tout le geste de rotation et ne "sauteraient" à
   * leur bonne orientation qu'à `moveend` (voir `updateRasterImageMounts`) — contrairement au chemin
   * SVG (`updateImageOverlayTransforms`), déjà rafraîchi à chaque frame. Ne boucle que sur les images
   * raster déjà montées (`rasterImageLayers`), jamais sur tous les candidats — coût borné au nombre
   * d'images "fixes à l'écran" réellement visibles, pas à l'ensemble du jeu de données.
   */
  private updateRotateWithMapRasterImages(): void {
    if (this.rasterImageLayers.size === 0) return;
    for (const shapeId of this.rasterImageLayers.keys()) {
      const shape = this.rasterImageShapeCandidates.find((s) => s.id === shapeId);
      if (shape?.image.rotateWithMap) this.upsertRasterShapeImage(shape);
    }
  }

  /**
   * Crée la source/le layer d'une image raster si besoin, sinon la repositionne en place
   * (`ImageSource.setCoordinates`, gère nativement déplacement/rotation). Si son calque
   * (`Shape.layer`, ex. "Monter"/"Descendre") ou l'URL cible (ex. nouvelle taille demandée au CDN au
   * zoom) a changé depuis, supprime/recrée plutôt que de tenter un `moveLayer`/une mise à jour de
   * source en place : plus simple à garder correct, et bon marché (le navigateur sert l'image
   * depuis son cache HTTP, la même URL n'étant re-demandée que si elle a justement changé).
   */
  private upsertRasterShapeImage(shape: Shape & { image: ShapeImage }): void {
    if (!this.shouldRenderShapeImage(shape) || !this.map || isSvgShapeImage(shape.image.src, shape.image.svg)) return;

    const plainSrc = this.resolveImageUrl
      ? this.resolveImageUrl(shape.image.src, this.computeImageTargetSize(shape))
      : shape.image.src;
    const resolvedSrc = this.roundedRasterSrc(plainSrc, shape.image.radius ?? 0);
    if (resolvedSrc === null) return;
    const layerValue = shape.layer ?? null;
    const quad = this.imageQuadForClippedToShape(shape);
    const targetOpacity = shape.image.opacity ?? 1;

    const existing = this.rasterImageLayers.get(shape.id);
    const imageSource = existing ? this.map.getSource(existing.sourceId) : undefined;
    if (existing && existing.layerValue === layerValue && existing.resolvedSrc !== resolvedSrc && imageSource?.type === "image") {
      // Même couche, autre image : remplacée sur place, sans repartir du fondu d'apparition.
      (imageSource as maplibregl.ImageSource).updateImage({ url: resolvedSrc, coordinates: quad as maplibregl.Coordinates });
      existing.resolvedSrc = resolvedSrc;
      if (existing.opacity !== targetOpacity) {
        this.map.setPaintProperty(existing.layerId, "raster-opacity", targetOpacity);
        existing.opacity = targetOpacity;
      }
      return;
    }
    if (existing && (existing.layerValue !== layerValue || existing.resolvedSrc !== resolvedSrc)) {
      this.removeRasterShapeImage(shape.id);
    } else if (existing) {
      const source = this.map.getSource(existing.sourceId);
      if (source && source.type === "image") {
        (source as maplibregl.ImageSource).setCoordinates(quad as maplibregl.Coordinates);
      }
      if (existing.opacity !== targetOpacity) {
        this.map.setPaintProperty(existing.layerId, "raster-opacity", targetOpacity);
        existing.opacity = targetOpacity;
      }
      return;
    }

    const sourceId = `${RASTER_IMAGE_SOURCE_ID_PREFIX}-${shape.id}`;
    const layerId = `${RASTER_IMAGE_LAYER_ID_PREFIX}-${shape.id}`;
    this.map.addSource(sourceId, {
      type: "image",
      url: resolvedSrc,
      coordinates: quad as maplibregl.Coordinates,
    });
    // Si cette forme est déjà exclue vers la source d'édition (voir
    // `updateRasterImageLayerPositionsForEditingChange`), son fond est dessiné par les layers fixes
    // `editing-shape-*`, au-dessus de tout — il faut créer l'image directement au même niveau
    // (sous les labels), sinon elle naîtrait sous son propre fond.
    const beforeId = this.currentEditingFilterIds?.includes(shape.id)
      ? SHAPES_TEXT_LAYER_ID
      : this.beforeIdAboveLayerValue(layerValue);
    this.map.addLayer(
      { id: layerId, type: "raster" as const, source: sourceId, paint: { "raster-opacity": 0 } },
      beforeId,
    );
    // `-transition` n'existe pas dans le type `paint` généré par maplibre-gl-style-spec (bien que
    // supporté à l'exécution pour toute propriété transitionnable) — il faut donc passer par
    // `setPaintProperty`, dont la signature accepte un `name` en `string` non contraint.
    this.map.setPaintProperty(layerId, "raster-opacity-transition", {
      duration: SHAPE_FADE_IN_DURATION_MS,
    });
    this.rasterImageLayers.set(shape.id, { sourceId, layerId, layerValue, resolvedSrc, opacity: targetOpacity });
    // Le layer est créé à opacité 0 ci-dessus ; on ne repasse à sa cible (`Shape.image.opacity`,
    // défaut 1) qu'au prochain frame pour que ce premier état soit effectivement peint avant la
    // transition — sinon les deux valeurs se compressent dans le même commit de style et rien ne s'anime.
    const map = this.map;
    requestAnimationFrame(() => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, "raster-opacity", targetOpacity);
      }
    });
  }

  /**
   * `src` aux coins arrondis (`ShapeImage.radius`) : une texture raster MapLibre ne se découpe pas,
   * l'image est donc redessinée une fois dans un canevas, puis servie en `blob:` depuis un cache.
   * `null` tant que ce dessin est en cours — la forme est redessinée à la fin. Un échec (image sans
   * CORS) retombe sur l'image d'origine, aux coins droits.
   */
  private roundedRasterSrc(src: string, radius: number): string | null {
    if (radius <= 0) return src;

    // Au-delà de 0,5, c'est toujours un cercle : une seule entrée de cache.
    const effectiveRadius = Math.min(radius, 0.5);
    const key = `${effectiveRadius}|${src}`;
    const cached = this.roundedImageSrcs.get(key);
    if (cached !== undefined) return cached;

    // Dessiné après une courte pause, en gardant le précédent affiché : un curseur en demande un par cran.
    clearTimeout(this.roundedImageTimers.get(src));
    this.roundedImageTimers.set(src, setTimeout(() => {
      this.roundedImageTimers.delete(src);
      void this.drawRoundedImage(src, effectiveRadius, key);
    }, ROUNDED_IMAGE_DRAW_DELAY_MS));

    return this.lastRoundedImageSrcs.get(src) ?? null;
  }

  private async drawRoundedImage(src: string, radius: number, key: string): Promise<void> {
    if (!this.roundedSourceImages.has(src)) {
      this.roundedSourceImages.set(src, new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
      }));
    }

    const img = await this.roundedSourceImages.get(src)!;
    let rounded = src;

    if (img) {
      try {
        const ratio = Math.min(1, ROUNDED_IMAGE_MAX_SIDE_PX / Math.max(img.naturalWidth, img.naturalHeight));
        const width = Math.max(1, Math.round(img.naturalWidth * ratio));
        const height = Math.max(1, Math.round(img.naturalHeight * ratio));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d")!;
        context.beginPath();
        if (radius >= 0.5) context.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, Math.PI * 2);
        else context.roundRect(0, 0, width, height, radius * Math.min(width, height));
        context.clip();
        context.drawImage(img, 0, 0, width, height);
        // `toBlob` : encodage hors du fil principal, contrairement à `toDataURL`.
        const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/png"));
        rounded = blob ? URL.createObjectURL(blob) : src;
      } catch {
        rounded = src;
      }
    }

    // Cache borné par image ; le `blob:` évincé est libéré (la carte a déjà chargé ce qu'elle affiche).
    this.roundedImageSrcs.delete(key);
    this.roundedImageSrcs.set(key, rounded);
    const sameSrc = [...this.roundedImageSrcs.keys()].filter(k => k.endsWith(`|${src}`));
    sameSrc.slice(0, Math.max(0, sameSrc.length - ROUNDED_IMAGE_CACHE_PER_SRC)).forEach(k => {
      const evicted = this.roundedImageSrcs.get(k);
      this.roundedImageSrcs.delete(k);
      if (evicted?.startsWith("blob:") && evicted !== this.lastRoundedImageSrcs.get(src)) URL.revokeObjectURL(evicted);
    });

    this.lastRoundedImageSrcs.set(src, rounded);
    this.renderShapes(this.lastShapes);
  }

  private removeRasterShapeImage(shapeId: string): void {
    const existing = this.rasterImageLayers.get(shapeId);
    if (!existing || !this.map) return;
    this.map.removeLayer(existing.layerId);
    this.map.removeSource(existing.sourceId);
    this.rasterImageLayers.delete(shapeId);
  }

  /** Exclut (ou réintègre) une ou plusieurs formes de la source principale, le temps qu'elles soient
   * éditées — une seule forme en édition classique (`enableEditMode`), ou tout un groupe pendant un
   * déplacement de sélection multiple (voir `handleSelectionMoveMove`), jamais les deux à la fois.
   * Appliqué à TOUTES les bandes courantes (une forme éditée peut appartenir à n'importe laquelle),
   * et mémorisé (`currentEditingFilterIds`) pour qu'une bande créée en cours d'édition en hérite
   * aussitôt — voir `ensureLayerBand`. */
  private setEditingShapeFilter(shapeIds: string[] | null): void {
    if (!this.map) return;
    const previousIds = this.currentEditingFilterIds;
    this.currentEditingFilterIds = shapeIds && shapeIds.length > 0 ? shapeIds : null;
    this.updateRasterImageLayerPositionsForEditingChange(previousIds, this.currentEditingFilterIds);
    for (const band of this.shapeLayerBands.values()) {
      this.applyEditingFilterToBand(band, this.currentEditingFilterIds);
    }
    this.map.setFilter(
      SHAPES_EXTRUSION_LAYER_ID,
      this.currentEditingFilterIds
        ? (["all", SHAPES_EXTRUSION_FILTER, ["!", ["in", ["get", "id"], ["literal", this.currentEditingFilterIds]]]] as maplibregl.FilterSpecification)
        : SHAPES_EXTRUSION_FILTER,
    );
    // Le mur d'une ligne en cours d'édition suivrait sinon son ancien tracé pendant le geste.
    this.map.setFilter(
      LINE_WALL_LAYER_ID,
      this.currentEditingFilterIds ? ["!", ["in", ["get", "id"], ["literal", this.currentEditingFilterIds]]] : null,
    );
    // Même exclusion que pour les bandes ci-dessus, mais sur le layer de labels (pas de notion de
    // bande pour lui, voir SHAPES_TEXT_SOURCE_ID) : sans ça, le label de la forme en cours d'édition
    // resterait figé à son ancienne position pendant tout le drag, en double avec sa version live
    // dans EDITING_SHAPE_TEXT_LAYER_ID.
    const editingExclusion: maplibregl.ExpressionSpecification | null = this.currentEditingFilterIds
      ? ["!", ["in", ["get", "id"], ["literal", this.currentEditingFilterIds]]]
      : null;
    for (const [layerId, orientationFilter] of [
      [SHAPES_TEXT_LAYER_ID, GROUND_TEXT_FILTER],
      [SHAPES_TEXT_UPRIGHT_LAYER_ID, UPRIGHT_TEXT_FILTER],
    ] as const) {
      this.map.setFilter(
        layerId,
        (editingExclusion ? ["all", orientationFilter, editingExclusion] : orientationFilter) as maplibregl.FilterSpecification,
      );
    }
  }

  /** Le fond/la bordure d'une forme en cours d'édition sont dessinés par les layers fixes
   * `editing-shape-fill`/`editing-shape-outline*` (voir `init()`), placés au-dessus de TOUTES les
   * bandes/images pour que la forme éditée reste visible par-dessus d'éventuelles formes
   * chevauchantes. Le layer d'image raster d'une forme (voir `upsertRasterShapeImage`), lui, est créé
   * une fois pour toutes juste au-dessus de sa propre bande — tant que la forme n'est pas éditée, ça
   * suffit à garder son image au-dessus de son propre fond. Mais dès qu'elle passe dans la source
   * d'édition, son fond "saute" par-dessus TOUT, y compris son image restée en place plus bas — d'où
   * ce remontage explicite juste sous les labels (`SHAPES_TEXT_LAYER_ID`), au-dessus de tout
   * l'empilement d'édition, et le remontage inverse quand la forme sort d'édition. */
  private updateRasterImageLayerPositionsForEditingChange(
    previousIds: string[] | null,
    nextIds: string[] | null,
  ): void {
    if (!this.map) return;
    const previousSet = new Set(previousIds ?? []);
    const nextSet = new Set(nextIds ?? []);
    for (const id of nextSet) {
      if (previousSet.has(id)) continue;
      const layer = this.rasterImageLayers.get(id);
      if (layer) this.map.moveLayer(layer.layerId, SHAPES_TEXT_LAYER_ID);
    }
    for (const id of previousSet) {
      if (nextSet.has(id)) continue;
      const layer = this.rasterImageLayers.get(id);
      if (layer) this.map.moveLayer(layer.layerId, this.beforeIdAboveLayerValue(layer.layerValue));
    }
  }

  private applyEditingFilterToBand(band: ShapeLayerBand, excludedIds: string[] | null): void {
    if (!this.map) return;
    const valueFilter = bandLayerFilter(band.layerValue);
    const idFilter: maplibregl.FilterSpecification | null =
      excludedIds ? ["!", ["in", ["get", "id"], ["literal", excludedIds]]] : null;
    this.map.setFilter(band.haloLayerId, (idFilter ? ["all", valueFilter, idFilter] : valueFilter) as maplibregl.FilterSpecification);
    this.map.setFilter(
      band.fillLayerId,
      (idFilter
        ? ["all", valueFilter, NOT_LINE_FILTER, idFilter]
        : ["all", valueFilter, NOT_LINE_FILTER]) as maplibregl.FilterSpecification,
    );
    this.map.setFilter(
      band.outlineLayerId,
      (idFilter
        ? ["all", valueFilter, NOT_DEFAULT_BORDER_FILTER, NOT_BORDER_DASHED_FILTER, idFilter]
        : ["all", valueFilter, NOT_DEFAULT_BORDER_FILTER, NOT_BORDER_DASHED_FILTER]) as maplibregl.FilterSpecification,
    );
    this.map.setFilter(
      band.outlineDashedLayerId,
      (idFilter
        ? ["all", valueFilter, NOT_DEFAULT_BORDER_FILTER, BORDER_DASHED_FILTER, idFilter]
        : ["all", valueFilter, NOT_DEFAULT_BORDER_FILTER, BORDER_DASHED_FILTER]) as maplibregl.FilterSpecification,
    );
    this.map.setFilter(
      band.outlineDefaultLayerId,
      (idFilter
        ? ["all", valueFilter, DEFAULT_BORDER_FILTER, idFilter]
        : ["all", valueFilter, DEFAULT_BORDER_FILTER]) as maplibregl.FilterSpecification,
    );
    this.map.setFilter(band.hoverOutlineLayerId, (idFilter ? ["all", valueFilter, idFilter] : valueFilter) as maplibregl.FilterSpecification);
    this.map.setFilter(band.circleHaloLayerId, (idFilter ? ["all", valueFilter, idFilter] : valueFilter) as maplibregl.FilterSpecification);
    this.map.setFilter(band.circleLayerId, (idFilter ? ["all", valueFilter, idFilter] : valueFilter) as maplibregl.FilterSpecification);
    this.map.setFilter(
      band.lineHitLayerId,
      (idFilter
        ? ["all", valueFilter, IS_LINE_FILTER, idFilter]
        : ["all", valueFilter, IS_LINE_FILTER]) as maplibregl.FilterSpecification,
    );
  }

  /** Met à jour la source légère dédiée à la ou les formes en cours d'édition (1 feature pour
   * `enableEditMode`, potentiellement plusieurs pendant un déplacement de sélection multiple).
   * `selected` : voir `shapesToGeoJSON` — `true` pour les appels de sélection multiple. */
  private updateEditingShapeSource(shapes: Shape[], selected = false): void {
    this.lastEditingShapes = { shapes, selected };
    const source = this.map?.getSource(EDITING_SHAPE_SOURCE_ID);
    if (source && source.type === "geojson") {
      (source as maplibregl.GeoJSONSource).setData(
        shapesToGeoJSON(shapes, this.editMode, this.colors, selected, (id) => this.numericFeatureId(id), {}, this.hoverPreviewShapeIds),
      );
    }

    const textSource = this.map?.getSource(EDITING_SHAPE_TEXT_SOURCE_ID);
    if (textSource && textSource.type === "geojson") {
      (textSource as maplibregl.GeoJSONSource).setData(shapesTextToGeoJSON(shapes, this.map!, null, this.themes, this.defaultTextRotationDeg(), this.hoverPreviewShapeIds));
    }
  }

  /**
   * Coalesce les évènements `"zoom"` (un geste en émet plusieurs par frame) en UN SEUL appel à
   * `refreshShapesTextSize` par frame réellement rendue (`requestAnimationFrame`) — recalculer à
   * chaque évènement brut serait redondant (plusieurs arrivent avant le prochain repaint) sans rien
   * apporter de plus fluide, `measureText` étant de toute façon sub-milliseconde même pour un
   * millier de formes texte (voir `fitTextSizePx`).
   */
  private scheduleShapesTextSizeRefresh(): void {
    if (this.shapesTextSizeRefreshScheduled) return;
    this.shapesTextSizeRefreshScheduled = true;
    requestAnimationFrame(() => {
      this.shapesTextSizeRefreshScheduled = false;
      this.refreshShapesTextSize();
    });
  }

  /**
   * Recalcule `SHAPES_TEXT_SOURCE_ID` (labels des formes non éditées) pour le zoom courant — les
   * tailles portées par `shapesTextToGeoJSON` sont exprimées AU ZOOM DE L'APPEL (voir son
   * commentaire), donc périmées dès que le zoom change sans passage par `renderShapes`. Appelé à
   * chaque frame d'un zoom en cours (voir `scheduleShapesTextSizeRefresh`, sur `"zoom"`) ET sur
   * `"moveend"` (voir `init()`, comme `updateImageOverlayMounts` juste au-dessus) — ce dernier
   * couvre aussi un `setCenter`/`setZoom` programmatique instantané (pas de `"zoom"` intermédiaire)
   * et sert de filet de sécurité si la dernière frame de zoom n'avait pas encore recalculé.
   */
  private refreshShapesTextSize(): void {
    const textSource = this.map?.getSource(SHAPES_TEXT_SOURCE_ID);
    if (textSource && textSource.type === "geojson") {
      (textSource as maplibregl.GeoJSONSource).setData(this.shapesTextSourceData());
    }
  }

  /**
   * Recalcule `SHAPES_TEXT_SOURCE_ID` sur un changement de survol (voir `handleShapeMouseMove`/
   * `clearHoveredShape`) — nécessaire en plus du feature-state `"hovered"` (voir `shapesToGeoJSON`,
   * qui suffit pour fond/bordure) car `Shape.hoverText` change le TEXTE affiché lui-même, pas
   * seulement une couleur : `fitTextSizePx` doit être recalculé pour ce nouveau texte, donc toute la
   * source doit être régénérée plutôt qu'un simple bascule de state. Coût négligeable même à l'échelle
   * réelle (voir le commentaire de `scheduleShapesTextSizeRefresh` : `measureText` reste
   * sub-milliseconde pour un millier de formes), et ne se déclenche qu'aux transitions de survol
   * (pas à chaque `mousemove`, voir l'appelant).
   */
  private refreshShapesTextForHover(): void {
    const textSource = this.map?.getSource(SHAPES_TEXT_SOURCE_ID);
    if (textSource && textSource.type === "geojson") {
      (textSource as maplibregl.GeoJSONSource).setData(this.shapesTextSourceData());
    }
  }

  private shapesTextSourceData(): GeoJSON.FeatureCollection {
    const collection = this.markUnderZoom(
      shapesTextToGeoJSON(this.lastZoomVisibleShapes, this.map!, this.hoveredShapeId, this.themes, this.defaultTextRotationDeg(), this.hoverPreviewShapeIds),
    );
    for (const feature of collection.features) {
      feature.id = this.numericFeatureId(String(feature.properties!.id));
    }
    return collection;
  }

  private startShapesFadeIn(shapeIds: string[]): void {
    if (!this.map || shapeIds.length === 0) return;
    const now = performance.now();
    for (const shapeId of shapeIds) {
      this.shapeFadeInStartTimes.set(shapeId, now);
      this.setShapeFadeIn(shapeId, 0);
    }
    this.shapeFadeInFrame ??= requestAnimationFrame(this.tickShapesFadeIn);
  }

  private tickShapesFadeIn = (): void => {
    this.shapeFadeInFrame = null;
    if (!this.map) return;
    const now = performance.now();
    // Le fondu n'avance qu'une fois les nouvelles données prêtes : avec beaucoup de formes, leur
    // découpage en tuiles en consommerait sinon le début.
    if (!this.map.isSourceLoaded(SHAPES_SOURCE_ID) || !this.map.isSourceLoaded(SHAPES_TEXT_SOURCE_ID)) {
      for (const shapeId of this.shapeFadeInStartTimes.keys()) this.shapeFadeInStartTimes.set(shapeId, now);
      this.shapeFadeInFrame = requestAnimationFrame(this.tickShapesFadeIn);
      return;
    }
    for (const [shapeId, startTime] of this.shapeFadeInStartTimes) {
      const progress = Math.min(1, (now - startTime) / SHAPE_FADE_IN_DURATION_MS);
      this.setShapeFadeIn(shapeId, 1 - (1 - progress) ** 2);
      if (progress === 1) this.shapeFadeInStartTimes.delete(shapeId);
    }
    if (this.shapeFadeInStartTimes.size > 0) this.shapeFadeInFrame = requestAnimationFrame(this.tickShapesFadeIn);
  };

  private setShapeFadeIn(shapeId: string, opacity: number): void {
    this.setShapeFeatureState(shapeId, { fadeIn: opacity });
    this.map?.setFeatureState({ source: SHAPES_TEXT_SOURCE_ID, id: this.numericFeatureId(shapeId) }, { fadeIn: opacity });
  }

  private stopShapesFadeIn(): void {
    if (this.shapeFadeInFrame !== null) cancelAnimationFrame(this.shapeFadeInFrame);
    this.shapeFadeInFrame = null;
    this.shapeFadeInStartTimes.clear();
  }
}
